import React, { useState, useRef, useCallback, useEffect } from 'react';

import axios from 'axios';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, UnderlineType } from 'docx';
import { saveAs } from 'file-saver';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
// import VoiceInterface from './VoiceInterface';
import { conv2d } from '@tensorflow/tfjs-core';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Add CSS animations for pulse and spinner effects
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.9;
    }
  }
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  
  @media print {
    body * {
      visibility: hidden;
    }
    #analysis-results, #analysis-results * {
      visibility: visible;
    }
    #analysis-results {
      position: absolute;
      left: 0;
      top: 0;
    }
  }
`;
if (!document.querySelector('style[data-pulse-animation]')) {
  styleSheet.setAttribute('data-pulse-animation', 'true');
  document.head.appendChild(styleSheet);
}

// Comprehensive Emergency Conditions Database
const EMERGENCY_CONDITIONS = [
  // ==================== CARDIOVASCULAR EMERGENCIES ====================
  {
    condition: 'Bradycardia',
    icd10: 'R00.1',
    category: 'Cardiovascular',
    severity: 'High',
    vitalSigns: {
      hr: { min: null, max: 50 },
      rr: { min: null, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Dizziness or syncope',
      'Fatigue or weakness',
      'Chest pain',
      'Shortness of breath',
      'Confusion (if severe)'
    ],
    confirmatoryFindings: [
      'ECG showing sinus bradycardia (HR < 50)',
      'ECG showing heart block (2nd or 3rd degree)',
      'Presence of symptoms (dizziness, syncope) strengthens diagnosis',
      'Normal HR on ECG would rule out',
      'Medication history (beta-blockers, calcium channel blockers, digoxin)'
    ],
    differentials: ['Hypothyroidism', 'Increased intracranial pressure', 'Athletic conditioning', 'Sick sinus syndrome', 'Medication effect'],
    redFlags: [
      'HR < 40 bpm',
      'Symptomatic (syncope, altered consciousness)',
      'Evidence of heart block on ECG',
      'Hypotension (SBP < 90)'
    ],
    assessmentPathway: 'ECG (12-lead) → Continuous cardiac monitoring → Electrolytes → Thyroid function tests → Consider atropine if symptomatic'
  },
  
  {
    condition: 'Tachycardia',
    icd10: 'R00.0',
    category: 'Cardiovascular',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: 120, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Palpitations',
      'Chest discomfort',
      'Dizziness',
      'Shortness of breath',
      'Anxiety'
    ],
    confirmatoryFindings: [
      'ECG showing narrow complex tachycardia (SVT) or wide complex (VT)',
      'HR > 150 with hemodynamic instability confirms urgency',
      'Regular vs irregular rhythm guides differential',
      'Response to vagal maneuvers (suggests SVT)',
      'Elevated troponin if demand ischemia present'
    ],
    differentials: ['Supraventricular tachycardia (SVT)', 'Atrial fibrillation', 'Ventricular tachycardia', 'Sinus tachycardia (physiologic)', 'Thyrotoxicosis'],
    redFlags: [
      'HR > 150 with hypotension',
      'Chest pain with tachycardia',
      'Altered consciousness',
      'Signs of shock'
    ],
    assessmentPathway: 'ECG (12-lead) → Assess hemodynamic stability → Vagal maneuvers if SVT → Consider adenosine or cardioversion → Electrolytes → Troponin'
  },

  {
    condition: 'Acute Myocardial Infarction (STEMI)',
    icd10: 'I21.9',
    category: 'Cardiovascular',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 50, max: 120 },
      rr: { min: 16, max: 30 },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 95 }
    },
    presentations: [
      'Severe crushing chest pain > 20 minutes',
      'Pain radiating to left arm, jaw, or back',
      'Diaphoresis',
      'Nausea/vomiting',
      'Shortness of breath',
      'Sense of impending doom'
    ],
    confirmatoryFindings: [
      'ECG showing ST elevation > 1mm in 2+ contiguous leads',
      'Elevated troponin (though may be delayed 4-6 hours)',
      'New LBBB on ECG',
      'Regional wall motion abnormality on echo',
      'Reciprocal ST depression'
    ],
    differentials: ['Unstable angina', 'Pericarditis', 'Pulmonary embolism', 'Aortic dissection', 'Esophageal spasm'],
    redFlags: [
      'ST elevation on ECG',
      'Hypotension with chest pain',
      'Cardiogenic shock',
      'New heart murmur (papillary muscle rupture)'
    ],
    assessmentPathway: 'ECG within 10 minutes → Aspirin 300mg → Activate cath lab → Troponin → Dual antiplatelet therapy → Consider thrombolysis if PCI not available'
  },

  {
    condition: 'Atrial Fibrillation',
    icd10: 'I48.91',
    category: 'Cardiovascular',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: 80, max: 160 },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Irregularly irregular pulse',
      'Palpitations',
      'Shortness of breath',
      'Fatigue',
      'May be asymptomatic'
    ],
    confirmatoryFindings: [
      'ECG showing absence of P waves and irregularly irregular rhythm',
      'Variable ventricular response rate',
      'Echo may show atrial thrombus',
      'Elevated BNP if heart failure present'
    ],
    differentials: ['Atrial flutter', 'Multifocal atrial tachycardia', 'Sinus rhythm with frequent PACs'],
    redFlags: [
      'Rapid ventricular response (RVR > 140)',
      'Hemodynamic instability',
      'Active chest pain',
      'Signs of stroke'
    ],
    assessmentPathway: 'ECG → Assess onset (<48hrs vs >48hrs) → Rate vs rhythm control → Anticoagulation assessment → Echo → Thyroid function'
  },

  {
    condition: 'Hypertensive Emergency',
    icd10: 'I16.9',
    category: 'Cardiovascular',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: 180, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Severe headache',
      'Visual changes',
      'Chest pain',
      'Shortness of breath',
      'Altered mental status',
      'Seizures'
    ],
    confirmatoryFindings: [
      'BP > 180/120 with end-organ damage',
      'Papilledema on fundoscopy',
      'Elevated troponin (if cardiac involvement)',
      'Elevated creatinine (if renal involvement)',
      'CT head showing hemorrhage or edema'
    ],
    differentials: ['Hypertensive urgency (no end-organ damage)', 'Stroke', 'Encephalopathy', 'Acute coronary syndrome'],
    redFlags: [
      'Altered mental status',
      'Chest pain',
      'Acute pulmonary edema',
      'Acute kidney injury',
      'Aortic dissection'
    ],
    assessmentPathway: 'Assess for end-organ damage → ECG → Troponin → Renal function → Urinalysis → CT head if neurological symptoms → IV antihypertensive (labetalol, nicardipine)'
  },

  // ==================== RESPIRATORY EMERGENCIES ====================
  
  {
    condition: 'Respiratory Depression',
    icd10: 'R06.89',
    category: 'Respiratory',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: 10 },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 92 }
    },
    presentations: [
      'Decreased respiratory rate (< 10/min)',
      'Shallow breathing',
      'Decreased level of consciousness',
      'Cyanosis',
      'Pinpoint pupils (if opioid-related)'
    ],
    confirmatoryFindings: [
      'ABG showing elevated pCO2 (> 45 mmHg)',
      'Decreased SpO2 despite supplemental oxygen',
      'Positive urine toxicology for opioids/benzodiazepines',
      'Response to naloxone confirms opioid cause',
      'History of CNS depressant use'
    ],
    differentials: ['Opioid overdose', 'Benzodiazepine overdose', 'CNS trauma/stroke', 'Neuromuscular disorder', 'Severe metabolic derangement'],
    redFlags: [
      'RR < 8/min',
      'Apneic episodes',
      'GCS < 8',
      'SpO2 < 85% on room air'
    ],
    assessmentPathway: 'Airway assessment → Consider bag-valve-mask ventilation → Naloxone if opioid suspected → ABG → Urine toxicology → Consider intubation if GCS < 8'
  },

  {
    condition: 'Acute Asthma Exacerbation',
    icd10: 'J45.901',
    category: 'Respiratory',
    severity: 'Moderate-Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 25, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 92 }
    },
    presentations: [
      'Wheezing',
      'Shortness of breath',
      'Chest tightness',
      'Prolonged expiratory phase',
      'Use of accessory muscles',
      'Unable to complete sentences'
    ],
    confirmatoryFindings: [
      'Peak flow < 50% predicted confirms severe',
      'Decreased breath sounds (silent chest = life-threatening)',
      'Hyperinflation on CXR',
      'Response to bronchodilators',
      'Eosinophilia on FBC'
    ],
    differentials: ['COPD exacerbation', 'Pneumonia', 'Pulmonary embolism', 'Anaphylaxis', 'Foreign body aspiration'],
    redFlags: [
      'Silent chest (no wheeze)',
      'Altered consciousness',
      'Cyanosis',
      'SpO2 < 90%',
      'Unable to speak'
    ],
    assessmentPathway: 'Salbutamol nebulizer → Ipratropium nebulizer → Oral/IV corticosteroids → Peak flow measurement → CXR → ABG if severe → Consider IV magnesium → ICU if life-threatening'
  },

  {
    condition: 'Pneumonia',
    icd10: 'J18.9',
    category: 'Respiratory',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 94 },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Productive cough',
      'Fever',
      'Pleuritic chest pain',
      'Shortness of breath',
      'Crackles on auscultation',
      'Confusion (elderly)'
    ],
    confirmatoryFindings: [
      'CXR showing consolidation',
      'Elevated WCC (> 15,000)',
      'Elevated CRP',
      'Positive sputum culture',
      'Dullness to percussion, increased vocal resonance'
    ],
    differentials: ['Bronchitis', 'Pulmonary embolism', 'Heart failure', 'Lung cancer', 'TB'],
    redFlags: [
      'SpO2 < 90%',
      'RR > 30',
      'Altered consciousness',
      'Hypotension',
      'Multilobar involvement on CXR'
    ],
    assessmentPathway: 'CXR → Sputum culture → Blood cultures → FBC, CRP → Calculate CURB-65 score → Antibiotics (amoxicillin + clarithromycin) → Consider hospital admission if CURB-65 ≥ 2'
  },

  {
    condition: 'Pulmonary Embolism',
    icd10: 'I26.99',
    category: 'Cardiovascular/Respiratory',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: 94 }
    },
    presentations: [
      'Sudden onset dyspnea',
      'Pleuritic chest pain',
      'Hemoptysis',
      'Tachycardia',
      'Unilateral leg swelling (DVT)',
      'Sense of impending doom'
    ],
    confirmatoryFindings: [
      'CTPA showing filling defect in pulmonary artery',
      'Elevated D-dimer (>500)',
      'ECG showing S1Q3T3 pattern, new RBBB, or tachycardia',
      'Elevated troponin/BNP (if massive PE)',
      'Wells score > 4 increases probability'
    ],
    differentials: ['Acute MI', 'Pneumonia', 'Pneumothorax', 'Anxiety/panic attack', 'Costochondritis'],
    redFlags: [
      'Hypotension (massive PE)',
      'Syncope',
      'Right heart strain on echo',
      'Elevated troponin',
      'Oxygen requirement'
    ],
    assessmentPathway: 'Wells score → D-dimer → CTPA (gold standard) → ECG → Troponin/BNP → Echo if massive PE suspected → Anticoagulation (LMWH or DOAC) → Thrombolysis if hemodynamically unstable'
  },

  // ==================== NEUROLOGICAL EMERGENCIES ====================
  
  {
    condition: 'Ischemic Stroke',
    icd10: 'I63.9',
    category: 'Neurological',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Sudden onset focal neurological deficit',
      'Facial droop',
      'Arm weakness',
      'Speech difficulty',
      'Confusion',
      'Visual field defect'
    ],
    confirmatoryFindings: [
      'CT head showing hypodense area (may be normal in first 24 hours)',
      'MRI showing restricted diffusion (DWI)',
      'Carotid ultrasound showing stenosis',
      'ECG showing atrial fibrillation (cardioembolic source)',
      'FAST/NIHSS score documents severity'
    ],
    differentials: ['Hemorrhagic stroke', 'Seizure with Todds paresis', 'Hypoglycemia', 'Hemiplegic migraine', 'Brain tumor'],
    redFlags: [
      'Presentation within thrombolysis window (< 4.5 hours)',
      'Large vessel occlusion',
      'Progressive symptoms',
      'Decreased GCS'
    ],
    assessmentPathway: 'FAST assessment → Glucose check → CT head (non-contrast) → Activate stroke team → Thrombolysis if within 4.5hrs and no contraindications → Consider thrombectomy → Aspirin 300mg (after hemorrhage excluded)'
  },

  {
    condition: 'Seizure (Tonic-Clonic)',
    icd10: 'G40.911',
    category: 'Neurological',
    severity: 'High',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Loss of consciousness',
      'Tonic-clonic movements',
      'Tongue biting',
      'Urinary incontinence',
      'Post-ictal confusion',
      'Muscle aches'
    ],
    confirmatoryFindings: [
      'Witnessed seizure activity',
      'Elevated lactate (post-ictal)',
      'Elevated CK (if prolonged)',
      'Low antiepileptic drug levels (if known epileptic)',
      'EEG showing epileptiform activity',
      'CT/MRI may show structural cause'
    ],
    differentials: ['Syncope', 'Psychogenic non-epileptic seizure', 'Stroke', 'Hypoglycemia', 'Alcohol withdrawal'],
    redFlags: [
      'Status epilepticus (seizure > 5 minutes)',
      'No return to baseline after 30 minutes',
      'First seizure',
      'Focal neurological signs post-ictal',
      'Head trauma'
    ],
    assessmentPathway: 'Protect airway → Recovery position → Benzodiazepine if prolonged (>5min) → Glucose check → Antiepileptic drug levels → CT head if first seizure → Consider LP if meningitis suspected'
  },

  {
    condition: 'Meningitis',
    icd10: 'G03.9',
    category: 'Neurological/Infectious',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: 18, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Severe headache',
      'Neck stiffness',
      'Fever',
      'Photophobia',
      'Altered consciousness',
      'Non-blanching rash (meningococcal)'
    ],
    confirmatoryFindings: [
      'LP showing elevated WCC, low glucose, high protein',
      'Positive CSF gram stain/culture',
      'Positive blood cultures',
      'CT head normal (or shows meningeal enhancement)',
      'Positive Kernigs/Brudzinskis sign'
    ],
    differentials: ['Subarachnoid hemorrhage', 'Encephalitis', 'Migraine', 'Sinusitis'],
    redFlags: [
      'Non-blanching purpuric rash',
      'Altered consciousness',
      'Seizures',
      'Focal neurological signs',
      'Signs of raised ICP'
    ],
    assessmentPathway: 'Blood cultures → CT head (if concern for raised ICP) → LP (unless contraindicated) → Empiric antibiotics immediately (ceftriaxone) → Consider aciclovir if encephalitis suspected → Dexamethasone'
  },

  // ==================== SHOCK & SEPSIS ====================
  
  {
    condition: 'Septic Shock',
    icd10: 'R65.21',
    category: 'Shock/Sepsis',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 22, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Hypotension refractory to fluid resuscitation',
      'Altered mental status',
      'Tachycardia',
      'Tachypnea',
      'Fever or hypothermia',
      'Oliguria',
      'Mottled skin'
    ],
    confirmatoryFindings: [
      'Lactate > 2 mmol/L',
      'Positive blood cultures',
      'WCC > 12 or < 4',
      'Elevated procalcitonin',
      'Elevated creatinine (AKI)',
      'qSOFA score ≥ 2'
    ],
    differentials: ['Hypovolemic shock', 'Cardiogenic shock', 'Anaphylactic shock', 'Neurogenic shock'],
    redFlags: [
      'Persistent hypotension despite 30ml/kg fluid',
      'Lactate > 4',
      'Altered consciousness',
      'Oliguria',
      'Multi-organ failure'
    ],
    assessmentPathway: 'Sepsis 6 bundle (within 1 hour): → Blood cultures → Lactate → Urine output → Broad-spectrum antibiotics → IV fluids (30ml/kg) → Oxygen → Consider vasopressors (noradrenaline) if fluid-refractory → ICU referral'
  },

  {
    condition: 'Hypovolemic Shock',
    icd10: 'R57.1',
    category: 'Shock',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Hypotension',
      'Tachycardia',
      'Cool, clammy skin',
      'Delayed capillary refill (> 2 sec)',
      'Altered mental status',
      'Oliguria',
      'Weakness'
    ],
    confirmatoryFindings: [
      'Elevated lactate (> 2)',
      'Low CVP (if monitored)',
      'Hb drop if hemorrhagic',
      'Response to fluid bolus',
      'Ultrasound showing IVC collapse'
    ],
    differentials: ['Septic shock', 'Cardiogenic shock', 'Distributive shock'],
    redFlags: [
      'Active bleeding',
      'SBP < 70',
      'Altered consciousness',
      'Suspected ruptured AAA'
    ],
    assessmentPathway: 'Two large-bore IV access → Fluid resuscitation (crystalloid bolus 500ml-1L) → Blood products if hemorrhagic → Identify and control bleeding source → Lactate monitoring → Consider massive transfusion protocol'
  },

  // ==================== METABOLIC EMERGENCIES ====================
  
  {
    condition: 'Diabetic Ketoacidosis (DKA)',
    icd10: 'E11.65',
    category: 'Metabolic/Endocrine',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Polyuria, polydipsia',
      'Nausea/vomiting',
      'Abdominal pain',
      'Kussmaul breathing (deep, rapid)',
      'Fruity breath odor (ketones)',
      'Altered consciousness',
      'Dehydration'
    ],
    confirmatoryFindings: [
      'Glucose > 11 mmol/L (>200 mg/dL)',
      'pH < 7.3',
      'Ketones in blood (>3 mmol/L) or urine',
      'Anion gap > 10',
      'Low bicarbonate (< 15)',
      'Elevated potassium initially (then drops with treatment)'
    ],
    differentials: ['Hyperosmolar hyperglycemic state (HHS)', 'Starvation ketosis', 'Alcoholic ketoacidosis', 'Sepsis'],
    redFlags: [
      'pH < 7.0',
      'K+ < 3.5 (risk of arrhythmia with insulin)',
      'GCS < 12',
      'Cerebral edema (headache, vomiting, decreased GCS)'
    ],
    assessmentPathway: 'VBG → Glucose → Ketones → Electrolytes → Fluid resuscitation (0.9% saline) → Fixed rate insulin infusion (0.1 units/kg/hr) → Potassium replacement → Identify precipitant (infection, MI, non-compliance) → Hourly monitoring'
  },

  {
    condition: 'Hypoglycemia',
    icd10: 'E16.2',
    category: 'Metabolic/Endocrine',
    severity: 'High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Confusion',
      'Sweating',
      'Tremor',
      'Palpitations',
      'Hunger',
      'Seizures (if severe)',
      'Altered consciousness'
    ],
    confirmatoryFindings: [
      'Glucose < 4 mmol/L (< 70 mg/dL)',
      'Rapid improvement with glucose administration',
      'History of diabetes or insulin use',
      'C-peptide levels (if insulinoma suspected)'
    ],
    differentials: ['Stroke', 'Seizure', 'Intoxication', 'Sepsis', 'Insulinoma'],
    redFlags: [
      'Glucose < 2.5 mmol/L',
      'Seizures',
      'GCS < 8',
      'No response to oral glucose'
    ],
    assessmentPathway: 'Glucose check → If conscious: oral glucose (15-20g) → If unconscious: IV 10% glucose or IM glucagon → Recheck glucose in 15 minutes → Identify cause (missed meal, excess insulin, sepsis) → Consider admission if recurrent or intentional overdose'
  },

  // ==================== ABDOMINAL EMERGENCIES ====================
  
  {
    condition: 'Acute Appendicitis',
    icd10: 'K35.80',
    category: 'Abdominal',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: 80, max: 110 },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 37.5, max: 39 }
    },
    presentations: [
      'Right lower quadrant pain (McBurneys point)',
      'Pain initially periumbilical then migrating to RLQ',
      'Anorexia',
      'Nausea/vomiting',
      'Low-grade fever',
      'Rebound tenderness',
      'Guarding'
    ],
    confirmatoryFindings: [
      'CT showing inflamed appendix (> 6mm diameter)',
      'Ultrasound showing non-compressible appendix',
      'Elevated WCC (10-15,000)',
      'Elevated CRP',
      'Positive Rovsings sign, psoas sign'
    ],
    differentials: ['Ectopic pregnancy', 'Ovarian torsion', 'PID', 'Mesenteric adenitis', 'Gastroenteritis'],
    redFlags: [
      'Peritonitis (rigid abdomen)',
      'Signs of perforation',
      'Hemodynamic instability',
      'High fever (> 39°C)'
    ],
    assessmentPathway: 'Urinalysis (exclude UTI) → Pregnancy test (females) → CT abdomen → Alvarado score → Surgical consult → Keep nil by mouth → IV fluids → IV antibiotics if perforated → Appendectomy'
  },

  {
    condition: 'Bowel Obstruction',
    icd10: 'K56.60',
    category: 'Abdominal',
    severity: 'High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Colicky abdominal pain',
      'Abdominal distension',
      'Vomiting (may be feculent)',
      'Absolute constipation (no flatus or stool)',
      'High-pitched bowel sounds (early) or absent (late)',
      'Previous abdominal surgery (adhesions)'
    ],
    confirmatoryFindings: [
      'AXR showing dilated bowel loops, air-fluid levels',
      'CT showing transition point',
      'Elevated lactate if ischemic bowel',
      'Elevated WCC if perforation/ischemia'
    ],
    differentials: ['Ileus', 'Pseudo-obstruction', 'Constipation', 'Volvulus'],
    redFlags: [
      'Peritonitis',
      'Elevated lactate (ischemic bowel)',
      'Fever',
      'Tachycardia',
      'Closed-loop obstruction'
    ],
    assessmentPathway: 'Keep nil by mouth → NG tube insertion → IV fluids → AXR → CT abdomen → Lactate → Surgical consult → Consider laparotomy if strangulation/perforation'
  },

  // ==================== TRAUMA ====================
  
  {
    condition: 'Tension Pneumothorax',
    icd10: 'S27.0XXA',
    category: 'Trauma/Respiratory',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 120, max: null },
      rr: { min: 25, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: 90 }
    },
    presentations: [
      'Severe respiratory distress',
      'Tracheal deviation away from affected side',
      'Absent breath sounds on affected side',
      'Hyperresonance to percussion',
      'Distended neck veins',
      'Hypotension',
      'Cyanosis'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis - do NOT wait for imaging',
      'CXR showing complete lung collapse with mediastinal shift (but emergency needle decompression should precede)',
      'Improved hemodynamics after needle decompression confirms'
    ],
    differentials: ['Simple pneumothorax', 'Hemothorax', 'Massive PE', 'Cardiac tamponade'],
    redFlags: [
      'Tracheal deviation',
      'Hemodynamic instability',
      'Severe hypoxia',
      'Cardiac arrest'
    ],
    assessmentPathway: 'IMMEDIATE needle decompression (2nd intercostal space, midclavicular line) → Chest tube insertion → CXR post-procedure → Monitor for re-expansion'
  },

  {
    condition: 'Traumatic Brain Injury (Severe)',
    icd10: 'S06.9X0A',
    category: 'Trauma/Neurological',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Altered consciousness (GCS < 8)',
      'Amnesia',
      'Vomiting',
      'Seizures',
      'Battle sign (bruising behind ears)',
      'Raccoon eyes (periorbital bruising)',
      'CSF leak from nose/ears',
      'Focal neurological deficits'
    ],
    confirmatoryFindings: [
      'CT head showing intracranial hemorrhage, contusions, or midline shift',
      'Declining GCS',
      'Cushing triad (bradycardia, hypertension, irregular breathing) = raised ICP'
    ],
    differentials: ['Concussion (mild TBI)', 'Intoxication', 'Hypoglycemia', 'Stroke'],
    redFlags: [
      'GCS < 8',
      'Dilated/unreactive pupils',
      'Posturing',
      'Skull fracture',
      'Penetrating injury'
    ],
    assessmentPathway: 'C-spine immobilization → ABCDE assessment → GCS monitoring → CT head (non-contrast) → Neurosurgical consult → Consider intubation if GCS < 8 → ICP monitoring → Mannitol/hypertonic saline if raised ICP'
  },

  // ==================== POISONING/TOXICOLOGY ====================
  
  {
    condition: 'Opioid Overdose',
    icd10: 'T40.2X1A',
    category: 'Poisoning/Toxicology',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: null, max: 60 },
      rr: { min: null, max: 8 },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: 90 }
    },
    presentations: [
      'Respiratory depression (RR < 10)',
      'Pinpoint pupils (miosis)',
      'Decreased consciousness',
      'Bradycardia',
      'Hypotension',
      'Track marks (IV drug use)',
      'Cyanosis'
    ],
    confirmatoryFindings: [
      'Response to naloxone (reversal confirms diagnosis)',
      'Positive urine toxicology for opioids',
      'ABG showing respiratory acidosis',
      'Circumstantial evidence (needles, pills)'
    ],
    differentials: ['Benzodiazepine overdose', 'Alcohol intoxication', 'Stroke', 'Hypoglycemia'],
    redFlags: [
      'Apnea',
      'GCS < 8',
      'Cardiac arrest',
      'Mixed overdose (opioid + benzodiazepine)'
    ],
    assessmentPathway: 'Airway management → Naloxone (0.4-2mg IV/IM/IN) → Repeat naloxone if inadequate response → Bag-valve-mask if needed → Observe for re-sedation (naloxone half-life shorter than most opioids) → Consider naloxone infusion → Admit for observation'
  },

  {
    condition: 'Paracetamol (Acetaminophen) Overdose',
    icd10: 'T39.1X1A',
    category: 'Poisoning/Toxicology',
    severity: 'High',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Often asymptomatic initially',
      'Nausea/vomiting (early)',
      'Abdominal pain',
      'Jaundice (delayed, 24-72 hours)',
      'Confusion (hepatic encephalopathy)',
      'Coagulopathy'
    ],
    confirmatoryFindings: [
      'Paracetamol level plotted on treatment nomogram',
      'Elevated ALT/AST (hepatotoxicity)',
      'Elevated INR (severe hepatotoxicity)',
      'Elevated creatinine (renal failure)',
      'Metabolic acidosis (severe)'
    ],
    differentials: ['Other hepatotoxins', 'Viral hepatitis', 'Alcoholic hepatitis'],
    redFlags: [
      'Ingestion > 150mg/kg or > 10g in adults',
      'Elevated paracetamol level on nomogram',
      'INR > 2',
      'Encephalopathy',
      'Acute liver failure'
    ],
    assessmentPathway: 'Timing of ingestion crucial → Paracetamol level at 4 hours post-ingestion → Plot on nomogram → N-acetylcysteine (NAC) if above treatment line → LFTs, INR, creatinine → Repeat bloods at 24hrs → Liver transplant assessment if fulminant hepatic failure'
  },

  // ==================== ALLERGIC/IMMUNOLOGIC ====================
  
  {
    condition: 'Anaphylaxis',
    icd10: 'T78.2XXA',
    category: 'Allergic/Immunologic',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 22, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: 92 }
    },
    presentations: [
      'Acute onset (minutes)',
      'Urticaria, angioedema',
      'Stridor, wheeze',
      'Shortness of breath',
      'Hypotension',
      'Nausea, vomiting, diarrhea',
      'Sense of impending doom'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis',
      'Elevated serum tryptase (within 1-4 hours)',
      'Response to adrenaline confirms',
      'History of allergen exposure (food, drug, insect sting)'
    ],
    differentials: ['Vasovagal syncope', 'Panic attack', 'Asthma exacerbation', 'Sepsis'],
    redFlags: [
      'Airway compromise (stridor)',
      'Hypotension',
      'Decreased consciousness',
      'Respiratory distress'
    ],
    assessmentPathway: 'IMMEDIATE IM adrenaline (0.5mg, 1:1000) into anterolateral thigh → Repeat every 5 minutes if needed → High-flow oxygen → IV fluids (if hypotensive) → Antihistamine (chlorphenamine) → Corticosteroid (hydrocortisone) → Observe for biphasic reaction (4-6 hours) → Prescribe EpiPen → Allergy referral'
  },

  // ==================== OBSTETRIC EMERGENCIES ====================
  
  {
    condition: 'Eclampsia',
    icd10: 'O15.00',
    category: 'Obstetric',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: 160, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Seizures in pregnancy (>20 weeks) or postpartum',
      'Severe hypertension',
      'Headache',
      'Visual disturbances',
      'Right upper quadrant pain',
      'Hyperreflexia',
      'Altered consciousness'
    ],
    confirmatoryFindings: [
      'BP > 160/110',
      'Proteinuria (> 300mg/24hr or 2+ on dipstick)',
      'Elevated liver enzymes',
      'Thrombocytopenia (< 100,000)',
      'Elevated creatinine'
    ],
    differentials: ['Epilepsy', 'Stroke', 'Intracranial hemorrhage', 'Encephalitis'],
    redFlags: [
      'Seizures',
      'Severe hypertension',
      'HELLP syndrome',
      'Placental abruption',
      'Fetal distress'
    ],
    assessmentPathway: 'Magnesium sulfate IV (4-6g loading, then 1-2g/hr) → Antihypertensive (labetalol/hydralazine) → Seizure management (protect airway) → Urgent obstetric consult → Plan for delivery (only cure) → Continuous fetal monitoring'
  },

  {
    condition: 'Ectopic Pregnancy',
    icd10: 'O00.9',
    category: 'Obstetric',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Missed period or amenorrhea',
      'Lower abdominal pain (unilateral)',
      'Vaginal bleeding',
      'Shoulder tip pain (if ruptured)',
      'Dizziness, syncope (if ruptured)',
      'Cervical motion tenderness'
    ],
    confirmatoryFindings: [
      'Positive pregnancy test (βhCG)',
      'Transvaginal ultrasound showing no intrauterine pregnancy with βhCG > 1500',
      'Free fluid in pouch of Douglas (if ruptured)',
      'Falling Hb if ruptured',
      'Discriminatory βhCG level'
    ],
    differentials: ['Miscarriage', 'Ovarian cyst', 'Appendicitis', 'PID'],
    redFlags: [
      'Hemodynamic instability (ruptured)',
      'Peritonitis',
      'Shoulder tip pain',
      'Falling Hb'
    ],
    assessmentPathway: 'Pregnancy test → βhCG level → Transvaginal ultrasound → Group and save → If stable: methotrexate vs expectant management → If ruptured/unstable: emergency laparoscopy/laparotomy → Rhogam if Rh-negative'
  },

  // ==================== RENAL EMERGENCIES ====================
  
  {
    condition: 'Acute Kidney Injury',
    icd10: 'N17.9',
    category: 'Renal',
    severity: 'High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: 110 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Oliguria (< 0.5ml/kg/hr)',
      'Confusion',
      'Nausea/vomiting',
      'Edema',
      'Fluid overload',
      'Uremic features (if severe)'
    ],
    confirmatoryFindings: [
      'Elevated creatinine (≥50% increase from baseline)',
      'Elevated urea',
      'Low urine output',
      'Elevated potassium',
      'Metabolic acidosis',
      'Ultrasound showing hydronephrosis (if obstructive)'
    ],
    differentials: ['Chronic kidney disease', 'Pre-renal AKI (dehydration)', 'Intrinsic AKI (ATN)', 'Post-renal AKI (obstruction)'],
    redFlags: [
      'K+ > 6.5 mmol/L',
      'Severe metabolic acidosis',
      'Pulmonary edema',
      'Uremic pericarditis',
      'Indications for dialysis (AEIOU)'
    ],
    assessmentPathway: 'Urinalysis → Renal ultrasound → Identify cause (pre/intrinsic/post-renal) → Fluid assessment → Stop nephrotoxic drugs → Treat hyperkalemia if present → Renal replacement therapy if indicated → Nephrology consult'
  },

  // ==================== PSYCHIATRIC EMERGENCIES ====================
  
  {
    condition: 'Acute Psychosis',
    icd10: 'F29',
    category: 'Psychiatric',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Hallucinations (auditory, visual)',
      'Delusions',
      'Disorganized speech',
      'Agitation',
      'Paranoia',
      'Impaired insight'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis based on history and mental state exam',
      'Urine drug screen (exclude substance-induced)',
      'CT head/LP if first episode (exclude organic cause)',
      'Collateral history from family/carers'
    ],
    differentials: ['Schizophrenia', 'Drug-induced psychosis', 'Delirium', 'Mania', 'Severe depression with psychotic features'],
    redFlags: [
      'Risk to self or others',
      'Command hallucinations',
      'Severe agitation',
      'Unable to care for self'
    ],
    assessmentPathway: 'Mental state examination → Risk assessment (suicide, violence) → Urine drug screen → Consider organic causes (CT head if first episode) → Oral antipsychotic (if accepts) → IM antipsychotic if severely agitated → Psychiatric assessment → Consider involuntary admission'
  },

  {
    condition: 'Suicidal Ideation/Attempt',
    icd10: 'R45.851',
    category: 'Psychiatric',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Expressed intent to harm self',
      'Suicide attempt',
      'Hopelessness',
      'Giving away possessions',
      'Withdrawal from others',
      'Substance abuse'
    ],
    confirmatoryFindings: [
      'Clinical assessment of suicide risk (plan, means, intent)',
      'Previous attempts increase risk',
      'Depression screening (PHQ-9)',
      'Collateral history',
      'Toxicology if overdose'
    ],
    differentials: ['Major depressive disorder', 'Bipolar disorder', 'Substance abuse', 'Personality disorder'],
    redFlags: [
      'Active plan and means',
      'Previous attempts',
      'Social isolation',
      'Psychotic symptoms',
      'Substance intoxication'
    ],
    assessmentPathway: 'Remove means → Constant observation → Risk assessment (SAD PERSONS score) → Treat underlying condition (depression, psychosis) → Psychiatric consultation → Safety plan → Consider involuntary admission if high risk'
  },

  // ==================== MISCELLANEOUS EMERGENCIES ====================
  
  {
    condition: 'Hyperkalemia',
    icd10: 'E87.5',
    category: 'Metabolic',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Often asymptomatic',
      'Muscle weakness',
      'Paresthesias',
      'Nausea',
      'Palpitations',
      'Cardiac arrest (if severe)'
    ],
    confirmatoryFindings: [
      'K+ > 5.5 mmol/L (mild), > 6.0 (moderate), > 6.5 (severe)',
      'ECG changes: peaked T waves, wide QRS, absent P waves, sine wave pattern',
      'Elevated creatinine (if renal cause)',
      'Hemolyzed sample (exclude pseudohyperkalemia)'
    ],
    differentials: ['Pseudohyperkalemia (hemolysis)', 'Acute kidney injury', 'Rhabdomyolysis', 'Tumor lysis syndrome', 'Medication (ACEi, spironolactone)'],
    redFlags: [
      'K+ > 6.5 mmol/L',
      'ECG changes',
      'Arrhythmias',
      'Muscle weakness'
    ],
    assessmentPathway: 'Immediate ECG → If ECG changes: IV calcium gluconate (cardiac protection) → Shift K+ intracellularly: insulin-dextrose, salbutamol nebulizer, sodium bicarbonate → Remove K+: calcium resonium, dialysis if severe → Recheck K+ hourly → Identify and treat cause'
  },

  {
    condition: 'Hypothyroidism (Myxedema Coma)',
    icd10: 'E03.9',
    category: 'Metabolic/Endocrine',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: null, max: 50 },
      rr: { min: null, max: 12 },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null },
      temp: { min: null, max: 35 }
    },
    presentations: [
      'Hypothermia',
      'Bradycardia',
      'Altered consciousness',
      'Hypotension',
      'Hypoventilation',
      'Non-pitting edema',
      'Delayed reflexes'
    ],
    confirmatoryFindings: [
      'Low free T4',
      'Elevated TSH (primary hypothyroidism)',
      'Hyponatremia',
      'Hypoglycemia',
      'Elevated CK',
      'ECG showing bradycardia, low voltage'
    ],
    differentials: ['Sepsis', 'Hypoglycemia', 'Stroke', 'Sedative overdose'],
    redFlags: [
      'Altered consciousness',
      'Hypothermia < 35°C',
      'Hypotension',
      'Respiratory failure'
    ],
    assessmentPathway: 'IV T4 (levothyroxine) loading dose → IV hydrocortisone (cover for adrenal insufficiency) → Passive rewarming → Treat hypoglycemia → ICU admission → Identify precipitant (infection, cold exposure, medication non-compliance)'
  },

  // ==================== ADDITIONAL CARDIOVASCULAR ====================
  
  {
    condition: 'Aortic Dissection',
    icd10: 'I71.00',
    category: 'Cardiovascular',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: 18, max: null },
      sbp: { min: 140, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Sudden severe tearing chest pain radiating to back',
      'Blood pressure differential between arms (>20mmHg)',
      'Syncope',
      'Stroke symptoms',
      'Absent pulses',
      'Aortic regurgitation murmur'
    ],
    confirmatoryFindings: [
      'CT angiography showing intimal flap',
      'CXR showing widened mediastinum',
      'Elevated D-dimer',
      'BP difference between arms',
      'Transoesophageal echo showing dissection'
    ],
    differentials: ['Acute MI', 'Pulmonary embolism', 'Pericarditis', 'Musculoskeletal pain'],
    redFlags: [
      'Hypotension (rupture)',
      'Neurological deficits',
      'Cardiac tamponade',
      'Limb ischemia'
    ],
    assessmentPathway: 'Immediate CT angiography → BP control (labetalol target SBP 100-120) → Analgesia → Cardiothoracic surgical consult → Type A: emergency surgery → Type B: medical management vs TEVAR'
  },

  {
    condition: 'Cardiogenic Shock',
    icd10: 'R57.0',
    category: 'Cardiovascular',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: 94 }
    },
    presentations: [
      'Hypotension',
      'Cool, clammy skin',
      'Altered mental status',
      'Oliguria',
      'Pulmonary edema',
      'Jugular venous distension'
    ],
    confirmatoryFindings: [
      'Elevated troponin (if MI)',
      'Echo showing reduced ejection fraction',
      'Elevated BNP',
      'Pulmonary edema on CXR',
      'Elevated lactate'
    ],
    differentials: ['Septic shock', 'Hypovolemic shock', 'Massive PE'],
    redFlags: [
      'Persistent hypotension',
      'Reduced cardiac output',
      'Multi-organ failure',
      'Cardiac arrest'
    ],
    assessmentPathway: 'Identify cause (MI, arrhythmia, valve rupture) → Echo → Inotropes (dobutamine) → Consider mechanical support (IABP, ECMO) → Urgent revascularization if MI → ICU'
  },

  {
    condition: 'Pericarditis',
    icd10: 'I30.9',
    category: 'Cardiovascular',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: 80, max: 110 },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Sharp chest pain worse on lying flat, better sitting forward',
      'Pain worse with inspiration',
      'Pericardial friction rub',
      'Fever (if infectious)',
      'Dyspnea'
    ],
    confirmatoryFindings: [
      'ECG showing widespread ST elevation with PR depression',
      'Echo showing pericardial effusion',
      'Elevated troponin (if myopericarditis)',
      'Elevated inflammatory markers'
    ],
    differentials: ['Acute MI', 'Pulmonary embolism', 'Pneumonia', 'Costochondritis'],
    redFlags: [
      'Cardiac tamponade',
      'Large pericardial effusion',
      'Hemodynamic instability'
    ],
    assessmentPathway: 'ECG → Echo → NSAIDs (ibuprofen) + colchicine → Treat underlying cause → Monitor for tamponade → Consider pericardiocentesis if tamponade'
  },

  {
    condition: 'Acute Heart Failure',
    icd10: 'I50.9',
    category: 'Cardiovascular',
    severity: 'High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: 22, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 94 }
    },
    presentations: [
      'Severe dyspnea',
      'Orthopnea',
      'Paroxysmal nocturnal dyspnea',
      'Bilateral leg edema',
      'Bibasal crackles',
      'Pink frothy sputum'
    ],
    confirmatoryFindings: [
      'Elevated BNP (>400 pg/mL)',
      'CXR showing pulmonary edema, cardiomegaly',
      'Echo showing reduced EF',
      'Elevated JVP'
    ],
    differentials: ['Pneumonia', 'COPD exacerbation', 'PE', 'Renal failure'],
    redFlags: [
      'Cardiogenic shock',
      'Respiratory failure',
      'Acute MI',
      'Severe hypoxia'
    ],
    assessmentPathway: 'Sit upright → High-flow oxygen → Furosemide IV → Nitrates (if BP adequate) → CPAP/BiPAP if needed → Treat precipitant → Consider intubation if failing'
  },

  // ==================== ADDITIONAL RESPIRATORY ====================

  {
    condition: 'COPD Exacerbation',
    icd10: 'J44.1',
    category: 'Respiratory',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 92 }
    },
    presentations: [
      'Increased dyspnea',
      'Increased sputum production',
      'Sputum purulence',
      'Wheeze',
      'Use of accessory muscles',
      'Pursed lip breathing'
    ],
    confirmatoryFindings: [
      'ABG showing hypercapnia (pCO2 > 45)',
      'CXR showing hyperinflation',
      'Spirometry (not in acute setting)',
      'Response to bronchodilators'
    ],
    differentials: ['Pneumonia', 'Heart failure', 'PE', 'Pneumothorax'],
    redFlags: [
      'Type 2 respiratory failure',
      'Altered consciousness',
      'Hemodynamic instability',
      'Silent chest'
    ],
    assessmentPathway: 'Controlled oxygen (target SpO2 88-92%) → Salbutamol nebulizer → Ipratropium nebulizer → Prednisolone 40mg PO → Antibiotics if purulent sputum → ABG → Consider NIV if pH < 7.35'
  },

  {
    condition: 'Pneumothorax (Spontaneous)',
    icd10: 'J93.0',
    category: 'Respiratory',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 94 }
    },
    presentations: [
      'Sudden onset pleuritic chest pain',
      'Dyspnea',
      'Reduced breath sounds on affected side',
      'Hyperresonance to percussion',
      'Tall, thin body habitus (primary)'
    ],
    confirmatoryFindings: [
      'CXR showing visceral pleural line',
      'Absent lung markings peripherally',
      'CT showing pneumothorax if small'
    ],
    differentials: ['Pulmonary embolism', 'MI', 'Pneumonia', 'Costochondritis'],
    redFlags: [
      'Large pneumothorax (>2cm)',
      'Bilateral pneumothorax',
      'Tension physiology',
      'Underlying lung disease'
    ],
    assessmentPathway: 'CXR → If small (<2cm) and asymptomatic: conservative with oxygen → If large or symptomatic: aspiration or chest drain → Repeat CXR → Refer to respiratory for follow-up'
  },

  {
    condition: 'Pleural Effusion (Large)',
    icd10: 'J90',
    category: 'Respiratory',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: 18, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 94 }
    },
    presentations: [
      'Progressive dyspnea',
      'Pleuritic chest pain',
      'Reduced breath sounds',
      'Dullness to percussion',
      'Reduced tactile fremitus'
    ],
    confirmatoryFindings: [
      'CXR showing blunted costophrenic angle',
      'Ultrasound showing fluid collection',
      'Pleural tap (diagnostic/therapeutic)'
    ],
    differentials: ['Pneumonia', 'Heart failure', 'PE'],
    redFlags: [
      'Massive effusion',
      'Hemodynamic compromise',
      'Empyema',
      'Malignant effusion'
    ],
    assessmentPathway: 'CXR → Ultrasound → Diagnostic pleural tap → Analyze fluid (protein, LDH, pH, cytology, culture) → Therapeutic drain if large → Treat underlying cause'
  },

  {
    condition: 'Aspiration Pneumonitis',
    icd10: 'J69.0',
    category: 'Respiratory',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 92 }
    },
    presentations: [
      'Witnessed aspiration event',
      'Cough',
      'Dyspnea',
      'Fever',
      'Tachypnea',
      'Crackles',
      'Risk factors: dysphagia, decreased consciousness'
    ],
    confirmatoryFindings: [
      'CXR showing right lower lobe infiltrate',
      'History of aspiration',
      'Elevated WCC',
      'Hypoxia'
    ],
    differentials: ['Community-acquired pneumonia', 'Pulmonary edema', 'ARDS'],
    redFlags: [
      'Severe hypoxia',
      'ARDS',
      'Lung abscess',
      'Empyema'
    ],
    assessmentPathway: 'Airway protection → Oxygen → CXR → If chemical pneumonitis: supportive care (no antibiotics initially) → If aspiration pneumonia suspected: antibiotics (amoxicillin-clavulanate) → Consider bronchoscopy if foreign body'
  },

  // ==================== ADDITIONAL NEUROLOGICAL ====================

  {
    condition: 'Subarachnoid Hemorrhage',
    icd10: 'I60.9',
    category: 'Neurological',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 80, max: 120 },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Sudden onset thunderclap headache ("worst headache of life")',
      'Neck stiffness',
      'Photophobia',
      'Vomiting',
      'Decreased consciousness',
      'Focal neurological signs'
    ],
    confirmatoryFindings: [
      'CT head showing hyperdensity in subarachnoid space',
      'LP showing xanthochromia (if CT negative)',
      'CT angiography showing aneurysm',
      'Elevated opening pressure on LP'
    ],
    differentials: ['Meningitis', 'Migraine', 'Cervical artery dissection'],
    redFlags: [
      'Decreased GCS',
      'Seizures',
      'Rebleeding',
      'Hydrocephalus'
    ],
    assessmentPathway: 'Immediate CT head → Neurosurgical referral → CT angiography → Nimodipine (prevent vasospasm) → BP control → Aneurysm coiling or clipping → ICU monitoring'
  },

  {
    condition: 'Status Epilepticus',
    icd10: 'G41.9',
    category: 'Neurological',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Continuous seizure activity > 5 minutes',
      'Recurrent seizures without regaining consciousness',
      'Tonic-clonic movements',
      'Apnea during seizure'
    ],
    confirmatoryFindings: [
      'Witnessed prolonged seizure',
      'Elevated lactate post-ictal',
      'Glucose to exclude hypoglycemia',
      'CT/MRI if first presentation'
    ],
    differentials: ['Single seizure', 'Psychogenic non-epileptic seizure', 'Syncope'],
    redFlags: [
      'Seizure > 5 minutes',
      'Refractory to benzodiazepines',
      'Hypoxia',
      'Aspiration'
    ],
    assessmentPathway: 'Protect airway → IV lorazepam 4mg (or buccal midazolam) → If continues: 2nd dose lorazepam → If continues: phenytoin/levetiracetam loading → If refractory: intubation + propofol/thiopentone → ICU'
  },

  {
    condition: 'Transient Ischemic Attack (TIA)',
    icd10: 'G45.9',
    category: 'Neurological',
    severity: 'High',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Transient focal neurological deficit (<24 hours)',
      'Facial droop',
      'Arm weakness',
      'Speech disturbance',
      'Complete resolution of symptoms'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis',
      'CT/MRI head (exclude hemorrhage, show ischemia)',
      'Carotid Doppler',
      'ECG (atrial fibrillation)',
      'ABCD2 score for stroke risk'
    ],
    differentials: ['Stroke', 'Migraine with aura', 'Hypoglycemia', 'Seizure with Todds paresis'],
    redFlags: [
      'High ABCD2 score (≥4)',
      'Crescendo TIAs',
      'Carotid stenosis >50%',
      'Atrial fibrillation'
    ],
    assessmentPathway: 'FAST assessment → Glucose → CT head → ABCD2 score → If high risk: admit for observation → Aspirin 300mg → Carotid Doppler → Echo → Risk factor modification → TIA clinic within 24hrs'
  },

  {
    condition: 'Guillain-Barré Syndrome',
    icd10: 'G61.0',
    category: 'Neurological',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: 18 },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Ascending symmetrical weakness',
      'Areflexia',
      'Sensory symptoms',
      'Respiratory muscle weakness',
      'Autonomic dysfunction',
      'History of recent infection'
    ],
    confirmatoryFindings: [
      'LP showing albuminocytologic dissociation (high protein, normal cells)',
      'Nerve conduction studies showing demyelination',
      'FVC declining (respiratory involvement)',
      'Recent Campylobacter or viral infection'
    ],
    differentials: ['Myasthenia gravis', 'Botulism', 'Spinal cord compression', 'Polymyositis'],
    redFlags: [
      'FVC < 20 ml/kg',
      'Bulbar weakness',
      'Autonomic instability',
      'Rapid progression'
    ],
    assessmentPathway: 'Serial FVC measurements → Neurology consult → LP → Nerve conduction studies → IVIG or plasmapheresis → ICU if respiratory compromise → Monitor for dysautonomia'
  },

  {
    condition: 'Encephalitis',
    icd10: 'G04.90',
    category: 'Neurological/Infectious',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Altered consciousness',
      'Confusion',
      'Seizures',
      'Focal neurological signs',
      'Fever',
      'Behavioral changes'
    ],
    confirmatoryFindings: [
      'LP showing lymphocytic pleocytosis',
      'PCR for HSV in CSF',
      'MRI showing temporal lobe changes',
      'EEG showing slow wave activity'
    ],
    differentials: ['Meningitis', 'Brain abscess', 'Stroke', 'Metabolic encephalopathy'],
    redFlags: [
      'Rapidly declining GCS',
      'Seizures',
      'Raised ICP',
      'HSV encephalitis (untreated mortality >70%)'
    ],
    assessmentPathway: 'CT head → LP (unless contraindicated) → Empiric aciclovir immediately → PCR for HSV → MRI → EEG → Continue aciclovir if HSV confirmed → Supportive care → ICU if severe'
  },

  // ==================== ADDITIONAL ABDOMINAL ====================

  {
    condition: 'Acute Pancreatitis',
    icd10: 'K85.90',
    category: 'Abdominal',
    severity: 'High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Severe epigastric pain radiating to back',
      'Nausea and vomiting',
      'Abdominal tenderness',
      'History of gallstones or alcohol',
      'Fever'
    ],
    confirmatoryFindings: [
      'Amylase/lipase >3x upper limit of normal',
      'CT showing pancreatic inflammation/necrosis',
      'Ultrasound showing gallstones',
      'Elevated CRP',
      'Hypocalcemia (severe)'
    ],
    differentials: ['Perforated peptic ulcer', 'Mesenteric ischemia', 'Cholecystitis', 'MI'],
    redFlags: [
      'Severe pancreatitis (modified Glasgow score ≥3)',
      'Organ failure',
      'Pancreatic necrosis',
      'Hemorrhagic pancreatitis'
    ],
    assessmentPathway: 'Amylase/lipase → IV fluid resuscitation (aggressive, 250-500ml/hr) → Analgesia → Keep nil by mouth → CT if severe → ERCP if gallstone pancreatitis with cholangitis → ICU if organ failure'
  },

  {
    condition: 'Acute Cholecystitis',
    icd10: 'K81.0',
    category: 'Abdominal',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: 80, max: 110 },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 37.5, max: 39 }
    },
    presentations: [
      'Right upper quadrant pain',
      'Murphys sign positive',
      'Fever',
      'Nausea/vomiting',
      'Pain after fatty meals'
    ],
    confirmatoryFindings: [
      'Ultrasound showing thickened gallbladder wall, pericholecystic fluid',
      'Gallstones present',
      'Positive sonographic Murphys sign',
      'Elevated WCC, CRP'
    ],
    differentials: ['Biliary colic', 'Hepatitis', 'Peptic ulcer disease', 'Pneumonia'],
    redFlags: [
      'Peritonitis',
      'Gangrenous cholecystitis',
      'Ascending cholangitis',
      'Sepsis'
    ],
    assessmentPathway: 'Ultrasound → IV fluids → IV antibiotics (ceftriaxone + metronidazole) → Analgesia → Nil by mouth → Surgical consult → Early cholecystectomy (within 72 hours) vs delayed'
  },

  {
    condition: 'Gastrointestinal Bleeding (Upper)',
    icd10: 'K92.2',
    category: 'Abdominal',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 18, max: null },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Hematemesis (fresh blood or coffee ground)',
      'Melena',
      'Epigastric pain',
      'Dizziness',
      'Signs of shock if massive bleed'
    ],
    confirmatoryFindings: [
      'Hemoglobin drop',
      'Elevated urea (out of proportion to creatinine)',
      'Upper GI endoscopy showing source',
      'Rockall score for risk stratification'
    ],
    differentials: ['Lower GI bleed', 'Epistaxis (swallowed blood)', 'Esophageal varices'],
    redFlags: [
      'Hemodynamic instability',
      'Fresh hematemesis',
      'Known varices',
      'Hb < 70 g/L'
    ],
    assessmentPathway: 'Two large-bore IV access → Fluid resuscitation → Transfuse if Hb <70 (or <100 if CAD) → IV PPI (pantoprazole) → Nil by mouth → Glasgow-Blatchford score → Urgent endoscopy if unstable → Treat cause (PPI, variceal banding, adrenaline injection)'
  },

  {
    condition: 'Perforated Peptic Ulcer',
    icd10: 'K25.1',
    category: 'Abdominal',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Sudden onset severe epigastric pain',
      'Rigid abdomen (board-like)',
      'Rebound tenderness',
      'Absent bowel sounds',
      'History of NSAID use or H. pylori'
    ],
    confirmatoryFindings: [
      'Erect CXR showing free air under diaphragm',
      'CT showing pneumoperitoneum',
      'Elevated lactate',
      'Peritonitis on examination'
    ],
    differentials: ['Pancreatitis', 'MI', 'Ruptured AAA', 'Mesenteric ischemia'],
    redFlags: [
      'Peritonitis',
      'Septic shock',
      'Free air on imaging'
    ],
    assessmentPathway: 'Keep nil by mouth → IV fluid resuscitation → Broad-spectrum antibiotics → Erect CXR → CT abdomen → Urgent surgical consult → Emergency laparotomy → Graham patch repair'
  },

  {
    condition: 'Diverticulitis',
    icd10: 'K57.92',
    category: 'Abdominal',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: 80, max: 110 },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 37.5, max: 39 }
    },
    presentations: [
      'Left lower quadrant pain',
      'Fever',
      'Altered bowel habit',
      'Nausea',
      'Localized tenderness',
      'Palpable mass if abscess'
    ],
    confirmatoryFindings: [
      'CT showing colonic wall thickening, diverticula',
      'Elevated WCC, CRP',
      'Abscess formation on imaging'
    ],
    differentials: ['Appendicitis', 'Bowel obstruction', 'Colorectal cancer', 'IBD'],
    redFlags: [
      'Perforation',
      'Abscess formation',
      'Fistula',
      'Peritonitis'
    ],
    assessmentPathway: 'CT abdomen → If uncomplicated: oral antibiotics (co-amoxiclav) + clear fluids → If complicated: admit, IV antibiotics, nil by mouth → Abscess drainage if >3-4cm → Colonoscopy 6 weeks after resolution → Elective resection if recurrent'
  },

  // ==================== ADDITIONAL INFECTIOUS ====================

  {
    condition: 'Urinary Tract Infection (Pyelonephritis)',
    icd10: 'N10',
    category: 'Infectious/Renal',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Fever and rigors',
      'Loin pain',
      'Nausea and vomiting',
      'Dysuria, frequency, urgency',
      'Renal angle tenderness'
    ],
    confirmatoryFindings: [
      'Urinalysis showing nitrites, leukocytes, blood',
      'Urine culture >10^5 organisms',
      'Elevated WCC, CRP',
      'Ultrasound if recurrent (exclude obstruction)'
    ],
    differentials: ['Lower UTI', 'Appendicitis', 'Renal colic', 'PID'],
    redFlags: [
      'Sepsis',
      'Obstructive uropathy',
      'Pregnancy',
      'Immunocompromised'
    ],
    assessmentPathway: 'Urinalysis + culture → IV fluids → IV antibiotics (ceftriaxone or gentamicin) → Blood cultures if septic → Ultrasound if concern for obstruction → Oral antibiotics once improving → 7-14 day course'
  },

  {
    condition: 'Cellulitis',
    icd10: 'L03.90',
    category: 'Infectious',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 37.5, max: null }
    },
    presentations: [
      'Erythema',
      'Warmth',
      'Swelling',
      'Tenderness',
      'Fever',
      'Lymphangitis'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis',
      'Elevated WCC, CRP',
      'Blood cultures if systemic features',
      'Mark extent of erythema'
    ],
    differentials: ['DVT', 'Necrotizing fasciitis', 'Gout', 'Contact dermatitis'],
    redFlags: [
      'Rapidly spreading erythema',
      'Systemic toxicity',
      'Crepitus (necrotizing infection)',
      'Immunocompromised'
    ],
    assessmentPathway: 'Mark extent of erythema → If mild: oral flucloxacillin → If severe: IV flucloxacillin → Elevate affected limb → Analgesia → Review in 24-48 hours → If not improving: consider imaging, alternative antibiotics, or necrotizing fasciitis'
  },

  {
    condition: 'Septic Arthritis',
    icd10: 'M00.9',
    category: 'Infectious/Musculoskeletal',
    severity: 'High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Hot, swollen, painful joint',
      'Severely reduced range of motion',
      'Fever',
      'Usually monoarticular',
      'Unable to weight-bear if lower limb'
    ],
    confirmatoryFindings: [
      'Joint aspiration showing WCC >50,000, neutrophils >75%',
      'Gram stain and culture positive',
      'Elevated serum WCC, CRP',
      'X-ray (may be normal initially)'
    ],
    differentials: ['Gout', 'Pseudogout', 'Reactive arthritis', 'Rheumatoid flare'],
    redFlags: [
      'Prosthetic joint',
      'Immunocompromised',
      'Gonococcal arthritis',
      'Delayed treatment (joint destruction)'
    ],
    assessmentPathway: 'Urgent joint aspiration → Synovial fluid analysis → Blood cultures → X-ray → IV antibiotics (flucloxacillin + consideration for MRSA coverage) → Orthopedic washout (urgent) → Repeat aspirations/washouts as needed'
  },

  {
    condition: 'Infective Endocarditis',
    icd10: 'I33.0',
    category: 'Cardiovascular/Infectious',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Fever',
      'New or changing heart murmur',
      'Night sweats',
      'Weight loss',
      'Splinter hemorrhages',
      'Osler nodes, Janeway lesions'
    ],
    confirmatoryFindings: [
      'Blood cultures positive (3 sets)',
      'Echo showing vegetation',
      'Duke criteria (modified)',
      'Elevated inflammatory markers'
    ],
    differentials: ['Rheumatic fever', 'Atrial myxoma', 'Culture-negative endocarditis'],
    redFlags: [
      'Heart failure',
      'Embolic phenomena (stroke, splenic infarct)',
      'Aortic root abscess',
      'Heart block'
    ],
    assessmentPathway: 'Blood cultures (3 sets from different sites) → Empiric IV antibiotics (vancomycin + gentamicin) → Echo (transthoracic then TOE) → Cardiology/ID consult → Prolonged IV antibiotics (4-6 weeks) → Consider surgery if complications'
  },

  // ==================== ADDITIONAL METABOLIC/ENDOCRINE ====================

  {
    condition: 'Hyperosmolar Hyperglycemic State (HHS)',
    icd10: 'E11.01',
    category: 'Metabolic/Endocrine',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Severe dehydration',
      'Altered consciousness',
      'Polyuria, polydipsia',
      'Weakness',
      'Visual disturbances',
      'No Kussmaul breathing (unlike DKA)'
    ],
    confirmatoryFindings: [
      'Glucose > 30 mmol/L',
      'Serum osmolality > 320 mOsm/kg',
      'pH > 7.3 (no acidosis)',
      'No significant ketones',
      'Elevated sodium'
    ],
    differentials: ['DKA', 'Stroke', 'Sepsis', 'Hypoglycemia'],
    redFlags: [
      'Severe hyperosmolality',
      'Thrombotic complications',
      'Seizures',
      'Coma'
    ],
    assessmentPathway: 'IV fluid resuscitation (0.9% saline, slower than DKA) → Insulin (lower dose than DKA, 0.05 units/kg/hr) → Potassium replacement → Identify precipitant → Thromboprophylaxis → Gradual correction (avoid cerebral edema) → Monitor osmolality'
  },

  {
    condition: 'Addisonian Crisis',
    icd10: 'E27.2',
    category: 'Metabolic/Endocrine',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Hypotension refractory to fluids',
      'Nausea, vomiting, abdominal pain',
      'Confusion',
      'Hyperpigmentation (chronic)',
      'History of steroid use or autoimmune disease'
    ],
    confirmatoryFindings: [
      'Hyponatremia',
      'Hyperkalemia',
      'Hypoglycemia',
      'Low cortisol',
      'Eosinophilia'
    ],
    differentials: ['Septic shock', 'Gastroenteritis', 'Acute abdomen'],
    redFlags: [
      'Refractory hypotension',
      'Severe hyponatremia',
      'Cardiac arrest'
    ],
    assessmentPathway: 'IV hydrocortisone 100mg stat → 0.9% saline fluid resuscitation → Dextrose if hypoglycemic → Identify and treat precipitant → Cortisol sample (before hydrocortisone if possible) → Continue hydrocortisone 6-hourly → Taper once stable'
  },

  {
    condition: 'Thyroid Storm',
    icd10: 'E05.5',
    category: 'Metabolic/Endocrine',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 140, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 39, max: null }
    },
    presentations: [
      'Severe tachycardia',
      'High fever',
      'Agitation, confusion',
      'Tremor',
      'Diarrhea',
      'Goiter',
      'Known hyperthyroidism'
    ],
    confirmatoryFindings: [
      'Very low TSH, very high free T4/T3',
      'Atrial fibrillation on ECG',
      'Dehydration',
      'Elevated liver enzymes'
    ],
    differentials: ['Sepsis', 'Sympathomimetic overdose', 'Malignant hyperthermia'],
    redFlags: [
      'Heart failure',
      'Altered consciousness',
      'Multi-organ failure'
    ],
    assessmentPathway: 'IV fluids + cooling → Propylthiouracil or carbimazole → Beta-blocker (propranolol) → Hydrocortisone → Lugols iodine (1 hour after antithyroid drug) → Treat precipitant → ICU'
  },

  {
    condition: 'Rhabdomyolysis',
    icd10: 'M62.82',
    category: 'Metabolic/Renal',
    severity: 'High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Muscle pain, weakness',
      'Dark urine (myoglobinuria)',
      'History of trauma, seizures, or drug use',
      'Compartment syndrome if severe'
    ],
    confirmatoryFindings: [
      'CK > 1000 U/L (often >10,000)',
      'Myoglobinuria',
      'Elevated potassium',
      'Acute kidney injury',
      'Elevated phosphate'
    ],
    differentials: ['Myositis', 'Acute kidney injury (other cause)', 'Hemolysis'],
    redFlags: [
      'CK > 5000',
      'AKI',
      'Severe hyperkalemia',
      'Compartment syndrome'
    ],
    assessmentPathway: 'Aggressive IV fluid resuscitation (0.9% saline, target urine output 200-300ml/hr) → Monitor CK, electrolytes, renal function → Treat hyperkalemia → Consider bicarbonate if severe → Dialysis if refractory AKI → Treat underlying cause'
  },

  // ==================== HEMATOLOGY EMERGENCIES ====================

  {
    condition: 'Deep Vein Thrombosis (DVT)',
    icd10: 'I82.40',
    category: 'Cardiovascular/Hematology',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Unilateral leg swelling',
      'Calf pain and tenderness',
      'Warmth and erythema',
      'Palpable cord',
      'Risk factors: immobility, surgery, cancer'
    ],
    confirmatoryFindings: [
      'Ultrasound showing non-compressible vein',
      'Elevated D-dimer (if Wells score low)',
      'Wells score for probability'
    ],
    differentials: ['Cellulitis', 'Ruptured Bakers cyst', 'Muscle strain', 'Chronic venous insufficiency'],
    redFlags: [
      'Phlegmasia cerulea dolens',
      'Bilateral DVT',
      'Upper limb DVT',
      'Concurrent PE'
    ],
    assessmentPathway: 'Wells score → D-dimer if low probability → Ultrasound doppler → Anticoagulation (LMWH or DOAC) → Consider thrombophilia screen if unprovoked → Compression stockings → Duration based on provoked vs unprovoked'
  },

  {
    condition: 'Disseminated Intravascular Coagulation (DIC)',
    icd10: 'D65',
    category: 'Hematology',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Bleeding from multiple sites',
      'Petechiae, purpura',
      'Thrombosis',
      'Organ dysfunction',
      'Underlying sepsis, trauma, or malignancy'
    ],
    confirmatoryFindings: [
      'Thrombocytopenia',
      'Prolonged PT, APTT',
      'Low fibrinogen',
      'Elevated D-dimer',
      'Microangiopathic hemolytic anemia'
    ],
    differentials: ['Thrombotic thrombocytopenic purpura', 'Immune thrombocytopenia', 'Liver failure'],
    redFlags: [
      'Active bleeding',
      'Organ failure',
      'Underlying malignancy'
    ],
    assessmentPathway: 'Treat underlying cause → Platelet transfusion if bleeding or platelets <10 → FFP if bleeding with prolonged PT → Cryoprecipitate if fibrinogen <1 → Avoid heparin unless thrombosis predominant → ICU'
  },

  // ==================== ENVIRONMENTAL EMERGENCIES ====================

  {
    condition: 'Hypothermia',
    icd10: 'T68',
    category: 'Environmental',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: null, max: 60 },
      rr: { min: null, max: 12 },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null },
      temp: { min: null, max: 35 }
    },
    presentations: [
      'Shivering (stops when severe)',
      'Confusion',
      'Slurred speech',
      'Bradycardia',
      'Arrhythmias (J waves on ECG)',
      'Decreased consciousness'
    ],
    confirmatoryFindings: [
      'Core temperature <35°C',
      'ECG showing J (Osborn) waves',
      'Ventricular arrhythmias if severe',
      'Prolonged QT'
    ],
    differentials: ['Hypoglycemia', 'Sepsis', 'Hypothyroidism', 'Alcohol intoxication'],
    redFlags: [
      'Core temp <28°C (severe)',
      'Ventricular fibrillation',
      'Cardiac arrest',
      'No shivering'
    ],
    assessmentPathway: 'Remove wet clothing → Passive rewarming (blankets) if mild → Active external rewarming (warm fluids, forced air warming) if moderate → Active internal rewarming (warmed IV fluids, bladder/gastric lavage) if severe → ECMO if cardiac arrest → Handle gently (risk of VF)'
  },

  {
    condition: 'Heat Stroke',
    icd10: 'T67.0',
    category: 'Environmental',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 120, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 40, max: null }
    },
    presentations: [
      'Core temperature >40°C',
      'Altered consciousness',
      'Hot, dry skin (classic) or sweating (exertional)',
      'Seizures',
      'Multi-organ dysfunction'
    ],
    confirmatoryFindings: [
      'Core temperature >40°C',
      'Elevated liver enzymes',
      'Acute kidney injury',
      'Rhabdomyolysis',
      'DIC'
    ],
    differentials: ['Sepsis', 'Malignant hyperthermia', 'Neuroleptic malignant syndrome', 'Serotonin syndrome'],
    redFlags: [
      'Altered consciousness',
      'Multi-organ failure',
      'DIC',
      'Rhabdomyolysis'
    ],
    assessmentPathway: 'Immediate cooling (ice packs to groin/axillae, evaporative cooling) → IV fluids → Monitor core temperature → Treat seizures → Check for organ dysfunction → Avoid antipyretics (ineffective) → ICU'
  },

  // ==================== ADDITIONAL TRAUMA ====================

  {
    condition: 'Traumatic Cardiac Tamponade',
    icd10: 'I31.4',
    category: 'Trauma/Cardiovascular',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Hypotension',
      'Muffled heart sounds',
      'Raised JVP',
      'Pulsus paradoxus',
      'History of chest trauma',
      'Becks triad'
    ],
    confirmatoryFindings: [
      'Echo showing pericardial effusion with right atrial/ventricular collapse',
      'CXR showing globular heart',
      'Electrical alternans on ECG',
      'Improvement after pericardiocentesis'
    ],
    differentials: ['Tension pneumothorax', 'Massive hemothorax', 'Cardiogenic shock'],
    redFlags: [
      'Cardiac arrest',
      'Severe hypotension',
      'Penetrating chest trauma'
    ],
    assessmentPathway: 'Emergency pericardiocentesis → Ultrasound-guided if stable → Immediate thoracotomy if arrest → Cardiothoracic surgical backup → Monitor for re-accumulation'
  },

  {
    condition: 'Ruptured Abdominal Aortic Aneurysm (AAA)',
    icd10: 'I71.3',
    category: 'Cardiovascular/Trauma',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Sudden severe abdominal/back pain',
      'Hypotension/shock',
      'Pulsatile abdominal mass',
      'Syncope',
      'Usually elderly male, smoker'
    ],
    confirmatoryFindings: [
      'CT showing ruptured AAA with retroperitoneal hematoma',
      'Ultrasound showing AAA >5.5cm',
      'Free fluid in abdomen',
      'Falling Hb'
    ],
    differentials: ['Renal colic', 'Pancreatitis', 'MI', 'Perforated viscus'],
    redFlags: [
      'Hypotension',
      'Expanding mass',
      'Loss of consciousness',
      'Any AAA rupture is life-threatening'
    ],
    assessmentPathway: 'Do NOT delay for imaging if unstable → Activate vascular surgery → Permissive hypotension (SBP 80-90) → Massive transfusion protocol → Emergency EVAR or open repair → No IV fluids if BP adequate (risk of worsening bleeding)'
  },

  {
    condition: 'Spinal Cord Injury',
    icd10: 'S14.109A',
    category: 'Trauma/Neurological',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: null, max: 60 },
      rr: { min: null, max: 20 },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Weakness or paralysis below level of injury',
      'Loss of sensation',
      'Loss of bowel/bladder control',
      'Neurogenic shock (bradycardia + hypotension)',
      'Priapism (males)'
    ],
    confirmatoryFindings: [
      'MRI showing cord compression/transection',
      'CT showing spinal fracture',
      'Complete motor/sensory loss',
      'Absent rectal tone'
    ],
    differentials: ['Cord compression', 'Guillain-Barré', 'Transverse myelitis'],
    redFlags: [
      'High cervical injury (C1-C4, respiratory compromise)',
      'Complete cord injury',
      'Neurogenic shock'
    ],
    assessmentPathway: 'Spinal immobilization → ABCDE → High-dose methylprednisolone if within 8 hours (controversial) → MRI → Neurosurgical consult → Surgical decompression if indicated → Prevent secondary injury'
  },

  {
    condition: 'Acute Compartment Syndrome',
    icd10: 'T79.A0',
    category: 'Trauma/Musculoskeletal',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Severe pain out of proportion to injury',
      'Pain on passive stretch',
      'Tense, swollen compartment',
      'Paresthesias',
      'Pallor, pulselessness (late signs)',
      'History of fracture or crush injury'
    ],
    confirmatoryFindings: [
      'Compartment pressure > 30 mmHg',
      'Delta pressure < 30 mmHg (diastolic BP - compartment pressure)',
      'Clinical diagnosis primarily',
      'Elevated CK if muscle necrosis'
    ],
    differentials: ['Deep vein thrombosis', 'Cellulitis', 'Fracture pain'],
    redFlags: [
      'Loss of pulses (late, irreversible)',
      'Hard, tense compartment',
      'Progressive neurological deficit'
    ],
    assessmentPathway: 'Clinical diagnosis → Do NOT delay for pressure measurement if highly suspicious → Emergency fasciotomy → Elevate limb to heart level only → Remove constrictive dressings → Treat underlying cause → Monitor CK and renal function'
  },

  {
    condition: 'Major Burn Injury',
    icd10: 'T30.0',
    category: 'Trauma',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 18, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Burn injury >10% TBSA (adult) or >5% (child)',
      'Circumferential burns',
      'Burns to face, hands, feet, genitals',
      'Inhalational injury',
      'Signs of shock if severe'
    ],
    confirmatoryFindings: [
      'Total body surface area (TBSA) calculation using rule of nines',
      'Partial thickness (blisters) vs full thickness (white/charred)',
      'Carboxyhemoglobin if smoke inhalation',
      'Bronchoscopy showing soot/edema'
    ],
    differentials: ['Toxic epidermal necrolysis', 'Stevens-Johnson syndrome'],
    redFlags: [
      'Airway burns (singed nasal hairs, hoarse voice)',
      'Circumferential burns (compartment syndrome)',
      'TBSA >20%',
      'Inhalational injury'
    ],
    assessmentPathway: 'ABCDE → Early intubation if airway concern → IV fluid resuscitation (Parkland formula: 4ml/kg/%TBSA over 24hrs) → Analgesia → Keep warm → Escharotomy if circumferential → Tetanus prophylaxis → Early burns unit referral'
  },

  {
    condition: 'Fat Embolism Syndrome',
    icd10: 'T79.1',
    category: 'Trauma/Respiratory',
    severity: 'High',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 22, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 92 }
    },
    presentations: [
      'Petechial rash (chest, axillae, conjunctiva)',
      'Hypoxia',
      'Confusion',
      '24-72 hours after long bone fracture',
      'Tachycardia, tachypnea'
    ],
    confirmatoryFindings: [
      'Clinical triad: respiratory, neurological, dermatological',
      'CXR showing bilateral infiltrates',
      'Thrombocytopenia',
      'Fat globules in urine/sputum (unreliable)'
    ],
    differentials: ['ARDS', 'Pulmonary embolism', 'Pneumonia'],
    redFlags: [
      'Severe hypoxia',
      'Decreased consciousness',
      'ARDS'
    ],
    assessmentPathway: 'High-flow oxygen → Early fracture stabilization → Supportive care → Mechanical ventilation if respiratory failure → No specific treatment → Monitor for complications'
  },

  // ==================== ELECTROLYTE EMERGENCIES ====================

  {
    condition: 'Hyponatremia (Severe)',
    icd10: 'E87.1',
    category: 'Metabolic',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Confusion, altered consciousness',
      'Seizures',
      'Nausea, vomiting',
      'Headache',
      'Muscle cramps',
      'Weakness'
    ],
    confirmatoryFindings: [
      'Serum sodium < 125 mmol/L (severe < 120)',
      'Urine osmolality and sodium',
      'Assess volume status (hypovolemic, euvolemic, hypervolemic)',
      'Identify cause (SIADH, diuretics, adrenal insufficiency)'
    ],
    differentials: ['Hypoglycemia', 'Stroke', 'Meningitis'],
    redFlags: [
      'Seizures',
      'GCS < 8',
      'Na+ < 120 mmol/L',
      'Acute onset (<48 hours)'
    ],
    assessmentPathway: 'Assess acuity and symptoms → If severe with symptoms: hypertonic saline (3%) cautiously → Correct slowly (max 10-12 mmol/L per 24hrs to avoid osmotic demyelination) → Treat underlying cause → Monitor sodium q2-4 hours'
  },

  {
    condition: 'Hypercalcemia',
    icd10: 'E83.52',
    category: 'Metabolic',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Stones, bones, groans, psychiatric overtones',
      'Polyuria, polydipsia',
      'Nausea, vomiting, constipation',
      'Confusion',
      'Shortened QT interval',
      'Muscle weakness'
    ],
    confirmatoryFindings: [
      'Corrected calcium > 2.6 mmol/L (severe > 3.5)',
      'PTH level (high in primary hyperparathyroidism, low in malignancy)',
      'ECG showing shortened QT',
      'Elevated ALP if bony metastases'
    ],
    differentials: ['Primary hyperparathyroidism', 'Malignancy', 'Vitamin D toxicity', 'Sarcoidosis'],
    redFlags: [
      'Corrected Ca2+ > 3.5 mmol/L',
      'Altered consciousness',
      'Renal impairment',
      'Cardiac arrhythmias'
    ],
    assessmentPathway: 'IV fluid resuscitation (0.9% saline) → Bisphosphonates (zoledronic acid) → Calcitonin if severe → Furosemide once euvolemic → Treat underlying cause → Avoid thiazides'
  },

  {
    condition: 'Hypokalemia (Severe)',
    icd10: 'E87.6',
    category: 'Metabolic',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Muscle weakness',
      'Cramps',
      'Constipation',
      'Palpitations',
      'Fatigue',
      'Paralysis if severe'
    ],
    confirmatoryFindings: [
      'K+ < 3.5 mmol/L (severe < 2.5)',
      'ECG showing flattened T waves, U waves, ST depression',
      'Arrhythmias (VT, torsades)',
      'Elevated CK if rhabdomyolysis'
    ],
    differentials: ['Diuretic use', 'Vomiting/diarrhea', 'Renal losses', 'Hyperaldosteronism'],
    redFlags: [
      'K+ < 2.5 mmol/L',
      'ECG changes',
      'Arrhythmias',
      'Muscle paralysis'
    ],
    assessmentPathway: 'ECG monitoring → IV potassium replacement (10-20 mmol/hr via central line if severe) → Oral supplements if mild → Replace magnesium (K+ will not correct if Mg low) → Monitor K+ q2-4 hours → Treat underlying cause'
  },

  {
    condition: 'Hypocalcemia (Severe)',
    icd10: 'E83.51',
    category: 'Metabolic',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Perioral paresthesias',
      'Carpopedal spasm',
      'Tetany',
      'Chvosteks sign (facial twitch)',
      'Trousseaus sign (BP cuff induced spasm)',
      'Seizures if severe'
    ],
    confirmatoryFindings: [
      'Corrected calcium < 2.1 mmol/L',
      'Prolonged QT interval on ECG',
      'Low PTH or low vitamin D',
      'Hypomagnesemia often concurrent'
    ],
    differentials: ['Hypoparathyroidism', 'Vitamin D deficiency', 'Pancreatitis', 'Post-thyroidectomy'],
    redFlags: [
      'Seizures',
      'Laryngospasm',
      'Cardiac arrhythmias',
      'Symptomatic hypocalcemia'
    ],
    assessmentPathway: 'If symptomatic: IV calcium gluconate 10ml of 10% over 10 minutes → Continuous cardiac monitoring → Correct magnesium → Oral calcium + vitamin D for chronic → Recheck levels'
  },

  // ==================== ADDITIONAL TOXICOLOGY ====================

  {
    condition: 'Alcohol Withdrawal Syndrome',
    icd10: 'F10.239',
    category: 'Poisoning/Toxicology',
    severity: 'Moderate-Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 18, max: null },
      sbp: { min: 140, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Tremor',
      'Sweating',
      'Anxiety',
      'Nausea',
      'Hallucinations (visual, tactile)',
      'Seizures',
      'Delirium tremens (DTs) if severe'
    ],
    confirmatoryFindings: [
      'CIWA-Ar score > 10',
      'History of chronic alcohol use with cessation',
      'Hypomagnesemia, hypophosphatemia',
      'Elevated liver enzymes'
    ],
    differentials: ['Sepsis', 'Thyroid storm', 'Sympathomimetic toxicity', 'Meningitis'],
    redFlags: [
      'Delirium tremens (confusion, agitation, autonomic instability)',
      'Seizures',
      'Severe hypertension/tachycardia',
      'Hallucinations'
    ],
    assessmentPathway: 'CIWA-Ar score → Benzodiazepines (diazepam or lorazepam, symptom-triggered or fixed-dose) → IV thiamine 100mg → IV fluids → Electrolyte replacement (Mg, K, PO4) → Treat complications'
  },

  {
    condition: 'Tricyclic Antidepressant (TCA) Overdose',
    icd10: 'T43.011A',
    category: 'Poisoning/Toxicology',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: null, max: 20 },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Altered consciousness',
      'Seizures',
      'Arrhythmias',
      'Hypotension',
      'Anticholinergic features (dilated pupils, dry mouth, urinary retention)',
      'QRS widening on ECG'
    ],
    confirmatoryFindings: [
      'ECG showing QRS > 100ms, QTc prolongation',
      'Terminal R wave in aVR',
      'Ventricular arrhythmias',
      'Metabolic acidosis'
    ],
    differentials: ['Other drug overdoses', 'Sepsis', 'Stroke'],
    redFlags: [
      'QRS > 100ms',
      'Seizures',
      'Ventricular arrhythmias',
      'Hypotension'
    ],
    assessmentPathway: 'Activated charcoal if within 1 hour → IV sodium bicarbonate (target pH 7.45-7.55 if QRS wide or arrhythmias) → Treat seizures with benzodiazepines → Intubate if GCS < 8 → Avoid Class Ia/Ic antiarrhythmics → ICU'
  },

  {
    condition: 'Salicylate Poisoning',
    icd10: 'T39.011A',
    category: 'Poisoning/Toxicology',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 25, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Tinnitus',
      'Nausea, vomiting',
      'Hyperventilation',
      'Altered consciousness',
      'Hyperthermia',
      'History of aspirin ingestion'
    ],
    confirmatoryFindings: [
      'Salicylate level > 300 mg/L (severe)',
      'Mixed respiratory alkalosis and metabolic acidosis',
      'Hypoglycemia (or normal glucose with low CSF glucose)',
      'Elevated anion gap'
    ],
    differentials: ['DKA', 'Methanol/ethylene glycol poisoning', 'Sepsis'],
    redFlags: [
      'Altered consciousness',
      'Severe acidosis',
      'Salicylate level > 500 mg/L',
      'Pulmonary edema'
    ],
    assessmentPathway: 'Activated charcoal if within 1 hour → IV fluid resuscitation → Urinary alkalinization (sodium bicarbonate, target urine pH > 7.5) → Dextrose (even if normoglycemic) → Hemodialysis if severe → Monitor salicylate levels q2-4h'
  },

  {
    condition: 'Carbon Monoxide Poisoning',
    icd10: 'T58',
    category: 'Poisoning/Toxicology',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: 18, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Headache',
      'Nausea, vomiting',
      'Confusion',
      'Cherry red skin (rare, late sign)',
      'Syncope',
      'Seizures if severe',
      'History of smoke/exhaust exposure'
    ],
    confirmatoryFindings: [
      'Carboxyhemoglobin (COHb) level > 10% (> 3% in non-smokers)',
      'Normal pulse oximetry (falsely normal)',
      'Metabolic acidosis',
      'Elevated lactate'
    ],
    differentials: ['Cyanide poisoning', 'Viral illness', 'Migraine'],
    redFlags: [
      'COHb > 25%',
      'Altered consciousness',
      'Cardiac ischemia',
      'Pregnancy'
    ],
    assessmentPathway: 'Remove from source → 100% oxygen via non-rebreather mask → Hyperbaric oxygen if COHb > 25%, pregnant, or neurological symptoms → Monitor troponin (cardiac involvement) → Neuropsychiatric follow-up (delayed sequelae)'
  },

  {
    condition: 'Serotonin Syndrome',
    icd10: 'G25.1',
    category: 'Poisoning/Toxicology',
    severity: 'Moderate-Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 20, max: null },
      sbp: { min: 140, max: null },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Altered mental status',
      'Neuromuscular hyperactivity (clonus, hyperreflexia)',
      'Autonomic instability (hyperthermia, tachycardia)',
      'Mydriasis',
      'Diaphoresis',
      'Recent increase in serotonergic drugs'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis (Hunters criteria)',
      'Recent addition of serotonergic medication',
      'Inducible clonus',
      'Ocular clonus',
      'Elevated CK if rhabdomyolysis'
    ],
    differentials: ['Neuroleptic malignant syndrome', 'Malignant hyperthermia', 'Sympathomimetic toxicity', 'Sepsis'],
    redFlags: [
      'Hyperthermia > 38.5°C',
      'Rigidity',
      'Rhabdomyolysis',
      'Seizures'
    ],
    assessmentPathway: 'Stop serotonergic agents → Supportive care → Benzodiazepines for agitation → Active cooling → Cyproheptadine (serotonin antagonist) → Avoid physical restraints → ICU if severe'
  },

  {
    condition: 'Neuroleptic Malignant Syndrome',
    icd10: 'G21.0',
    category: 'Poisoning/Toxicology',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Hyperthermia',
      'Lead-pipe rigidity',
      'Altered mental status',
      'Autonomic instability',
      'Recent antipsychotic use or dose increase',
      'Diaphoresis'
    ],
    confirmatoryFindings: [
      'Elevated CK (often >1000)',
      'Leukocytosis',
      'Myoglobinuria',
      'Metabolic acidosis',
      'Acute kidney injury'
    ],
    differentials: ['Serotonin syndrome', 'Malignant hyperthermia', 'Sepsis', 'Thyroid storm'],
    redFlags: [
      'Severe hyperthermia',
      'Rigidity',
      'Rhabdomyolysis',
      'Multi-organ failure'
    ],
    assessmentPathway: 'Stop antipsychotic → Supportive care → Active cooling → IV fluids → Benzodiazepines → Dantrolene or bromocriptine → Monitor CK, renal function → ICU'
  },

  // ==================== OBSTETRIC/GYNECOLOGICAL ====================

  {
    condition: 'Postpartum Hemorrhage',
    icd10: 'O72.1',
    category: 'Obstetric',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Bleeding > 500ml after vaginal delivery or > 1000ml after C-section',
      'Signs of shock',
      'Continued bleeding',
      'Boggy uterus (atony)'
    ],
    confirmatoryFindings: [
      'Falling Hb',
      'Coagulopathy (DIC)',
      'Identify cause (4 Ts: Tone, Trauma, Tissue, Thrombin)'
    ],
    differentials: ['Uterine atony', 'Retained placenta', 'Genital tract trauma', 'Coagulopathy'],
    redFlags: [
      'Hemodynamic instability',
      'Continued bleeding despite uterotonic',
      'DIC'
    ],
    assessmentPathway: 'Uterine massage → Oxytocin IV → Tranexamic acid → Carboprost or misoprostol → Bimanual compression → Explore for retained products/trauma → Balloon tamponade → Surgical intervention (B-Lynch suture, hysterectomy) → Massive transfusion protocol'
  },

  {
    condition: 'Ovarian Torsion',
    icd10: 'N83.51',
    category: 'Obstetric/Gynecological',
    severity: 'High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Sudden onset severe unilateral pelvic pain',
      'Intermittent pain',
      'Nausea and vomiting',
      'Adnexal mass',
      'More common in reproductive age'
    ],
    confirmatoryFindings: [
      'Ultrasound showing enlarged ovary with absent or decreased flow',
      'Whirlpool sign',
      'Ovarian mass (cyst, dermoid)',
      'Free fluid'
    ],
    differentials: ['Ectopic pregnancy', 'Appendicitis', 'Ovarian cyst rupture', 'PID'],
    redFlags: [
      'Peritonitis',
      'Complete absence of ovarian blood flow',
      'Ovarian necrosis'
    ],
    assessmentPathway: 'Urgent pelvic ultrasound with Doppler → Gynecology consult → Emergency laparoscopy → Ovarian detorsion (if viable) or oophorectomy → Time-critical (ideally within 4-6 hours)'
  },

  {
    condition: 'Pelvic Inflammatory Disease (PID)',
    icd10: 'N73.9',
    category: 'Obstetric/Gynecological',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 37.5, max: null }
    },
    presentations: [
      'Lower abdominal pain',
      'Vaginal discharge',
      'Fever',
      'Cervical motion tenderness',
      'Dyspareunia',
      'Irregular bleeding'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis (low threshold)',
      'Swabs for chlamydia/gonorrhea',
      'Elevated WCC, CRP',
      'Ultrasound may show tubo-ovarian abscess',
      'Pregnancy test to exclude ectopic'
    ],
    differentials: ['Ectopic pregnancy', 'Appendicitis', 'Ovarian cyst', 'UTI'],
    redFlags: [
      'Tubo-ovarian abscess',
      'Peritonitis',
      'Pregnancy',
      'Fitz-Hugh-Curtis syndrome'
    ],
    assessmentPathway: 'Pregnancy test → STI swabs → If mild: oral antibiotics (ceftriaxone + doxycycline + metronidazole) → If severe: admit for IV antibiotics → Contact tracing → Screen for other STIs'
  },

  // ==================== OPHTHALMOLOGY EMERGENCIES ====================

  {
    condition: 'Acute Angle-Closure Glaucoma',
    icd10: 'H40.20',
    category: 'Ophthalmology',
    severity: 'High',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Severe eye pain',
      'Headache',
      'Blurred vision, halos around lights',
      'Nausea and vomiting',
      'Red eye',
      'Fixed mid-dilated pupil',
      'Hazy cornea'
    ],
    confirmatoryFindings: [
      'Intraocular pressure > 30 mmHg (normal 10-21)',
      'Shallow anterior chamber',
      'Corneal edema',
      'Gonioscopy showing closed angle'
    ],
    differentials: ['Uveitis', 'Corneal abrasion', 'Migraine', 'Cluster headache'],
    redFlags: [
      'IOP > 50 mmHg',
      'Vision loss',
      'Bilateral involvement'
    ],
    assessmentPathway: 'Urgent ophthalmology referral → Topical beta-blocker (timolol) → Topical alpha-agonist (apraclonidine) → Acetazolamide 500mg IV/PO → Analgesia + antiemetic → Definitive: laser peripheral iridotomy'
  },

  {
    condition: 'Central Retinal Artery Occlusion',
    icd10: 'H34.10',
    category: 'Ophthalmology',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Sudden painless unilateral vision loss',
      'Relative afferent pupillary defect (RAPD)',
      'Cherry red spot on fundoscopy',
      'Boxcarring of vessels',
      'History of cardiovascular disease'
    ],
    confirmatoryFindings: [
      'Fundoscopy showing pale retina with cherry red spot',
      'RAPD',
      'Decreased visual acuity',
      'Carotid Doppler showing stenosis',
      'Giant cell arteritis work-up if age > 50'
    ],
    differentials: ['Retinal detachment', 'Vitreous hemorrhage', 'Optic neuritis', 'Giant cell arteritis'],
    redFlags: [
      'Time-critical (irreversible after 90 minutes)',
      'Bilateral involvement',
      'Signs of giant cell arteritis'
    ],
    assessmentPathway: 'EMERGENCY - time critical → Immediate ophthalmology referral → Ocular massage → Anterior chamber paracentesis → Sublingual GTN → IV acetazolamide → Treat underlying cause → Consider intra-arterial thrombolysis (experimental) → ESR/CRP if GCA suspected'
  },

  {
    condition: 'Retinal Detachment',
    icd10: 'H33.00',
    category: 'Ophthalmology',
    severity: 'High',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Flashes of light (photopsia)',
      'Floaters',
      'Shadow or curtain in vision',
      'Painless',
      'Visual field defect'
    ],
    confirmatoryFindings: [
      'Fundoscopy showing detached retina',
      'Ultrasound if view obscured',
      'RAPD if macula involved'
    ],
    differentials: ['Vitreous detachment', 'Vitreous hemorrhage', 'Migraine aura'],
    redFlags: [
      'Macula-off detachment',
      'Recent eye surgery',
      'Trauma'
    ],
    assessmentPathway: 'Urgent ophthalmology referral (within 24 hours) → Posture head to keep detachment inferior → No definitive ED treatment → Surgical repair (vitrectomy, scleral buckle, pneumatic retinopexy)'
  },

  // ==================== ENT EMERGENCIES ====================

  {
    condition: 'Epiglottitis',
    icd10: 'J05.10',
    category: 'ENT/Respiratory',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 22, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: 94 },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Severe sore throat',
      'Drooling',
      'Tripod positioning',
      'Muffled voice',
      'Stridor',
      'Dysphagia',
      'High fever'
    ],
    confirmatoryFindings: [
      'Lateral neck X-ray showing "thumb sign"',
      'Direct visualization showing cherry-red epiglottis (in controlled setting)',
      'Elevated WCC'
    ],
    differentials: ['Croup', 'Foreign body', 'Peritonsillar abscess', 'Anaphylaxis'],
    redFlags: [
      'Stridor',
      'Respiratory distress',
      'Drooling',
      'Unable to swallow'
    ],
    assessmentPathway: 'DO NOT examine throat or lay patient flat → Keep calm, sitting position → Senior anesthetist + ENT → Prepare for difficult airway → Nebulized adrenaline temporizing → Intubation in theatre with surgical airway backup → IV antibiotics (ceftriaxone) → ICU'
  },

  {
    condition: 'Peritonsillar Abscess (Quinsy)',
    icd10: 'J36',
    category: 'ENT/Infectious',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Severe unilateral sore throat',
      'Trismus (difficulty opening mouth)',
      'Muffled "hot potato" voice',
      'Uvula deviation',
      'Drooling',
      'Unilateral tonsillar swelling'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis',
      'Aspiration yielding pus',
      'Contrast CT if concern for deep space infection'
    ],
    differentials: ['Tonsillitis', 'Epiglottitis', 'Retropharyngeal abscess', 'Ludwig\'s angina'],
    redFlags: [
      'Airway compromise',
      'Sepsis',
      'Deep neck space involvement'
    ],
    assessmentPathway: 'Analgesia → IV antibiotics (co-amoxiclav or clindamycin) → Needle aspiration or incision and drainage → IV fluids → ENT referral → Consider tonsillectomy (interval or immediate)'
  },

  {
    condition: 'Epistaxis (Severe)',
    icd10: 'R04.0',
    category: 'ENT',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Nosebleed',
      'Anterior (90%) or posterior (10%)',
      'Signs of blood loss if severe',
      'History of hypertension, anticoagulation, trauma'
    ],
    confirmatoryFindings: [
      'Identify bleeding point (anterior vs posterior)',
      'Assess hemodynamic status',
      'FBC if significant blood loss',
      'Coagulation screen if on anticoagulants'
    ],
    differentials: ['Nasal trauma', 'Coagulopathy', 'Nasal tumor', 'Foreign body'],
    redFlags: [
      'Hemodynamic instability',
      'Unable to control with first aid measures',
      'Posterior bleed',
      'Anticoagulation'
    ],
    assessmentPathway: 'Pinch soft part of nose for 15 minutes → If continues: topical adrenaline/cocaine → Anterior nasal packing → If continues: posterior packing or balloon → Silver nitrate cautery if visible vessel → ENT referral → Correct coagulopathy → Consider embolization if refractory'
  },

  // ==================== DERMATOLOGY EMERGENCIES ====================

  {
    condition: 'Stevens-Johnson Syndrome / TEN',
    icd10: 'L51.1',
    category: 'Dermatology',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 18, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Blistering skin with epidermal detachment',
      'Painful skin',
      'Mucosal involvement (mouth, eyes, genitals)',
      'Positive Nikolskys sign',
      'Recent drug exposure (allopurinol, anticonvulsants, antibiotics)',
      'Target lesions'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis',
      'Skin biopsy showing full-thickness epidermal necrosis',
      'Body surface area involvement (SJS < 10%, SJS/TEN 10-30%, TEN > 30%)',
      'SCORTEN score for prognosis'
    ],
    differentials: ['Staphylococcal scalded skin syndrome', 'Toxic epidermal necrolysis', 'Burns'],
    redFlags: [
      'BSA > 10%',
      'Mucosal involvement',
      'Respiratory involvement',
      'Sepsis'
    ],
    assessmentPathway: 'Stop causative drug → Burns unit referral → IV fluids (as per burns) → Analgesia → Wound care → Avoid systemic steroids → IVIG controversial → Ophthalmology review → High mortality (10-50%)'
  },

  {
    condition: 'Necrotizing Fasciitis',
    icd10: 'M72.6',
    category: 'Infectious/Dermatology',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Rapidly spreading erythema',
      'Pain out of proportion',
      'Skin necrosis, bullae',
      'Crepitus',
      'Systemic toxicity',
      'Anesthesia of affected area'
    ],
    confirmatoryFindings: [
      'LRINEC score ≥ 6 (CRP, WCC, Hb, Na, Cr, glucose)',
      'CT/MRI showing fascial gas',
      'Intraoperative finding of gray necrotic fascia',
      'Elevated CK, lactate'
    ],
    differentials: ['Cellulitis', 'Gas gangrene', 'Pyomyositis'],
    redFlags: [
      'Crepitus',
      'Rapid progression',
      'Systemic toxicity',
      'Skin necrosis'
    ],
    assessmentPathway: 'Urgent surgical consult → Empiric broad-spectrum antibiotics (pip-tazo + clindamycin) → Aggressive IV fluid resuscitation → Emergency surgical debridement (within 6 hours) → Repeated debridement as needed → High mortality (20-30%)'
  },

  // ==================== UROLOGICAL EMERGENCIES ====================

  {
    condition: 'Acute Urinary Retention',
    icd10: 'R33.9',
    category: 'Renal/Urological',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Unable to pass urine',
      'Suprapubic pain',
      'Palpable bladder',
      'Urge to void',
      'History of BPH, medications, neurological disease'
    ],
    confirmatoryFindings: [
      'Bladder scan showing > 300ml residual',
      'Palpable/percussable bladder',
      'Post-void residual > 200ml'
    ],
    differentials: ['BPH', 'Urethral stricture', 'Prostate cancer', 'Medications', 'Neurogenic bladder'],
    redFlags: [
      'Renal impairment',
      'Bladder > 1L',
      'Recurrent retention',
      'Neurological signs (cord compression)'
    ],
    assessmentPathway: 'Bladder scan → Urethral catheterization → If failed: suprapubic catheter → Monitor for post-obstructive diuresis → Renal function → Urology referral → Trial without catheter vs TURP'
  },

  {
    condition: 'Testicular Torsion',
    icd10: 'N44.00',
    category: 'Urological',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Sudden onset severe testicular pain',
      'Nausea and vomiting',
      'High-riding testicle',
      'Absent cremasteric reflex',
      'Horizontal lie of testis',
      'More common in adolescents'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis - do not delay for imaging',
      'Doppler ultrasound showing absent/decreased testicular blood flow',
      'Abnormal testicular lie'
    ],
    differentials: ['Epididymitis', 'Orchitis', 'Testicular tumor', 'Inguinal hernia'],
    redFlags: [
      'Time-critical (salvage rate drops after 6 hours)',
      'Bilateral torsion',
      'Delayed presentation'
    ],
    assessmentPathway: 'Urgent urology consult → Emergency scrotal exploration and detorsion → Do NOT delay for ultrasound if high suspicion → Bilateral orchidopexy → Manual detorsion may be attempted but surgery still needed'
  },

  {
    condition: 'Fournier\'s Gangrene',
    icd10: 'N49.3',
    category: 'Urological/Infectious',
    severity: 'Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 100 },
      spo2: { min: null, max: null },
      temp: { min: 38, max: null }
    },
    presentations: [
      'Scrotal/perineal pain and swelling',
      'Scrotal erythema, crepitus',
      'Systemic toxicity',
      'Foul-smelling discharge',
      'Skin necrosis',
      'Risk factors: diabetes, immunosuppression'
    ],
    confirmatoryFindings: [
      'CT showing subcutaneous gas in perineum/scrotum',
      'Elevated lactate',
      'Leukocytosis',
      'Intraoperative necrotic tissue'
    ],
    differentials: ['Scrotal abscess', 'Epididymo-orchitis', 'Hernia'],
    redFlags: [
      'Crepitus',
      'Rapid progression',
      'Septic shock',
      'Skin necrosis'
    ],
    assessmentPathway: 'Urgent urology + surgical consult → Broad-spectrum antibiotics (pip-tazo + clindamycin + metronidazole) → Aggressive fluid resuscitation → Emergency surgical debridement → Repeated debridement often needed → Hyperbaric oxygen (controversial) → High mortality (20-40%)'
  },

  // ==================== FRACTURES / ORTHOPAEDIC TRAUMA ====================

  {
    condition: 'Hip Fracture (Neck of Femur)',
    icd10: 'S72.0',
    category: 'Trauma/Orthopaedic',
    severity: 'High',
    vitalSigns: {
      hr: { min: 90, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Hip pain following fall',
      'Unable to weight bear',
      'Shortened, externally rotated leg',
      'Usually elderly patients',
      'History of osteoporosis or low-impact trauma'
    ],
    confirmatoryFindings: [
      'X-ray showing femoral neck or intertrochanteric fracture',
      'MRI if X-ray negative but high suspicion',
      'CT for complex fractures',
      'Blood loss assessment (Hb)'
    ],
    differentials: ['Pelvic fracture', 'Acetabular fracture', 'Hip dislocation', 'Pubic rami fracture'],
    redFlags: [
      'Hemodynamic instability (significant blood loss)',
      'Open fracture',
      'Neurovascular compromise',
      'High mortality in elderly (20-30% at 1 year)'
    ],
    assessmentPathway: 'Analgesia (femoral nerve block) → X-ray (AP pelvis + lateral hip) → Orthopaedic consult → NBM for surgery → Surgical fixation within 36 hours → VTE prophylaxis → Early mobilization'
  },

  {
    condition: 'Femoral Shaft Fracture',
    icd10: 'S72.3',
    category: 'Trauma/Orthopaedic',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 100, max: null },
      rr: { min: 18, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Severe thigh pain',
      'Deformity, swelling',
      'Unable to move leg',
      'High-energy trauma (MVA, fall from height)',
      'Signs of shock if significant blood loss'
    ],
    confirmatoryFindings: [
      'X-ray showing femoral shaft fracture',
      'Assess for open fracture',
      'Falling Hb (can lose 1-2L blood into thigh)',
      'Check distal neurovascular status'
    ],
    differentials: ['Hip fracture', 'Knee dislocation', 'Pelvic fracture'],
    redFlags: [
      'Hemodynamic instability',
      'Open fracture',
      'Neurovascular injury',
      'Fat embolism',
      'Compartment syndrome'
    ],
    assessmentPathway: 'ATLS if polytrauma → Analgesia → Splinting/traction → IV fluid resuscitation → X-ray → Urgent orthopaedic consult → Intramedullary nailing → Monitor for complications (fat embolism, compartment syndrome)'
  },

  {
    condition: 'Tibial/Fibular Fracture',
    icd10: 'S82.2',
    category: 'Trauma/Orthopaedic',
    severity: 'Moderate-High',
    vitalSigns: {
      hr: { min: 85, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Lower leg pain',
      'Swelling, deformity',
      'Unable to weight bear',
      'Tenderness over tibia/fibula',
      'May be open fracture'
    ],
    confirmatoryFindings: [
      'X-ray showing tibial/fibular fracture',
      'Assess compartments (pain, tense swelling)',
      'Check distal pulses and sensation',
      'Isolated fibular fracture often stable'
    ],
    differentials: ['Ankle fracture', 'Knee injury', 'Soft tissue injury'],
    redFlags: [
      'Open fracture (high infection risk)',
      'Compartment syndrome',
      'Vascular injury',
      'Gustilo-Anderson classification for open fractures'
    ],
    assessmentPathway: 'Analgesia → Splinting → X-ray (include knee and ankle) → If closed and stable: cast → If open or displaced: IV antibiotics + tetanus + urgent ortho → ORIF or external fixation → Monitor for compartment syndrome'
  },

  {
    condition: 'Humerus Fracture',
    icd10: 'S42.3',
    category: 'Trauma/Orthopaedic',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Upper arm pain',
      'Swelling, deformity',
      'Inability to move arm',
      'History of fall or direct blow',
      'May have wrist drop if radial nerve injury'
    ],
    confirmatoryFindings: [
      'X-ray showing humeral shaft fracture',
      'Radial nerve examination (wrist extension, thumb extension)',
      'Assess for open fracture',
      'Check distal pulses'
    ],
    differentials: ['Shoulder dislocation', 'Clavicle fracture', 'Elbow injury'],
    redFlags: [
      'Radial nerve palsy (wrist drop)',
      'Vascular injury (brachial artery)',
      'Open fracture',
      'Pathological fracture'
    ],
    assessmentPathway: 'Analgesia → Splinting (U-slab or hanging cast) → X-ray → Check radial nerve → Most treated conservatively with functional bracing → ORIF if open, vascular injury, or polytrauma → Follow-up for nerve recovery'
  },

  {
    condition: 'Radius/Ulna Fracture (Forearm)',
    icd10: 'S52.5',
    category: 'Trauma/Orthopaedic',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Forearm pain',
      'Swelling, deformity',
      'Inability to rotate forearm',
      'History of fall onto outstretched hand (FOOSH)',
      'Tenderness over radius/ulna'
    ],
    confirmatoryFindings: [
      'X-ray showing radius/ulna fracture',
      'Assess for compartment syndrome',
      'Check median, radial, ulnar nerve function',
      'Assess for Monteggia or Galeazzi fracture-dislocation'
    ],
    differentials: ['Wrist fracture', 'Elbow injury', 'Soft tissue injury'],
    redFlags: [
      'Open fracture',
      'Compartment syndrome',
      'Neurovascular compromise',
      'Monteggia/Galeazzi fracture-dislocation'
    ],
    assessmentPathway: 'Analgesia → Splinting → X-ray (include wrist and elbow joints) → If undisplaced: cast → If displaced: reduction + cast or ORIF → Monitor for compartment syndrome → Check nerve function'
  },

  {
    condition: 'Colles Fracture (Distal Radius)',
    icd10: 'S52.5',
    category: 'Trauma/Orthopaedic',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Wrist pain following fall',
      'Dinner fork deformity',
      'Swelling, tenderness at distal radius',
      'Common in elderly with osteoporosis',
      'Fall onto outstretched hand (FOOSH)'
    ],
    confirmatoryFindings: [
      'X-ray showing distal radius fracture with dorsal angulation',
      'Assess for ulnar styloid fracture',
      'Check median nerve (carpal tunnel syndrome)',
      'Assess joint involvement'
    ],
    differentials: ['Scaphoid fracture', 'Wrist sprain', 'Smith fracture (volar angulation)'],
    redFlags: [
      'Open fracture',
      'Median nerve compression (acute carpal tunnel)',
      'Intra-articular fracture',
      'Significant displacement or angulation'
    ],
    assessmentPathway: 'Analgesia → X-ray (wrist + forearm) → If minimally displaced: cast → If displaced: manipulation under anesthesia (Bier block) + cast → If unstable: K-wire fixation or ORIF → Check median nerve function → Follow-up X-ray at 1 week'
  },

  {
    condition: 'Ankle Fracture',
    icd10: 'S82.8',
    category: 'Trauma/Orthopaedic',
    severity: 'Moderate',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Ankle pain and swelling',
      'Unable to weight bear',
      'Deformity',
      'History of inversion or eversion injury',
      'Tenderness over malleoli'
    ],
    confirmatoryFindings: [
      'X-ray showing malleolar fracture',
      'Ottawa ankle rules to guide imaging',
      'Weber classification (A, B, C based on fibula fracture level)',
      'Assess for syndesmotic injury',
      'Check for joint congruence'
    ],
    differentials: ['Ankle sprain', 'Achilles tendon rupture', 'Tibial fracture'],
    redFlags: [
      'Open fracture',
      'Neurovascular compromise',
      'Dislocation',
      'Talar shift (unstable)',
      'Compartment syndrome (rare)'
    ],
    assessmentPathway: 'Analgesia → Splinting → X-ray (ankle + foot) → If stable Weber A: boot or cast → If unstable (Weber B/C, bimalleolar, trimalleolar): ORIF → Post-op: non-weight bearing → VTE prophylaxis'
  },

  {
    condition: 'Clavicle Fracture',
    icd10: 'S42.0',
    category: 'Trauma/Orthopaedic',
    severity: 'Low-Moderate',
    vitalSigns: {
      hr: { min: null, max: null },
      rr: { min: null, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Shoulder pain',
      'Visible deformity or bump',
      'Holding arm close to body',
      'History of fall or direct blow',
      'Tenderness over clavicle'
    ],
    confirmatoryFindings: [
      'X-ray showing clavicle fracture',
      'Most common in middle third',
      'Assess skin integrity',
      'Check neurovascular status (brachial plexus, subclavian vessels)'
    ],
    differentials: ['Shoulder dislocation', 'AC joint injury', 'Scapula fracture'],
    redFlags: [
      'Open fracture',
      'Neurovascular compromise',
      'Tenting of skin',
      'Significant displacement',
      'Pneumothorax (rare)'
    ],
    assessmentPathway: 'Analgesia → X-ray (clavicle + CXR if trauma) → Most treated conservatively with broad arm sling → ORIF if open, neurovascular injury, or significantly displaced → Early mobilization → Heals in 6-8 weeks'
  },

  {
    condition: 'Open Fracture',
    icd10: 'S42.9',
    category: 'Trauma/Orthopaedic',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 95, max: null },
      rr: { min: 18, max: null },
      sbp: { min: null, max: null },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Fracture with overlying wound',
      'Bone visible through skin',
      'Significant soft tissue injury',
      'High-energy trauma',
      'Contamination risk'
    ],
    confirmatoryFindings: [
      'Clinical diagnosis: wound communicating with fracture',
      'X-ray to characterize fracture',
      'Gustilo-Anderson classification (I, II, IIIA, IIIB, IIIC)',
      'Assess neurovascular status'
    ],
    differentials: ['Closed fracture with laceration', 'Soft tissue injury'],
    redFlags: [
      'Vascular injury (pulseless limb)',
      'Compartment syndrome',
      'Gross contamination',
      'High Gustilo grade (III)',
      'Limb-threatening injury'
    ],
    assessmentPathway: 'ATLS if polytrauma → Remove gross contamination → Sterile dressing → Analgesia → IV antibiotics (cefazolin + gentamicin; add penicillin if soil contamination) → Tetanus prophylaxis → Splinting → URGENT orthopaedic consult → Surgical debridement + fixation within 6 hours → Consider amputation if severe'
  },

  {
    condition: 'Pelvic Fracture',
    icd10: 'S32.5',
    category: 'Trauma/Orthopaedic',
    severity: 'High-Critical',
    vitalSigns: {
      hr: { min: 110, max: null },
      rr: { min: 20, max: null },
      sbp: { min: null, max: 90 },
      spo2: { min: null, max: null }
    },
    presentations: [
      'Pelvic pain',
      'Unable to weight bear',
      'History of high-energy trauma (MVA, fall)',
      'Leg length discrepancy',
      'Signs of shock',
      'Blood at urethral meatus',
      'Scrotal/perineal hematoma'
    ],
    confirmatoryFindings: [
      'X-ray (AP pelvis) showing fracture',
      'CT for detailed assessment',
      'FAST scan for free fluid',
      'Young-Burgess or Tile classification',
      'Urinalysis (hematuria)',
      'Falling Hb'
    ],
    differentials: ['Hip fracture', 'Acetabular fracture', 'Sacral fracture'],
    redFlags: [
      'Hemodynamic instability (life-threatening hemorrhage)',
      'Open-book fracture (unstable)',
      'Vertical shear injury',
      'Urethral injury',
      'Rectal injury',
      'Bladder rupture'
    ],
    assessmentPathway: 'ATLS protocol → Pelvic binder or sheet → IV fluid resuscitation → Massive transfusion protocol if unstable → CT if stable → Angiography + embolization for ongoing bleeding → External fixation if unstable → Definitive ORIF when stable → Do NOT spring pelvis clinically'
  }
];

// Vital sign matching thresholds
const VITAL_SIGN_MATCH_WEIGHT = {
  hr: 0.3,
  rr: 0.25,
  sbp: 0.25,
  spo2: 0.2
};

// Bound what goes into an assessment prompt: the first few turns (the presenting
// complaint) plus the most recent ones, with an explicit marker for what was
// dropped so the model is not left to infer a gap it cannot see.
const TRANSCRIPT_HEAD = 4;
const TRANSCRIPT_TAIL = 36;
function windowTranscript(history) {
  const usable = (history || []).filter(entry => entry && entry.text);
  const line = (entry) => `[${entry.speakerLabel || 'Unknown'}]: ${entry.text}`;
  if (usable.length <= TRANSCRIPT_HEAD + TRANSCRIPT_TAIL) return usable.map(line).join('\n');
  const dropped = usable.length - TRANSCRIPT_HEAD - TRANSCRIPT_TAIL;
  return [
    ...usable.slice(0, TRANSCRIPT_HEAD).map(line),
    `[...${dropped} earlier turns omitted...]`,
    ...usable.slice(-TRANSCRIPT_TAIL).map(line)
  ].join('\n');
}

const VideoFrameAnalyzer = () => {
  const [videoFile, setVideoFile] = useState(null);

  const [currentFrame, setCurrentFrame] = useState(null);

  const [frameDescription, setFrameDescription] = useState('');

  const [triageScore, setTriageScore] = useState(null);

  const [triageReasoning, setTriageReasoning] = useState('');

  const [triageLogprobs, setTriageLogprobs] = useState(null);
  const [scoreConfidences, setScoreConfidences] = useState(null);

  const [assessmentStage, setAssessmentStage] = useState(null);

  const [abcdeFindings, setAbcdeFindings] = useState(null);

  const [hoveredAbcdeElement, setHoveredAbcdeElement] = useState(null);

  const [assessmentRecommendations, setAssessmentRecommendations] = useState('');

  const [actionPlan, setActionPlan] = useState(null);

  const [diagnosisPrediction, setDiagnosisPrediction] = useState(null);

  const [emergencyGuidelines, setEmergencyGuidelines] = useState(null);

  const [structuredAssessment, setStructuredAssessment] = useState(null);

  const [medicationRecommendations, setMedicationRecommendations] = useState(null);

  const [transportationRecommendations, setTransportationRecommendations] = useState(null);

  // Vital signs trending (time-series data)
  const [vitalSignsHistory, setVitalSignsHistory] = useState([]);

  // API request initiation timestamp tracking
  const [lastAnalyzeFrameRequest, setLastAnalyzeFrameRequest] = useState(null);
  const [lastAssessmentStageRequest, setLastAssessmentStageRequest] = useState(null);
  const [lastTriageScoreRequest, setLastTriageScoreRequest] = useState(null);
  
  // Timer state for forcing re-renders to update elapsed time displays
  const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0);

  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [error, setError] = useState('');

  const [success, setSuccess] = useState('');

  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [expandedHistoryItems, setExpandedHistoryItems] = useState(new Set());

  const [heartRate, setHeartRate] = useState(null);

  const [heartRateConfidence, setHeartRateConfidence] = useState(0);

  // Vital sign overrides (persistent for session)
  const [vitalOverrides, setVitalOverrides] = useState({
    heartRate: null,
    breathingRate: null,
    bloodPressureSystolic: null,
    bloodPressureDiastolic: null,
    spo2: null,
    temperature: null,
    bmi: null
  });
  const [showOverrideSection, setShowOverrideSection] = useState(false);

  // Function to log manual vital sign entry to analysis history
  const logManualVitalEntry = useCallback((vitalName, value, unit) => {
    const timestamp = new Date().toLocaleTimeString('en-AU', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
    
    const entry = {
      timestamp: timestamp,
      description: `📋 Manual Entry: ${vitalName} = ${value}${unit} (Clinically measured with equipment)`,
      type: 'manual_vital',
      vitalName: vitalName,
      value: value,
      unit: unit
    };
    
    setAnalysisHistory(prev => [entry, ...prev]);
    console.log(`[MANUAL-VITAL] Logged ${vitalName}: ${value}${unit}`);
  }, []);

  // Belief-Guided Vision state
  const [beliefGuidedVision, setBeliefGuidedVision] = useState({
    enabled: false,  // Toggle for BGV mode
    initialized: false,
    frameCount: 0,
    structure: null,
    dynamics: null,
    precision: null,
    lastObservation: null
  });

  const [isHeartRateDetecting, setIsHeartRateDetecting] = useState(false);

  const [heartRateHistory, setHeartRateHistory] = useState([]);
  
  // Clinical Decision Support state
  const [cdsExpanded, setCdsExpanded] = useState(true); // Auto-expand on load
  const [cdsSearchTerm, setCdsSearchTerm] = useState('');
  const [cdsSelectedCategory, setCdsSelectedCategory] = useState('All');
  const [matchedConditions, setMatchedConditions] = useState([]);

  const [healthRecord, setHealthRecord] = useState([]);
  const [isHealthRecordAnalyzing, setIsHealthRecordAnalyzing] = useState(false);
  const [isAutoAnalysisEnabled, setIsAutoAnalysisEnabled] = useState(false);

  const [autoAnalysisInterval, setAutoAnalysisInterval] = useState(5000); // 5 seconds default

  const [isVisionAnalyzing, setIsVisionAnalyzing] = useState(false);

  const [isMetaAnalyzing, setIsMetaAnalyzing] = useState(false);

  const [metaAnalysis, setMetaAnalysis] = useState('');

  const [metaAnalysisInterval, setMetaAnalysisInterval] = useState(10000); // 10 seconds default

  // bmi extracted from vision analysis
  const [bmi, setBmi] = useState(null);
  // BMI from webcam analysis
  const [webcamBmi, setWebcamBmi] = useState(null);
  
  // Webcam state
  const [isWebcamActive, setIsWebcamActive] = useState(false);
  const [showEnhancedView, setShowEnhancedView] = useState(false); // Toggle between enhanced and simple webcam view (right sidebar)
  const [showLeftSidebar, setShowLeftSidebar] = useState(false); // Toggle left sidebar
  const [showHeartRateOverlay, setShowHeartRateOverlay] = useState(true); // Toggle heart rate overlay on webcam (legacy - not used)
  const [showBreathingOverlay, setShowBreathingOverlay] = useState(true); // Toggle breathing overlay on webcam (legacy - not used)
  const [isMhrLoading, setIsMhrLoading] = useState(false);
  const [lastMhrStatus, setLastMhrStatus] = useState('');
  const [showMedicareCardModal, setShowMedicareCardModal] = useState(false);
  const [medicareCardScanned, setMedicareCardScanned] = useState(false);
  const [medicareCardAnalysis, setMedicareCardAnalysis] = useState('');
  const [isAnalyzingMedicareCard, setIsAnalyzingMedicareCard] = useState(false);
  const [showPatientDetailsModal, setShowPatientDetailsModal] = useState(false);
  const [isListeningForDetails, setIsListeningForDetails] = useState(false);
  const [patientDetailsTranscript, setPatientDetailsTranscript] = useState('');
  const [patientDetailsRecognition, setPatientDetailsRecognition] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [webcamImage, setWebcamImage] = useState(null);
  const [webcamBpm, setWebcamBpm] = useState(null);
  const [webcamBreathingRate, setWebcamBreathingRate] = useState(null);
  const [webcamBloodPressure, setWebcamBloodPressure] = useState(null); // { systolic, diastolic, confidence }
  const [webcamSpo2, setWebcamSpo2] = useState(null); // { value, confidence, calibrated }
  const [videoSource, setVideoSource] = useState('local'); // 'local' or 'ngrok'
  const [ngrokUrl, setNgrokUrl] = useState('https://flying-strangely-gelding.ngrok-free.app/video_feed');
  const [bpmHistory, setBpmHistory] = useState([]);
  const [rdrObservations, setRdrObservations] = useState([]); // RDR chart history
  const lastRdrUpdateRef = useRef(null); // Track last RDR update time
  const [selectedVitalChart, setSelectedVitalChart] = useState(null); // For chart modal
  const rdrTableRef = useRef(null); // For auto-scrolling to newest readings
  const lastSnapshotTimeRef = useRef(null);
  
  // LLM-derived vitals
  const [llmVitals, setLlmVitals] = useState({
    gcs: null,
    consciousness: 'Alert',
    painScore: null,
    sedationScore: null,
    temperature: null,
    bgl: null,
    newPain: 'N'
  });
  const lastLlmUpdateRef = useRef(null); // Track last LLM update time
  
  // Auto-scroll RDR table to show newest readings (rightmost)
  useEffect(() => {
    if (rdrTableRef.current && rdrObservations.length > 0) {
      // Scroll to the far right to show newest readings
      rdrTableRef.current.scrollLeft = rdrTableRef.current.scrollWidth;
    }
  }, [rdrObservations]);

  // Render vital sign chart modal
  const renderVitalChart = () => {
    if (!selectedVitalChart || rdrObservations.length === 0) return null;

    const { parameter, label, unit, data, color, normalRange } = selectedVitalChart;
    
    // Filter out null values and prepare chart data
    const chartData = rdrObservations
      .map((obs, idx) => ({ time: obs.time, value: obs[parameter], index: idx }))
      .filter(d => d.value !== null && d.value !== undefined);

    if (chartData.length === 0) {
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => setSelectedVitalChart(null)}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0', color: '#212529' }}>{label} Over Time</h3>
            <p style={{ color: '#6c757d' }}>No data available for this parameter yet.</p>
            <button
              onClick={() => setSelectedVitalChart(null)}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Close
            </button>
          </div>
        </div>
      );
    }

    const maxValue = Math.max(...chartData.map(d => d.value));
    const minValue = Math.min(...chartData.map(d => d.value));
    const range = maxValue - minValue || 1;
    const padding = range * 0.2;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }} onClick={() => setSelectedVitalChart(null)}>
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '800px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto'
        }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#212529' }}>{label} Over Time</h3>
            <button
              onClick={() => setSelectedVitalChart(null)}
              style={{
                padding: '4px 12px',
                backgroundColor: '#e5e7eb',
                color: '#212529',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              ✕
            </button>
          </div>

          {/* Chart */}
          <div style={{ 
            position: 'relative', 
            height: '300px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '16px'
          }}>
            <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                const y = 280 * (1 - ratio);
                const value = (minValue - padding + (maxValue - minValue + 2 * padding) * ratio).toFixed(1);
                return (
                  <g key={idx}>
                    <line
                      x1="40"
                      y1={y}
                      x2="100%"
                      y2={y}
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      strokeDasharray="4"
                    />
                    <text
                      x="35"
                      y={y + 4}
                      textAnchor="end"
                      fontSize="10"
                      fill="#6c757d"
                    >
                      {value}
                    </text>
                  </g>
                );
              })}

              {/* Normal range shading */}
              {normalRange && (
                <rect
                  x="40"
                  y={280 * (1 - (normalRange.max - (minValue - padding)) / (maxValue - minValue + 2 * padding))}
                  width="calc(100% - 40px)"
                  height={280 * ((normalRange.max - normalRange.min) / (maxValue - minValue + 2 * padding))}
                  fill="#d1fae5"
                  opacity="0.3"
                />
              )}

              {/* Line chart */}
              <polyline
                points={chartData.map((d, idx) => {
                  const x = 40 + (idx / (chartData.length - 1 || 1)) * (window.innerWidth * 0.8 - 80);
                  const y = 280 * (1 - (d.value - (minValue - padding)) / (maxValue - minValue + 2 * padding));
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinejoin="round"
              />

              {/* Data points */}
              {chartData.map((d, idx) => {
                const x = 40 + (idx / (chartData.length - 1 || 1)) * (window.innerWidth * 0.8 - 80);
                const y = 280 * (1 - (d.value - (minValue - padding)) / (maxValue - minValue + 2 * padding));
                return (
                  <g key={idx}>
                    <circle
                      cx={x}
                      cy={y}
                      r="5"
                      fill={color}
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                    <text
                      x={x}
                      y={y - 12}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="600"
                      fill="#212529"
                    >
                      {d.value}
                    </text>
                  </g>
                );
              })}

              {/* X-axis labels */}
              {chartData.map((d, idx) => {
                const x = 40 + (idx / (chartData.length - 1 || 1)) * (window.innerWidth * 0.8 - 80);
                return (
                  <text
                    key={idx}
                    x={x}
                    y="295"
                    textAnchor="middle"
                    fontSize="9"
                    fill="#6c757d"
                  >
                    {d.time}
                  </text>
                );
              })}
            </svg>
          </div>

          {/* Summary stats */}
          <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6c757d' }}>
            <div>
              <strong>Latest:</strong> {chartData[chartData.length - 1].value} {unit}
            </div>
            <div>
              <strong>Min:</strong> {minValue.toFixed(1)} {unit}
            </div>
            <div>
              <strong>Max:</strong> {maxValue.toFixed(1)} {unit}
            </div>
            <div>
              <strong>Avg:</strong> {(chartData.reduce((sum, d) => sum + d.value, 0) / chartData.length).toFixed(1)} {unit}
            </div>
            {normalRange && (
              <div>
                <strong>Normal:</strong> {normalRange.min}-{normalRange.max} {unit}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Add new RDR observation
  const addRdrObservation = useCallback((vitals) => {
    const now = new Date();
    const observation = {
      timestamp: now,
      time: now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }),
      date: now.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }),
      rr: vitals.rr || null,
      spo2: vitals.spo2 || null,
      o2Flow: vitals.o2Flow || null,
      sbp: vitals.sbp || null,
      dbp: vitals.dbp || null,
      hr: vitals.hr || null,
      temp: vitals.temp || null,
      consciousness: vitals.consciousness || 'Alert',
      gcs: vitals.gcs || null, // Glasgow Coma Scale (3-15)
      sedationScore: vitals.sedationScore !== undefined ? vitals.sedationScore : null,
      painScore: vitals.painScore !== undefined ? vitals.painScore : null,
      newPain: vitals.newPain || 'N', // New/Unexpected Pain (Y/N)
      bgl: vitals.bgl || null
    };
    
    setRdrObservations(prev => [...prev, observation].slice(-20)); // Keep last 20 observations
  }, []);
  
  // Update RDR chart periodically (every 5 seconds for testing, normally 15 minutes)
  useEffect(() => {
    if (!isWebcamActive) return; // Only run when webcam is active
    
    const checkAndUpdateRdr = () => {
      const now = Date.now();
      const shouldUpdate = !lastRdrUpdateRef.current || (now - lastRdrUpdateRef.current) > 5000; // 5 sec for testing (was 900000 = 15 min)
      
      // Check if we have any vitals (heartRate can be 0, so check !== null)
      const hasVitals = (heartRate !== null && heartRate !== undefined) || webcamBreathingRate || webcamBloodPressure || webcamSpo2;
      
      // Debug logging (disabled)
      // console.log('[RDR-CHECK] shouldUpdate:', shouldUpdate, 'hasVitals:', hasVitals, 'heartRate:', heartRate, 'type:', typeof heartRate);
      
      if (shouldUpdate && hasVitals) {
        // Combine video vitals + LLM vitals + overrides
        const vitals = {
          // Video-derived vitals (with overrides taking priority)
          rr: vitalOverrides.breathingRate || webcamBreathingRate,
          spo2: vitalOverrides.spo2 || webcamSpo2?.value,
          hr: vitalOverrides.heartRate || heartRate,
          sbp: vitalOverrides.bloodPressureSystolic || webcamBloodPressure?.systolic,
          dbp: vitalOverrides.bloodPressureDiastolic || webcamBloodPressure?.diastolic,
          temp: vitalOverrides.temperature || llmVitals.temperature,
          
          // LLM-derived vitals
          gcs: llmVitals.gcs,
          consciousness: llmVitals.consciousness,
          painScore: llmVitals.painScore,
          sedationScore: llmVitals.sedationScore,
          bgl: llmVitals.bgl,
          newPain: llmVitals.newPain
        };
        
        // Note: LLM vitals (GCS, Pain, Sedation, Temp, BGL) will be null until
        // LLM endpoints are called with real patient data
        console.log('[RDR] ✅ Adding observation with vitals:', vitals);
        console.log('[RDR-DEBUG] heartRate state value:', heartRate);
        
        addRdrObservation(vitals);
        lastRdrUpdateRef.current = now;
      } else {
        console.log('[RDR] ❌ Skipping update - shouldUpdate:', shouldUpdate, 'hasVitals:', hasVitals);
      }
    };
    
    // Run immediately on mount/change
    checkAndUpdateRdr();
    
    // Then set up interval to check every 2 seconds
    const intervalId = setInterval(checkAndUpdateRdr, 2000);
    
    return () => clearInterval(intervalId);
  }, [isWebcamActive, heartRate, webcamBreathingRate, webcamBloodPressure, webcamSpo2, 
      llmVitals.gcs, llmVitals.consciousness, llmVitals.painScore, llmVitals.sedationScore, 
      llmVitals.temperature, llmVitals.bgl, llmVitals.newPain, addRdrObservation,
      vitalOverrides.heartRate, vitalOverrides.breathingRate, vitalOverrides.bloodPressureSystolic, 
      vitalOverrides.bloodPressureDiastolic, vitalOverrides.spo2, vitalOverrides.temperature]);
  
  // RDR Scoring Function
  const calculateRDRScore = (vitals) => {
    let totalScore = 0;
    let scores = {};
    let alerts = [];
    
    // Respiratory Rate (breaths/min)
    if (vitals.rr) {
      if (vitals.rr <= 8) { scores.rr = 3; alerts.push('Critical RR'); }
      else if (vitals.rr <= 11) { scores.rr = 1; }
      else if (vitals.rr <= 20) { scores.rr = 0; }
      else if (vitals.rr <= 24) { scores.rr = 2; }
      else { scores.rr = 3; alerts.push('Critical RR'); }
      totalScore += scores.rr;
    }
    
    // SpO2 (%)
    if (vitals.spo2) {
      if (vitals.spo2 <= 91) { scores.spo2 = 3; alerts.push('Critical SpO2'); }
      else if (vitals.spo2 <= 93) { scores.spo2 = 2; }
      else if (vitals.spo2 <= 95) { scores.spo2 = 1; }
      else { scores.spo2 = 0; }
      totalScore += scores.spo2;
    }
    
    // Heart Rate (bpm)
    if (vitals.hr) {
      if (vitals.hr <= 40) { scores.hr = 3; alerts.push('Critical HR'); }
      else if (vitals.hr <= 50) { scores.hr = 1; }
      else if (vitals.hr <= 90) { scores.hr = 0; }
      else if (vitals.hr <= 110) { scores.hr = 1; }
      else if (vitals.hr <= 130) { scores.hr = 2; }
      else { scores.hr = 3; alerts.push('Critical HR'); }
      totalScore += scores.hr;
    }
    
    // Systolic BP (mmHg)
    if (vitals.sbp) {
      if (vitals.sbp <= 90) { scores.sbp = 3; alerts.push('Critical BP'); }
      else if (vitals.sbp <= 100) { scores.sbp = 2; }
      else if (vitals.sbp <= 110) { scores.sbp = 1; }
      else if (vitals.sbp <= 219) { scores.sbp = 0; }
      else { scores.sbp = 3; alerts.push('Critical BP'); }
      totalScore += scores.sbp;
    }
    
    // Temperature (°C) - if available
    if (vitals.temp) {
      if (vitals.temp <= 35.0) { scores.temp = 3; alerts.push('Critical Temp'); }
      else if (vitals.temp <= 36.0) { scores.temp = 1; }
      else if (vitals.temp <= 38.0) { scores.temp = 0; }
      else if (vitals.temp <= 39.0) { scores.temp = 1; }
      else { scores.temp = 2; }
      totalScore += scores.temp;
    }
    
    // Consciousness (AVPU)
    if (vitals.consciousness && vitals.consciousness !== 'Alert') {
      scores.consciousness = 3;
      alerts.push('Altered Consciousness');
      totalScore += 3;
    } else {
      scores.consciousness = 0;
    }
    
    // Determine escalation level
    let escalation = 'routine';
    let escalationColor = '#10b981'; // green
    
    if (totalScore >= 7 || alerts.length > 0) {
      escalation = 'emergency';
      escalationColor = '#ef4444'; // red
    } else if (totalScore >= 5) {
      escalation = 'urgent';
      escalationColor = '#f97316'; // orange
    } else if (totalScore >= 3) {
      escalation = 'increased';
      escalationColor = '#eab308'; // yellow
    }
    
    return { totalScore, scores, alerts, escalation, escalationColor };
  };
  const [spectrumData, setSpectrumData] = useState({ frequencies: [], amplitudes: [] });
  const [emotionData, setEmotionData] = useState({ dominant: null, confidence: {}, age: null, gender: null });
  const [overlaySettings, setOverlaySettings] = useState({ pose: false, hr_quadrants: false, br_quadrants: false });
  const [selectedScenario, setSelectedScenario] = useState("1");
  
  // BP Cuff overlay state
  const [isBpCuffActive, setIsBpCuffActive] = useState(false);
  const [isBpCuffUploading, setIsBpCuffUploading] = useState(false);
  const [showMediaDropdown, setShowMediaDropdown] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState('video:diagrammatic_BP_instruction_video.mp4');
  
  // Available media options
  const [mediaOptions] = useState([
    { value: 'video:diagrammatic_BP_instruction_video.mp4', label: '🎥 BP Instruction Video', type: 'video' },
    { value: 'video:ikea_style_BP_instruction_video.mp4', label: '🎥 Ikea-style BP Instruction Video', type: 'video' },
    { value: 'video:open_box_medicine.mp4', label: '🎥 Open Box Medicine Video', type: 'video' },
    { value: 'video:pulse_oximeter_video.mp4', label: '🎥 Pulse Oximeter Video', type: 'video' },
    { value: 'video:transcutaneous_patch_apply_video.mp4', label: '🎥 Transcutaneous Patch Apply Video', type: 'video' },
    { value: 'image:bp_cuff_step_1.png', label: '📷 BP Cuff Step 1', type: 'image' },
    { value: 'image:bp_cuff_step_2.png', label: '📷 BP Cuff Step 2', type: 'image' },
    { value: 'image:urine-colour-chart.png', label: '📊 Urine Color Chart', type: 'chart' },
    { value: 'clear', label: '🚫 Clear Overlay', type: 'clear' }
  ]);
  
  // Medical action types dictionary
  const [actions] = useState({
    'route_plane': { label: 'Route Plane', status: null, category: 'transport', description: 'Arrange air medical transport' },
    'open_chest': { label: 'Open Medical Chest', status: null, category: 'medical supplies', description: 'Provide access to locally stored medical supplies' }
  });
  
  // Process logs state for debugging
  const [showProcessLogs, setShowProcessLogs] = useState(false);
  const [processLogs, setProcessLogs] = useState([]);
  
  // Continuous analysis state
  const [continuousAnalysis, setContinuousAnalysis] = useState(false);
  
  // Debug BMI values
  useEffect(() => {
    console.log('BMI Debug - bmi:', bmi, 'type:', typeof bmi, 'webcamBmi:', webcamBmi, 'type:', typeof webcamBmi);
  }, [bmi, webcamBmi]);

  // Monitor actionPlan changes
  useEffect(() => {
    if (actionPlan) {
      console.log('ActionPlan modified:', actionPlan);
      // Function stub - implement your custom logic here
      handleActionPlanChange(actionPlan);
    }
  }, [actionPlan]);

  // Continuous analysis effect
  useEffect(() => {
    let interval;
    // console.log('Continuous analysis effect triggered. continuousAnalysis:', continuousAnalysis, 'currentFrame:', currentFrame ? 'available' : 'null', 'isAnalyzing:', isAnalyzing);
    if (continuousAnalysis && currentFrame && !isAnalyzing) {
      interval = setInterval(() => {
        if (currentFrame && !isAnalyzing) {
          console.log('running new frame analysis...');
          analyzeFrameWithData(currentFrame);
        }
      }, 500); // Analyze every 0.5s after vision API completes (downstream processing continues async)
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [continuousAnalysis, currentFrame, isAnalyzing]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMediaDropdown && !event.target.closest('[data-media-dropdown]')) {
        setShowMediaDropdown(false);
      }
    };

    if (showMediaDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMediaDropdown]);

  // Function stub that runs when actionPlan is modified
  const handleActionPlanChange = (newActionPlan) => {
    //if the actionPlan mentions urine
    if (newActionPlan && typeof newActionPlan === 'object') {
      // if the action plan json dictionary includes the word 'urine' or 'dehydration'
      const mentionsUrine = Object.values(newActionPlan).some(item => 
        typeof item === 'string' && item.toLowerCase().includes('urine')
      );
      const mentionsDehydration = Object.values(newActionPlan).some(item => 
        typeof item === 'string' && item.toLowerCase().includes('dehydration')
      );

      if (mentionsUrine || mentionsDehydration) {
        // show the urine color chart to the client by sending the image to /api/upload-frame
        axios.post('/api/upload-frame', { command: 'urine-chart' })
          .then(response => {
            console.log('Urine color chart uploaded successfully:', response.data);
          })
          .catch(error => {
            console.error('Error uploading urine color chart:', error);
          });
      }
    }

    // Example usage scenarios:
    // - Validate the action plan
    // - Update UI components based on the plan
    // - Send updates to server
    // - Trigger medical workflow steps
    // - Log action plan history
  };
  
  // Smart condition matching function
  const matchConditionsToVitals = useCallback((hr, rr, sbp, spo2) => {
    if (!hr && !rr && !sbp && !spo2) {
      // No vitals available yet
      setMatchedConditions([]);
      return;
    }

    const matches = EMERGENCY_CONDITIONS.map(condition => {
      let score = 0;
      let matchCount = 0;
      const reasons = [];

      // Heart Rate matching
      if (hr && condition.vitalSigns.hr) {
        const hrRange = condition.vitalSigns.hr;
        if ((hrRange.min === null || hr >= hrRange.min) && (hrRange.max === null || hr <= hrRange.max)) {
          score += VITAL_SIGN_MATCH_WEIGHT.hr;
          matchCount++;
          reasons.push(`HR ${hr} matches range`);
        }
      }

      // Respiratory Rate matching
      if (rr && condition.vitalSigns.rr) {
        const rrRange = condition.vitalSigns.rr;
        if ((rrRange.min === null || rr >= rrRange.min) && (rrRange.max === null || rr <= rrRange.max)) {
          score += VITAL_SIGN_MATCH_WEIGHT.rr;
          matchCount++;
          reasons.push(`RR ${rr} matches range`);
        }
      }

      // Systolic BP matching
      if (sbp && condition.vitalSigns.sbp) {
        const sbpRange = condition.vitalSigns.sbp;
        if ((sbpRange.min === null || sbp >= sbpRange.min) && (sbpRange.max === null || sbp <= sbpRange.max)) {
          score += VITAL_SIGN_MATCH_WEIGHT.sbp;
          matchCount++;
          reasons.push(`SBP ${sbp} matches range`);
        }
      }

      // SpO2 matching
      if (spo2 && condition.vitalSigns.spo2) {
        const spo2Range = condition.vitalSigns.spo2;
        if ((spo2Range.min === null || spo2 >= spo2Range.min) && (spo2Range.max === null || spo2 <= spo2Range.max)) {
          score += VITAL_SIGN_MATCH_WEIGHT.spo2;
          matchCount++;
          reasons.push(`SpO2 ${spo2} matches range`);
        }
      }

      return {
        ...condition,
        matchScore: score,
        matchCount,
        matchReasons: reasons
      };
    }).filter(c => c.matchScore > 0) // Only show conditions with at least one vital sign match
      .sort((a, b) => {
        // Sort by score first, then by match count
        if (b.matchScore !== a.matchScore) {
          return b.matchScore - a.matchScore;
        }
        return b.matchCount - a.matchCount;
      })
      .slice(0, 10); // Top 10 matches

    setMatchedConditions(matches);
  }, []);

  // Auto-match conditions whenever vitals change
  useEffect(() => {
    // Extract systolic BP from structured assessment if available
    let sbp = null;
    if (structuredAssessment?.vital_signs?.blood_pressure) {
      const bpMatch = structuredAssessment.vital_signs.blood_pressure.match(/(\d+)\/(\d+)/);
      if (bpMatch) {
        sbp = parseInt(bpMatch[1]);
      }
    }

    // Extract SpO2 from structured assessment if available
    let spo2 = null;
    if (structuredAssessment?.vital_signs?.spo2) {
      const spo2Match = structuredAssessment.vital_signs.spo2.match(/(\d+)%?/);
      if (spo2Match) {
        spo2 = parseInt(spo2Match[1]);
      }
    }

    matchConditionsToVitals(
      heartRate || webcamBpm,
      webcamBreathingRate,
      sbp,
      spo2
    );
  }, [heartRate, webcamBpm, webcamBreathingRate, structuredAssessment, matchConditionsToVitals]);
  
  // Speech-to-text state
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [transcriptHistory, setTranscriptHistory] = useState([]);
  const [isClassifying, setIsClassifying] = useState(false);
  const recognitionRef = useRef(null);
  const transcriptScrollRef = useRef(null);
  
  // Fetch LLM-derived vitals from assessment endpoints
  const fetchLlmVitals = useCallback(async () => {
    try {
      // console.log('[LLM-VITALS] Starting fetch...');
      
      // Build comprehensive description from Analysis History (same as other endpoints)
      const allAnalyses = analysisHistory
        .filter(entry => entry.type !== 'transcript' && entry.visionAnalysis)
        .map(entry => entry.visionAnalysis);
      
      const combinedDescription = allAnalyses.join('\n\n--- Previous Analysis ---\n\n');
      
      // Build transcript text.
      //
      // This used to send the ENTIRE history on every pass, so the prompt grew
      // for the whole length of the consult: slower and dearer with every turn,
      // and a long repetitive history invites the model to repeat itself. The
      // server uses a supplied transcript whole -- it only slices its own
      // fallback -- so nothing downstream was bounding this either.
      //
      // Keep the OPENING, where the presenting complaint is stated, and the most
      // recent turns. A plain tail would let the chief complaint scroll out of
      // the assessment on any consult longer than a few minutes.
      const transcriptText = windowTranscript(transcriptHistory);
      
      // Add transcript if available
      let medicalDescription = transcriptText 
        ? `${combinedDescription}\n\n--- Clinical Transcript ---\n${transcriptText}`
        : combinedDescription;
      
      // Add vitals if available
      if (heartRate) {
        medicalDescription += `\n\n--- Vital Signs ---\nHeart Rate: ${heartRate} bpm`;
      }
      if (webcamBreathingRate) {
        medicalDescription += `\nRespiratory Rate: ${webcamBreathingRate} br/min`;
      }
      if (webcamBloodPressure) {
        medicalDescription += `\nBlood Pressure: ${webcamBloodPressure.systolic}/${webcamBloodPressure.diastolic} mmHg`;
      }
      if (webcamSpo2) {
        medicalDescription += `\nSpO2: ${webcamSpo2.value}%`;
      }
      if (webcamBmi) {
        medicalDescription += `\nBMI: ${webcamBmi}`;
      }
      
      // Fallback if no data available
      if (!medicalDescription || medicalDescription.trim() === '') {
        medicalDescription = 'Patient assessment in progress. Vital signs being monitored.';
      }
      
      // console.log('[LLM-VITALS] Using description from Analysis History:', medicalDescription.substring(0, 200) + '...');
      
      // console.log('[LLM-VITALS] Calling /api/structured-assessment...');
      // Call Structured Assessment endpoint
      const structuredResponse = await axios.post('/api/structured-assessment', {
        description: medicalDescription,
        triageScore: analysisHistory.length > 0 ? analysisHistory[0].triageScore : 3,
        assessmentStage: analysisHistory.length > 0 ? analysisHistory[0].assessmentStage : 'Primary Survey',
        abcdeFindings: analysisHistory.length > 0 ? analysisHistory[0].abcdeFindings : {},
        transcript: transcriptText
      });
      
      // console.log('[LLM-VITALS] Structured assessment response:', JSON.stringify(structuredResponse.data, null, 2));
      
      // console.log('[LLM-VITALS] Calling /api/assessment-stage...');
      // Call ABCDE Assessment endpoint
      const abcdeResponse = await axios.post('/api/assessment-stage', {
        description: medicalDescription,
        historyCount: analysisHistory.length,
        previousTriageScores: analysisHistory.map(entry => entry.triageScore).filter(Boolean)
      });
      
      // console.log('[LLM-VITALS] ABCDE assessment response:', JSON.stringify(abcdeResponse.data, null, 2));
      
      // Parse vital signs from Structured Assessment
      const vitalSigns = structuredResponse.data?.physical_examination?.vital_signs || {};
      // console.log('[LLM-VITALS] Parsed vital signs:', vitalSigns);
      
      // Parse GCS, AVPU, and other vitals from ABCDE Disability assessment (structured fields)
      const disability = abcdeResponse.data?.abcde_findings?.disability || {};
      
      // Debug: Log the raw disability object (disabled)
      // console.log('[LLM-VITALS] ===== RAW DISABILITY OBJECT =====');
      // console.log('[LLM-VITALS] Full disability:', JSON.stringify(disability, null, 2));
      // console.log('[LLM-VITALS] gcs_score type:', typeof disability.gcs_score, 'value:', disability.gcs_score);
      // console.log('[LLM-VITALS] pain_score type:', typeof disability.pain_score, 'value:', disability.pain_score);
      // console.log('[LLM-VITALS] sedation_score type:', typeof disability.sedation_score, 'value:', disability.sedation_score);
      // console.log('[LLM-VITALS] temperature type:', typeof disability.temperature, 'value:', disability.temperature);
      // console.log('[LLM-VITALS] blood_glucose type:', typeof disability.blood_glucose, 'value:', disability.blood_glucose);
      // console.log('[LLM-VITALS] ===================================');
      
      const gcs = disability.gcs_score ? parseInt(disability.gcs_score) : null;
      const consciousness = disability.avpu || 'Alert';
      
      // Extract Pain, Sedation, Temp, BGL from the same disability response (now included in assessment-stage)
      // Parse strings to numbers (LLM might return "0" instead of 0)
      const painScore = disability.pain_score !== null && disability.pain_score !== undefined 
        ? (typeof disability.pain_score === 'number' ? disability.pain_score : parseFloat(disability.pain_score))
        : null;
      const sedationScore = disability.sedation_score !== null && disability.sedation_score !== undefined
        ? (typeof disability.sedation_score === 'number' ? disability.sedation_score : parseFloat(disability.sedation_score))
        : null;
      const temperature = disability.temperature !== null && disability.temperature !== undefined
        ? (typeof disability.temperature === 'number' ? disability.temperature : parseFloat(disability.temperature))
        : null;
      const bgl = disability.blood_glucose !== null && disability.blood_glucose !== undefined
        ? (typeof disability.blood_glucose === 'number' ? disability.blood_glucose : parseFloat(disability.blood_glucose))
        : null;
      
      // Extract New Pain from disability section (Y/N or null)
      const newPain = disability.new_pain !== null && disability.new_pain !== undefined
        ? disability.new_pain
        : 'N'; // Default to 'N' if not provided
      
      // console.log('[LLM-VITALS] Parsed from disability section:', { gcs, consciousness, painScore, sedationScore, temperature, bgl, newPain });
      
      // Update LLM vitals state
      setLlmVitals({
        gcs,
        consciousness,
        painScore,
        sedationScore,
        temperature,
        bgl,
        newPain
      });
      
      // console.log('[LLM-VITALS] Updated:', {
      //   gcs,
      //   consciousness,
      //   painScore,
      //   sedationScore,
      //   temperature,
      //   bgl,
      //   newPain
      // });
      
    } catch (error) {
      console.error('[LLM-VITALS] Error fetching LLM vitals:', error);
    }
  }, [analysisHistory, transcriptHistory, heartRate, webcamBreathingRate, webcamBloodPressure, webcamSpo2, webcamBmi]);
  
  // Fetch LLM vitals periodically (every 60 seconds)
  // Now reads from Analysis History like other endpoints
  // OR triggers if we have vital signs from webcam (even without vision analysis)
  useEffect(() => {
    // console.log('[LLM-VITALS] useEffect triggered');
    const now = Date.now();
    const shouldUpdate = !lastLlmUpdateRef.current || (now - lastLlmUpdateRef.current) > 5000; // 5 sec for testing (was 30)
    
    // Check if we have any data to work with
    const hasAnalysisHistory = analysisHistory.length > 0;
    const hasVitalSigns = heartRate || webcamBreathingRate || webcamBloodPressure || webcamSpo2;
    const hasTranscript = transcriptHistory.length > 0;
    const hasAnyData = hasAnalysisHistory || hasVitalSigns || hasTranscript;
    
    // console.log('[LLM-VITALS] Should update?', shouldUpdate, 'Has data?', hasAnyData, 
    //             '(analysis:', hasAnalysisHistory, 'vitals:', hasVitalSigns, 'transcript:', hasTranscript, ')');
    
    // Trigger if we have ANY data (not just analysis history)
    if (shouldUpdate && hasAnyData) {
      // console.log('[LLM-VITALS] Triggering fetchLlmVitals...');
      fetchLlmVitals();
      lastLlmUpdateRef.current = now;
    }
  }, [fetchLlmVitals, analysisHistory.length, heartRate, webcamBreathingRate, webcamBloodPressure, webcamSpo2, transcriptHistory.length]);
  
  // No separate webcam state - we'll use the same analysis state as video upload!
  
  // Collapsible sections state
  const [isSceneExpanded, setIsSceneExpanded] = useState(true);
  const [isAbcdeExpanded, setIsAbcdeExpanded] = useState(true);
  const [isDiagnosisExpanded, setIsDiagnosisExpanded] = useState(true);
  const [isActionsExpanded, setIsActionsExpanded] = useState(true);
  const [isGuidelinesExpanded, setIsGuidelinesExpanded] = useState(true);
  const [isClinicalSummaryExpanded, setIsClinicalSummaryExpanded] = useState(true);
  const [isRdrChartExpanded, setIsRdrChartExpanded] = useState(true);
  const [isVitalSignsExpanded, setIsVitalSignsExpanded] = useState(true);
  const [isEmotionExpanded, setIsEmotionExpanded] = useState(true);
  const [isEventTimelineExpanded, setIsEventTimelineExpanded] = useState(true);
  const [isAiActionsExpanded, setIsAiActionsExpanded] = useState(true);
  const [isExploringExpanded, setIsExploringExpanded] = useState(true);
  
  // Timeline state
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [treatmentPathways, setTreatmentPathways] = useState([]);
  
  // Video path is hardcoded in the backend server

  
  
  const videoRef = useRef(null);

  const canvasRef = useRef(null);

  const heartRateIntervalRef = useRef(null);

  const autoAnalysisIntervalRef = useRef(null);

  const metaAnalysisIntervalRef = useRef(null);

  const webcamPollingRef = useRef(null); // legacy, kept for compat
  const webcamWsRef = useRef(null);
  const bpmSmoothingBuffer = useRef([]);
  const bpmSmoothingBufferSize = 20; // Average over 20 readings for smoother display




  const handleVideoLoad = useCallback(() => {

    if (videoRef.current) {

      // Set video to first frame

      videoRef.current.currentTime = 0;

    }

  }, []);



  const extractFrame = useCallback(() => {

    if (!videoRef.current || !canvasRef.current) return null;



    const video = videoRef.current;

    const canvas = canvasRef.current;

    const ctx = canvas.getContext('2d');



    // Set canvas dimensions to match video

    canvas.width = video.videoWidth;

    canvas.height = video.videoHeight;



    // Draw current frame to canvas

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);



    // Convert canvas to base64 image

    const frameData = canvas.toDataURL('image/jpeg', 0.8);

    return frameData;

  }, []);



  const detectHeartRate = useCallback(async (frameData) => {

    try {

      console.log('Starting heart rate detection...');

      setIsHeartRateDetecting(true);

      
      
      const response = await axios.post('/api/detect-heart-rate', {

        imageData: frameData

      });



      console.log('Heart rate response:', response.data);
      console.log('[DEBUG] Keys in response:', Object.keys(response.data));
      console.log('[DEBUG] SpO2 field exists?', 'spo2' in response.data, 'Value:', response.data.spo2);



      if (response.data.bpm) {

        console.log('Setting heart rate:', response.data.bpm, 'confidence:', response.data.confidence);

        setHeartRate(response.data.bpm);

        setHeartRateConfidence(response.data.confidence);

        
        
        // Add to heart rate history

        const hrEntry = {

          id: Date.now(),

          timestamp: new Date().toLocaleTimeString(),

          bpm: response.data.bpm,

          confidence: response.data.confidence,

          faceDetected: response.data.faceDetected

        };

        setHeartRateHistory(prev => [hrEntry, ...prev].slice(0, 20)); // Keep last 20 readings

      } else {

        console.log('No heart rate data received');

      }

      // Update breathing rate
      if (response.data.breathingRate) {
        setWebcamBreathingRate(response.data.breathingRate);
      }

      // Update BMI
      if (response.data.bmi) {
        setWebcamBmi(response.data.bmi.toFixed(1));
      }

      // Update Blood Pressure
      if (response.data.bloodPressure) {
        setWebcamBloodPressure(response.data.bloodPressure);
        console.log('Blood Pressure:', response.data.bloodPressure);
      }

      // Update SpO2
      if (response.data.spo2) {
        setWebcamSpo2(response.data.spo2);
        console.log('[SPO2] Received from backend:', response.data.spo2);
      } else {
        console.log('[SPO2] No SpO2 data in response');
      }

    } catch (error) {

      console.error('Heart rate detection error:', error);

    } finally {

      setIsHeartRateDetecting(false);

    }

  }, []);



  // MHR access functions
  
  const analyseHealthRecord = useCallback(async (recordData = healthRecord, historyEntryId = null) => {
    if (!recordData || Object.keys(recordData).length === 0) {
      setError('No health record available to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError('');
    setSuccess('');

    const maxRetries = 3;
    let retryCount = 0;
    let finalDescription = '';
    let finalEvaluation = null;

    while (retryCount < maxRetries) {
      try {
        // Show retry status if this is a retry attempt
        if (retryCount > 0) {
          setSuccess(`Retrying health record analysis (attempt ${retryCount + 1}/${maxRetries})...`);
        }

        // Process health record through dedicated endpoint
        const response = await axios.post('/api/process-health-record', {
          healthRecord: recordData,
          historyCount: analysisHistory.length,
          previousTriageScores: analysisHistory.map(entry => entry.triageScore),
          // prompt: retryCount === 0 
          //   ? 'Analyze this health record thoroughly. Identify key medical conditions, allergies, medications, and any risk factors or concerns that would be relevant for emergency medical assessment.'
          //   : 'Please provide a more detailed and comprehensive analysis of this health record. Focus on specific medical conditions, risk factors, medication interactions, and clinical significance for emergency care.'
        });

        if (response.data.success) {
          finalDescription = response.data.analysis;
          finalEvaluation = { level: 'complete', confidence: 0.9, wordCount: finalDescription.split(' ').length };
          break;
        } else {
          console.log('Health record analysis failed:', response.data);
          throw new Error(response.data.error || 'Health record processing failed');
        }

      } catch (err) {
        if (retryCount === maxRetries - 1) {
          throw err; // Re-throw on final attempt
        }
        retryCount++;
        setError(`Health record analysis attempt ${retryCount} failed, retrying...`);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      }
    }

    // Proceed with the final analysis integration
    try {
      // Set the health record analysis as the current frame description
      setFrameDescription(finalDescription);

      // Update existing history entry or create new one
      if (historyEntryId) {
        // Update the placeholder entry created by MHR_access
        setAnalysisHistory(prev => prev.map(entry =>
          entry.id === historyEntryId
            ? {
                ...entry,
                visionAnalysis: finalDescription,
                evaluation: finalEvaluation,
                isUnprocessedHealthRecord: false,
                retryCount: retryCount
              }
            : entry
        ));
      } else {
        // Create new entry if no ID provided (shouldn't happen with MHR flow)
      const historyEntry = {
        id: Date.now(),
        timestamp: new Date().toLocaleTimeString(),
          frameData: null,
        visionAnalysis: finalDescription,
        evaluation: finalEvaluation,
        triageScore: null,
        assessmentStage: null,
        isProcessing: true,
        retryCount: retryCount,
        healthRecord: recordData,
        isHealthRecordAnalysis: true,
          isUnprocessedHealthRecord: false,
          source: 'mhr'
        };
        setAnalysisHistory(prev => [historyEntry, ...prev].slice(0, 50));
      }

      // Build comprehensive description including full history (like webcam analysis)
      const allAnalyses = [
        finalDescription,  // Current MHR analysis
        ...analysisHistory
          .filter(entry => entry.type !== 'transcript' && entry.visionAnalysis) // Only non-transcript entries with vision analysis
          .map(entry => entry.visionAnalysis)
      ];
      const combinedDescription = allAnalyses.join('\n\n--- Previous Analysis ---\n\n');
      
      // Build transcript text.
      //
      // This used to send the ENTIRE history on every pass, so the prompt grew
      // for the whole length of the consult: slower and dearer with every turn,
      // and a long repetitive history invites the model to repeat itself. The
      // server uses a supplied transcript whole -- it only slices its own
      // fallback -- so nothing downstream was bounding this either.
      //
      // Keep the OPENING, where the presenting complaint is stated, and the most
      // recent turns. A plain tail would let the chief complaint scroll out of
      // the assessment on any consult longer than a few minutes.
      const transcriptText = windowTranscript(transcriptHistory);
      
      // Add transcript if available
      let fullDescription = transcriptText 
        ? `${combinedDescription}\n\n--- Clinical Transcript ---\n${transcriptText}`
        : combinedDescription;
      
      // Add vitals if available
      if (heartRate) {
        fullDescription += `\n\n--- Vital Signs ---\nHeart Rate: ${heartRate} bpm`;
      }
      if (webcamBreathingRate) {
        fullDescription += `\nRespiratory Rate: ${webcamBreathingRate} br/min`;
      }
      if (webcamBmi && webcamBmi > 0) {
        fullDescription += `\nBMI: ${webcamBmi}`;
      }
      
      // Record when assessment and triage requests are initiated
      setLastAssessmentStageRequest(new Date());
      setLastTriageScoreRequest(new Date());
      
      // Run standard assessment and triage analysis
      const [assessmentResponse, triageResponse] = await Promise.all([  
        axios.post('/api/assessment-stage', {             
          description: fullDescription,
          historyCount: analysisHistory.length + 1,
          previousTriageScores: analysisHistory
            .filter(entry => entry.type !== 'transcript')
            .map(entry => entry.triageScore),
          transcript: transcriptText || null,
          isHealthRecordAnalysis: true
        }),     
        axios.post('/api/triage-score', {   
          description: fullDescription,
          historyCount: analysisHistory.length + 1,
          previousTriageScores: analysisHistory
            .filter(entry => entry.type !== 'transcript')
            .map(entry => entry.triageScore),
          transcript: transcriptText || null,
          isHealthRecordAnalysis: true
        })      
      ]);

      // Batch all state updates together to avoid multiple renders
      const triageScoreValue = triageResponse.data.score;
      const assessmentStageValue = assessmentResponse.data.stage;
      
      setAssessmentStage(assessmentStageValue);
      setAbcdeFindings(assessmentResponse.data.abcde_findings);
      setAssessmentRecommendations(assessmentResponse.data.recommendations);
      setTriageScore(triageScoreValue);
      setTriageReasoning(triageResponse.data.reasoning);
      setTriageLogprobs(triageResponse.data.logprobs);
      setScoreConfidences(triageResponse.data.scoreConfidences);

      // Update the history entry with triage and assessment data (batched in same render cycle)
      if (historyEntryId) {
      setAnalysisHistory(prev => prev.map(entry => 
          entry.id === historyEntryId
            ? { ...entry, triageScore: triageScoreValue, assessmentStage: assessmentStageValue, isProcessing: false }
          : entry
      ));
      }

      // Generate action plan, diagnosis prediction, emergency guidelines, and structured assessment         
      const [actionPlanResponse, diagnosisResponse, guidelinesResponse, structuredAssessmentResponse] = await Promise.all([       
        axios.post('/api/plan-of-action', { 
          description: fullDescription,    
          triageScore: triageResponse.data.score,         
          assessmentStage: assessmentResponse.data.stage,
          historyCount: analysisHistory.length + 1,
          previousTriageScores: analysisHistory
            .filter(entry => entry.type !== 'transcript')
            .map(entry => entry.triageScore),
          transcript: transcriptText || null,
          isHealthRecordAnalysis: true
        }),     
        axios.post('/api/diagnosis-prediction', {         
          description: fullDescription,    
          triageScore: triageResponse.data.score,         
          assessmentStage: assessmentResponse.data.stage, 
          abcdeFindings: assessmentResponse.data.abcde_findings,
          historyCount: analysisHistory.length + 1,
          previousTriageScores: analysisHistory
            .filter(entry => entry.type !== 'transcript')
            .map(entry => entry.triageScore),
          transcript: transcriptText || null,
          isHealthRecordAnalysis: true
        }),     
        axios.post('/api/emergency-guidelines', {         
          primaryDiagnosis: 'Health Record Assessment', 
          triageScore: triageResponse.data.score,         
          assessmentStage: assessmentResponse.data.stage,
          historyCount: analysisHistory.length + 1,
          previousTriageScores: analysisHistory
            .filter(entry => entry.type !== 'transcript')
            .map(entry => entry.triageScore),
          transcript: transcriptText || null,
          isHealthRecordAnalysis: true
        }),
        axios.post('/api/structured-assessment', {
          description: fullDescription,
          triageScore: triageResponse.data.score,
          assessmentStage: assessmentResponse.data.stage,
          abcdeFindings: assessmentResponse.data.abcde_findings,
          transcript: transcriptText || null,
          isHealthRecordAnalysis: true
        })      
      ]);

      setActionPlan(actionPlanResponse.data);             
      setDiagnosisPrediction(diagnosisResponse.data);     
      setEmergencyGuidelines(guidelinesResponse.data);    
      setStructuredAssessment(structuredAssessmentResponse.data);    
      
      if (retryCount > 0) {
        setSuccess(`Health record analysis completed successfully! (Required ${retryCount + 1} attempts)`);
    } else {

        setSuccess('Health record analysis completed successfully!');
      }
      
      return finalDescription;
      
    } catch (err) {
      setError(`Error analyzing health record: ${err.message}`);
      console.error('Health record analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  }, [healthRecord, analysisHistory, transcriptHistory, heartRate, webcamBreathingRate, webcamBmi]);

  const MHR_access = useCallback(async (scenario = selectedScenario || "1") => {
    if (isMhrLoading) {
      // prevent concurrent requests
      console.warn('MHR access already in progress');
      return;
    }
    try {
      setIsMhrLoading(true);
      setLastMhrStatus('Retrieving My Health Record...');
      
      // Add timeline event for MHR access
      const identificationTime = new Date();
      setTimelineEvents(prev => [...prev, {
        time: identificationTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        title: 'My Health Record Access',
        description: 'Retrieving patient health record data'
      }]);
      
      // Proceed with actual MHR retrieval
      setLastMhrStatus(`Retrieving MHR (Scenario ${scenario})...`);
      // retry once with small delay
      let response;
      const apiBase = (process.env.REACT_APP_API_BASE || 'http://localhost:5001');
      const mhrUrl = `${apiBase}/api/health-record/get_record?scenario=${scenario}`;
      try {
        response = await axios.get(mhrUrl, { timeout: 5000 });
      } catch (e1) {
        setLastMhrStatus('Retrying...');
        await new Promise(r => setTimeout(r, 600));
        response = await axios.get(mhrUrl, { timeout: 7000 });
      }
      // pause for 500 ms to give server time to respond
      await new Promise(r => setTimeout(r, 500));
      if (!response || response.status !== 200 || !response.data?.success) {
        const msg = `Server responded ${response?.status || 'N/A'}`;
        throw new Error(msg);
      }
      console.log('MHR access successful:', response.data);
      setLastMhrStatus('MHR retrieved. Analysing...');
      // update healthRecord state
      const recordData = response.data || {};
      setHealthRecord(recordData);
      console.log('Record data:', recordData);
      
      // Step 6: Clear Medicare card display and add timeline event for MHR integration
      try {
        await sendMediaToClient('clear');
        console.log('Cleared Medicare card display from client');
      } catch (clearError) {
        console.warn('Could not clear Medicare card display:', clearError);
      }
      
      const now = new Date();
      setTimelineEvents(prev => [...prev, {
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        title: 'MHR Retrieved & Integrated',
        description: recordData?.data ? 'Health record data reconciled into assessment' : 'No MHR data found'
      }]);

      // Step 7: Trigger health record analysis flow WITHOUT a placeholder
      // This will create the history entry with complete data in one go
      await analyseHealthRecord(recordData, null);
      setLastMhrStatus('MHR integrated');
    } catch (error) {
      console.error('MHR access error:', error);
      const msg = (error && (error.message || String(error))) || 'Unknown error';
      setLastMhrStatus(`MHR retrieval error: ${msg}`);
      setError(`MHR retrieval error: ${msg}`);
      const nowFail = new Date();
      setTimelineEvents(prev => [...prev, {
        time: nowFail.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        title: 'MHR Retrieval Failed',
        description: msg
      }]);
    } finally {
      setIsMhrLoading(false);
    }
  }, [analyseHealthRecord, selectedScenario]);

  // Track triage score changes and add to timeline
  const prevTriageScoreRef = useRef(null);
  useEffect(() => {
    if (triageScore !== null && prevTriageScoreRef.current !== null && triageScore !== prevTriageScoreRef.current) {
      // Triage score changed
      const now = new Date();
      setTimelineEvents(prev => [...prev, {
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        title: 'Triage Priority Changed',
        description: `Updated from Priority ${prevTriageScoreRef.current} to Priority ${triageScore}`
      }]);
    }
    prevTriageScoreRef.current = triageScore;
  }, [triageScore]);

  // Track assessment stage changes and add to timeline
  const prevAssessmentStageRef = useRef(null);
  useEffect(() => {
    if (assessmentStage !== null && prevAssessmentStageRef.current !== null && assessmentStage !== prevAssessmentStageRef.current) {
      // Assessment stage changed
      const now = new Date();
      setTimelineEvents(prev => [...prev, {
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        title: 'Assessment Stage Changed',
        description: `Progressed from ${prevAssessmentStageRef.current} to ${assessmentStage}`
      }]);
    }
    prevAssessmentStageRef.current = assessmentStage;
  }, [assessmentStage]);



  // Webcam Functions
  const startWebcamAnalysis = useCallback(async () => {
    try {
      setIsWebcamActive(true);
      setSuccess(`Starting webcam analysis (${videoSource === 'ngrok' ? 'Remote Stream' : 'Local Webcam'})...`);
      
      // Add initial timeline event
      const now = new Date();
      setTimelineEvents([{
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        title: 'Webcam Monitoring Started',
        description: `Real-time vital signs monitoring initiated (${videoSource === 'ngrok' ? 'Remote Stream' : 'Local Webcam'})`
      }]);
      
      // Start Python heart rate analysis script
      await axios.post('/api/start-webcam-analysis');
      
      // Connect via WebSocket for real-time push (replaces HTTP polling)
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.hostname}:5001`;
      const ws = new WebSocket(wsUrl);
      webcamWsRef.current = ws;

      // Throttle: only process the latest frame per animation frame
      let latestFrameData = null;
      let rafPending = false;

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (!parsed.image) return;
          latestFrameData = parsed;

          if (!rafPending) {
            rafPending = true;
            requestAnimationFrame(() => {
              rafPending = false;
              const data = latestFrameData;
              if (!data) return;

              processWebcamFrame(data);
            });
          }
        } catch (e) {
          // ignore malformed messages
        }
      };

      // Process a single frame's data into React state
      function processWebcamFrame(data) {
          setWebcamImage(data.image);
          setWebcamBpm(data.integerValue || 0);
          setWebcamBreathingRate(data.breathingRate || null);

          if (data.image) {
            setCurrentFrame(data.image);
          }
          if (data.spectrum) {
            setSpectrumData(data.spectrum);
          }
          if (data.emotion) {
            setEmotionData(data.emotion);
          }
          if (data.bmi) {
            const bmiValue = parseInt(data.bmi, 10);
            if (!isNaN(bmiValue) && bmiValue > 0) {
              setWebcamBmi(bmiValue);
            }
          }
          if (data.bloodPressure) {
            setWebcamBloodPressure(data.bloodPressure);
          }
          if (data.spo2) {
            setWebcamSpo2(data.spo2);
          }

          // Track vital signs history for trending
          if (data.integerValue || data.breathingRate) {
            setVitalSignsHistory(prev => {
              const newEntry = {
                timestamp: new Date(),
                heartRate: data.integerValue || null,
                breathingRate: data.breathingRate || null,
                bmi: data.bmi || null
              };
              const updated = [...prev, newEntry];
              return updated.slice(-100);
            });
          }

          // Smoothing buffer
          const bpm = data.integerValue;
          if (typeof bpm === 'number') {
            bpmSmoothingBuffer.current.push(bpm);
          }
          if (bpmSmoothingBuffer.current.length > bpmSmoothingBufferSize) {
            bpmSmoothingBuffer.current.shift();
          }

          const smoothedBpm = Math.round(
            bpmSmoothingBuffer.current.reduce((sum, val) => sum + val, 0) /
            bpmSmoothingBuffer.current.length
          );

          setHeartRate(smoothedBpm);
          setWebcamBpm(smoothedBpm);
          setHeartRateConfidence(0.8);

          // Snapshot every 10 seconds for graph
          if (smoothedBpm > 0) {
            const now = new Date();
            const shouldTakeSnapshot = !lastSnapshotTimeRef.current ||
              (now.getTime() - lastSnapshotTimeRef.current.getTime() >= 10000);
            if (shouldTakeSnapshot) {
              lastSnapshotTimeRef.current = now;
              setBpmHistory(prevHistory => [...prevHistory, { time: now, bpm: smoothedBpm }]);
            }
          }
      }

      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
      };

      ws.onclose = () => {
        webcamWsRef.current = null;
      };
      
    } catch (error) {
      console.error('Error starting webcam analysis:', error);
      setError('Failed to start webcam analysis: ' + error.message);
    }
  }, []);

  const stopWebcamAnalysis = useCallback(async () => {
    try {
      setIsWebcamActive(false);
      setWebcamImage(null);
      setWebcamBpm(null);
      setHeartRate(null);
      setHeartRateConfidence(0);
      setBpmHistory([]);
      lastSnapshotTimeRef.current = null;
      setSpectrumData({ frequencies: [], amplitudes: [] });
      setEmotionData({ dominant: null, confidence: {}, age: null, gender: null });
      setTimelineEvents([]);
      setTreatmentPathways([]);
      
      // Stop transcription
      if (isTranscribing) {
        stopTranscription();
      }
      
      // Don't clear shared analysis state - it's needed for history display!
      // (frameDescription, triageScore, assessmentStage, etc. are shared with video)
      
      // Clear smoothing buffer
      bpmSmoothingBuffer.current = [];
      
      // Close WebSocket connection
      if (webcamWsRef.current) {
        webcamWsRef.current.close();
        webcamWsRef.current = null;
      }
      // Clear legacy polling interval if active
      if (webcamPollingRef.current) {
        clearInterval(webcamPollingRef.current);
        webcamPollingRef.current = null;
      }
      
      // Stop Python analysis
      await axios.post('/api/stop-webcam-analysis');
      setSuccess('Webcam analysis stopped');
      
    } catch (error) {

      console.error('Error stopping webcam analysis:', error);
      setError('Failed to stop webcam analysis: ' + error.message);
    }
  }, [isTranscribing, videoSource, ngrokUrl]);

  // LLM-based speaker classification
  const classifySpeaker = useCallback(async (utteranceText, history) => {
    try {
      console.log('🤖 Classifying speaker for:', utteranceText);
      setIsClassifying(true);
      
      const startTime = Date.now();
      const response = await axios.post('/api/classify-speaker', {
        transcriptHistory: history,
        newUtterance: utteranceText
      });
      
      const elapsed = Date.now() - startTime;
      console.log(`✅ Speaker classified as "${response.data.speaker}" in ${elapsed}ms`);
      
      return response.data.speaker; // 'clinician' or 'patient'
    } catch (error) {
      console.error('❌ Error classifying speaker:', error.message || error);
      console.error('Full error:', error);
      // Fallback to 'unknown'
      return 'unknown';
    } finally {
      setIsClassifying(false);
    }
  }, []);

  const getSpeakerLabel = useCallback((speaker) => {
    const speakerInfo = {
      'clinician': { label: 'Clinician', icon: '👨‍⚕️', color: '#17a2b8' },
      'patient': { label: 'Patient', icon: '🤒', color: '#ffc107' },
      'helper': { label: 'Helper', icon: '👥', color: '#28a745' },
      'unknown': { label: 'Unknown', icon: '❓', color: '#6c757d' }
    };
    
    return speakerInfo[speaker] || speakerInfo['unknown'];
  }, []);

  // Speech recognition functions
  const startTranscription = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Speech recognition not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => {
      setIsTranscribing(true);
      console.log('Speech recognition started');
    };
    
    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + ' ';
        } else {
          interim += transcript;
        }
      }
      
      if (final) {
        const utteranceText = final.trim();
        const timestamp = new Date().toLocaleTimeString();
        const entryId = Date.now();
        
        // Add entry immediately with 'unknown' speaker to avoid blocking
        const tempEntry = {
          text: utteranceText,
          timestamp: timestamp,
          speaker: 'unknown',
          speakerLabel: 'Unknown'
        };
        
        // Add to analysis history immediately
        const tempAnalysisEntry = {
          id: entryId,
          timestamp: timestamp,
          type: 'transcript',
          speaker: 'unknown',
          speakerLabel: 'Unknown',
          text: utteranceText,
          source: 'transcript'
        };
        setAnalysisHistory(prev => [tempAnalysisEntry, ...prev].slice(0, 50));
        
        // Add to history immediately
        setTranscriptHistory(prev => {
          const newHistory = [...prev, tempEntry];
          
          // Classify speaker asynchronously (non-blocking)
          classifySpeaker(utteranceText, prev).then(speaker => {
            const speakerInfo = getSpeakerLabel(speaker);
            
            // Update the entry with classified speaker
            setTranscriptHistory(current => {
              const updated = [...current];
              const index = updated.findIndex(e => e.timestamp === timestamp && e.text === utteranceText);
              if (index !== -1) {
                updated[index] = {
                  ...updated[index],
                  speaker: speaker,
                  speakerLabel: speakerInfo.label
                };
              }
              return updated;
            });

            // Update the analysis history entry with classified speaker
            setAnalysisHistory(prev => {
              const updated = [...prev];
              const index = updated.findIndex(e => e.id === entryId);
              if (index !== -1) {
                updated[index] = {
                  ...updated[index],
                  speaker: speaker,
                  speakerLabel: speakerInfo.label
                };
              }
              return updated;
            });
            
            // Rebuild full transcript
            setTranscript(current => {
              if (!current) return `[${speakerInfo.label}]: ${utteranceText}\n`;
              const lines = current.split('\n').filter(l => l.trim());
              const lastLine = lines[lines.length - 1];
              if (lastLine && lastLine.includes(utteranceText)) {
                lines[lines.length - 1] = `[${speakerInfo.label}]: ${utteranceText}`;
              }
              return lines.join('\n') + '\n';
            });
          }).catch(err => {
            console.error('Speaker classification failed:', err);
          });
          
          return newHistory;
        });
        
        // Update full transcript with temporary label
        setTranscript(prev => prev + `[Unknown]: ${utteranceText}\n`);
      }
      
      setInterimTranscript(interim);
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        // Silently handle no-speech (user paused)
        return;
      }
      setError(`Speech recognition error: ${event.error}`);
    };
    
    recognition.onend = () => {
      // Auto-restart if still transcribing (unless manually stopped)
      // Check recognitionRef to see if we're still supposed to be running
      if (recognitionRef.current) {
        try {
          console.log('Speech recognition ended, restarting...');
          recognitionRef.current.start();
        } catch (e) {
          console.log('Recognition restart failed:', e);
          // If restart fails, clean up
          setIsTranscribing(false);
          recognitionRef.current = null;
        }
      } else {
        // If recognitionRef is null, we were manually stopped
        console.log('Speech recognition ended (manually stopped)');
        setIsTranscribing(false);
      }
    };
    
    recognitionRef.current = recognition;
    recognition.start();
  }, []);

  const stopTranscription = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsTranscribing(false);
    setInterimTranscript('');
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setTranscriptHistory([]);
  }, []);

  // Patient details voice transcription functions
  const startPatientDetailsListening = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-AU'; // Australian English
    
    recognition.onstart = () => {
      setIsListeningForDetails(true);
      console.log('Patient details speech recognition started');
    };
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      
      // Only process new final results (from the last processed index)
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + ' ';
        }
      }
      
      // Append only the new final transcript content
      if (finalTranscript.trim()) {
        setPatientDetailsTranscript(prev => {
          return (prev + ' ' + finalTranscript).trim();
        });
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Patient details speech recognition error:', event.error);
      setIsListeningForDetails(false);
      if (event.error !== 'no-speech') {
        alert(`Speech recognition error: ${event.error}`);
      }
    };
    
    recognition.onend = () => {
      setIsListeningForDetails(false);
      console.log('Patient details speech recognition ended');
    };
    
    setPatientDetailsRecognition(recognition);
    recognition.start();
  }, []);

  const stopPatientDetailsListening = useCallback(() => {
    if (patientDetailsRecognition) {
      patientDetailsRecognition.stop();
      setPatientDetailsRecognition(null);
    }
    setIsListeningForDetails(false);
  }, [patientDetailsRecognition]);

  const clearPatientDetailsTranscript = useCallback(() => {
    setPatientDetailsTranscript('');
  }, []);

  // Analyze Medicare card function
  const analyzeMedicareCard = useCallback(async () => {
    try {
      setIsAnalyzingMedicareCard(true);
      setMedicareCardAnalysis('');
      
      // Convert Medicare card image to base64
      const img = new Image();
      // Remove crossOrigin for local files to avoid CORS issues
      // img.crossOrigin = 'anonymous';
      
      return new Promise((resolve, reject) => {
        img.onload = async () => {
          console.log('Medicare card image loaded successfully');
          console.log('Image natural dimensions:', img.naturalWidth, 'x', img.naturalHeight);
          try {
            // Create canvas to convert image to base64
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            
            // Convert to base64
            const base64Data = canvas.toDataURL('image/jpeg', 0.8);
            
            // Debug: Check if we actually got image data
            console.log('Image dimensions:', img.width, 'x', img.height);
            console.log('Canvas dimensions:', canvas.width, 'x', canvas.height);
            console.log('Base64 data length:', base64Data.length);
            console.log('Base64 data preview:', base64Data.substring(0, 100) + '...');
            
            // Validate we have actual image data
            if (base64Data.length < 100 || base64Data === 'data:,') {
              throw new Error('Failed to convert Medicare card image to base64 - empty or invalid data');
            }
            
            console.log('Sending Medicare card to analyze-frame endpoint...');
            
            // Send to analyze-frame endpoint
            // Create AbortController for timeout handling
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
              console.log(`Vision API call timed out after 45 seconds`);
              controller.abort();
            }, 45000); // 45 second timeout
            const visionPrompt = 'Analyze this mock Medicare card. Extract the patient name, Medicare number, card expiry date, and any other relevant information. Verify that this appears to be a valid Australian Medicare card. Return results in the following JSON format: { "number":"2345678901", "card_expiry":"12/2028", "entries": [{"number": "1", "Patient Name":  "..." }, {"number": "2", "Patient Name":  "..." }, ...], "valid": true/false}';
            const response = await fetch('/api/analyze-frame', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                image: base64Data,
                prompt: visionPrompt,
              }),
              signal: controller.signal
            });

            clearTimeout(timeoutId); // Clear timeout on success

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Handle streaming response like the working code
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let finalDescription = '';
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';
              
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    if (data.type === 'chunk' && data.content) {
                      finalDescription += data.content;
                    } else if (data.type === 'done') {
                      finalDescription = data.description || finalDescription;
                    } else if (data.type === 'error') {
                      throw new Error(data.error || 'Vision API error');
                    }
                  } catch (e) {
                    // Ignore parsing errors for malformed JSON chunks
                  }
                }
              }
            }

            if (finalDescription && finalDescription.trim().length > 0) {
              setMedicareCardAnalysis(finalDescription);
              console.log('Medicare card analysis completed:', finalDescription);
              resolve(finalDescription);
            } else {
              throw new Error('No analysis returned from server');
            }
          } catch (error) {
            console.error('Error analyzing Medicare card:', error);
            setMedicareCardAnalysis('Failed to analyze Medicare card: ' + error.message);
            reject(error);
          }
        };
        
        img.onerror = (error) => {
          console.error('Failed to load Medicare card image:', error);
          console.error('Image src was:', img.src);
          setMedicareCardAnalysis('Failed to load Medicare card image from: ' + img.src);
          reject(new Error('Failed to load Medicare card image'));
        };
        
        // Load the Medicare card image
        console.log('Attempting to load Medicare card image from:', '/mock_medicare_card.jpg');
        img.src = '/mock_medicare_card.jpg';
      });
    } catch (error) {
      console.error('Error in analyzeMedicareCard:', error);
      setMedicareCardAnalysis('Error analyzing Medicare card: ' + error.message);
    } finally {
      setIsAnalyzingMedicareCard(false);
    }
  }, []);

  // Send media to client function
  const sendMediaToClient = useCallback(async (mediaCommand) => {
    setIsBpCuffUploading(true);
    
    try {
      let uploadResponse;
      
      if (mediaCommand === 'clear') {
        // Clear overlay
        uploadResponse = await axios.post('/api/upload-frame', {}, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } else if (mediaCommand.startsWith('image:')) {
        // Handle image uploads
        const imageName = mediaCommand.replace('image:', '');
        const response = await fetch(`/${imageName}`);
        const blob = await response.blob();
        
        const formData = new FormData();
        formData.append('frame', blob, imageName);
        
        uploadResponse = await axios.post('/api/upload-frame', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        } else {
        // Handle video/command uploads
        uploadResponse = await axios.post('/api/upload-frame', {
          command: mediaCommand
        }, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }
      
      if (uploadResponse.data.success) {
        setIsBpCuffActive(mediaCommand !== 'clear');
        console.log(`Media ${mediaCommand} sent successfully`);
        setShowMediaDropdown(false);
        } else {
        throw new Error('Failed to send media');
      }
    } catch (error) {
      console.error('Error sending media:', error);
      setError(`Failed to send media: ${error.message}`);
    } finally {
      setIsBpCuffUploading(false);
    }
  }, [isBpCuffUploading]);

  // BP Cuff overlay toggle function (now uses selected media)
  const toggleBpCuff = useCallback(async () => {
    if (isBpCuffActive) {
      // If active, clear the overlay
      await sendMediaToClient('clear');
    } else {
      // If not active, send the selected media
      await sendMediaToClient(selectedMedia);
    }
  }, [isBpCuffActive, selectedMedia, sendMediaToClient]);


  // Auto-scroll transcript to bottom when new messages arrive
  useEffect(() => {
    if (transcriptScrollRef.current) {
      transcriptScrollRef.current.scrollTop = transcriptScrollRef.current.scrollHeight;
    }
  }, [transcriptHistory, interimTranscript]);

  // Analyze current webcam frame - uses the SAME analyzeFrameWithData function as video upload!
  const analyzeWebcamFrame = useCallback(async () => {
    console.log('analyzeWebcamFrame called', { webcamImage: !!webcamImage, isAnalyzing });
    
    if (!webcamImage) {
      console.error('No webcam frame available to analyze');
      setError('No webcam frame available to analyze');
      return;
    }

    if (isAnalyzing) {
      console.warn('Analysis already in progress, ignoring click');
      return;
    }

    console.log('Starting webcam scene analysis...');

    // Add timeline event for analysis start
    const now = new Date();
    setTimelineEvents(prev => [...prev, {
      time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      title: 'Scene Analysis Initiated',
      description: 'AI analyzing current frame'
    }]);

    // Create history entry immediately (placeholder) and update later as results arrive
    const historyId = Date.now();
    const placeholderEntry = {
      id: historyId,
      timestamp: new Date().toLocaleTimeString(),
      frameData: webcamImage,
      visionAnalysis: '',
      triageScore: null,
      assessmentStage: null,
      isProcessing: true,
      source: 'webcam'
    };
    setAnalysisHistory(prev => [placeholderEntry, ...prev].slice(0, 50));

    // Set the current frame and trigger analysis with webcam source marker
    setCurrentFrame(webcamImage);
    
    console.log('About to call analyzeFrameWithData...');
    try {
      await analyzeFrameWithData(webcamImage, 'webcam', historyId);
      console.log('analyzeFrameWithData completed successfully');
    } catch (error) {
      console.error('Error in analyzeWebcamFrame:', error);
      setError(`Analysis failed: ${error.message}`);
      setIsAnalyzing(false); // Ensure state is reset on error
      
      // Mark history entry as failed
      setAnalysisHistory(prev => prev.map(entry => 
        entry.id === historyId 
          ? { ...entry, isProcessing: false, error: error.message }
          : entry
      ));
    }
  }, [webcamImage, isAnalyzing]); // Don't include analyzeFrameWithData to avoid circular dependency

  // Get current analysis status for traffic light
  const getAnalysisStatus = useCallback(() => {
    if (isAnalyzing) {
      return { level: 'in-progress', color: '#ff8800', text: 'Analysing...' };
    }
    
    // Check completeness
    const hasTriageScore = triageScore !== null;
    const hasAbcde = abcdeFindings !== null;
    const hasDiagnosis = diagnosisPrediction !== null;
    const hasVitals = webcamBpm > 0 || webcamBreathingRate > 0;
    
    const completionScore = 
      (hasTriageScore ? 1 : 0) + 
      (hasAbcde ? 1 : 0) + 
      (hasDiagnosis ? 1 : 0) + 
      (hasVitals ? 1 : 0);
    
    if (completionScore >= 3) {
      return { level: 'complete', color: '#44ff44', text: 'Complete' };
    } else if (completionScore >= 2) {
      return { level: 'moderate', color: '#ff8800', text: 'Moderate' };
    } else if (completionScore >= 1) {
      return { level: 'partial', color: '#ff4444', text: 'Partial' };
    } else {
      return { level: 'incomplete', color: '#4a4a4a', text: 'No Data' };
    }
  }, [isAnalyzing, triageScore, abcdeFindings, diagnosisPrediction, webcamBpm, webcamBreathingRate]);

  // Generate Handover Documentation using ISBAR Template
  const generateHandoverDoc = useCallback(async () => {
    try {
      console.log('Generating ISBAR handover document...');
      
      // Prepare data to send to server
      const documentData = {
        triageScore,
        triageReasoning,
        heartRate,
        webcamBpm,
        webcamBreathingRate,
        webcamBmi,
        webcamBloodPressure,
        webcamSpo2,
        structuredAssessment,
        abcdeFindings,
        assessmentStage,
        diagnosisPrediction,
        assessmentRecommendations,
        transcript
      };
      
      // Call server endpoint to generate document using template
      const response = await fetch('/api/generate-isbar-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(documentData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate document');
      }
      
      // Download the file
      const blob = await response.blob();
      const filename = `RFDS_ISBAR_Handover_${new Date().toISOString().replace(/[:.]/g, '-')}.docx`;
      saveAs(blob, filename);
      
      console.log('ISBAR handover document generated successfully:', filename);
    } catch (error) {
      console.error('Error generating ISBAR handover document:', error);
      alert(`Error generating handover document: ${error.message}`);
    }
  }, [
    triageScore,
    triageReasoning,
    heartRate,
    webcamBpm,
    webcamBreathingRate,
    webcamBmi,
    webcamBloodPressure,
    webcamSpo2,
    structuredAssessment,
    abcdeFindings,
    assessmentStage,
    diagnosisPrediction,
    assessmentRecommendations,
    transcript
  ]);

  // OLD CODE - Keeping for reference but not used
  const generateHandoverDocOLD = useCallback(async () => {
    try {
      const currentDate = new Date().toLocaleString('en-AU', { 
        dateStyle: 'full', 
        timeStyle: 'short',
        timeZone: 'Australia/Sydney'
      });

      // Helper function to create ISBAR section
      const createISBARSection = (title, description, content) => [
        new Paragraph({
          text: title,
          heading: HeadingLevel.HEADING_1,
          bold: true,
          spacing: { before: 300, after: 100 }
        }),
        new Paragraph({
          text: description,
          italics: true,
          color: "666666",
          spacing: { after: 150 }
        }),
        ...content,
        new Paragraph({ text: "", spacing: { after: 200 } })
      ];

      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Header
            new Paragraph({
              text: "ISBAR HANDOVER TEMPLATE",
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
              bold: true,
              spacing: { after: 100 }
            }),
            new Paragraph({
              text: "Royal Flying Doctor Service",
              alignment: AlignmentType.CENTER,
              bold: true,
              spacing: { after: 100 }
            }),
            new Paragraph({
              text: `Generated: ${currentDate}`,
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),

            // I - IDENTITY
            ...createISBARSection(
              "Identity",
              "Identifying factors of person undergoing the consultation (Name, DOB, Medicare details)",
              [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Patient Name: ", bold: true }),
                    new TextRun({ text: structuredAssessment?.patient_name || "Not provided" })
                  ],
                  spacing: { after: 100 }
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: "Date of Birth: ", bold: true }),
                    new TextRun({ text: structuredAssessment?.date_of_birth || "Not provided" })
                  ],
                  spacing: { after: 100 }
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: "Medicare Number: ", bold: true }),
                    new TextRun({ text: structuredAssessment?.medicare_number || "Not provided" })
                  ],
                  spacing: { after: 100 }
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: "Triage Score: ", bold: true }),
                    new TextRun({ 
                      text: triageScore ? `${triageScore}/5` : "Not assessed", 
                      color: triageScore >= 4 ? "FF0000" : triageScore >= 3 ? "FFA500" : "00AA00" 
                    })
                  ],
                  spacing: { after: 100 }
                })
              ]
            ),

            // S - SITUATION
            ...createISBARSection(
              "Situation",
              "The current issue/concern, why they have sought assistance, any proposed/suggested diagnosis",
              [
                new Paragraph({
                  children: [
                    new TextRun({ text: "Chief Complaint: ", bold: true }),
                    new TextRun({ text: structuredAssessment?.chief_complaint || "Not assessed" })
                  ],
                  spacing: { after: 100 }
                }),
                ...(triageReasoning ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Triage Reasoning: ", bold: true }),
                      new TextRun({ text: triageReasoning })
                    ],
                    spacing: { after: 100 }
                  })
                ] : []),
                ...(diagnosisPrediction?.primary_diagnosis ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Suggested Diagnosis: ", bold: true }),
                      new TextRun({ text: diagnosisPrediction.primary_diagnosis.condition || diagnosisPrediction.primary_diagnosis })
                    ],
                    spacing: { after: 100 }
                  }),
                  ...(diagnosisPrediction.primary_diagnosis.confidence_percentage ? [
                    new Paragraph({
                      children: [
                        new TextRun({ text: "Confidence: ", bold: true }),
                        new TextRun({ text: `${diagnosisPrediction.primary_diagnosis.confidence_percentage}%` })
                      ],
                      spacing: { after: 100 }
                    })
                  ] : [])
                ] : []),
                ...(structuredAssessment?.clinical_impression ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Clinical Impression: ", bold: true }),
                      new TextRun({ text: structuredAssessment.clinical_impression })
                    ],
                    spacing: { after: 100 }
                  })
                ] : [])
              ]
            ),

            // B - BACKGROUND
            ...createISBARSection(
              "Background",
              "Relevant previous history, past medical concerns, surgical operations, current medications, allergies & intolerances",
              [
                ...(structuredAssessment?.past_medical_history?.conditions?.length > 0 ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Past Medical History: ", bold: true }),
                      new TextRun({ text: structuredAssessment.past_medical_history.conditions.join(', ') })
                    ],
                    spacing: { after: 100 }
                  })
                ] : []),
                ...(structuredAssessment?.past_medical_history?.surgeries?.length > 0 ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Past Surgical History: ", bold: true }),
                      new TextRun({ text: structuredAssessment.past_medical_history.surgeries.join(', ') })
                    ],
                    spacing: { after: 100 }
                  })
                ] : []),
                ...(structuredAssessment?.medications?.current_medications?.length > 0 ? [
                  new Paragraph({
                    text: "Current Medications:",
                    bold: true,
                    spacing: { after: 50 }
                  }),
                  ...structuredAssessment.medications.current_medications.map(med =>
                    new Paragraph({
                      text: `  • ${med.name} ${med.dose} ${med.frequency} (${med.indication})`,
                      spacing: { after: 25 }
                    })
                  ),
                  new Paragraph({ text: "", spacing: { after: 50 } })
                ] : []),
                ...(structuredAssessment?.allergies?.drug_allergies?.length > 0 || 
                    structuredAssessment?.allergies?.food_allergies?.length > 0 ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Allergies: ", bold: true, color: "FF0000" }),
                      new TextRun({ 
                        text: [
                          ...(structuredAssessment.allergies.drug_allergies || []),
                          ...(structuredAssessment.allergies.food_allergies || [])
                        ].join(', '),
                        color: "FF0000"
                      })
                    ],
                    spacing: { after: 100 }
                  })
                ] : [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Allergies: ", bold: true }),
                      new TextRun({ text: "No known allergies" })
                    ],
                    spacing: { after: 100 }
                  })
                ])
              ]
            ),

            // A - ASSESSMENT
            ...createISBARSection(
              "Assessment",
              "Latest clinical details ascertained from the consultation (HR, Respiratory rate, temperature, anxiety, BMI, etc)",
              [
                // Vital Signs
                new Paragraph({
                  text: "Vital Signs:",
                  bold: true,
                  spacing: { after: 50 }
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: "Heart Rate: ", bold: true }),
                    new TextRun({ text: `${heartRate || webcamBpm || 'N/A'} BPM` })
                  ],
                  spacing: { after: 50 }
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: "Respiratory Rate: ", bold: true }),
                    new TextRun({ text: `${webcamBreathingRate || 'N/A'} breaths/min` })
                  ],
                  spacing: { after: 50 }
                }),
                ...(webcamBloodPressure?.sbp && webcamBloodPressure?.dbp ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Blood Pressure: ", bold: true }),
                      new TextRun({ text: `${webcamBloodPressure.sbp}/${webcamBloodPressure.dbp} mmHg` })
                    ],
                    spacing: { after: 50 }
                  })
                ] : []),
                ...(webcamSpo2 ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "SpO2: ", bold: true }),
                      new TextRun({ text: `${webcamSpo2}%` })
                    ],
                    spacing: { after: 50 }
                  })
                ] : []),
                ...(structuredAssessment?.physical_examination?.vital_signs?.temperature ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Temperature: ", bold: true }),
                      new TextRun({ text: `${structuredAssessment.physical_examination.vital_signs.temperature}°C` })
                    ],
                    spacing: { after: 50 }
                  })
                ] : []),
                new Paragraph({
                  children: [
                    new TextRun({ text: "BMI: ", bold: true }),
                    new TextRun({ text: `${webcamBmi || 'N/A'}` })
                  ],
                  spacing: { after: 100 }
                }),

                // ABCDE Assessment
                new Paragraph({
                  text: "ABCDE Assessment:",
                  bold: true,
                  spacing: { after: 50 }
                }),
                ...(abcdeFindings ? [
                  new Paragraph({
                    text: `  A - Airway: ${abcdeFindings.airway?.status || 'Not assessed'}`,
                    spacing: { after: 25 }
                  }),
                  new Paragraph({
                    text: `  B - Breathing: ${abcdeFindings.breathing?.status || 'Not assessed'}`,
                    spacing: { after: 25 }
                  }),
                  new Paragraph({
                    text: `  C - Circulation: ${abcdeFindings.circulation?.status || 'Not assessed'}`,
                    spacing: { after: 25 }
                  }),
                  new Paragraph({
                    text: `  D - Disability: ${abcdeFindings.disability?.status || 'Not assessed'}`,
                    spacing: { after: 25 }
                  }),
                  new Paragraph({
                    text: `  E - Exposure: ${abcdeFindings.exposure?.status || 'Not assessed'}`,
                    spacing: { after: 100 }
                  })
                ] : [
                  new Paragraph({
                    text: "  ABCDE assessment not yet performed",
                    italics: true,
                    spacing: { after: 100 }
                  })
                ]),

                // Assessment Stage
                new Paragraph({
                  children: [
                    new TextRun({ text: "Assessment Stage: ", bold: true }),
                    new TextRun({ text: assessmentStage || 'Primary Survey' })
                  ],
                  spacing: { after: 100 }
                }),

                // Red Flags
                ...(diagnosisPrediction?.red_flags?.length > 0 ? [
                  new Paragraph({
                    text: "⚠️ RED FLAGS:",
                    bold: true,
                    color: "FF0000",
                    spacing: { after: 50 }
                  }),
                  ...diagnosisPrediction.red_flags.map(flag => 
                    new Paragraph({
                      text: `  • ${flag}`,
                      color: "FF0000",
                      spacing: { after: 25 }
                    })
                  ),
                  new Paragraph({ text: "", spacing: { after: 50 } })
                ] : [])
              ]
            ),

            // R - RECOMMENDATION
            ...createISBARSection(
              "Recommendation/s",
              "Suggested actions the patient needs to take, next steps and follow up",
              [
                // Recommended Actions
                ...(assessmentRecommendations && (Array.isArray(assessmentRecommendations) ? assessmentRecommendations.length > 0 : assessmentRecommendations !== '') ? [
                  new Paragraph({
                    text: "Immediate Actions:",
                    bold: true,
                    spacing: { after: 50 }
                  }),
              ...(Array.isArray(assessmentRecommendations) 
                ? assessmentRecommendations.map((action, idx) => 
                    new Paragraph({
                      text: `  ${idx + 1}. ${action.action || action}${action.details ? ': ' + action.details : ''}`,
                      spacing: { after: 50 }
                    })
                  )
                : [
                    new Paragraph({
                      text: `  ${assessmentRecommendations}`,
                      spacing: { after: 50 }
                    })
                  ]
              ),
              new Paragraph({ text: "", spacing: { after: 100 } })
                ] : []),

                // Differential Diagnoses to Consider
                ...(diagnosisPrediction?.differential_diagnoses?.length > 0 ? [
                  new Paragraph({
                    text: "Differential Diagnoses to Consider:",
                    bold: true,
                    spacing: { after: 50 }
                  }),
                  ...diagnosisPrediction.differential_diagnoses.slice(0, 3).map((diff, idx) => 
                    new Paragraph({
                      text: `  ${idx + 1}. ${diff.condition || diff} ${diff.confidence_percentage ? `(${diff.confidence_percentage}% likelihood)` : ''}`,
                      spacing: { after: 25 }
                    })
                  ),
                  new Paragraph({ text: "", spacing: { after: 100 } })
                ] : []),

                // Follow-up Actions
                ...(diagnosisPrediction?.primary_diagnosis?.confirmatory_findings?.length > 0 ? [
                  new Paragraph({
                    text: "Follow-up Assessment Required:",
                    bold: true,
                    spacing: { after: 50 }
                  }),
                  ...diagnosisPrediction.primary_diagnosis.confirmatory_findings.slice(0, 5).map((finding, idx) => 
                    new Paragraph({
                      text: `  ${idx + 1}. ${finding}`,
                      spacing: { after: 25 }
                    })
                  ),
                  new Paragraph({ text: "", spacing: { after: 100 } })
                ] : []),

                // Clinical Notes
                ...(transcript && transcript.length > 0 ? [
                  new Paragraph({
                    text: "Clinical Notes / Transcript:",
                    bold: true,
                    spacing: { after: 50 }
                  }),
                  new Paragraph({
                    text: transcript.substring(0, 500) + (transcript.length > 500 ? '...' : ''),
                    spacing: { after: 100 }
                  })
                ] : [])
              ]
            ),

            // Footer
            new Paragraph({
              text: "---",
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 200 }
            }),
            new Paragraph({
              text: "This document was generated by MATILDA (Medical AI Triage Intelligence & Liaison Decision Assistant)",
              alignment: AlignmentType.CENTER,
              italics: true,
              spacing: { after: 100 }
            }),
            new Paragraph({
              text: "Royal Flying Doctor Service",
              alignment: AlignmentType.CENTER,
              bold: true
            })
          ]
        }]
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const filename = `RFDS_ISBAR_Handover_${new Date().toISOString().replace(/[:.]/g, '-')}.docx`;
      saveAs(blob, filename);

      console.log('OLD: ISBAR handover document generated successfully:', filename);
    } catch (error) {
      console.error('OLD: Error generating ISBAR handover document:', error);
      alert('OLD: Error generating handover document. Please try again.');
    }
  }, []); // Empty dependencies - this function is not used

  // Belief-Guided Vision Analysis
  const analyzeWithBeliefGuidedVision = useCallback(async (frameData) => {
    console.log('[BGV] Starting belief-guided vision analysis...');
    
    try {
      const frameType = beliefGuidedVision.initialized ? 'subsequent' : 'first';
      
      const response = await fetch('/api/belief-guided-vision', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageData: frameData,
          frameType,
          beliefState: {
            initialized: beliefGuidedVision.initialized,
            frameCount: beliefGuidedVision.frameCount,
            structure: beliefGuidedVision.structure,
            dynamics: beliefGuidedVision.dynamics,
            precision: beliefGuidedVision.precision
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`BGV API error: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log(`[BGV] Frame ${result.frame_count} analyzed (${result.type})`);
        
        // Update belief state
        setBeliefGuidedVision({
          enabled: true,
          initialized: true,
          frameCount: result.frame_count,
          structure: result.structure,
          dynamics: result.dynamics,
          precision: result.precision,
          lastObservation: result.observation
        });
        
        // Update frame description with the observation
        setFrameDescription(result.observation);
        
        // If changes were detected, log them
        if (result.changes_detected) {
          console.log('[BGV] Changes detected:', result.changes_detected);
          
          if (result.changes_detected.medical_concerns) {
            console.warn('[BGV] ⚠️ Medical concerns detected!');
          }
        }
        
        return {
          success: true,
          description: result.observation,
          beliefState: {
            structure: result.structure,
            dynamics: result.dynamics,
            precision: result.precision
          }
        };
      } else {
        throw new Error(result.error || 'BGV analysis failed');
      }
      
    } catch (error) {
      console.error('[BGV] Error:', error);
      setError(`Belief-guided vision error: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    }
  }, [beliefGuidedVision]);

  // Shared analysis function used by BOTH video and webcam
  // Define this BEFORE analyzeWebcamFrame to avoid issues, but don't add to dependencies
  const analyzeFrameWithData = useCallback(async (frameData, source = 'video', existingHistoryId = null) => {
    console.log('analyzeFrameWithData called', { source, existingHistoryId, hasFrameData: !!frameData });
    
    // check if isAnalysing is already true (only allow one analysis at a time)
    if (isAnalyzing) {
      console.warn('Analysis already in progress, ignoring call to analyzeFrameWithData');
      return;
    }
    
    if (!frameData) {
      console.error('No frame data provided to analyzeFrameWithData');
      setError('No frame data provided');
      return;
    }

    console.log('Setting isAnalyzing to true');
    setIsAnalyzing(true);
    setError('');
    setSuccess('Analysing frame...');

    try {
      // Step 1: Vision Analysis - Use Belief-Guided Vision if enabled, otherwise GPT-4o
      let finalDescription = '';
      setFrameDescription('');
      
      // Check if Belief-Guided Vision is enabled
      if (beliefGuidedVision.enabled) {
        console.log('[BGV] Using Belief-Guided Vision (MedGemma)');
        const bgvResult = await analyzeWithBeliefGuidedVision(frameData);
        
        if (bgvResult.success) {
          finalDescription = bgvResult.description;
          console.log(`[BGV] Frame ${beliefGuidedVision.frameCount} analyzed successfully`);
        } else {
          console.error('[BGV] Analysis failed, falling back to GPT-4o');
          // Fall through to GPT-4o analysis
        }
      }
      
      // If BGV not enabled or failed, use GPT-4o Vision
      if (!beliefGuidedVision.enabled || !finalDescription) {
        let apiEndpoint = '';
        let visionPrompt = '';
        const useGPU = false; // Set to false to use GPT-4o Vision API
        if (useGPU || window.navigator.userAgent.includes('GPU')) {
          console.log('Using GPU for analysis');
          apiEndpoint = '/api/analyze-frame-local-once';
          visionPrompt = 'Analyse this medical scene in detail. Describe what you see, focusing on any visible medical conditions, symptoms, patient status, injuries, or relevant clinical information. Be specific and thorough in your observations. If you see a patient in the image, please estimate their BMI, and include it at the end of your description in the format "[BMI:XX]". If no patient is visible, omit this. Avoid making assumptions beyond what is visually evident.';
        } else {
          console.log('Using GPT-4o Vision API');
          apiEndpoint = '/api/analyze-frame';
          visionPrompt = 'Describe what is happening in this image. Include details about the people, their actions, expressions, the environment, and any notable objects or situations.';
        }
        const startTime = Date.now();
        console.log(`Sending frame to ${apiEndpoint} at ${new Date().toLocaleTimeString()}...`);
        // Record when analyze-frame request is initiated
        setLastAnalyzeFrameRequest(new Date());
        
        // Create AbortController for timeout handling
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.log(`Vision API call timed out after 45 seconds`);
          controller.abort();
        }, 45000); // 45 second timeout
        
        const resp = await fetch(apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image: frameData,
            prompt: visionPrompt,
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId); // Clear timeout if request completes
        const responseTime = Date.now() - startTime;
        console.log(`Vision API response received after ${responseTime}ms`);
        
        if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
        
        // Handle streaming SSE response
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'chunk' && data.content) {
                  finalDescription += data.content;
                  setFrameDescription(finalDescription);
                } else if (data.type === 'done') {
                  finalDescription = data.description || finalDescription;
                  setFrameDescription(finalDescription);
                } else if (data.type === 'error') {
                  throw new Error(data.error || 'Vision API error');
                }
              } catch (e) {
                // Ignore parsing errors
              }
            }
          }
        }
        
        console.log('Vision streaming complete, description:', finalDescription);
      } // End of GPT-4o vision block
      
      // Check for BMI in the description
      if (finalDescription.includes('BMI:')) {
        const bmiMatch = finalDescription.match(/\[BMI:(\d{2,3})\]/);
        if (bmiMatch && bmiMatch.length > 1) {
          const bmiValue = parseInt(bmiMatch[1], 10);
          setBmi(bmiValue);
        }
      }
      
          if (existingHistoryId) {
            setAnalysisHistory(prev => prev.map(entry => 
              entry.id === existingHistoryId 
                ? { ...entry, visionAnalysis: finalDescription } 
                : entry
            ));
          }
      
      if (!finalDescription || finalDescription.trim().length === 0) {
          console.error('No description received from vision API; using generic description');
          finalDescription = 'Scene description unavailable. Proceed with ABCDE and triage based on current visual context.';
          setFrameDescription(finalDescription);
          if (existingHistoryId) {
            setAnalysisHistory(prev => prev.map(entry => 
              entry.id === existingHistoryId 
                ? { ...entry, visionAnalysis: finalDescription } 
                : entry
            ));
        }
      }
      
      console.log('Vision analysis complete, description length:', finalDescription.length);
      console.log('Vision analysis complete, description:', finalDescription);

      // Add to analysis history only if not already created (e.g., video path)
      let historyIdToUse = existingHistoryId;
      if (!historyIdToUse) {
        const historyEntry = {
          id: Date.now(),
          timestamp: new Date().toLocaleTimeString(),
          frameData: frameData,
          visionAnalysis: finalDescription,
          triageScore: null,
          assessmentStage: null,
          isProcessing: true,
          source: source
        };
        historyIdToUse = historyEntry.id;
        setAnalysisHistory(prev => [historyEntry, ...prev].slice(0, 50));
      }

      // Vision analysis complete - allow next continuous analysis to start
      // The downstream pipeline (assessment, triage, diagnosis, etc.) will continue asynchronously
      setIsAnalyzing(false);
      console.log('Vision complete, setIsAnalyzing(false) - next continuous analysis can proceed in 0.5s');

      // Build comprehensive description including full history
      const allAnalyses = [
        finalDescription,  // Current frame
        ...analysisHistory
          .filter(entry => entry.type !== 'transcript' && entry.visionAnalysis) // Only non-transcript entries with vision analysis
          .map(entry => entry.visionAnalysis)
      ];
      const combinedDescription = allAnalyses.join('\n\n--- Previous Analysis ---\n\n');
      
      // Build transcript text.
      //
      // This used to send the ENTIRE history on every pass, so the prompt grew
      // for the whole length of the consult: slower and dearer with every turn,
      // and a long repetitive history invites the model to repeat itself. The
      // server uses a supplied transcript whole -- it only slices its own
      // fallback -- so nothing downstream was bounding this either.
      //
      // Keep the OPENING, where the presenting complaint is stated, and the most
      // recent turns. A plain tail would let the chief complaint scroll out of
      // the assessment on any consult longer than a few minutes.
      const transcriptText = windowTranscript(transcriptHistory);
      
      // Add transcript if available
      let fullDescription = transcriptText 
        ? `${combinedDescription}\n\n--- Clinical Transcript ---\n${transcriptText}`
        : combinedDescription;
      
      // Add vitals if available
      if (heartRate) {
        fullDescription += `\n\n--- Vital Signs ---\nHeart Rate: ${heartRate} bpm`;
      }
      if (webcamBreathingRate) {
        fullDescription += `\nRespiratory Rate: ${webcamBreathingRate} br/min`;
      }
      if (webcamBmi && webcamBmi > 0) {
        fullDescription += `\nBMI: ${webcamBmi}`;
      }
      
      // Add MHR data if available
      if (healthRecord && Object.keys(healthRecord).length > 0) {
        fullDescription += `\n\n--- My Health Record Data Available ---\nPatient has provided access to their health record with medical history.`;
      }

      // Step 2a: Assessment stage (separate)
      console.log('Step 2a: Fetching assessment stage...');
      let assessmentResponse = null;
      let assessmentStageVal = null;
      try {
        // Record when assessment-stage request is initiated
        setLastAssessmentStageRequest(new Date());
        assessmentResponse = await axios.post('/api/assessment-stage', {
          description: fullDescription,
          historyCount: analysisHistory.length + 1,
          previousTriageScores: analysisHistory
            .filter(entry => entry.type !== 'transcript')
            .map(entry => entry.triageScore),
          transcript: transcriptText || null
        });
        if (assessmentResponse && assessmentResponse.data) {
          assessmentStageVal = assessmentResponse.data.stage;
          setAssessmentStage(assessmentResponse.data.stage);
          setAbcdeFindings(assessmentResponse.data.abcde_findings);
          
          // Log the API response
          setProcessLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            stage: 'Assessment Stage',
            data: assessmentResponse.data,
            duration: null
          }]);
          // Update history with assessment stage (keep processing open until triage completes)
          if (historyIdToUse) {
            setAnalysisHistory(prev => prev.map(entry => 
              entry.id === historyIdToUse 
                ? { ...entry, assessmentStage: assessmentResponse.data.stage }
                : entry
            ));
          }
          // Timeline for assessment
          const tsA = new Date();
          setTimelineEvents(prev => [...prev, {
            time: tsA.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            title: 'Assessment Stage Updated',
            description: assessmentResponse.data.stage
          }]);
        }
      } catch (e) {
        console.error('Step 2a (assessment) failed:', e);
      }

      // Step 2b: Triage score (separate)
      console.log('Step 2b: Fetching triage score...');
      let triageResponse = null;
      let triageScoreVal = null;
      try {
        // Record when triage-score request is initiated
        setLastTriageScoreRequest(new Date());
        triageResponse = await axios.post('/api/triage-score', {
          description: fullDescription,
          historyCount: analysisHistory.length + 1,
          previousTriageScores: analysisHistory
            .filter(entry => entry.type !== 'transcript')
            .map(entry => entry.triageScore),
          transcript: transcriptText || null
        });
        if (triageResponse && triageResponse.data) {
          triageScoreVal = triageResponse.data.score;
          setTriageScore(triageResponse.data.score);
          setTriageReasoning(triageResponse.data.reasoning);
          setTriageLogprobs(triageResponse.data.logprobs);
          setScoreConfidences(triageResponse.data.scoreConfidences);
          
          // Log the API response
          setProcessLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            stage: 'Triage',
            data: triageResponse.data,
            duration: null
          }]);
          // Timeline for triage
          const tsT = new Date();
          setTimelineEvents(prev => [...prev, {
            time: tsT.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            title: 'Triage Updated',
            description: `Priority ${triageResponse.data.score}`
          }]);
        }
      } catch (e) {
        console.error('Step 2b (triage) failed:', e);
      }

      // Finalize history entry for Step 2 results
      if (historyIdToUse) {
        setAnalysisHistory(prev => prev.map(entry => 
          entry.id === historyIdToUse 
            ? { ...entry, triageScore: (triageScoreVal ?? null), assessmentStage: (assessmentStageVal ?? null), isProcessing: false }
            : entry
        ));
      }

      // Step 3: Get action plan, diagnosis, guidelines, and structured assessment in parallel
      let actionPlanResponse, diagnosisResponse, guidelinesResponse, structuredAssessmentResponse;
      try {
        const triageScoreForStep3 = triageResponse?.data?.score ?? triageScoreVal ?? null;
        const assessmentStageForStep3 = assessmentResponse?.data?.stage ?? assessmentStageVal ?? null;
        const abcdeForStep3 = assessmentResponse?.data?.abcde_findings ?? null;
        
        [actionPlanResponse, diagnosisResponse, guidelinesResponse, structuredAssessmentResponse] = await Promise.all([
          axios.post('/api/plan-of-action', {
            description: fullDescription,
            triageScore: triageScoreForStep3,
            assessmentStage: assessmentStageForStep3,
            historyCount: analysisHistory.length + 1,
            previousTriageScores: analysisHistory
              .filter(entry => entry.type !== 'transcript')
              .map(entry => entry.triageScore),
            transcript: transcriptText || null
          }),
          axios.post('/api/diagnosis-prediction', {
            description: fullDescription,
            triageScore: triageScoreForStep3,
            assessmentStage: assessmentStageForStep3,
            abcdeFindings: abcdeForStep3,
            historyCount: analysisHistory.length + 1,
            previousTriageScores: analysisHistory
              .filter(entry => entry.type !== 'transcript')
              .map(entry => entry.triageScore),
            transcript: transcriptText || null
          }),
          axios.post('/api/emergency-guidelines', {
            primaryDiagnosis: 'Emergency Assessment Required',
            triageScore: triageScoreForStep3,
            assessmentStage: assessmentStageForStep3,
            historyCount: analysisHistory.length + 1,
            previousTriageScores: analysisHistory
              .filter(entry => entry.type !== 'transcript')
              .map(entry => entry.triageScore),
            transcript: transcriptText || null
          }),
          axios.post('/api/structured-assessment', {
            description: fullDescription,
            triageScore: triageScoreForStep3,
            assessmentStage: assessmentStageForStep3,
            abcdeFindings: abcdeForStep3,
            transcript: transcriptText || null
          })
        ]);
      } catch (e) {
        console.error('Step 3 failed:', e);
        // Do not throw here; we still want to mark completion and show partial results
      }

      setActionPlan(actionPlanResponse?.data || null);
      setDiagnosisPrediction(diagnosisResponse?.data || null);
      setEmergencyGuidelines(guidelinesResponse?.data || null);
      setStructuredAssessment(structuredAssessmentResponse?.data || null);
      
      // Log the API responses
      if (diagnosisResponse?.data) {
        setProcessLogs(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          stage: 'Diagnosis',
          data: diagnosisResponse.data,
          duration: null
        }]);
      }
      if (actionPlanResponse?.data) {
        setProcessLogs(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          stage: 'Action Plan',
          data: actionPlanResponse.data,
          duration: null
        }]);
      }
      if (structuredAssessmentResponse?.data) {
        setProcessLogs(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          stage: 'Structured Assessment',
          data: structuredAssessmentResponse.data,
          duration: null
        }]);
      }

      // Add timeline event for analysis completion
      const now = new Date();
      const triageLabel = (triageResponse?.data?.score != null) ? `Priority ${triageResponse.data.score}` : 'Priority unknown';
      const stageLabel = assessmentResponse?.data?.stage || 'Stage unknown';
      setTimelineEvents(prev => [...prev, {
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        title: 'Analysis Complete',
        description: `Triage: ${triageLabel} | Stage: ${stageLabel}`
      }]);

      // Generate treatment pathways based on diagnosis
      if (diagnosisResponse?.data && diagnosisResponse.data.differential_diagnoses) {
        const pathways = diagnosisResponse.data.differential_diagnoses.slice(0, 3).map((diagnosis, index) => {
          // Generate pathway with enhanced decision support info
          const pathwayEvents = [
            { timeOffset: '5 min', title: 'Initial Treatment', description: diagnosis.initial_management || 'Initiate protocol' },
            { timeOffset: '15 min', title: 'Re-assessment', description: 'Monitor response to treatment' },
            { timeOffset: '30 min', title: 'Decision Point', description: diagnosis.outcome || 'Evaluate next steps' }
          ];
          
          // Determine risk/benefit based on pathway priority
          const riskLevels = ['Low', 'Low-Moderate', 'Moderate'];
          const benefitLevels = ['High', 'Moderate-High', 'Moderate'];
          const resources = [
            ['Basic monitoring', 'Standard medications'],
            ['Diagnostic imaging', 'Specialist consultation'],
            ['Advanced monitoring', 'Specialized equipment']
          ];
          const timeSensitivity = ['Immediate (0-15 min)', 'Urgent (15-60 min)', 'Priority (1-4 hours)'];
          
          return {
            name: diagnosis.condition,
            probability: diagnosis.confidence_score ? `${(diagnosis.confidence_score * 100).toFixed(0)}%` : diagnosis.confidence,
            confidence: diagnosis.confidence,
            risk: riskLevels[index] || 'Moderate',
            benefit: benefitLevels[index] || 'Moderate',
            resources: resources[index] || ['Standard care'],
            timeSensitivity: timeSensitivity[index] || 'Standard',
            contraindications: diagnosis.contraindications || ['Review patient history', 'Check allergies'],
            expectedOutcome: diagnosis.outcome || 'Clinical improvement expected within 24-48 hours',
            events: pathwayEvents,
            rationale: diagnosis.rationale ? diagnosis.rationale.substring(0, 120) + '...' : 'Based on clinical presentation and diagnostic criteria'
          };
        });
        
        setTreatmentPathways(pathways);
        
        // Add treatment pathways initiated event
        setTimelineEvents(prev => [...prev, {
          time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          title: 'Treatment Pathways Generated',
          description: `${pathways.length} potential treatment options identified`
        }]);
      }

      setSuccess('Complete medical assessment generated successfully!');

    } catch (error) {
      // Check if it's a timeout error
      if (error.name === 'AbortError') {
        setError('Vision analysis timed out after 45 seconds. The vision model may be overloaded. Please try again.');
        console.error('Vision analysis timeout:', error);
      } else {
      setError(`Error analyzing frame: ${error.message}`);
      console.error('Analysis error:', error);
      }
      
      // Mark history entry as failed if it was created
      setAnalysisHistory(prev => prev.map(entry => 
        (existingHistoryId ? entry.id === existingHistoryId : (entry.isProcessing && entry.source === source))
          ? { ...entry, isProcessing: false, error: error.message }
          : entry
      ));
      
      // Reset analyzing flag in case vision failed before we could set it to false
      setIsAnalyzing(false);
    }
    // Note: No finally block - setIsAnalyzing(false) is called after vision completes or in catch
  }, [heartRate, webcamBreathingRate, webcamBmi, transcript, analysisHistory, isAnalyzing]);

  // Test heart rate display - DISABLED (was overwriting real webcam HR data)
  // useEffect(() => {
  //   // Set some initial heart rate data for testing
  //   setHeartRate(75);
  //   setHeartRateConfidence(0.85);
  //   setHeartRateHistory([{
  //     id: Date.now(),
  //     timestamp: new Date().toLocaleTimeString(),
  //     bpm: 75,
  //     confidence: 0.85,
  //     faceDetected: true
  //   }]);
  // }, []);


  // Continuous heart rate detection while video is playing

  useEffect(() => {

    if (videoFile && videoRef.current) {

      // Start continuous heart rate detection every 2 seconds

      heartRateIntervalRef.current = setInterval(() => {

        if (videoRef.current && !videoRef.current.paused) {

          const frameData = extractFrame();

          if (frameData) {

            detectHeartRate(frameData);

          }

        }

      }, 2000); // Every 2 seconds



      return () => {

        if (heartRateIntervalRef.current) {

          clearInterval(heartRateIntervalRef.current);

        }

      };

    }

  }, [videoFile, detectHeartRate, extractFrame]);



  const performMetaAnalysisOnHistory = useCallback(async () => {
    console.log('Starting meta-analysis on history...');
    if (analysisHistory.length === 0) {
      console.log('No analysis history available for meta-analysis.');
      return;
    }
    
    
    setIsMetaAnalyzing(true);

    
    
    try {

      // Combine all analysis history for comprehensive assessment in chronological order

      // Reverse the array to get chronological order (oldest first, newest last)

      const chronologicalHistory = [...analysisHistory].reverse();
      
      console.log('Chronological history for meta-analysis:', chronologicalHistory);
      const allAnalyses = chronologicalHistory.map((entry, index) => {

        const frameNumber = chronologicalHistory.length - index; // Frame numbers from oldest to newest

        const analysisText = entry.visionAnalysis || 'No analysis available';
        return `Frame #${frameNumber} (${entry.timestamp}):\n${analysisText}`;
      });

      
      
      const combinedDescription = allAnalyses.join('\n\n--- Next Frame ---\n\n');
      
      console.log('Combined description for meta-analysis:', combinedDescription);
      // Debug: Log what's being sent

      console.log('Meta-analysis sending:', {

        totalFrames: analysisHistory.length,

        chronologicalFrames: chronologicalHistory.length,

        descriptionLength: combinedDescription.length,

        firstFrame: chronologicalHistory[0]?.timestamp,

        lastFrame: chronologicalHistory[chronologicalHistory.length - 1]?.timestamp

      });

      
      
      const [assessmentResponse, triageResponse] = await Promise.all([  

        axios.post('/api/assessment-stage', {             

          description: combinedDescription,

          historyCount: analysisHistory.length,

          previousTriageScores: chronologicalHistory.map(entry => entry.triageScore) // Use chronological order

        }),     

        axios.post('/api/triage-score', {   

          description: combinedDescription,

          historyCount: analysisHistory.length,

          previousTriageScores: chronologicalHistory.map(entry => entry.triageScore) // Use chronological order

        })      

      ]);



      setAssessmentStage(assessmentResponse.data.stage);

      setAbcdeFindings(assessmentResponse.data.abcde_findings);

      setAssessmentRecommendations(assessmentResponse.data.recommendations);

      
      
      setTriageScore(triageResponse.data.score);

      setTriageReasoning(triageResponse.data.reasoning);

      setTriageLogprobs(triageResponse.data.logprobs);
      setScoreConfidences(triageResponse.data.scoreConfidences);

      
      
      // Update meta-analysis state

      setMetaAnalysis(`Meta-analysis completed on ${analysisHistory.length} frames. Assessment Stage: ${assessmentResponse.data.stage}, Triage Score: ${triageResponse.data.score}/5`);

      
      
      setSuccess('Meta-analysis completed on full history!');
      
      

    } catch (err) {

      setError(`Error in meta-analysis: ${err.message}`);

      console.error('Meta-analysis error:', err);

    } finally {

      setIsMetaAnalyzing(false);

    }

  }, [analysisHistory]);



  const analyzeFrameVisionOnly = useCallback(async () => {

    if (!videoRef.current) {

      setError('Please load a video first');

      return;

    }



    setIsVisionAnalyzing(true);

    setError('');

    setSuccess('');



    const maxRetries = 3;

    let retryCount = 0;

    let finalDescription = '';

    let finalEvaluation = null;



    // Extract frame data at the beginning to ensure it matches what's being analyzed

    const frameData = extractFrame();

    if (!frameData) {

      setError('Failed to extract frame');

      setIsVisionAnalyzing(false);

      return;

    }



    while (retryCount < maxRetries) {

      try {

        const response = await fetch('/api/analyze-frame', {

          method: 'POST',

          headers: {

            'Content-Type': 'application/json',

          },

          body: JSON.stringify({

            image: frameData,

            prompt: 'As you are unable to analyze specific medical frames, we require some general observations based on the image provided. Describe what you see in this image in detail. Focus on observable elements, actions, and visual details.'

          }),

        });



        if (!response.ok) {

          throw new Error(`HTTP error! status: ${response.status}`);

        }



        const reader = response.body.getReader();

        const decoder = new TextDecoder();

        let description = '';



        while (true) {

          const { done, value } = await reader.read();

          if (done) break;



          const chunk = decoder.decode(value);

          const lines = chunk.split('\n');



          for (const line of lines) {

            if (line.startsWith('data: ')) {

              try {

                const data = JSON.parse(line.slice(6));

                if (data.type === 'chunk') {

                  description += data.content;

                  setFrameDescription(description);

                } else if (data.type === 'done') {

                  finalDescription = data.description || description;

                  setFrameDescription(finalDescription);

                  
                  
                  // Get assessment stage and triage score

                  const [assessmentResponse, triageResponse] = await Promise.all([

                    axios.post('/api/assessment-stage', { 

                      description: finalDescription,

                      historyCount: analysisHistory.length,

                      previousTriageScores: analysisHistory.map(entry => entry.triageScore)

                    }),

                    axios.post('/api/triage-score', { 

                      description: finalDescription 

                    })

                  ]);



                  setAssessmentStage(assessmentResponse.data.stage);

                  setAbcdeFindings(assessmentResponse.data.abcde_findings);

                  setAssessmentRecommendations(assessmentResponse.data.recommendations);

                  
                  
                  setTriageScore(triageResponse.data.score);

                  setTriageReasoning(triageResponse.data.reasoning);

                  setTriageLogprobs(triageResponse.data.logprobs);
                  setScoreConfidences(triageResponse.data.scoreConfidences);



                  // Add to analysis history

                  const historyEntry = {

                    timestamp: new Date().toLocaleTimeString(),

                    visionAnalysis: finalDescription,

                    triageScore: triageResponse.data.score,

                    assessmentStage: assessmentResponse.data.stage,

                    abcdeFindings: assessmentResponse.data.abcde_findings,

                    isProcessing: false,

                    retryCount: retryCount

                  };

                  setAnalysisHistory(prev => [historyEntry, ...prev].slice(0, 50)); // Keep last 50 analyses

                  
                  
                  setIsVisionAnalyzing(false);

                  setSuccess('Frame analyzed successfully!');

                  return;

                }

              } catch (e) {

                // Ignore parsing errors for incomplete chunks

              }

            }

          }

        }

      } catch (error) {

        retryCount++;

        console.error(`Attempt ${retryCount} failed:`, error);

        
        
        if (retryCount >= maxRetries) {

          setError(`Failed to analyze frame after ${maxRetries} attempts: ${error.message}`);

          setIsVisionAnalyzing(false);

          return;

        }

        
        
        // Wait before retrying

        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));

      }

    }

    
    
    // If we get here, all retries failed

    setError('Failed to analyze frame after all retry attempts');

    setIsVisionAnalyzing(false);

    
    
    // Small delay to prevent overwhelming the system

    setTimeout(() => {

      if (!isVisionAnalyzing) {

        analyzeFrameVisionOnly();

      }

    }, 100); // 100ms delay to prevent infinite loops

  }, [videoRef, extractFrame, analysisHistory]);



  // Automatic frame analysis while video is playing

  useEffect(() => {

    if (isAutoAnalysisEnabled && videoFile && videoRef.current) {

      if (autoAnalysisInterval === 0) {

        // Continuous mode - start analysis immediately if not already analyzing

        if (videoRef.current && !videoRef.current.paused && !isVisionAnalyzing) {

          analyzeFrameVisionOnly();

        }

      } else {

        // Timer-based mode

        autoAnalysisIntervalRef.current = setInterval(() => {

          if (videoRef.current && !videoRef.current.paused && !isVisionAnalyzing) {

            // Only analyze if video is playing and not currently analyzing vision

            analyzeFrameVisionOnly();

          }

        }, autoAnalysisInterval);



        return () => {

          if (autoAnalysisIntervalRef.current) {

            clearInterval(autoAnalysisIntervalRef.current);

          }

        };

      }

    }

  }, [isAutoAnalysisEnabled, videoFile, autoAnalysisInterval, analyzeFrameVisionOnly, isVisionAnalyzing]);



  // Separate effect for continuous mode to trigger when not analyzing

  useEffect(() => {

    if (isAutoAnalysisEnabled && autoAnalysisInterval === 0 && videoFile && videoRef.current && !isVisionAnalyzing) {

      if (videoRef.current && !videoRef.current.paused) {

        analyzeFrameVisionOnly();

      }

    }

  }, [isAutoAnalysisEnabled, autoAnalysisInterval, videoFile, isVisionAnalyzing, analyzeFrameVisionOnly]);



  // Independent meta-analysis that runs on the full history

  useEffect(() => {

    if (isAutoAnalysisEnabled && analysisHistory.length > 0) {

      // Start meta-analysis timer

      metaAnalysisIntervalRef.current = setInterval(() => {

        if (!isMetaAnalyzing && analysisHistory.length > 0) {

          performMetaAnalysisOnHistory();

        }

      }, metaAnalysisInterval);



      return () => {

        if (metaAnalysisIntervalRef.current) {

          clearInterval(metaAnalysisIntervalRef.current);

        }

      };

    }

  }, [isAutoAnalysisEnabled, analysisHistory.length, metaAnalysisInterval, isMetaAnalyzing, performMetaAnalysisOnHistory]);



  // Trigger meta-analysis when health records are added
  useEffect(() => {
    // Check if the most recent entry is a health record and we're not already analyzing
    if (analysisHistory.length > 0 && 
        analysisHistory[0].isHealthRecordAnalysis && 
        analysisHistory[0].isUnprocessedHealthRecord &&
        !isMetaAnalyzing) {
      console.log('Health record detected in analysis history, triggering meta-analysis...');
      console.log('Current analysis history:', analysisHistory);
      analysisHistory[0].isUnprocessedHealthRecord = false; // Mark as processed
      performMetaAnalysisOnHistory();
    }
  }, [analysisHistory, isMetaAnalyzing, performMetaAnalysisOnHistory]);

  // Update displayed elapsed times every second for real-time updates
  useEffect(() => {
    const timer = setInterval(() => {
      // Force re-render by updating a simple counter state
      setTimeUpdateTrigger(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Cleanup webcam WebSocket/polling on unmount
  useEffect(() => {
    return () => {
      if (webcamWsRef.current) {
        webcamWsRef.current.close();
      }
      if (webcamPollingRef.current) {
        clearInterval(webcamPollingRef.current);
      }
    };
  }, []);


  const analyzeFrame = async () => {

    if (!videoRef.current) {

      setError('Please load a video first');

      return;

    }



    setIsAnalyzing(true);

    setError('');

    setSuccess('');



    const maxRetries = 3;

    let retryCount = 0;

    let finalDescription = '';

    let finalEvaluation = null;



    // Extract frame data at the beginning to ensure it matches what's being analyzed

    const frameData = extractFrame();

    if (!frameData) {

      setError('Failed to extract frame');

      setIsAnalyzing(false);

      return;

    }



    setCurrentFrame(frameData);



    while (retryCount < maxRetries) {

      try {

        // Show retry status if this is a retry attempt

        if (retryCount > 0) {

          setSuccess(`Retrying analysis (attempt ${retryCount + 1}/${maxRetries})...`);

        }



        // Start heart rate detection (only on first attempt)

        if (retryCount === 0) {

          detectHeartRate(frameData);

        }



        // Send frame to GPT Vision API with streaming

        const response = await fetch('/api/analyze-frame', {

          method: 'POST',

          headers: {

            'Content-Type': 'application/json',

          },

          body: JSON.stringify({

            image: frameData,

            prompt: retryCount === 0 

              ? 'Analyze this medical video frame. Describe what you see in detail, focusing on any medical conditions, symptoms, or relevant visual information.'

              : 'Please provide a more detailed and comprehensive analysis of this medical video frame. Focus on specific medical observations, patient condition, symptoms, and any clinical findings. Be thorough and specific in your assessment.'

          })

        });



        if (!response.ok) {

          throw new Error(`HTTP error! status: ${response.status}`);

        }



        const reader = response.body.getReader();

        const decoder = new TextDecoder();

        let currentDescription = '';

        let isFirstChunk = true;



        try {

          while (true) {

            const { done, value } = await reader.read();

            if (done) break;



            const chunk = decoder.decode(value);

            const lines = chunk.split('\n');



            for (const line of lines) {

              if (line.startsWith('data: ')) {

                try {

                  const data = JSON.parse(line.slice(6));

                  
                  
                  if (data.type === 'chunk') {

                    if (isFirstChunk) {

                      // Clear previous description only when first new chunk arrives

                      currentDescription = data.content;

                      isFirstChunk = false;

                    } else {

                      currentDescription += data.content;

                    }

                    setFrameDescription(currentDescription);

                  } else if (data.type === 'done') {

                    finalDescription = data.description;

                    finalEvaluation = getVisionAnalysisCompleteness(data.description);

                    
                    
                    // Check if analysis is complete enough

                    if (finalEvaluation.level === 'complete' || finalEvaluation.level === 'moderate' || retryCount === maxRetries - 1) {

                      // Analysis is good enough or we've reached max retries

                      break;

                    } else {

                      // Analysis is partial/incomplete, retry

                      retryCount++;

                      setSuccess(`Analysis incomplete, retrying... (attempt ${retryCount + 1}/${maxRetries})`);

                      break;

                    }

                  } else if (data.type === 'error') {

                    throw new Error(data.error);

                  }

                } catch (parseError) {

                  // Ignore parsing errors for incomplete chunks

                }

              }

            }

          }

        } finally {

          reader.releaseLock();

        }



        // If we have a good analysis or reached max retries, proceed with assessment

        if (finalDescription && (finalEvaluation.level === 'complete' || finalEvaluation.level === 'moderate' || retryCount === maxRetries - 1)) {

          break;

        }



      } catch (err) {

        if (retryCount === maxRetries - 1) {

          throw err; // Re-throw on final attempt

        }

        retryCount++;

        setError(`Analysis attempt ${retryCount} failed, retrying...`);

      }

    }



    // Proceed with the final analysis

    try {

      // Add to analysis history immediately with basic info

      const historyEntry = {

        id: Date.now(),

        timestamp: new Date().toLocaleTimeString(),

        frameData: frameData,

        visionAnalysis: finalDescription,

        evaluation: finalEvaluation,

        triageScore: null,

        assessmentStage: null,

        isProcessing: true,

        retryCount: retryCount,
      };

      setAnalysisHistory(prev => [historyEntry, ...prev].slice(0, 50)); // Keep last 50 analyses



      // Now get assessment stage, triage score, and action plan   

      // Include all analysis history for comprehensive assessment

      const allAnalyses = [finalDescription, ...analysisHistory.map(entry => entry.visionAnalysis || 'No analysis available')];
      const combinedDescription = allAnalyses.join('\n\n--- Previous Analysis ---\n\n');

      
      
      // Include transcript in description if available
      let fullDescription = transcript 
        ? `${combinedDescription}\n\n--- Clinical Transcript ---\n${transcript}`
        : combinedDescription;

      // include heart rate, breathing rate, and bmi if available
      if (heartRate) {
        fullDescription += `\n\n--- Heart Rate ---\n${heartRate}\n`;
      }
      if (webcamBreathingRate) {
        fullDescription += `\n\n--- Breathing Rate ---\n${webcamBreathingRate}\n`;
      }
      if (webcamBmi) {
        fullDescription += `\n\n--- BMI ---\n${webcamBmi}\n`;
      }

      console.log('Final description sent for assessment and triage:', fullDescription);
      const [assessmentResponse, triageResponse] = await Promise.all([  

        axios.post('/api/assessment-stage', {             

          description: fullDescription,

          historyCount: analysisHistory.length + 1,

          previousTriageScores: analysisHistory.map(entry => entry.triageScore),
          
          transcript: transcript || null

        }),     

        axios.post('/api/triage-score', {   

          description: fullDescription,

          historyCount: analysisHistory.length + 1,

          previousTriageScores: analysisHistory.map(entry => entry.triageScore),
          
          transcript: transcript || null

        })      

      ]);



      setAssessmentStage(assessmentResponse.data.stage);

      setAbcdeFindings(assessmentResponse.data.abcde_findings);

      setAssessmentRecommendations(assessmentResponse.data.recommendations);

      
      
      setTriageScore(triageResponse.data.score);

      setTriageReasoning(triageResponse.data.reasoning);

      setTriageLogprobs(triageResponse.data.logprobs);
      setScoreConfidences(triageResponse.data.scoreConfidences);



      // Update the history entry with triage and assessment data

      setAnalysisHistory(prev => prev.map(entry => 

        entry.id === historyEntry.id 

          ? { ...entry, triageScore: triageResponse.data.score, assessmentStage: assessmentResponse.data.stage, isProcessing: false }

          : entry

      ));



      // Generate action plan, diagnosis prediction, emergency guidelines, and structured assessment
      
      // Build transcript for structured assessment
      const transcriptForAssessment = transcriptHistory
        .filter(entry => entry && entry.text)
        .map(entry => 
        `[${entry.speakerLabel || 'Unknown'}]: ${entry.text}`
      ).join('\n');

      const [actionPlanResponse, diagnosisResponse, guidelinesResponse, structuredAssessmentResponse] = await Promise.all([       

        axios.post('/api/plan-of-action', { 

          description: fullDescription,    

          triageScore: triageResponse.data.score,         

          assessmentStage: assessmentResponse.data.stage,

          historyCount: analysisHistory.length + 1,

          previousTriageScores: analysisHistory.map(entry => entry.triageScore),
          
          transcript: transcript || null

        }),     

        axios.post('/api/diagnosis-prediction', {         

          description: fullDescription,    

          triageScore: triageResponse.data.score,         

          assessmentStage: assessmentResponse.data.stage, 

          abcdeFindings: assessmentResponse.data.abcde_findings,

          historyCount: analysisHistory.length + 1,

          previousTriageScores: analysisHistory.map(entry => entry.triageScore),
          
          transcript: transcript || null

        }),     

        axios.post('/api/emergency-guidelines', {         

          primaryDiagnosis: 'Emergency Assessment Required', // Will be updated after diagnosis     

          triageScore: triageResponse.data.score,         

          assessmentStage: assessmentResponse.data.stage,

          historyCount: analysisHistory.length + 1,

          previousTriageScores: analysisHistory.map(entry => entry.triageScore),
          
          transcript: transcript || null

        }),
        
        axios.post('/api/structured-assessment', {
          description: fullDescription,
          triageScore: triageResponse.data.score,
          assessmentStage: assessmentResponse.data.stage,
          abcdeFindings: assessmentResponse.data.abcde_findings,
          transcript: transcriptForAssessment || null
        })      

      ]);



      setActionPlan(actionPlanResponse.data);             

      setDiagnosisPrediction(diagnosisResponse.data);     

      setEmergencyGuidelines(guidelinesResponse.data);    

      setStructuredAssessment(structuredAssessmentResponse.data);

      // Step 4: Get medication and transportation recommendations in parallel (non-blocking)
      try {
        const [medicationResponse, transportResponse] = await Promise.all([
          axios.post('/api/medication-recommendations', {
            diagnosisPrediction: diagnosisResponse.data,
            structuredAssessment: structuredAssessmentResponse.data,
            triageScore: triageResponse.data.score,
            patientAge: null,
            patientWeight: null
          }),
          axios.post('/api/transportation-recommendations', {
            triageScore: triageResponse.data.score,
            diagnosisPrediction: diagnosisResponse.data,
            assessmentStage: assessmentResponse.data.stage,
            patientLocation: null,
            patientStability: structuredAssessmentResponse.data?.assessment_summary?.stability || null
          })
        ]);
        
        setMedicationRecommendations(medicationResponse.data);
        setTransportationRecommendations(transportResponse.data);
      } catch (err) {
        console.error('Medication/Transportation recommendations error:', err);
        // Non-critical, continue without them
      }

      
      
      if (retryCount > 0) {

        setSuccess(`Complete medical assessment generated successfully! (Required ${retryCount + 1} attempts)`);

      } else {

        setSuccess('Complete medical assessment generated successfully!');

      }
      
      

    } catch (err) {

      setError(`Error analyzing frame: ${err.message}`);

      console.error('Analysis error:', err);

    } finally {

      setIsAnalyzing(false);

    }

  };



  const seekToTime = (time) => {

    if (videoRef.current) {

      videoRef.current.currentTime = time;

    }

  };



  const getScoreProbabilities = () => {

    if (!triageLogprobs || !triageLogprobs.content) return null;



    // Look for the score token in the logprobs

    const scoreToken = triageLogprobs.content.find(token =>

      token.token && /^\d+$/.test(token.token.trim())

    );



    if (scoreToken && scoreToken.top_logprobs) {

      return scoreToken.top_logprobs.map(logprob => ({

        score: parseInt(logprob.token.trim()),

        probability: Math.exp(logprob.logprob),

        logprob: logprob.logprob

      })).sort((a, b) => b.probability - a.probability);

    }



    return null;

  };



  const getVisionAnalysisCompleteness = (analysisText = null) => {        

    const textToAnalyze = analysisText || frameDescription;

    if (!textToAnalyze) return { level: 'incomplete', score: 0, color: '#333' }; 



    const text = textToAnalyze.toLowerCase();

    const wordCount = textToAnalyze.split(' ').length;

    
    
    // Check for completeness indicators

    const hasMedicalTerms = /\b(medical|condition|symptom|patient|health|clinical|diagnosis|treatment|emergency|distress|pain|injury|illness|disease)\b/.test(text);

    const hasSpecificDetails = /\b(specific|detailed|precise|exact|clear|obvious|visible|apparent)\b/.test(text);

    const hasUncertainty = /\b(unable|unclear|uncertain|might|could|possibly|may|seems|appears|suggests)\b/.test(text);

    const hasActionWords = /\b(require|need|attention|intervention|treatment|assessment|evaluation|examination)\b/.test(text);

    
    
    // Calculate completeness score

    let score = 0;

    if (wordCount > 50) score += 1; // Good length

    if (hasMedicalTerms) score += 1; // Medical context

    if (hasSpecificDetails) score += 1; // Specific observations

    if (hasActionWords) score += 1; // Action-oriented

    if (!hasUncertainty) score += 1; // Confident analysis

    
    
    // Determine level and color

    if (score >= 4) return { level: 'complete', score: 5, color: '#44ff44', text: '🟢 COMPLETE' };

    if (score >= 3) return { level: 'moderate', score: 3, color: '#ff8800', text: '🟠 MODERATE' };

    if (score >= 2) return { level: 'partial', score: 2, color: '#ff4444', text: '🔴 PARTIAL' };

    return { level: 'incomplete', score: 1, color: '#333', text: '⚫ INCOMPLETE' };

  };



  const getDiagnosisProbabilities = () => {

    if (!diagnosisPrediction) return null;



    const diagnoses = [

      diagnosisPrediction.primary_diagnosis,

      ...(diagnosisPrediction.differential_diagnoses || [])

    ].filter(diag => diag && diag.confidence);



    if (diagnoses.length === 0) return null;



    // Convert confidence levels to base probabilities

    const baseProbabilities = diagnoses.map(diag => {

      switch (diag.confidence) {

        case 'High':

          return { ...diag, baseProb: 0.85 };

        case 'Moderate':

          return { ...diag, baseProb: 0.65 };

        case 'Low':

          return { ...diag, baseProb: 0.35 };

        default:

          return { ...diag, baseProb: 0.50 };

      }

    });



    // Normalize probabilities to sum to 100%

    const totalBaseProb = baseProbabilities.reduce((sum, diag) => sum + diag.baseProb, 0);

    const normalizedProbabilities = baseProbabilities.map(diag => ({

      ...diag,

      probability: diag.baseProb / totalBaseProb,

      color: diag.confidence === 'High' ? '#4caf50' : 

             diag.confidence === 'Moderate' ? '#ff9800' : '#f44336',

      backgroundColor: diag.confidence === 'High' ? '#e8f5e8' : 

                      diag.confidence === 'Moderate' ? '#fff3e0' : '#ffebee'

    }));



    return normalizedProbabilities;

  };



  const getDiagnosisConfidenceProbability = (confidence, condition) => {

    const probabilities = getDiagnosisProbabilities();

    if (!probabilities) {

      // Fallback to individual confidence levels if no probabilities available

      switch (confidence) {

        case 'High':

          return { probability: 0.85, color: '#4caf50', backgroundColor: '#e8f5e8' };

        case 'Moderate':

          return { probability: 0.65, color: '#ff9800', backgroundColor: '#fff3e0' };

        case 'Low':

          return { probability: 0.35, color: '#f44336', backgroundColor: '#ffebee' };

        default:

          return { probability: 0.50, color: '#9e9e9e', backgroundColor: '#f5f5f5' };

      }

    }



    // Find the normalized probability for this specific condition

    const diagnosis = probabilities.find(diag => diag.condition === condition);

    if (diagnosis) {

      return {

        probability: diagnosis.probability,

        color: diagnosis.color,

        backgroundColor: diagnosis.backgroundColor

      };

    }



    // Fallback

    return { probability: 0.50, color: '#9e9e9e', backgroundColor: '#f5f5f5' };

  };



  const loadVideoFromFile = () => {

    // Create file input for video upload

    const input = document.createElement('input');

    input.type = 'file';

    input.accept = 'video/*';

    input.onchange = (e) => {

      const file = e.target.files[0];

      if (file) {

        setVideoFile(file);

        const url = URL.createObjectURL(file);

        if (videoRef.current) {

          videoRef.current.src = url;

          // Enable auto analysis with 5 second intervals when video is loaded

          setIsAutoAnalysisEnabled(true);

          setAutoAnalysisInterval(5000);

          // Auto play the video after loading

          videoRef.current.addEventListener('loadeddata', () => {

            videoRef.current.play().catch(e => {

              console.log('Autoplay prevented by browser:', e);

            });

          }, { once: true });

        }

      }

    };

    input.click();

  };



  const loadDefaultVideo = () => {

    // Load the default video from the backend server

    const videoUrl = '/api/video';

    if (videoRef.current) {

      videoRef.current.src = videoUrl;

      setVideoFile({ name: 'Pitting Edema - Example.mp4' }); // Set a mock file object

      // Enable auto analysis with 5 second intervals when video is loaded

      setIsAutoAnalysisEnabled(true);

      setAutoAnalysisInterval(5000);

      // Auto play the default video after loading

      videoRef.current.addEventListener('loadeddata', () => {

        videoRef.current.play().catch(e => {

          console.log('Autoplay prevented by browser:', e);

        });

      }, { once: true });

    }

  };

  // Helper function to format elapsed time since last API completion
  const formatElapsedTime = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const now = new Date();
    const elapsed = Math.floor((now - timestamp) / 1000); // seconds
    
    if (elapsed < 60) {
      return `${elapsed}s ago`;
    } else if (elapsed < 3600) {
      const minutes = Math.floor(elapsed / 60);
      return `${minutes}m ${elapsed % 60}s ago`;
    } else {
      const hours = Math.floor(elapsed / 3600);
      const minutes = Math.floor((elapsed % 3600) / 60);
      return `${hours}h ${minutes}m ago`;
    }
  };

  return (

    <div>
      
      {/* Header with RFDS Logo */}

      <div style={{ 

        backgroundColor: '#f8f9fa', 

        padding: '20px', 

        marginBottom: '0px', 

        borderRadius: '8px',

        border: '1px solid #e9ecef',

        position: 'relative'

      }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

          <img 

            src="/logos/Logo-RFDS.png" 

            alt="RFDS Logo" 

            style={{ height: '60px', width: 'auto' }}

          />

          <div style={{ textAlign: 'center', flex: '1' }}>

            <h2 style={{ margin: '0 0 2px 0', fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>

              Remote EM Cockpit

            </h2>

            <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>

              Patient: Bob McBob · Case #A2Z-093 · RFDS Telehealth

            </div>

          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <img 

              src="/logos/uncapt-logo-1.svg" 

              alt="Uncapt Logo" 

              style={{ height: '14px', width: 'auto' }}

            />

          </div>

            </div>

              </div>

      <div className="video-container">

        <div style={{ position: 'relative', display: videoFile ? 'block' : 'none' }}>

          <video

            ref={videoRef}

            className="video-player"

            controls

            autoPlay

            onLoadedData={handleVideoLoad}

            onPlay={() => {

              // Start continuous analysis when video starts playing

              if (isAutoAnalysisEnabled && autoAnalysisInterval === 0 && !isVisionAnalyzing) {

                analyzeFrameVisionOnly();

              }

            }}

            style={{ 

              width: '100%',

              height: 'auto',

              maxHeight: '70vh',

              objectFit: 'contain'

            }}

          >

            Your browser does not support the video tag.

          </video>

          
          
          {/* Heart Rate Overlay */}

          {heartRate && (

            <div style={{

              position: 'absolute',

              top: '10px',

              left: '50%',

              transform: 'translateX(-50%)',

              backgroundColor: 'rgba(0, 0, 0, 0.8)',

              color: 'white',

              padding: '8px 12px',

              borderRadius: '6px',

              fontSize: '14px',

              fontWeight: 'bold',

              display: 'flex',

              alignItems: 'center',

              gap: '8px',

              zIndex: 10

            }}>

              <div style={{

                width: '8px',

                height: '8px',

                borderRadius: '50%',

                backgroundColor: heartRate > 100 ? '#ff4444' : heartRate > 80 ? '#ff8800' : '#44ff44',

                animation: 'pulse 1s infinite'

              }}></div>

              <span>HR: {heartRate} BPM</span>

              <span style={{ fontSize: '12px', opacity: 0.8 }}>

                ({Math.round(heartRateConfidence * 100)}%)

              </span>

              {bmi && webcamBmi && (

                <>

                  <span style={{ fontSize: '12px', opacity: 0.6 }}>•</span>

                  <span>AI BMI: {bmi} webcamBMI: {webcamBmi}</span>

                </>

              )}

            </div>

          )}

          
          
          {/* Heart Rate Detection Status */}

          {isHeartRateDetecting && (

            <div style={{

              position: 'absolute',

              top: '10px',

              left: '50%',

              transform: 'translateX(-50%)',

              backgroundColor: 'rgba(0, 0, 0, 0.8)',

              color: 'white',

              padding: '8px 12px',

              borderRadius: '6px',

              fontSize: '14px',

              zIndex: 10

            }}>

              <div style={{

                width: '8px',

                height: '8px',

                borderRadius: '50%',

                backgroundColor: '#007bff',

                animation: 'pulse 1s infinite',

                display: 'inline-block',

                marginRight: '8px'

              }}></div>

              Detecting Heart Rate...

            </div>

          )}



          {/* Auto Analysis Status */}

          {isAutoAnalysisEnabled && (

            <div style={{

              position: 'absolute',

              top: '50px',

              left: '50%',

              transform: 'translateX(-50%)',

              backgroundColor: 'rgba(0, 0, 0, 0.8)',

              color: 'white',

              padding: '8px 12px',

              borderRadius: '6px',

              fontSize: '14px',

              zIndex: 10

            }}>

              <div style={{

                width: '8px',

                height: '8px',

                borderRadius: '50%',

                backgroundColor: '#28a745',

                animation: 'pulse 1s infinite',

                display: 'inline-block',

                marginRight: '8px'

              }}></div>

                Auto Analysis: {autoAnalysisInterval === 0 ? 'Continuous' : `Every ${autoAnalysisInterval / 1000}s`}

            </div>

          )}



          {/* RDR Triage Assessment Overlay - Top Left */}

          {triageScore && (

            <div style={{

              position: 'absolute',

              top: '0px',

              left: '10px',

              backgroundColor: 'white',

              color: '#333',

              padding: '12px',

              borderRadius: '8px',

              fontSize: '12px',

              fontWeight: '500',

              zIndex: 10,

              width: '280px'

            }}>

              <div style={{ 

                fontSize: '14px', 

                fontWeight: 'bold', 

                marginBottom: '8px',

                color: triageScore === 1 ? '#ff4444' : 

                       triageScore === 2 ? '#ff8800' : 

                       triageScore === 3 ? '#ffc107' : '#44ff44',

                borderBottom: `1px solid ${triageScore === 1 ? '#ff4444' : 

                                          triageScore === 2 ? '#ff8800' : 

                                          triageScore === 3 ? '#ffc107' : '#44ff44'}`,

                paddingBottom: '4px'

              }}>

                RDR Triage Score

              </div>

              
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>

                <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Priority:</span>

                <span 

                  style={{ 

                    fontSize: '20px', 

                    fontWeight: 'bold',

                    color: triageScore === 1 ? '#ff4444' : 

                           triageScore === 2 ? '#ff8800' : 

                           triageScore === 3 ? '#ffc107' : 

                           triageScore === 4 ? '#20c997' : '#6c757d',

                    backgroundColor: triageScore === 1 ? '#ffebee' : 

                                   triageScore === 2 ? '#fff3e0' : 

                                   triageScore === 3 ? '#fff8e1' : 

                                   triageScore === 4 ? '#e8f5e8' : '#f5f5f5',

                    padding: '4px 8px',

                    borderRadius: '4px'

                  }}

                >

                  {triageScore}

                </span>

              </div>
              
              



              {/* Reasoning */}

              {triageReasoning && (

                <div style={{ marginBottom: '8px' }}>

                  <div style={{ 

                    fontSize: '9px', 

                    color: '#555', 

                    lineHeight: '1.3',

                    padding: '6px',

                    backgroundColor: '#f8f9fa',

                    borderRadius: '4px',

                    border: '1px solid #e9ecef'

                  }}>

                    {triageReasoning}

                  </div>

                </div>

              )}



              {/* Score Confidence */}

              {getScoreProbabilities() && (

                <div style={{ marginTop: '8px' }}>

                  <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', color: '#4a90e2' }}>

                    Score Confidence:

                  </div>

                  {getScoreProbabilities().slice(0, 3).map((prob, index) => (

                    <div key={index} style={{ 

                      display: 'flex', 

                      justifyContent: 'space-between', 

                      alignItems: 'center',

                      marginBottom: '2px',

                      padding: '2px 4px',

                      backgroundColor: prob.score === triageScore ? '#e3f2fd' : '#f5f5f5',

                      borderRadius: '3px',

                      fontSize: '10px'

                    }}>

                      <span style={{ fontWeight: prob.score === triageScore ? 'bold' : 'normal' }}>

                        Score {prob.score}

                      </span>

                      <span style={{ color: '#666' }}>

                        {(prob.probability * 100).toFixed(1)}%

                      </span>

                    </div>

                  ))}

                </div>

              )}

            </div>

          )}



          {/* ABCDE Assessment Overlay - Left Side under Triage */}

          {abcdeFindings && Object.keys(abcdeFindings).length > 0 && (

            <div style={{

              position: 'absolute',

              top: '250px',

              left: '10px',

              backgroundColor: 'white',

              color: '#333',

              padding: '12px',

              borderRadius: '8px',

              fontSize: '12px',

              fontWeight: '500',

              zIndex: 10,

              minWidth: '220px',

              maxWidth: '280px',

              textAlign: 'left'

            }}>

              <div style={{ 

                fontSize: '14px', 

                fontWeight: 'bold', 

                marginBottom: '8px',

                color: '#4a90e2',

                borderBottom: '1px solid #4a90e2',

                paddingBottom: '4px'

              }}>

                ABCDE Completeness

              </div>

              {Object.entries(abcdeFindings).map(([key, value]) => (

                <div key={key} style={{ marginBottom: '6px', textAlign: 'left' }}>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2px' }}>

                    <span style={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '11px', textAlign: 'left' }}>{key}:</span>

                    <span style={{ 

                      fontSize: '10px', 

                      fontWeight: '600',

                      color: value.completeness_percentage >= 80 ? '#44ff44' : 

                             value.completeness_percentage >= 60 ? '#ff8800' : '#ff4444',

                      textAlign: 'right'

                    }}>

                      {value.completeness_percentage}%

                    </span>

                  </div>

                  {/* Mini Progress Bar */}

                  <div style={{

                    width: '100%',

                    height: '3px',

                    backgroundColor: '#e9ecef',

                    borderRadius: '2px',

                    overflow: 'hidden',

                    marginBottom: '2px'

                  }}>

                    <div style={{

                      width: `${value.completeness_percentage}%`,

                      height: '100%',

                      backgroundColor: value.completeness_percentage >= 80 ? '#44ff44' : 

                                       value.completeness_percentage >= 60 ? '#ff8800' : '#ff4444',

                      borderRadius: '2px',

                      transition: 'width 0.3s ease'

                    }}></div>

                  </div>

                  <div style={{ fontSize: '10px', color: '#666', lineHeight: '1.2', marginBottom: '2px', textAlign: 'left' }}>

                    {value.status}

                  </div>

                  {value.outstanding_questions && value.outstanding_questions.length > 0 && (

                    <div style={{ fontSize: '9px', color: '#999', lineHeight: '1.1', textAlign: 'left' }}>

                      <strong>Missing:</strong> {value.outstanding_questions.slice(0, 2).join(', ')}

                      {value.outstanding_questions.length > 2 && '...'}

                    </div>

                  )}

                </div>

              ))}

            </div>

          )}







          {/* Diagnosis Prediction Overlay - Top Right */}

          {diagnosisPrediction && (

            <div style={{

              position: 'absolute',

              top: '10px',

              right: '10px',

              backgroundColor: 'white',

              color: '#333',

              padding: '12px',

              borderRadius: '8px',

              fontSize: '12px',

              fontWeight: '500',

              zIndex: 10,

              minWidth: '220px',

              maxWidth: '280px'

            }}>

              <div style={{ 

                fontSize: '14px', 

                fontWeight: 'bold', 

                marginBottom: '8px',

                color: '#2e7d32',

                borderBottom: '1px solid #2e7d32',

                paddingBottom: '4px'

              }}>

                Diagnosis Prediction

              </div>

              
              
              {/* Clinical Notes */}

              {diagnosisPrediction.clinical_notes && (

                <div style={{ marginBottom: '8px' }}>

                  <div style={{ 

                    fontSize: '11px', 

                    fontWeight: 'bold', 

                    color: '#1976d2',

                    marginBottom: '4px'

                  }}>

                    Clinical Notes

                  </div>

                  <div style={{ 

                    fontSize: '9px', 

                    color: '#555', 

                    lineHeight: '1.3',

                    padding: '6px',

                    backgroundColor: '#f3f4f6',

                    borderRadius: '4px',

                    border: '1px solid #e5e7eb'

                  }}>

                    {diagnosisPrediction.clinical_notes}

                  </div>

                </div>

              )}

              
              
              {/* Primary Diagnosis */}

              <div style={{ marginBottom: '8px' }}>

                <div style={{ 

                  fontSize: '13px', 

                  fontWeight: 'bold', 

                  color: '#1b5e20',

                  marginBottom: '4px'

                }}>

                  Primary Diagnosis

                </div>

                <div style={{ 

                  padding: '8px', 

                  backgroundColor: '#e8f5e8', 

                  borderRadius: '4px',

                  border: '1px solid #4caf50'

                }}>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>

                    <span style={{ fontWeight: 'bold', fontSize: '12px', color: '#1b5e20' }}>

                      {diagnosisPrediction.primary_diagnosis?.condition}

                    </span>

                    <span style={{ 

                      fontSize: '9px', 

                      padding: '2px 4px',

                      borderRadius: '3px',

                      backgroundColor: getDiagnosisConfidenceProbability(diagnosisPrediction.primary_diagnosis?.confidence, diagnosisPrediction.primary_diagnosis?.condition).backgroundColor,

                      color: getDiagnosisConfidenceProbability(diagnosisPrediction.primary_diagnosis?.confidence, diagnosisPrediction.primary_diagnosis?.condition).color,

                      fontWeight: 'bold'

                    }}>

                      {(getDiagnosisConfidenceProbability(diagnosisPrediction.primary_diagnosis?.confidence, diagnosisPrediction.primary_diagnosis?.condition).probability * 100).toFixed(0)}%

                    </span>

                  </div>

                  {diagnosisPrediction.primary_diagnosis?.icd10_code && (

                    <div style={{ fontSize: '10px', color: '#555', marginBottom: '4px' }}>

                      <strong>ICD-10:</strong> {diagnosisPrediction.primary_diagnosis.icd10_code}

                    </div>

                  )}

                  <div style={{ fontSize: '10px', color: '#555', lineHeight: '1.3' }}>

                    {diagnosisPrediction.primary_diagnosis?.rationale}

                  </div>

                </div>

              </div>



              {/* Differential Diagnoses */}

              {diagnosisPrediction.differential_diagnoses && diagnosisPrediction.differential_diagnoses.length > 0 && (

                <div style={{ marginBottom: '8px' }}>

                  <div style={{ 

                    fontSize: '11px', 

                    fontWeight: 'bold', 

                    color: '#1b5e20',

                    marginBottom: '4px'

                  }}>

                    Differential Diagnoses

                  </div>

                  {diagnosisPrediction.differential_diagnoses.slice(0, 2).map((diag, index) => (

                    <div key={index} style={{ 

                      marginBottom: '4px', 

                      padding: '6px', 

                      backgroundColor: '#f1f8e9', 

                      borderRadius: '4px',

                      border: '1px solid #a5d6a7'

                    }}>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>

                        <span style={{ fontWeight: 'bold', fontSize: '10px', color: '#2e7d32' }}>{diag.condition}</span>

                        <span style={{ 

                          fontSize: '8px', 

                          padding: '1px 3px',

                          borderRadius: '2px',

                          backgroundColor: getDiagnosisConfidenceProbability(diag.confidence, diag.condition).backgroundColor,

                          color: getDiagnosisConfidenceProbability(diag.confidence, diag.condition).color,

                          fontWeight: 'bold'

                        }}>

                          {(getDiagnosisConfidenceProbability(diag.confidence, diag.condition).probability * 100).toFixed(0)}%

                        </span>

                      </div>

                      {diag.icd10_code && (

                        <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>

                          <strong>ICD-10:</strong> {diag.icd10_code}

                        </div>

                      )}

                      <div style={{ fontSize: '9px', color: '#555', lineHeight: '1.2' }}>

                        {diag.rationale}

                      </div>

                    </div>

                  ))}

                </div>

              )}



              {/* Red Flags */}

              {diagnosisPrediction.red_flags && diagnosisPrediction.red_flags.length > 0 && (

                <div style={{ marginBottom: '8px' }}>

                  <div style={{ 

                    fontSize: '11px', 

                    fontWeight: 'bold', 

                    color: '#d32f2f',

                    marginBottom: '4px'

                  }}>

                    🚨 Red Flags

                  </div>

                  <div style={{ fontSize: '9px', color: '#555', lineHeight: '1.3' }}>

                    {diagnosisPrediction.red_flags.slice(0, 3).map((flag, index) => (

                      <div key={index} style={{ marginBottom: '2px', color: '#d32f2f' }}>

                        • {flag}

                      </div>

                    ))}

                  </div>

                </div>

              )}



            </div>

          )}

        </div>



        {/* Video Controls - Above Analysis History */}

        {videoFile && (

          <div style={{ 

            display: 'flex', 

            justifyContent: 'flex-end', 

            alignItems: 'center', 

            gap: '10px', 

            marginBottom: '10px',

            marginTop: '20px',

            paddingRight: '20px'

          }}>

            <span style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>

              {videoFile.name}

            </span>

            <button className="btn btn-secondary" onClick={loadVideoFromFile} style={{ padding: '5px 10px', fontSize: '12px' }}>

              Change Video

            </button>

          </div>

        )}

        {/* Analysis History - Moved to bottom */}

        {false && analysisHistory.length > 0 && (

          <div style={{

            marginTop: '20px',

            backgroundColor: 'white',

            borderRadius: '12px',

            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',

            border: '1px solid #e5e7eb',

            overflow: 'hidden'

          }}>

            <div style={{

              padding: '16px 20px',

              borderBottom: '1px solid #e5e7eb',

              backgroundColor: '#f9fafb'

            }}>

              <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>

                📋 Analysis History

              </h3>

              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>

                Recent frame analyses ({analysisHistory.length} entries)

              </p>

            </div>

            
            
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>

              {analysisHistory.map((entry, index) => (

                <div key={entry.id} style={{

                  padding: '16px 20px',

                  borderBottom: index < analysisHistory.length - 1 ? '1px solid #f3f4f6' : 'none',

                  backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'

                }}>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>

                        #{analysisHistory.length - index}

                      </span>

                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>

                        {entry.timestamp}

                      </span>

                      {entry.source === 'webcam' && (
                        <span style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: '#dbeafe',
                          color: '#1e40af',
                          fontWeight: '600'
                        }}>
                          📹 WEBCAM
                        </span>
                      )}

                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>         

                      {entry.triageScore !== null ? (

                        <span style={{  

                          fontSize: '11px',         

                          padding: '2px 6px',       

                          borderRadius: '4px',      

                          backgroundColor: entry.triageScore <= 2 ? '#fef2f2' :         

                                            entry.triageScore <= 3 ? '#fef3c7' : '#f0fdf4',

                          color: entry.triageScore <= 2 ? '#dc2626' :       

                                   entry.triageScore <= 3 ? '#d97706' : '#16a34a',        

                          fontWeight: '500'         

                        }}> 

                          Triage: {entry.triageScore}/5         

                        </span>

                      ) : (

                        <span style={{  

                          fontSize: '11px',         

                          padding: '2px 6px',       

                          borderRadius: '4px',      

                          backgroundColor: '#f3f4f6',

                          color: '#9ca3af',        

                          fontWeight: '500'         

                        }}> 

                          {entry.isProcessing ? '⏳ Processing...' : '⏳ Triage Pending'}

                        </span>

                      )}

                      
                      
                      {entry.assessmentStage !== null ? (

                        <span style={{  

                          fontSize: '11px',         

                          padding: '2px 6px',       

                          borderRadius: '4px',      

                          backgroundColor: '#e0f2fe',           

                          color: '#0369a1',         

                          fontWeight: '500'         

                        }}> 

                          {entry.assessmentStage}   

                        </span>

                      ) : (

                        <span style={{  

                          fontSize: '11px',         

                          padding: '2px 6px',       

                          borderRadius: '4px',      

                          backgroundColor: '#f3f4f6',

                          color: '#9ca3af',         

                          fontWeight: '500'         

                        }}> 

                          {entry.isProcessing ? '⏳ Processing...' : '⏳ Assessment Pending'}

                        </span>

                      )}

                      
                      
                      {entry.evaluation && (

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                          <span style={{  

                            fontSize: '11px',         

                            padding: '2px 6px',       

                            borderRadius: '4px',      

                            backgroundColor: entry.evaluation.level === 'complete' ? '#f0fdf4' : 

                                             entry.evaluation.level === 'moderate' ? '#fef3c7' : 

                                             entry.evaluation.level === 'partial' ? '#fef2f2' : '#f3f4f6',

                            color: entry.evaluation.level === 'complete' ? '#16a34a' : 

                                   entry.evaluation.level === 'moderate' ? '#d97706' : 

                                   entry.evaluation.level === 'partial' ? '#dc2626' : '#6b7280',

                            fontWeight: '500'         

                          }}> 

                            {entry.evaluation.level === 'complete' ? '✅ Complete' : 

                             entry.evaluation.level === 'moderate' ? '📋 Moderate' : 

                             entry.evaluation.level === 'partial' ? '⚠️ Partial' : '❌ Incomplete'}

                          </span>

                          
                          
                          {/* Traffic Light */}

                          <div style={{     

                            width: '16px',  

                            height: '40px', 

                            backgroundColor: '#2c2c2c', 

                            borderRadius: '8px',       

                            padding: '2px', 

                            display: 'flex',

                            flexDirection: 'column',    

                            justifyContent: 'space-between',        

                            alignItems: 'center',       

                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)',    

                            border: '1px solid #1a1a1a' 

                          }}>   

                            {/* Red Light */}

                            <div style={{   

                              width: '8px',

                              height: '8px',           

                              borderRadius: '50%',      

                              backgroundColor: entry.evaluation.level === 'partial' ? '#ff4444' : '#4a4a4a',

                              border: '1px solid #333', 

                              boxShadow: entry.evaluation.level === 'partial' ? '0 0 4px #ff4444' : 'inset 0 1px 1px rgba(0,0,0,0.5)',       

                              transition: 'all 0.3s ease'           

                            }}></div>       

                            {/* Orange Light */}

                            <div style={{   

                              width: '8px',

                              height: '8px',           

                              borderRadius: '50%',      

                              backgroundColor: entry.evaluation.level === 'moderate' ? '#ff8800' : '#4a4a4a',           

                              border: '1px solid #333', 

                              boxShadow: entry.evaluation.level === 'moderate' ? '0 0 4px #ff8800' : 'inset 0 1px 1px rgba(0,0,0,0.5)',      

                              transition: 'all 0.3s ease'           

                            }}></div>       

                            {/* Green Light */}

                            <div style={{   

                              width: '8px',

                              height: '8px',           

                              borderRadius: '50%',      

                              backgroundColor: entry.evaluation.level === 'complete' ? '#44ff44' : '#4a4a4a',           

                              border: '1px solid #333', 

                              boxShadow: entry.evaluation.level === 'complete' ? '0 0 4px #44ff44' : 'inset 0 1px 1px rgba(0,0,0,0.5)',      

                              transition: 'all 0.3s ease'           

                            }}></div>       

                          </div>

                        </div>

                      )}

                    </div>

                  </div>

                  
                  
                  <div style={{ display: 'flex', gap: '12px' }}>

                    <div style={{ flex: '0 0 120px' }}>

                      <img 

                        src={entry.frameData} 

                        alt={`Frame ${index + 1}`}

                        style={{

                          width: '100%',

                          height: '80px',

                          objectFit: 'cover',

                          borderRadius: '6px',

                          border: '1px solid #e5e7eb'

                        }}

                      />

                    </div>

                    <div style={{ flex: '1', minWidth: '0' }}>

                      {entry.evaluation && (
                      <div style={{ marginBottom: '8px' }}>

                        <span style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280' }}>

                          {entry.evaluation.level === 'complete' ? '✅ Comprehensive analysis' : 

                           entry.evaluation.level === 'moderate' ? '📋 Good analysis' : 

                           entry.evaluation.level === 'partial' ? '⚠️ Basic analysis' : '❌ Limited analysis'}

                        </span>

                      </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                        <span style={{ fontSize: '10px', color: '#9ca3af' }}>

                          {entry.visionAnalysis ? entry.visionAnalysis.split(' ').length : 0} words

                        </span>

                        {entry.retryCount > 0 && (

                          <span style={{ 

                            fontSize: '9px', 

                            color: '#f59e0b',

                            backgroundColor: '#fef3c7',

                            padding: '2px 4px',

                            borderRadius: '3px',

                            fontWeight: '500'

                          }}>

                            {entry.retryCount} retry{entry.retryCount > 1 ? 'ies' : ''}

                          </span>

                        )}

                      </div>

                      <div style={{ 

                        fontSize: '12px', 

                        color: '#374151', 

                        lineHeight: '1.4',

                        marginTop: '6px',

                        maxHeight: '60px',

                        overflow: 'hidden',

                        textOverflow: 'ellipsis',

                        display: '-webkit-box',

                        WebkitLineClamp: 3,

                        WebkitBoxOrient: 'vertical'

                      }}>

                        {entry.visionAnalysis}

                      </div>

                    </div>

                  </div>

                </div>

              ))}

            </div>

          </div>

        )}
        
        

        {!videoFile && !isWebcamActive && (
          <div style={{ padding: '40px', textAlign: 'center' }}>

            <p>Choose how to load your video or start webcam analysis:</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>

              <button className="btn btn-primary" onClick={loadVideoFromFile}>

                Upload Your Own Video

              </button>

              <button className="btn btn-secondary" onClick={loadDefaultVideo}>

                Load Default Video (Pitting Edema)

              </button>

              {/* Video Source Selector */}
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#333' }}>
                  📹 Video Source:
                </label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <button
                    onClick={() => setVideoSource('local')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: videoSource === 'local' ? '#28a745' : '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    📷 Local Webcam
                  </button>
                  <button
                    onClick={() => setVideoSource('ngrok')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: videoSource === 'ngrok' ? '#28a745' : '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    🌐 Remote Stream
                  </button>
                </div>
                
                {videoSource === 'ngrok' && (
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#666' }}>
                      Stream URL:
                    </label>
                    <input
                      type="text"
                      value={ngrokUrl}
                      onChange={(e) => setNgrokUrl(e.target.value)}
                      placeholder="https://your-tunnel.ngrok.io/video_feed"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    />
                  </div>
                )}
              </div>

              <button className="btn btn-success" onClick={startWebcamAnalysis}>
                📹 Enable Webcam
              </button>
            </div>

          </div>

        )}

        
        {/* Webcam Display - Professional Monitor Layout */}
        {isWebcamActive && (
          <div style={{ 
            padding: '30px', 
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            
            {/* MAIN CONTENT AREA - DYNAMIC COLUMN LAYOUT */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: showLeftSidebar && showEnhancedView 
                ? 'minmax(250px, 1fr) 2fr minmax(250px, 1fr)' 
                : showLeftSidebar 
                  ? 'minmax(250px, 1fr) 2fr'
                  : showEnhancedView
                    ? '2fr minmax(250px, 1fr)'
                    : '1fr',
              gap: '0',
              backgroundColor: '#f0f4f8',
              border: '3px solid #0066cc',
              borderRadius: '16px',
              minHeight: '500px',
              overflow: 'hidden'
            }}>
              
              {/* LEFT SIDEBAR - Patient Info & Controls */}
              {showLeftSidebar && <div style={{
                padding: '20px',
                backgroundColor: '#ffffff',
                borderRight: '2px solid #e0e7ef',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                minWidth: 0,
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                {/* TRIAGE SCORE PLACEHOLDER */}
                {!triageScore && (
                  <div style={{
                    padding: '14px',
                    border: '2px dashed #ced4da',
                    borderRadius: '8px',
                    color: '#6c757d',
                    fontSize: '12px',
                    textAlign: 'center',
                    marginTop: '0px'
                  }}>
                    Triage assessment pending analysis
                  </div>
                )}

                {/* TRIAGE SCORE - Prominent Display */}
                {triageScore && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#ffffff',
                    border: triageScore <= 2 
                      ? '5px solid #dc3545'
                      : triageScore === 3
                      ? '5px solid #ffc107'
                      : '5px solid #28a745',
                    borderRadius: '8px',
                    color: '#000000',
                    marginTop: '0px',
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}>
                    <div style={{ 
                      fontSize: '10px', 
                      opacity: 0.7, 
                      marginBottom: '8px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Triage Score
                    </div>
                    
                    {/* Score and Reasoning side by side */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '12px', 
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      {/* Score */}
                      <div style={{ 
                        fontSize: '48px', 
                        fontWeight: '900',
                        minWidth: '60px',
                        textAlign: 'center',
                        color: triageScore <= 2 
                          ? '#dc3545'
                          : triageScore === 3
                          ? '#ffc107'
                          : '#28a745'
                      }}>
                        {triageScore}
                      </div>
                      
                      {/* Reasoning */}
                      {triageReasoning && (
                        <div style={{
                          flex: 1,
                          fontSize: '10px',
                          lineHeight: '1.4',
                          opacity: 0.7,
                          textAlign: 'left'
                        }}>
                          {triageReasoning}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '8px' }}>
                      {triageScore === 1 ? 'CRITICAL' :
                       triageScore === 2 ? 'URGENT' :
                       triageScore === 3 ? 'MODERATE' :
                       triageScore === 4 ? 'LOW PRIORITY' :
                       'ROUTINE'}
                    </div>
                    
                    {/* Confidence Distribution - Compact */}
                    {scoreConfidences && (
                      <div style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(0,0,0,0.2)',
                        fontSize: '9px'
                      }}>
                        <div style={{ marginBottom: '4px', fontWeight: '600', color: '#000000', opacity: 0.7 }}>
                          Confidence Distribution
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '4px',
                          fontSize: '8px'
                        }}>
                          {[1, 2, 3, 4, 5].map((score) => {
                            // Get confidence from the scoreConfidences object
                            const confidence = scoreConfidences[score] || 0;
                            
                            const isSelected = score === triageScore;
                            const height = Math.max(confidence, 5);
                            
                            // Get the triage color for this score
                            const triageColor = score <= 2 
                              ? '#dc3545'
                              : score === 3
                              ? '#ffc107'
                              : '#28a745';
                            
                            return (
                              <div key={score} style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '2px'
                              }}>
                                <div style={{
                                  width: '100%',
                                  height: '30px',
                                  backgroundColor: '#ffffff',
                                  borderRadius: '2px',
                                  display: 'flex',
                                  alignItems: 'flex-end',
                                  justifyContent: 'center',
                                  border: isSelected ? `3px solid ${triageColor}` : '1px solid rgba(0,0,0,0.2)'
                                }}>
                                  <div style={{
                                    width: '100%',
                                    height: `${height}%`,
                                    backgroundColor: triageColor,
                                    borderRadius: '2px 2px 0 0',
                                    transition: 'height 0.3s ease',
                                    opacity: isSelected ? 1 : 0.5
                                  }} />
                                </div>
                                <div style={{
                                  fontWeight: isSelected ? '700' : '500',
                                  color: '#000000',
                                  opacity: isSelected ? 1 : 0.7
                                }}>
                                  {score}
                                </div>
                                {confidence > 0 && (
                                  <div style={{
                                    fontSize: '7px',
                                    color: '#000000',
                                    opacity: 0.7,
                                    fontWeight: isSelected ? '600' : '400'
                                  }}>
                                    {confidence.toFixed(1)}%
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* RDR ADULT OBSERVATION CHART */}
                {rdrObservations.length > 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    color: '#212529',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    marginTop: '12px',
                    overflow: 'auto'
                  }}>
                    <div style={{ 
                      fontSize: '10px', 
                      opacity: 0.9, 
                      marginBottom: isRdrChartExpanded ? '10px' : '0',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      color: '#6c757d',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span 
                        onClick={() => setIsRdrChartExpanded(!isRdrChartExpanded)}
                        style={{ 
                          cursor: 'pointer', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          flex: 1,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <span>Adult Observation Chart</span>
                        <span style={{ 
                          fontSize: '9px',
                          opacity: 0.6,
                          transition: 'transform 0.2s ease',
                          transform: isRdrChartExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                        }}>▼</span>
                      </span>
                      {isRdrChartExpanded && (
                      <button
                        onClick={() => {
                          // Manual observation entry
                          console.log('[RDR] Manual Add Now clicked');
                          console.log('[RDR] Current vitals:', {
                            rr: webcamBreathingRate,
                            spo2: webcamSpo2?.value,
                            spo2_full: webcamSpo2,
                            hr: heartRate,
                            sbp: webcamBloodPressure?.systolic,
                            dbp: webcamBloodPressure?.diastolic,
                            llmVitals: llmVitals
                          });
                          
                          // Combine video vitals + LLM vitals
                          const vitals = {
                            // Video-derived vitals
                            rr: webcamBreathingRate,
                            spo2: webcamSpo2?.value,
                            hr: heartRate,
                            sbp: webcamBloodPressure?.systolic,
                            dbp: webcamBloodPressure?.diastolic,
                            
                            // LLM-derived vitals
                            gcs: llmVitals.gcs,
                            consciousness: llmVitals.consciousness,
                            painScore: llmVitals.painScore,
                            sedationScore: llmVitals.sedationScore,
                            temp: llmVitals.temperature,
                            bgl: llmVitals.bgl,
                            newPain: llmVitals.newPain
                          };
                          addRdrObservation(vitals);
                          console.log('[RDR] Observation added, current count:', rdrObservations.length + 1);
                        }}
                        style={{
                          padding: '4px 8px',
                          fontSize: '9px',
                          fontWeight: '600',
                          color: '#3b82f6',
                          backgroundColor: '#eff6ff',
                          border: '1px solid #3b82f6',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        + Add Now
                      </button>
                      )}
                    </div>
                    
                    {isRdrChartExpanded && (
                    <div>
                    {/* Observation Chart Table */}
                    <div ref={rdrTableRef} style={{ overflowX: 'auto', fontSize: '9px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f3f4f6' }}>
                            <th style={{ padding: '6px 4px', border: '1px solid #e5e7eb', fontWeight: '600', textAlign: 'left', width: '100px', position: 'sticky', left: 0, backgroundColor: '#f3f4f6', zIndex: 10 }}>Parameter</th>
                            {rdrObservations.slice(-6).map((obs, idx) => (
                              <th key={idx} style={{ padding: '6px 4px', border: '1px solid #e5e7eb', fontWeight: '600', textAlign: 'center', minWidth: '60px' }}>
                                <div>{obs.time}</div>
                                <div style={{ fontSize: '8px', opacity: 0.7 }}>{obs.date}</div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Respiratory Rate */}
                          <tr>
                            <td 
                              style={{ 
                                padding: '4px', 
                                border: '1px solid #e5e7eb', 
                                fontWeight: '600', 
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                position: 'sticky',
                                left: 0,
                                zIndex: 9
                              }}
                              onClick={() => setSelectedVitalChart({
                                parameter: 'rr',
                                label: 'Respiratory Rate',
                                unit: 'br/min',
                                color: '#3b82f6',
                                normalRange: { min: 12, max: 20 }
                              })}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            >
                              📈 RR (br/min)
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const rr = obs.rr;
                              let bgColor = '#ffffff';
                              if (rr) {
                                if (rr <= 8 || rr >= 25) bgColor = '#fecaca'; // Red zone
                                else if (rr <= 11 || rr >= 21) bgColor = '#fde68a'; // Yellow zone
                              }
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600' }}>
                                  {rr || '-'}
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* SpO2 */}
                          <tr>
                            <td 
                              style={{ 
                                padding: '4px', 
                                border: '1px solid #e5e7eb', 
                                fontWeight: '600', 
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                position: 'sticky',
                                left: 0,
                                zIndex: 9
                              }}
                              onClick={() => setSelectedVitalChart({
                                parameter: 'spo2',
                                label: 'Oxygen Saturation',
                                unit: '%',
                                color: '#10b981',
                                normalRange: { min: 95, max: 100 }
                              })}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            >
                              📈 SpO₂ (%)
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const spo2 = obs.spo2;
                              let bgColor = '#ffffff';
                              if (spo2) {
                                if (spo2 <= 91) bgColor = '#fecaca'; // Red zone
                                else if (spo2 <= 93) bgColor = '#fed7aa'; // Orange zone
                                else if (spo2 <= 95) bgColor = '#fde68a'; // Yellow zone
                              }
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600' }}>
                                  {spo2 || '-'}
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* Blood Pressure */}
                          <tr>
                            <td 
                              style={{ 
                                padding: '4px', 
                                border: '1px solid #e5e7eb', 
                                fontWeight: '600', 
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                position: 'sticky',
                                left: 0,
                                zIndex: 9
                              }}
                              onClick={() => setSelectedVitalChart({
                                parameter: 'sbp',
                                label: 'Systolic Blood Pressure',
                                unit: 'mmHg',
                                color: '#ef4444',
                                normalRange: { min: 110, max: 140 }
                              })}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            >
                              📈 BP (mmHg)
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const sbp = obs.sbp;
                              const dbp = obs.dbp;
                              let bgColor = '#ffffff';
                              if (sbp) {
                                if (sbp <= 90 || sbp >= 220) bgColor = '#fecaca'; // Red zone
                                else if (sbp <= 100) bgColor = '#fed7aa'; // Orange zone
                                else if (sbp <= 110) bgColor = '#fde68a'; // Yellow zone
                              }
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600' }}>
                                  {sbp && dbp ? `${sbp}/${dbp}` : '-'}
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* Heart Rate */}
                          <tr>
                            <td 
                              style={{ 
                                padding: '4px', 
                                border: '1px solid #e5e7eb', 
                                fontWeight: '600', 
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                position: 'sticky',
                                left: 0,
                                zIndex: 9
                              }}
                              onClick={() => setSelectedVitalChart({
                                parameter: 'hr',
                                label: 'Heart Rate',
                                unit: 'bpm',
                                color: '#f59e0b',
                                normalRange: { min: 60, max: 100 }
                              })}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            >
                              📈 HR (bpm)
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const hr = obs.hr;
                              let bgColor = '#ffffff';
                              if (hr) {
                                if (hr <= 40 || hr >= 131) bgColor = '#fecaca'; // Red zone
                                else if (hr <= 50 || hr >= 111) bgColor = '#fde68a'; // Yellow zone
                              }
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600' }}>
                                  {hr || '-'}
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* Temperature */}
                          <tr>
                            <td 
                              style={{ 
                                padding: '4px', 
                                border: '1px solid #e5e7eb', 
                                fontWeight: '600', 
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                position: 'sticky',
                                left: 0,
                                zIndex: 9
                              }}
                              onClick={() => setSelectedVitalChart({
                                parameter: 'temp',
                                label: 'Temperature',
                                unit: '°C',
                                color: '#8b5cf6',
                                normalRange: { min: 36.5, max: 37.5 }
                              })}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            >
                              📈 Temp (°C)
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const temp = obs.temp;
                              let bgColor = '#ffffff';
                              if (temp) {
                                if (temp <= 35.0 || temp >= 39.1) bgColor = '#fecaca'; // Red zone
                                else if (temp <= 36.0 || temp >= 38.1) bgColor = '#fde68a'; // Yellow zone
                              }
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600' }}>
                                  {temp ? temp.toFixed(1) : '-'}
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* Consciousness */}
                          <tr>
                            <td style={{ padding: '4px', border: '1px solid #e5e7eb', fontWeight: '600', backgroundColor: '#f9fafb', position: 'sticky', left: 0, zIndex: 9 }}>
                              AVPU
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const consciousness = obs.consciousness;
                              const bgColor = consciousness !== 'Alert' ? '#fecaca' : '#ffffff';
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600', fontSize: '8px' }}>
                                  {consciousness ? consciousness.charAt(0) : 'A'}
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* Glasgow Coma Scale */}
                          <tr>
                            <td 
                              style={{ 
                                padding: '4px', 
                                border: '1px solid #e5e7eb', 
                                fontWeight: '600', 
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                position: 'sticky',
                                left: 0,
                                zIndex: 9
                              }}
                              onClick={() => setSelectedVitalChart({
                                parameter: 'gcs',
                                label: 'Glasgow Coma Scale',
                                unit: '',
                                color: '#ec4899',
                                normalRange: { min: 15, max: 15 }
                              })}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            >
                              📈 GCS (3-15)
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const gcs = obs.gcs;
                              let bgColor = '#ffffff';
                              if (gcs) {
                                if (gcs <= 8) bgColor = '#fecaca'; // Severe (Coma)
                                else if (gcs <= 12) bgColor = '#fed7aa'; // Moderate
                                else if (gcs <= 14) bgColor = '#fde68a'; // Mild
                              }
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600' }}>
                                  {gcs || '-'}
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* Sedation Score */}
                          <tr>
                            <td 
                              style={{ 
                                padding: '4px', 
                                border: '1px solid #e5e7eb', 
                                fontWeight: '600', 
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                position: 'sticky',
                                left: 0,
                                zIndex: 9
                              }}
                              onClick={() => setSelectedVitalChart({
                                parameter: 'sedationScore',
                                label: 'Sedation Score',
                                unit: '',
                                color: '#06b6d4',
                                normalRange: { min: 0, max: 1 }
                              })}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            >
                              📈 Sedation (0-3)
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const sedation = obs.sedationScore;
                              const bgColor = sedation >= 2 ? '#fde68a' : '#ffffff';
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600' }}>
                                  {sedation !== null && sedation !== undefined ? sedation : '-'}
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* Pain Score */}
                          <tr>
                            <td 
                              style={{ 
                                padding: '4px', 
                                border: '1px solid #e5e7eb', 
                                fontWeight: '600', 
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                position: 'sticky',
                                left: 0,
                                zIndex: 9
                              }}
                              onClick={() => setSelectedVitalChart({
                                parameter: 'painScore',
                                label: 'Pain Score',
                                unit: '',
                                color: '#f97316',
                                normalRange: { min: 0, max: 3 }
                              })}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            >
                              📈 Pain (0-10)
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const pain = obs.painScore;
                              let bgColor = '#ffffff';
                              if (pain >= 7) bgColor = '#fecaca'; // Severe pain
                              else if (pain >= 4) bgColor = '#fde68a'; // Moderate pain
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600' }}>
                                  {pain !== null && pain !== undefined ? pain : '-'}
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* New/Unexpected Pain */}
                          <tr>
                            <td style={{ padding: '4px', border: '1px solid #e5e7eb', fontWeight: '600', backgroundColor: '#f9fafb', position: 'sticky', left: 0, zIndex: 9 }}>
                              ⚠️ New Pain
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const newPain = obs.newPain;
                              const bgColor = newPain === 'Y' ? '#fecaca' : '#ffffff';
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600', fontSize: '10px' }}>
                                  {newPain || 'N'}
                                </td>
                              );
                            })}
                          </tr>

                          {/* Blood Glucose Level */}
                          <tr>
                            <td 
                              style={{ 
                                padding: '4px', 
                                border: '1px solid #e5e7eb', 
                                fontWeight: '600', 
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                position: 'sticky',
                                left: 0,
                                zIndex: 9
                              }}
                              onClick={() => setSelectedVitalChart({
                                parameter: 'bgl',
                                label: 'Blood Glucose Level',
                                unit: 'mmol/L',
                                color: '#84cc16',
                                normalRange: { min: 4.0, max: 7.8 }
                              })}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            >
                              📈 BGL (mmol/L)
                            </td>
                            {rdrObservations.slice(-6).map((obs, idx) => {
                              const bgl = obs.bgl;
                              let bgColor = '#ffffff';
                              if (bgl) {
                                if (bgl < 4.0 || bgl > 15.0) bgColor = '#fecaca'; // Critical
                                else if (bgl < 5.0 || bgl > 10.0) bgColor = '#fde68a'; // Caution
                              }
                              return (
                                <td key={idx} style={{ padding: '4px', border: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: bgColor, fontWeight: '600' }}>
                                  {bgl ? bgl.toFixed(1) : '-'}
                                </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Legend */}
                    <div style={{ marginTop: '8px', fontSize: '8px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '12px', height: '12px', backgroundColor: '#fecaca', border: '1px solid #e5e7eb' }}></div>
                        <span>Critical</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '12px', height: '12px', backgroundColor: '#fed7aa', border: '1px solid #e5e7eb' }}></div>
                        <span>High Risk</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '12px', height: '12px', backgroundColor: '#fde68a', border: '1px solid #e5e7eb' }}></div>
                        <span>Caution</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '12px', height: '12px', backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}></div>
                        <span>Normal</span>
                      </div>
                    </div>
                  </div>
                  )}
                  </div>
                )}

                {/* ABCDE QUICK VIEW PLACEHOLDER */}
                {(!abcdeFindings || Object.keys(abcdeFindings).length === 0) && (
                  <div style={{
                    padding: '14px',
                    border: '2px dashed #ced4da',
                    borderRadius: '8px',
                    color: '#6c757d',
                    fontSize: '12px',
                    textAlign: 'center',
                    marginTop: '0px'
                  }}>
                    ABCDE assessment pending analysis
                  </div>
                )}

                {/* ABCDE QUICK SUMMARY - Simplified */}
                {abcdeFindings && Object.keys(abcdeFindings).length > 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    color: '#212529',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    marginTop: '0px'
                  }}>
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: isAbcdeExpanded ? '10px' : '0'
                    }}>
                      <div 
                        onClick={() => setIsAbcdeExpanded(!isAbcdeExpanded)}
                        style={{ 
                          fontSize: '10px', 
                          opacity: 0.9, 
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          color: '#6c757d',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flex: 1,
                          transition: 'all 0.2s ease'
                        }}>
                        <span>ABCDE Quick View</span>
                        <span style={{ 
                          fontSize: '9px',
                          opacity: 0.6,
                          transition: 'transform 0.2s ease',
                          transform: isAbcdeExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                        }}>▼</span>
                      </div>
                      {isAbcdeExpanded && (
                      <div>
                      {(() => {
                        const values = Object.values(abcdeFindings);
                        const total = values.reduce((sum, val) => {
                          const percentage = Number(val.completeness_percentage) || 0;
                          return sum + percentage;
                        }, 0);
                        const avgCompleteness = Math.round(total / values.length);
                        return (
                          <div style={{
                            fontSize: '8px',
                            fontWeight: '600',
                            color: avgCompleteness >= 80 ? '#28a745' : avgCompleteness >= 60 ? '#ffc107' : '#dc3545',
                            backgroundColor: avgCompleteness >= 80 ? 'rgba(40, 167, 69, 0.1)' : avgCompleteness >= 60 ? 'rgba(255, 193, 7, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>
                            {avgCompleteness}% Complete
                          </div>
                        );
                      })()}
                      </div>
                      )}
                    </div>
                    
                    {isAbcdeExpanded && (
                    <div>
                    {Object.entries(abcdeFindings).map(([key, value]) => {
                      const completeness = Number(value.completeness_percentage) || 0;
                      const showTooltip = hoveredAbcdeElement === key;
                      
                      return (
                        <div key={key} style={{ marginBottom: '8px', position: 'relative' }}>
                          <div 
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '3px',
                              fontSize: '11px',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={() => setHoveredAbcdeElement(key)}
                            onMouseLeave={() => setHoveredAbcdeElement(null)}
                          >
                            <span style={{ 
                              color: '#212529', 
                              fontWeight: '700',
                              textTransform: 'uppercase',
                              minWidth: '20px'
                            }}>
                              {key}:
                            </span>
                            <span style={{ 
                              flex: 1,
                              marginLeft: '8px',
                              fontSize: '9px',
                              color: '#495057',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {value.status || 'Not assessed'}
                            </span>
                            <span style={{ 
                              fontSize: '7px',
                              marginLeft: '6px',
                              fontWeight: '600',
                              color: '#6c757d',
                              minWidth: '28px',
                              textAlign: 'right'
                            }}>
                              {completeness}%
                            </span>
                          </div>
                          
                          {/* Tooltip on Hover */}
                          {showTooltip && (value.status || value.missing_information || value.questions || value.outstanding_questions) && (
                            <div style={{
                              position: 'absolute',
                              top: '-8px',
                              left: '0',
                              right: '0',
                              backgroundColor: '#2c3e50',
                              color: '#ffffff',
                              padding: '10px',
                              borderRadius: '6px',
                              fontSize: '10px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                              zIndex: 1000,
                              transform: 'translateY(-100%)',
                              pointerEvents: 'none',
                              lineHeight: '1.4'
                            }}>
                              <div style={{ fontWeight: '700', marginBottom: '6px', fontSize: '11px' }}>
                                {key.toUpperCase()} - {completeness}% Complete
                              </div>
                              {value.status && (
                                <div style={{ marginBottom: '6px' }}>
                                  <strong>Status:</strong> {value.status}
                                </div>
                              )}
                              {value.missing_information && value.missing_information.length > 0 && (
                                <div style={{ marginBottom: '6px' }}>
                                  <strong>Missing:</strong>
                                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                                    {value.missing_information.map((item, idx) => (
                                      <li key={idx} style={{ marginBottom: '2px' }}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {value.questions && value.questions.length > 0 && (
                                <div style={{ marginBottom: '6px' }}>
                                  <strong>Questions:</strong>
                                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                                    {value.questions.map((q, idx) => (
                                      <li key={idx} style={{ marginBottom: '2px' }}>{q}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {value.outstanding_questions && value.outstanding_questions.length > 0 && (
                                <div>
                                  <strong>Outstanding Questions:</strong>
                                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                                    {value.outstanding_questions.map((q, idx) => (
                                      <li key={idx} style={{ marginBottom: '2px' }}>{q}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {/* Arrow pointing down */}
                              <div style={{
                                position: 'absolute',
                                bottom: '-6px',
                                left: '20px',
                                width: '0',
                                height: '0',
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderTop: '6px solid #2c3e50'
                              }} />
                            </div>
                          )}
                          
                          {/* Mini Progress Bar */}
                          <div style={{
                            width: '100%',
                            height: '3px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '2px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${completeness}%`,
                              height: '100%',
                              backgroundColor: completeness >= 80 ? '#28a745' : completeness >= 60 ? '#ffc107' : '#dc3545',
                              transition: 'width 0.3s ease',
                              borderRadius: '2px'
                            }} />
                          </div>
                        </div>
                      );
                    })}
                    </div>
                    )}
                  </div>
                )}

                {/* Session Info removed per request */}

                {/* VITAL SIGNS - Compact Display - Always visible */}
                <div style={{
                    padding: '12px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    color: '#212529',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    marginTop: '15px'
                  }}>
                    <div 
                      onClick={() => setIsVitalSignsExpanded(!isVitalSignsExpanded)}
                      style={{ 
                        fontSize: '10px', 
                        opacity: 0.9, 
                        marginBottom: isVitalSignsExpanded ? '10px' : '0',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        color: '#6c757d',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s ease'
                      }}>
                      <span>Key Vital Signs</span>
                      <span style={{ 
                        fontSize: '9px', 
                        opacity: 0.6,
                        transform: isVitalSignsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s ease'
                      }}>▼</span>
                    </div>
                    
                    {isVitalSignsExpanded && (
                    <div>
                    {/* Heart Rate - Always visible */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '8px',
                      fontSize: '11px',
                      alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#6c757d', fontWeight: '600' }}>❤️ Heart Rate:</span>
                        <button
                          onClick={async () => {
                            const newSetting = !overlaySettings.hr_quadrants;
                            const newSettings = { ...overlaySettings, hr_quadrants: newSetting };
                            setOverlaySettings(newSettings);
                            try {
                              if (webcamWsRef.current && webcamWsRef.current.readyState === WebSocket.OPEN) {
                                webcamWsRef.current.send(JSON.stringify({ type: 'overlay', ...newSettings }));
                              } else {
                                await axios.post('/api/overlay-settings', newSettings);
                              }
                            } catch (e) {
                              console.error('Failed to update HR quadrants:', e);
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '2px',
                            opacity: overlaySettings.hr_quadrants ? 1 : 0.4,
                            transition: 'opacity 0.2s ease'
                          }}
                          title={overlaySettings.hr_quadrants ? 'Hide HR quadrants' : 'Show HR quadrants'}
                        >
                          👁️
                        </button>
                      </div>
                      <span style={{ fontWeight: '700', color: (vitalOverrides.heartRate || heartRate) ? '#212529' : '#9ca3af' }}>
                        {vitalOverrides.heartRate ? (
                          <span style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '3px' }}>
                            {vitalOverrides.heartRate} bpm
                          </span>
                        ) : heartRate ? `${heartRate} bpm` : '-'}
                      </span>
                    </div>
                    
                    {/* Respiratory Rate - Always visible */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '8px',
                      fontSize: '11px',
                      alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#6c757d', fontWeight: '600' }}>🫁 Resp Rate:</span>
                        <button
                          onClick={async () => {
                            const newSetting = !overlaySettings.br_quadrants;
                            const newSettings = { ...overlaySettings, br_quadrants: newSetting };
                            setOverlaySettings(newSettings);
                            try {
                              if (webcamWsRef.current && webcamWsRef.current.readyState === WebSocket.OPEN) {
                                webcamWsRef.current.send(JSON.stringify({ type: 'overlay', ...newSettings }));
                              } else {
                                await axios.post('/api/overlay-settings', newSettings);
                              }
                            } catch (e) {
                              console.error('Failed to update BR quadrants:', e);
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '2px',
                            opacity: overlaySettings.br_quadrants ? 1 : 0.4,
                            transition: 'opacity 0.2s ease'
                          }}
                          title={overlaySettings.br_quadrants ? 'Hide BR quadrants' : 'Show BR quadrants'}
                        >
                          👁️
                        </button>
                      </div>
                      <span style={{ fontWeight: '700', color: (vitalOverrides.breathingRate || webcamBreathingRate) ? '#212529' : '#9ca3af' }}>
                        {vitalOverrides.breathingRate ? (
                          <span style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '3px' }}>
                            {vitalOverrides.breathingRate} br/min
                          </span>
                        ) : webcamBreathingRate ? `${webcamBreathingRate} br/min` : '-'}
                      </span>
                    </div>
                    {/* BMI - Only show if available */}
                    {(vitalOverrides.bmi || (webcamBmi && webcamBmi > 0)) && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        marginBottom: '8px',
                        fontSize: '11px'
                      }}>
                        <span style={{ color: '#6c757d', fontWeight: '600' }}>📊 BMI:</span>
                        <span style={{ fontWeight: '700', color: '#212529' }}>
                          {vitalOverrides.bmi ? (
                            <span style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '3px' }}>
                              {vitalOverrides.bmi}
                            </span>
                          ) : webcamBmi}
                        </span>
                      </div>
                    )}
                    
                    {/* Blood Pressure - Always visible */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '8px',
                      fontSize: '11px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#6c757d', fontWeight: '600' }}>🩺 Blood Pressure:</span>
                        {webcamBloodPressure && webcamBloodPressure.systolic && !webcamBloodPressure.calibrated && (
                          <span style={{ 
                            fontSize: '9px', 
                            color: '#f59e0b', 
                            fontWeight: '500',
                            backgroundColor: '#fef3c7',
                            padding: '1px 4px',
                            borderRadius: '3px'
                          }}>
                            UNCAL
                          </span>
                        )}
                      </div>
                      <span style={{ 
                        fontWeight: '700', 
                        color: (vitalOverrides.bloodPressureSystolic || (webcamBloodPressure && webcamBloodPressure.systolic)) ? '#212529' : '#9ca3af'
                      }}>
                        {vitalOverrides.bloodPressureSystolic && vitalOverrides.bloodPressureDiastolic ? (
                          <span style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '3px' }}>
                            {vitalOverrides.bloodPressureSystolic}/{vitalOverrides.bloodPressureDiastolic} mmHg
                          </span>
                        ) : (webcamBloodPressure && webcamBloodPressure.systolic && webcamBloodPressure.diastolic) 
                          ? `${webcamBloodPressure.systolic}/${webcamBloodPressure.diastolic} mmHg`
                          : '-'}
                      </span>
                    </div>
                    {webcamBloodPressure && webcamBloodPressure.systolic && !webcamBloodPressure.calibrated && (
                      <button
                        onClick={async () => {
                          // Prompt user for their actual BP reading
                          const systolic = prompt('Enter your systolic BP (top number) from cuff:', '120');
                          const diastolic = prompt('Enter your diastolic BP (bottom number) from cuff:', '80');
                          
                          if (systolic && diastolic) {
                            try {
                              const response = await axios.post('/api/calibrate-bp', {
                                systolic: parseInt(systolic),
                                diastolic: parseInt(diastolic)
                              });
                              if (response.data.success) {
                                console.log(`BP calibration successful: ${systolic}/${diastolic} mmHg`);
                                // The calibrated flag will be updated in the next BP reading
                              }
                            } catch (error) {
                              console.error('BP calibration failed:', error);
                              alert('Failed to calibrate BP. Make sure both face and hand are visible.');
                            }
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          color: '#ffffff',
                          backgroundColor: '#3b82f6',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginTop: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        📍 Calibrate with Cuff
                      </button>
                    )}

                    {/* SpO2 - Always visible */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '8px',
                      fontSize: '11px',
                      marginTop: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#6c757d', fontWeight: '600' }}>🫁 SpO₂:</span>
                        {webcamSpo2 && webcamSpo2.value && !webcamSpo2.calibrated && (
                          <span style={{ 
                            fontSize: '9px', 
                            color: '#f59e0b', 
                            fontWeight: '500',
                            backgroundColor: '#fef3c7',
                            padding: '1px 4px',
                            borderRadius: '3px'
                          }}>
                            UNCAL
                          </span>
                        )}
                      </div>
                      <span style={{ 
                        fontWeight: '700', 
                        color: (vitalOverrides.spo2 || (webcamSpo2 && webcamSpo2.value))
                          ? (vitalOverrides.spo2 
                              ? (vitalOverrides.spo2 < 90 ? '#ef4444' : (vitalOverrides.spo2 < 95 ? '#f59e0b' : '#10b981'))
                              : (webcamSpo2.value < 90 ? '#ef4444' : (webcamSpo2.value < 95 ? '#f59e0b' : '#10b981')))
                          : '#9ca3af'
                      }}>
                        {vitalOverrides.spo2 ? (
                          <span style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '3px' }}>
                            {vitalOverrides.spo2}%
                          </span>
                        ) : webcamSpo2 && webcamSpo2.value ? `${webcamSpo2.value}%` : '-'}
                      </span>
                    </div>
                    {webcamSpo2 && webcamSpo2.value && !webcamSpo2.calibrated && (
                      <button
                        onClick={async () => {
                          // Prompt user for reference SpO2
                          const refSpo2 = prompt('Enter reference SpO2 from pulse oximeter (%):', '98');
                          if (refSpo2) {
                            try {
                              const response = await axios.post('/api/calibrate-spo2', {
                                spo2: parseInt(refSpo2)
                              });
                              if (response.data.success) {
                                console.log('SpO2 calibration successful');
                              }
                            } catch (error) {
                              console.error('SpO2 calibration failed:', error);
                              alert('Failed to calibrate SpO2. Make sure face is visible.');
                            }
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          color: '#ffffff',
                          backgroundColor: '#10b981',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginTop: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                      >
                        📍 Calibrate with Pulse Ox
                      </button>
                    )}
                    </div>
                    )}
                </div>

                {/* Emotion Detection Display */}
                {emotionData.dominant && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    color: '#212529',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    marginTop: '15px'
                  }}>
                    <div 
                      onClick={() => setIsEmotionExpanded(!isEmotionExpanded)}
                      style={{ 
                        fontSize: '12px', 
                        opacity: 0.9, 
                        marginBottom: isEmotionExpanded ? '8px' : '0',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        color: '#6c757d',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s ease'
                      }}>
                      <span>Emotion</span>
                      <span style={{ 
                        fontSize: '9px', 
                        opacity: 0.6,
                        transform: isEmotionExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s ease'
                      }}>▼</span>
                    </div>
                    
                    {isEmotionExpanded && (
                    <div>
                    <div style={{ fontSize: '28px', fontWeight: '900', marginBottom: '8px', textTransform: 'capitalize' }}>
                      {emotionData.dominant}
                    </div>
                    <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '8px', color: '#6c757d' }}>
                      {emotionData.confidence[emotionData.dominant]?.toFixed(1)}% confident
                    </div>
                    
                    {/* Age and Gender */}
                    {(emotionData.age || emotionData.gender || (emotionData.race && (emotionData.race.dominant || Object.keys(emotionData.race.confidence || {}).length))) && (
                      <div style={{
                        borderTop: '1px solid #e9ecef',
                        paddingTop: '8px',
                        marginTop: '8px',
                        fontSize: '11px',
                        opacity: 0.9,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}>
                        {emotionData.age && (
                          <div>
                            <strong>Age:</strong> {Math.round(emotionData.age)}
                            {emotionData.age_range && (
                              <span style={{ fontSize: '9px', marginLeft: '4px', opacity: 0.7 }}>
                                ({emotionData.age_range})
                              </span>
                            )}
                          </div>
                        )}
                        {emotionData.gender && (
                          <div>
                            <strong>Gender:</strong> {emotionData.gender}
                            {emotionData.gender_confidence && emotionData.gender_confidence[emotionData.gender] && (
                              <span style={{ fontSize: '9px', marginLeft: '4px', opacity: 0.7 }}>
                                ({emotionData.gender_confidence[emotionData.gender].toFixed(1)}%)
                              </span>
                            )}
                          </div>
                        )}
                        {emotionData.race && emotionData.race.dominant && (
                          <div>
                            <strong>Race:</strong> {emotionData.race.dominant}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Top 3 Emotions */}
                    {emotionData.confidence && Object.keys(emotionData.confidence).length > 0 && (
                      <div style={{
                        borderTop: '1px solid #e9ecef',
                        paddingTop: '8px',
                        marginTop: '8px',
                        fontSize: '10px',
                        opacity: 0.85,
                        color: '#495057'
                      }}>
                        {Object.entries(emotionData.confidence)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 3)
                          .map(([emotion, conf]) => (
                            <div key={emotion} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '3px',
                              textTransform: 'capitalize'
                            }}>
                              <span>{emotion}:</span>
                              <span style={{ fontWeight: '700' }}>{conf.toFixed(1)}%</span>
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Race confidence (Top 2) */}
                    {emotionData.race && emotionData.race.confidence && Object.keys(emotionData.race.confidence).length > 0 && (
                      <div style={{
                        borderTop: '1px solid #e9ecef',
                        paddingTop: '8px',
                        marginTop: '8px',
                        fontSize: '10px',
                        opacity: 0.85,
                        color: '#495057'
                      }}>
                        {Object.entries(emotionData.race.confidence)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 2)
                          .map(([race, conf]) => (
                            <div key={race} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '3px',
                              textTransform: 'capitalize'
                            }}>
                              <span>{race}:</span>
                              <span style={{ fontWeight: '700' }}>{conf.toFixed(1)}%</span>
                            </div>
                          ))}
                      </div>
                    )}
                    </div>
                    )}
                  </div>
                )}

                {/* Event Timeline - Collapsible Card */}
                {timelineEvents.length > 0 && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    color: '#212529',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    marginTop: '15px'
                  }}>
                    <div 
                      onClick={() => setIsEventTimelineExpanded(!isEventTimelineExpanded)}
                      style={{ 
                        fontSize: '12px', 
                        opacity: 0.9, 
                        marginBottom: isEventTimelineExpanded ? '12px' : '0',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        color: '#6c757d',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s ease'
                      }}>
                      <span>Event Timeline</span>
                      <span style={{ 
                        fontSize: '9px', 
                        opacity: 0.6,
                        transform: isEventTimelineExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s ease'
                      }}>▼</span>
                    </div>
                    
                    {isEventTimelineExpanded && (
                    <div style={{
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                      {/* Past Events - Reversed order (newest first) */}
                      <div style={{ position: 'relative', paddingLeft: '15px' }}>
                        {/* Vertical line for past events */}
                        <div style={{
                          position: 'absolute',
                          left: '6px',
                          top: '0',
                          bottom: '0',
                          width: '2px',
                          backgroundColor: '#28a745'
                        }} />

                        {[...timelineEvents].reverse().map((event, index) => (
                          <div key={index} style={{
                            marginBottom: '12px',
                            position: 'relative'
                          }}>
                            {/* Event dot */}
                            <div style={{
                              position: 'absolute',
                              left: '-9px',
                              top: '2px',
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: '#28a745',
                              border: '2px solid white',
                              boxShadow: '0 0 0 2px #28a745'
                            }} />
                            
                            <div style={{
                              fontSize: '9px',
                              color: '#6c757d',
                              marginBottom: '2px',
                              fontWeight: '600',
                              textAlign: 'left',
                              paddingLeft: '8px'
                            }}>
                              {event.time}
                            </div>
                            <div style={{
                              fontSize: '11px',
                              color: '#212529',
                              fontWeight: '600',
                              marginBottom: '2px',
                              textAlign: 'left',
                              paddingLeft: '8px'
                            }}>
                              {event.title}
                            </div>
                            {event.description && (
                              <div style={{
                                fontSize: '10px',
                                color: '#6c757d',
                                lineHeight: '1.3',
                                textAlign: 'left',
                                paddingLeft: '8px'
                              }}>
                                {event.description}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Current Moment Marker */}
                      <div style={{
                        marginTop: '15px',
                        paddingLeft: '15px',
                        position: 'relative'
                      }}>
                        <div style={{
                          position: 'absolute',
                          left: '0',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '14px',
                          height: '14px',
                          borderRadius: '50%',
                          backgroundColor: '#ffc107',
                          border: '3px solid white',
                          boxShadow: '0 0 0 2px #ffc107, 0 0 12px rgba(255, 193, 7, 0.6)',
                          animation: 'pulse 2s ease-in-out infinite'
                        }} />
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '800',
                          color: '#ffc107',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          textAlign: 'left'
                        }}>
                          NOW
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                )}

              </div>}

              {/* CENTER - Webcam Feed */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#ffffff',
                position: 'relative'
              }}>
                {/* Assessment Stage Timeline - Always visible */}
                <div style={{
                  padding: '20px',
                  borderBottom: '2px solid #e0e7ef',
                  backgroundColor: 'transparent'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    maxWidth: '900px',
                    margin: '0 auto',
                    position: 'relative'
                  }}>
                    {/* Timeline connecting line */}
                    <div style={{
                      position: 'absolute',
                      top: '20px',
                      left: '6%',
                      right: '6%',
                      height: '3px',
                      backgroundColor: '#dee2e6',
                      zIndex: 0
                    }} />
                    
                    {/* Timeline stages */}
                    {['Primary Survey', 'Secondary Assessment', 'Definitive Care', 'Ongoing Monitoring & Reassessment', 'Transfer/Handover'].map((stage, index) => {
                      // Default to Primary Survey if no stage set yet
                      const currentStage = assessmentStage || 'Primary Survey';
                      const isActive = currentStage === stage;
                      const stageOrder = ['Primary Survey', 'Secondary Assessment', 'Definitive Care', 'Ongoing Monitoring & Reassessment', 'Transfer/Handover'];
                      const isPassed = stageOrder.indexOf(currentStage) > index;
                      
                      // Short labels for display
                      const shortLabels = ['Primary', 'Secondary', 'Treatment', 'Monitoring', 'Handover'];
                      
                      // Stage progression requirements
                      const stageRequirements = {
                        'Primary Survey': {
                          gate: 'Entry Gate',
                          required: ['ABCDE assessment initiated', 'Vital signs detected', 'Immediate life threats identified'],
                          threshold: 'Start assessment',
                          color: '#dc3545'
                        },
                        'Secondary Assessment': {
                          gate: 'Progression Gate 1',
                          required: ['ABCDE ≥75% complete', 'Initial triage score', 'No immediate life threats'],
                          threshold: '75% ABCDE completeness',
                          color: '#fd7e14'
                        },
                        'Definitive Care': {
                          gate: 'Progression Gate 2',
                          required: ['Full assessment complete', 'Diagnosis identified', 'Treatment plan documented'],
                          threshold: '100% ABCDE + Diagnosis',
                          color: '#ffc107'
                        },
                        'Ongoing Monitoring & Reassessment': {
                          gate: 'Progression Gate 3',
                          required: ['Treatment initiated', 'Patient stable', 'Response to interventions documented'],
                          threshold: 'Treatment started',
                          color: '#28a745'
                        },
                        'Transfer/Handover': {
                          gate: 'Exit Gate',
                          required: ['All assessments complete', 'Patient ready for transfer', 'Handover documentation prepared'],
                          threshold: 'Ready for handover',
                          color: '#17a2b8'
                        }
                      };
                      
                      const requirement = stageRequirements[stage];
                      
                      // Calculate if gate is open (for current stage)
                      const overallComplete = abcdeFindings?.overall_completeness_percentage || 0;
                      const hasData = abcdeFindings !== null;
                      const isGateOpen = isActive && overallComplete >= 75;
                      
                      return (
                        <div key={stage} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          flex: 1,
                          position: 'relative',
                          zIndex: 1
                        }}
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.querySelector('.stage-tooltip');
                          if (tooltip) tooltip.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          const tooltip = e.currentTarget.querySelector('.stage-tooltip');
                          if (tooltip) tooltip.style.opacity = '0';
                        }}
                        >
                          {/* Hover Tooltip - Progression Requirements */}
                          <div className="stage-tooltip" style={{
                            position: 'absolute',
                            top: '60px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'white',
                            color: '#333',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            lineHeight: '1.5',
                            width: '220px',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                            opacity: '0',
                            pointerEvents: 'none',
                            transition: 'opacity 0.2s ease',
                            zIndex: 1000,
                            border: `2px solid ${requirement.color}`
                          }}>
                            {/* Arrow pointer - pointing up */}
                            <div style={{
                              position: 'absolute',
                              top: '-8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '0',
                              height: '0',
                              borderLeft: '8px solid transparent',
                              borderRight: '8px solid transparent',
                              borderBottom: `8px solid ${requirement.color}`
                            }}></div>
                            
                            <div style={{
                              fontWeight: '700',
                              fontSize: '11px',
                              marginBottom: '6px',
                              color: requirement.color,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {requirement.gate}
                            </div>
                            <div style={{
                              fontSize: '10px',
                              fontWeight: '600',
                              marginBottom: '6px',
                              color: '#856404'
                            }}>
                              Threshold: {requirement.threshold}
                            </div>
                            <div style={{ marginBottom: '4px', fontSize: '9px', opacity: 0.7, color: '#6c757d' }}>Requirements:</div>
                            {requirement.required.map((req, idx) => (
                              <div key={idx} style={{
                                fontSize: '9px',
                                marginLeft: '8px',
                                marginBottom: '3px',
                                color: '#495057'
                              }}>
                                • {req}
                              </div>
                            ))}
                            {isActive && (
                              <div style={{
                                marginTop: '8px',
                                paddingTop: '6px',
                                borderTop: '1px solid #dee2e6',
                                fontSize: '9px',
                                color: isGateOpen ? '#28a745' : hasData ? '#fd7e14' : '#6c757d',
                                fontWeight: '600'
                              }}>
                                {isGateOpen 
                                  ? 'Gate Open - Ready to Progress' 
                                  : hasData 
                                    ? `${overallComplete.toFixed(0)}% Complete` 
                                    : 'Awaiting assessment data'}
                              </div>
                            )}
                          </div>

                          {/* Stage circle */}
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            backgroundColor: '#ffffff',
                            border: isActive ? '3px solid #17a2b8' : isPassed ? '3px solid #28a745' : '2px solid #ced4da',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '900',
                            fontSize: '12px',
                            color: isActive ? '#17a2b8' : isPassed ? '#28a745' : '#6c757d',
                            boxShadow: isActive ? '0 3px 8px rgba(23, 162, 184, 0.5)' : 
                                       isPassed ? '0 2px 6px rgba(40, 167, 69, 0.3)' : 'none',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            animation: isActive ? 'pulse 2s ease-in-out infinite' : 'none',
                            cursor: 'help'
                          }}>
                            {isPassed ? '✓' : index + 1}
                            {isActive && (
                              <div style={{
                                position: 'absolute',
                                top: '-2px',
                                right: '-2px',
                                width: '8px',
                                height: '8px',
                                backgroundColor: '#ffc107',
                                borderRadius: '50%',
                                border: '1px solid white',
                                animation: 'pulse 1.5s ease-in-out infinite'
                              }} />
                            )}
                          </div>
                          
                          {/* Stage label */}
                          <div style={{
                            marginTop: '8px',
                            fontSize: '9px',
                            fontWeight: isActive ? '700' : '600',
                            color: isActive ? '#17a2b8' : isPassed ? '#28a745' : '#6c757d',
                            textAlign: 'center',
                            maxWidth: '70px',
                            lineHeight: '1.3'
                          }}>
                            {shortLabels[index]}
                          </div>
                        </div>
                      );
                    })}

                    {/* Generate Handover Docs Button */}
                    <button
                      onClick={generateHandoverDoc}
                      style={{
                        marginLeft: '12px',
                        padding: '6px 12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        color: '#ffffff',
                        backgroundColor: '#17a2b8',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                        alignSelf: 'center',
                        zIndex: 10,
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#138496';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 3px 6px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#17a2b8';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      Generate Handover Docs
                    </button>
                  </div>
                </div>

                {/* Webcam Feed Container */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px'
                }}>
                {webcamImage && (
                  <div style={{ 
                    position: 'relative', 
                    width: '100%', 
                    maxWidth: '640px',
                    overflow: 'hidden' // Crop the right side in simple view
                  }}>
                    <img 
                      src={webcamImage} 
                      alt="Webcam feed" 
                      style={{ 
                        width: '100%',
                        height: 'auto',
                        border: '3px solid #28a745',
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                        display: 'block'
                      }} 
                    />
                    
                    {/* Left Arrow Button */}
                    <button
                      onClick={() => setShowLeftSidebar(!showLeftSidebar)}
                      style={{
                        position: 'absolute',
                        left: '-20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: showLeftSidebar ? 'rgba(108, 117, 125, 0.9)' : 'rgba(111, 66, 193, 0.9)',
                        border: '2px solid white',
                        color: 'white',
                        fontSize: '20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        zIndex: 20,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        padding: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                      }}
                    >
                      {showLeftSidebar ? '◀' : '▶'}
                    </button>
                    
                    {/* Right Arrow Button */}
                    <button
                      onClick={() => setShowEnhancedView(!showEnhancedView)}
                      style={{
                        position: 'absolute',
                        right: '-20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: showEnhancedView ? 'rgba(108, 117, 125, 0.9)' : 'rgba(111, 66, 193, 0.9)',
                        border: '2px solid white',
                        color: 'white',
                        fontSize: '20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        zIndex: 20,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        padding: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                      }}
                    >
                      {showEnhancedView ? '▶' : '◀'}
                    </button>
                    
                    {/* Pose Toggle Button - Bottom Right */}
                    <button
                      onClick={async () => {
                        const next = !overlaySettings.pose;
                        const newSettings = { ...overlaySettings, pose: next };
                        setOverlaySettings(newSettings);
                        try {
                          if (webcamWsRef.current && webcamWsRef.current.readyState === WebSocket.OPEN) {
                            webcamWsRef.current.send(JSON.stringify({ type: 'overlay', ...newSettings }));
                          } else {
                            await axios.post('/api/overlay-settings', newSettings);
                          }
                        } catch (e) {
                          console.error('Failed to toggle pose:', e);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        right: '15px',
                        bottom: '15px',
                        width: '45px',
                        height: '45px',
                        borderRadius: '50%',
                        backgroundColor: overlaySettings.pose ? 'rgba(40, 167, 69, 0.9)' : 'rgba(108, 117, 125, 0.9)',
                        border: '2px solid white',
                            color: 'white',
                        fontSize: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        zIndex: 20,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        padding: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      title={overlaySettings.pose ? 'Hide pose overlay' : 'Show pose overlay'}
                    >
                      🧍
                    </button>

                    {/* Analysis Controls - Top Left - Minimal Icons */}

                    {/* Traffic Light - Analysis Status - Top Right */}
                    <div style={{
                      position: 'absolute',
                      right: '15px',
                      top: '15px',
                      width: '20px',
                      height: '50px',
                      backgroundColor: '#2c2c2c',
                      borderRadius: '10px',
                      padding: '3px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.3)',
                      border: '2px solid #1a1a1a',
                      zIndex: 20
                    }}>
                      {/* Red Light */}
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: getAnalysisStatus().level === 'partial' ? '#ff4444' : '#4a4a4a',
                        border: '1px solid #333',
                        boxShadow: getAnalysisStatus().level === 'partial' ? '0 0 6px #ff4444' : 'inset 0 1px 1px rgba(0,0,0,0.5)',
                        transition: 'all 0.3s ease'
                      }}></div>

                      {/* Orange Light */}
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: (getAnalysisStatus().level === 'moderate' || getAnalysisStatus().level === 'in-progress') ? '#ff8800' : '#4a4a4a',
                        border: '1px solid #333',
                        boxShadow: (getAnalysisStatus().level === 'moderate' || getAnalysisStatus().level === 'in-progress') ? '0 0 6px #ff8800' : 'inset 0 1px 1px rgba(0,0,0,0.5)',
                        transition: 'all 0.3s ease'
                      }}></div>

                      {/* Green Light */}
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: getAnalysisStatus().level === 'complete' ? '#44ff44' : '#4a4a4a',
                        border: '1px solid #333',
                        boxShadow: getAnalysisStatus().level === 'complete' ? '0 0 6px #44ff44' : 'inset 0 1px 1px rgba(0,0,0,0.5)',
                        transition: 'all 0.3s ease'
                      }}></div>
                    </div>
                  </div>
                )}
                
                {!webcamImage && (
                  <div style={{
                    width: '100%',
                    height: '400px',
                    border: '3px dashed #666',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#999',
                    fontSize: '16px'
                  }}>
                    <div>Waiting for webcam data...</div>
                  </div>
                )}
                </div>

                {/* Process Logs Panel - appears when toggled */}
                {showProcessLogs && processLogs.length > 0 && (
                <div style={{ 
                  padding: '20px',
                    backgroundColor: '#1e1e1e',
                    color: '#00ff00',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    maxHeight: '600px',
                    overflowY: 'auto',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    margin: '20px'
                  }}>
                    <div style={{
                  display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '15px',
                      paddingBottom: '10px',
                      borderBottom: '1px solid #333'
                    }}>
                      <h4 style={{ margin: 0, color: '#00ff00', fontSize: '14px' }}>
                        🔍 Assessment Pipeline Logs
                      </h4>
                    <button 
                        onClick={() => setProcessLogs([])}
                      style={{
                          padding: '4px 8px',
                          fontSize: '10px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Clear Logs
                    </button>
                    </div>
                    {processLogs.map((log, index) => (
                      <div key={index} style={{ 
                        marginBottom: '15px', 
                        padding: '10px',
                        backgroundColor: '#2a2a2a',
                        borderRadius: '4px',
                        borderLeft: `3px solid ${
                          log.stage === 'Triage' ? '#ffc107' :
                          log.stage === 'Assessment Stage' ? '#17a2b8' :
                          log.stage === 'Diagnosis' ? '#dc3545' :
                          log.stage === 'Action Plan' ? '#28a745' :
                          '#6c757d'
                        }`
                      }}>
                    <div style={{
                          color: '#00bfff', 
                          fontWeight: 'bold', 
                          marginBottom: '5px',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}>
                          <span>[{log.timestamp}] {log.stage}</span>
                          <span style={{ color: '#888', fontSize: '10px' }}>
                            {log.duration ? `${log.duration}ms` : ''}
                          </span>
                    </div>
                        <pre style={{ 
                          margin: '5px 0 0 0', 
                          whiteSpace: 'pre-wrap', 
                          wordBreak: 'break-word',
                          color: '#d4d4d4',
                          fontSize: '10px',
                          lineHeight: '1.4'
                        }}>
                          {JSON.stringify(log.data, null, 2)}
                        </pre>
                  </div>
                    ))}
                  </div>
                )}

                {/* FULL WIDTH LAYOUT - Analysis History */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '0 20px',
                  marginTop: '20px'
                }}>
                  <div style={{
                    width: '100%',
                    maxWidth: '640px'
                  }}>
                  
                  {/* Analysis History - Full Width */}
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '500px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                      paddingBottom: '0px'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '700',
                        color: '#6c757d',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        Analysis History
                        {isTranscribing && (
                          <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#dc3545',
                            animation: 'pulse 1.5s ease-in-out infinite'
                          }} />
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {!isTranscribing ? (
                    <button 
                            onClick={startTranscription}
                      style={{
                              background: '#28a745',
                              border: 'none',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '9px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            Start transcription
                    </button>
                        ) : (
                          <button
                            onClick={stopTranscription}
                            style={{
                              background: '#dc3545',
                              border: 'none',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '9px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            Stop Transcription
                          </button>
                        )}
                        
                        {/* Analyze Scene Button */}
                        <button
                          onClick={analyzeWebcamFrame}
                          disabled={isAnalyzing || !webcamImage}
                          style={{
                            background: isAnalyzing ? '#6c757d' : '#007bff',
                            border: 'none',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '9px',
                            cursor: isAnalyzing || !webcamImage ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            opacity: isAnalyzing || !webcamImage ? 0.6 : 1,
                            transition: 'all 0.2s ease',
                            boxShadow: isAnalyzing || !webcamImage ? 'none' : '0 2px 4px rgba(0,123,255,0.2)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}
                          onMouseEnter={(e) => {
                            if (!isAnalyzing && webcamImage) {
                              e.currentTarget.style.background = '#0056b3';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,123,255,0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isAnalyzing && webcamImage) {
                              e.currentTarget.style.background = '#007bff';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,123,255,0.2)';
                            }
                          }}
                          title={isAnalyzing ? 'Analysing...' : 'Analyse scene'}
                        >
                          {isAnalyzing ? 'Analysing...' : 'Analyse scene'}
                        </button>
                        
                        {/* Continuous Analysis Toggle */}
                        <button
                          onClick={() => setContinuousAnalysis(!continuousAnalysis)}
                          style={{
                            background: continuousAnalysis ? '#dc3545' : '#6c757d',
                            border: continuousAnalysis ? '1px solid #c82333' : '1px solid #5a6268',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '9px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            boxShadow: continuousAnalysis ? '0 2px 4px rgba(220,53,69,0.3)' : '0 2px 4px rgba(108,117,125,0.2)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}
                          onMouseEnter={(e) => {
                            if (continuousAnalysis) {
                              e.currentTarget.style.background = '#c82333';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(220,53,69,0.4)';
                            } else {
                              e.currentTarget.style.background = '#5a6268';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(108,117,125,0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (continuousAnalysis) {
                              e.currentTarget.style.background = '#dc3545';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(220,53,69,0.3)';
                            } else {
                              e.currentTarget.style.background = '#6c757d';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(108,117,125,0.2)';
                            }
                          }}
                          title={continuousAnalysis ? 'Stop continuous analysis' : 'Start continuous analysis'}
                        >
                          {continuousAnalysis ? 'Stop continuous' : 'Start continuous'}
                        </button>
                        
                        {/* Stop Webcam Button */}
                        <button
                          onClick={stopWebcamAnalysis}
                          style={{
                            background: '#dc3545',
                            border: '1px solid #c82333',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '9px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(220,53,69,0.3)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#c82333';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(220,53,69,0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#dc3545';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(220,53,69,0.3)';
                          }}
                          title="Stop webcam & vitals"
                        >
                          Stop webcam
                        </button>
                      </div>
                    </div>
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      overflowY: 'auto'
                    }}>
                      {/* Real-time interim transcript - streaming at the top */}
                      {interimTranscript && (
                        <div style={{
                          padding: '4px 6px',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          borderLeft: '3px solid #dee2e6',
                          transition: 'all 0.2s ease',
                          fontSize: '10px'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '2px',
                            fontSize: '8px',
                            color: '#6c757d'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontWeight: '700', color: '#6c757d' }}>
                                🎤 Live
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{
                                width: '5px',
                                height: '5px',
                                borderRadius: '50%',
                                backgroundColor: '#dc3545',
                                animation: 'pulse 1.5s ease-in-out infinite'
                              }} />
                            </div>
                          </div>
                          <div style={{
                            color: '#6c757d',
                            fontWeight: '500',
                            fontStyle: 'italic',
                            fontSize: '9px'
                          }}>
                            {interimTranscript}
                          </div>
                        </div>
                      )}
                      
                      {analysisHistory.map((entry, index) => {
                        // Transcript entry - colored based on speaker like left side
                        if (entry.type === 'transcript') {
                          const speakerInfo = getSpeakerLabel(entry.speaker);
                          const colors = {
                            bg: speakerInfo.color === '#17a2b8' ? '#d1ecf1' :      // Clinician: cyan bg
                                speakerInfo.color === '#ffc107' ? '#fff3cd' :      // Patient: yellow bg
                                speakerInfo.color === '#28a745' ? '#d4edda' :      // Helper: green bg
                                '#e2e3e5',                                          // Unknown: gray bg
                            border: speakerInfo.color,
                            text: speakerInfo.color === '#17a2b8' ? '#0c5460' :    // Clinician: dark cyan
                                  speakerInfo.color === '#ffc107' ? '#856404' :    // Patient: dark yellow
                                  speakerInfo.color === '#28a745' ? '#155724' :    // Helper: dark green
                                  '#383d41'                                         // Unknown: dark gray
                          };
                          
                          return (
                            <div 
                              key={entry.id || index}
                              style={{
                                padding: '4px 6px',
                                backgroundColor: colors.bg,
                                borderRadius: '4px',
                                borderLeft: `3px solid ${colors.border}`,
                                transition: 'all 0.2s ease',
                                fontSize: '10px'
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '2px',
                                fontSize: '8px',
                                color: '#6c757d'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ fontWeight: '700', color: colors.border }}>
                                    {speakerInfo.icon} {speakerInfo.label}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span>{entry.timestamp}</span>
                                </div>
                              </div>
                              <div style={{
                                color: colors.text,
                                fontWeight: '500',
                                fontSize: '9px'
                              }}>
                                {entry.text}
                              </div>
                            </div>
                          );
                        }

                        // Analysis entry - original layout
                        return (
                          <div 
                            key={entry.id || index}
                      style={{
                              padding: '12px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '6px',
                              borderLeft: '3px solid #17a2b8',
                              cursor: expandedHistoryItems.has(index) ? 'default' : 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              gap: '12px'
                            }}
                            onClick={() => {
                              const newExpanded = new Set(expandedHistoryItems);
                              if (newExpanded.has(index)) {
                                newExpanded.delete(index);
                              } else {
                                newExpanded.add(index);
                              }
                              setExpandedHistoryItems(newExpanded);
                            }}
                          >
                            {/* Frame Image - Left Side */}
                            {entry.frameData && (
                              <img 
                                src={entry.frameData} 
                                alt="Frame"
                                style={{
                                  width: '150px',
                                  minWidth: '150px',
                                  height: '100px',
                                  borderRadius: '6px',
                                  objectFit: 'cover'
                                }}
                              />
                            )}

                            {/* Content - Right Side */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {/* Header: Timestamp, Source Badge, Triage */}
                    <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '6px',
                                flexWrap: 'wrap',
                                gap: '4px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                  <span style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#495057',
                                    fontFamily: "'Poppins', sans-serif"
                                  }}>
                                    {entry.timestamp || 'Unknown time'}
                                  </span>
                                  {entry.source === 'webcam' && (
                                    <span style={{
                      fontSize: '9px',
                                      padding: '2px 5px',
                                      borderRadius: '3px',
                                      backgroundColor: '#dbeafe',
                                      color: '#1e40af',
                                      fontWeight: '600'
                                    }}>
                                      📹 WEBCAM
                                    </span>
                                  )}
                                  {entry.source === 'mhr' && (
                                    <span style={{
                                      fontSize: '9px',
                                      padding: '2px 5px',
                                      borderRadius: '3px',
                                      backgroundColor: '#fef3c7',
                                      color: '#92400e',
                                      fontWeight: '600'
                                    }}>
                                      🏥 MHR
                                    </span>
                                  )}
                                </div>
                                {entry.triageScore && (
                                  <span style={{
                                    fontSize: '10px',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: entry.triageScore <= 2 ? '#dc3545' : entry.triageScore === 3 ? '#ffc107' : '#28a745',
                                    color: 'white',
                                    fontWeight: '600'
                                  }}>
                                    P{entry.triageScore}
                                  </span>
                                )}
                              </div>

                              {/* Description */}
                              <div style={{
                                fontSize: '10px',
                      color: '#6c757d',
                                lineHeight: '1.5',
                                maxHeight: expandedHistoryItems.has(index) ? 'none' : '60px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                marginBottom: '6px'
                              }}>
                                {entry.visionAnalysis || entry.sceneDescription || entry.description || 'Analysis in progress...'}
                    </div>

                              {/* Assessment Stage */}
                              {entry.assessmentStage && (
                                <div style={{
                                  fontSize: '10px',
                                  color: '#17a2b8',
                                  fontWeight: '600',
                                  marginBottom: '4px'
                                }}>
                                  Stage: {entry.assessmentStage}
                  </div>
                              )}

                              {!expandedHistoryItems.has(index) && (
                                <div style={{
                                  fontSize: '9px',
                                  color: '#17a2b8',
                                  fontStyle: 'italic'
                                }}>
                                  Click to expand...
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  </div>
                </div>

              </div>

              {/* RIGHT SIDEBAR - AI Assistant Panels */}
              {showEnhancedView && <div style={{
                padding: '20px',
                backgroundColor: '#ffffff',
                borderLeft: '2px solid #e0e7ef',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                minWidth: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                maxHeight: '800px'
              }}>
                {/* Header with MATILDA title and Logs Toggle */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '5px',
                  paddingBottom: '10px',
                  borderBottom: '2px solid #e0e7ef'
                }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#17a2b8',
                    letterSpacing: '0.5px',
                    fontFamily: "'Poppins', sans-serif"
                  }}>
                    MATILDA
                  </div>
                    <button 
                    onClick={() => setShowProcessLogs(!showProcessLogs)}
                      style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: showProcessLogs ? 'rgba(255, 193, 7, 0.9)' : 'rgba(108, 117, 125, 0.9)',
                      border: '2px solid #17a2b8',
                        color: 'white',
                      fontSize: '14px',
                      cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      padding: 0
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title={showProcessLogs ? 'Hide process logs' : 'Show process logs'}
                  >
                    📋
                    </button>
                </div>

                {/* CLINICAL SUMMARY - Compact Display */}
                {(heartRate || webcamBreathingRate || webcamBmi || webcamBloodPressure || webcamSpo2) && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    color: '#212529',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    marginTop: '12px',
                    textAlign: 'left'
                  }}>
                    <div 
                      onClick={() => setIsClinicalSummaryExpanded(!isClinicalSummaryExpanded)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        marginBottom: isClinicalSummaryExpanded ? '12px' : '0'
                      }}
                    >
                      <div style={{ 
                        fontSize: '10px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        color: '#6c757d',
                        letterSpacing: '0.5px'
                      }}>
                        Clinical Summary
                      </div>
                      <span style={{
                        fontSize: '9px',
                        color: '#6c757d',
                        opacity: 0.6,
                        transition: 'transform 0.2s',
                        transform: isClinicalSummaryExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        display: 'inline-block'
                      }}>
                        ▼
                      </span>
                    </div>

                    {isClinicalSummaryExpanded && (
                      <div>
                    
                    {/* Chief Complaint */}
                    {structuredAssessment?.chief_complaint && (
                      <div style={{ 
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <div style={{ 
                          color: '#6c757d', 
                          fontWeight: '600', 
                          fontSize: '10px',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          💬 Chief Complaint
                        </div>
                        <div style={{ 
                          fontSize: '11px',
                          color: '#212529',
                          lineHeight: '1.4',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {structuredAssessment.chief_complaint}
                        </div>
                      </div>
                    )}
                    
                    {/* Allergies - Drug Only with Yellow Box */}
                    {structuredAssessment?.allergies?.drug_allergies?.length > 0 && (
                      <div style={{ 
                        marginTop: '12px',
                        padding: '10px',
                        backgroundColor: '#fff3cd',
                        border: '2px solid #ffc107',
                        borderRadius: '6px'
                      }}>
                        <div style={{ 
                          color: '#856404', 
                          fontWeight: '700', 
                          fontSize: '10px',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          ⚠️ Drug Allergies
                        </div>
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#856404', 
                          lineHeight: '1.4',
                          fontWeight: '600'
                        }}>
                          {structuredAssessment.allergies.drug_allergies.join(', ')}
                        </div>
                      </div>
                    )}
                    
                    {/* Clinical Impression */}
                    {structuredAssessment?.assessment_summary?.clinical_impression && (
                      <div style={{ 
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <div style={{ 
                          color: '#6c757d', 
                          fontWeight: '600', 
                          fontSize: '10px',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          🩺 Clinical Impression
                        </div>
                        <div style={{ 
                          fontSize: '11px',
                          color: '#212529',
                          lineHeight: '1.4'
                        }}>
                          {structuredAssessment.assessment_summary.clinical_impression}
                        </div>
                      </div>
                    )}
                    </div>
                    )}
                  </div>
                )}

                {/* HERE'S WHAT I CAN DO FOR YOU - AI Actions (Always Show) */}
                <div style={{
                  padding: '15px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  textAlign: 'left'
                }}>
                  <div 
                    onClick={() => setIsAiActionsExpanded(!isAiActionsExpanded)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      marginBottom: isAiActionsExpanded ? '12px' : '0'
                    }}
                  >
                    <div style={{ 
                      fontSize: '10px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      color: '#6c757d',
                      letterSpacing: '0.5px'
                    }}>
                      Here's what I can do for you...
                    </div>
                    <span style={{
                      fontSize: '9px',
                      color: '#6c757d',
                      opacity: 0.6,
                      transition: 'transform 0.2s',
                      transform: isAiActionsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      display: 'inline-block'
                    }}>
                      ▼
                    </span>
                  </div>

                  {isAiActionsExpanded && (
                  <div>

                  {/* AI-Driven Actions */}
                  {actionPlan?.ai_driven_actions && actionPlan.ai_driven_actions.length > 0 ? (
                    actionPlan.ai_driven_actions.slice(0, 5).map((action, index) => (
                      <div 
                        key={index}
                        style={{
                          marginBottom: '8px',
                          paddingBottom: '8px',
                          borderBottom: index < Math.min(actionPlan.ai_driven_actions.length, 5) - 1 ? '1px solid rgba(13,110,253,0.2)' : 'none'
                        }}
                      >
                        {/* Action title with urgency badge inline */}
                    <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: action.requires_approval ? '8px' : '0',
                          gap: '6px'
                        }}>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#495057',
                            flex: 1,
                            lineHeight: '1.4'
                          }}>
                            {action.action}
                    </div>
                          {action.urgency && (
                            <span style={{
                              fontSize: '8px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: 'rgba(13,110,253,0.15)',
                              color: '#004085',
                              fontWeight: '600',
                      textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                              border: '1px solid rgba(13,110,253,0.3)'
                            }}>
                              {action.urgency}
                            </span>
                          )}
                  </div>

                        {/* Buttons */}
                        {action.requires_approval && (
                          <div style={{
                            display: 'flex',
                            gap: '6px'
                          }}>
                    <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('AI Action approved:', action);
                                alert(`✅ Approved: ${action.action}`);
                      }}
                      style={{
                                flex: 1,
                                padding: '6px 12px',
                                fontSize: '10px',
                        fontWeight: '600',
                                borderRadius: '5px',
                                border: '1px solid #28a745',
                                backgroundColor: '#ffffff',
                                color: '#28a745',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#28a745';
                                e.target.style.color = '#ffffff';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ffffff';
                                e.target.style.color = '#28a745';
                              }}
                            >
                              ✓ Approve
                    </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('AI Action denied:', action);
                                alert(`🚫 Denied: ${action.action}`);
                              }}
                              style={{
                                padding: '6px 12px',
                                fontSize: '10px',
                                fontWeight: '600',
                                borderRadius: '5px',
                                border: '1px solid #dc3545',
                                backgroundColor: '#ffffff',
                                color: '#dc3545',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#dc3545';
                                e.target.style.color = '#ffffff';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ffffff';
                                e.target.style.color = '#dc3545';
                              }}
                            >
                              ✕ Deny
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{
                      fontSize: '11px',
                      color: '#6c757d',
                      fontStyle: 'italic',
                      marginBottom: '12px'
                    }}>
                      Analysing scene for recommended actions...
                    </div>
                  )}

                  {/* Import My Health Record - AI Action */}
                  <div style={{
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '2px solid rgba(13,110,253,0.2)',
                    marginBottom: '8px',
                    paddingBottom: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                      gap: '6px'
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#495057',
                        flex: 1,
                        lineHeight: '1.4'
                      }}>
                        Integrate My Health Record
                      </div>
                      <span style={{
                        fontSize: '8px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(13,110,253,0.15)',
                        color: '#004085',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        border: '1px solid rgba(13,110,253,0.3)'
                      }}>
                        Medium
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: '6px'
                    }}>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          MHR_access("1");
                        }}
                        disabled={isMhrLoading}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          borderRadius: '5px',
                          border: '1px solid #28a745',
                          backgroundColor: isMhrLoading ? '#6c757d' : '#ffffff',
                          color: isMhrLoading ? '#ffffff' : '#28a745',
                          cursor: isMhrLoading ? 'wait' : 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (!isMhrLoading) {
                            e.target.style.backgroundColor = '#28a745';
                            e.target.style.color = '#ffffff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isMhrLoading) {
                            e.target.style.backgroundColor = '#ffffff';
                            e.target.style.color = '#28a745';
                          }
                        }}
                      >
                        {isMhrLoading ? '⏳ Loading...' : '✓ Import'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('MHR import denied');
                          alert('🚫 My Health Record import cancelled');
                        }}
                        disabled={isMhrLoading}
                        style={{
                          padding: '6px 12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          borderRadius: '5px',
                          border: '1px solid #dc3545',
                          backgroundColor: '#ffffff',
                          color: '#dc3545',
                          cursor: isMhrLoading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          opacity: isMhrLoading ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!isMhrLoading) {
                            e.target.style.backgroundColor = '#dc3545';
                            e.target.style.color = '#ffffff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isMhrLoading) {
                            e.target.style.backgroundColor = '#ffffff';
                            e.target.style.color = '#dc3545';
                          }
                        }}
                      >
                        ✕ Deny
                      </button>
                    </div>
                    {lastMhrStatus && (
                      <div style={{
                        marginTop: '6px',
                        fontSize: '9px',
                        color: '#28a745',
                        textAlign: 'left',
                        fontWeight: '600'
                      }}>
                        ✓ {lastMhrStatus.substring(0, 50)}...
                      </div>
                    )}
                  </div>
                  </div>
                  )}
                </div>

                {/* HERE'S WHAT I'M EXPLORING - Hypotheses/Differential Diagnoses */}
                <div style={{
                  padding: '15px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  textAlign: 'left',
                  marginTop: '12px'
                }}>
                  <div 
                    onClick={() => setIsExploringExpanded(!isExploringExpanded)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      marginBottom: isExploringExpanded ? '12px' : '0'
                    }}
                  >
                    <div style={{ 
                      fontSize: '10px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      color: '#6c757d',
                      letterSpacing: '0.5px'
                    }}>
                      Here's what I'm exploring...
                    </div>
                    <span style={{
                      fontSize: '9px',
                      color: '#6c757d',
                      opacity: 0.6,
                      transition: 'transform 0.2s',
                      transform: isExploringExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      display: 'inline-block'
                    }}>
                      ▼
                    </span>
                  </div>

                  {isExploringExpanded && (
                  <div>
                  {diagnosisPrediction ? (
                    <div style={{ fontSize: '11px', lineHeight: '1.6', color: '#495057', textAlign: 'left' }}>
                      
                      {/* Primary Diagnosis */}
                      {diagnosisPrediction.primary_diagnosis && (
                        <div style={{ 
                          marginBottom: '15px',
                          paddingBottom: '12px',
                          borderBottom: '2px solid rgba(255,193,7,0.4)',
                          backgroundColor: 'rgba(255,193,7,0.1)',
                          padding: '10px',
                          borderRadius: '6px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                            <span style={{ 
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              backgroundColor: '#856404',
                              color: 'white',
                              fontWeight: '700',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Primary
                            </span>
                            <span style={{ fontWeight: '700', fontSize: '12px' }}>{diagnosisPrediction.primary_diagnosis.condition}</span>
                            <span style={{ 
                              marginLeft: 'auto',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: '#856404',
                              color: 'white',
                              fontWeight: '700'
                            }}>
                              {diagnosisPrediction.primary_diagnosis.confidence_percentage || 
                               (diagnosisPrediction.primary_diagnosis.confidence === 'High' ? '85' : 
                                diagnosisPrediction.primary_diagnosis.confidence === 'Moderate' ? '65' : '45')}%
                            </span>
                          </div>
                          {diagnosisPrediction.primary_diagnosis.icd10_code && (
                            <div style={{ fontSize: '9px', color: '#856404', marginBottom: '4px', marginLeft: '0px' }}>
                              ICD-10: {diagnosisPrediction.primary_diagnosis.icd10_code}
                            </div>
                          )}
                          <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '6px' }}>
                            {diagnosisPrediction.primary_diagnosis.rationale}
                          </div>
                          
                          {/* Confirmatory Findings */}
                          {diagnosisPrediction.primary_diagnosis.confirmatory_findings && 
                           diagnosisPrediction.primary_diagnosis.confirmatory_findings.length > 0 && (
                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(133,100,4,0.2)' }}>
                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#856404', marginBottom: '4px', textTransform: 'uppercase' }}>
                                Assessment Pathway to Confirm:
                              </div>
                              <ul style={{ margin: '0', paddingLeft: '18px', fontSize: '9px', color: '#6c757d' }}>
                                {diagnosisPrediction.primary_diagnosis.confirmatory_findings.map((finding, idx) => (
                                  <li key={idx} style={{ marginBottom: '2px' }}>{finding}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Differential Diagnoses */}
                      {diagnosisPrediction.differential_diagnoses && diagnosisPrediction.differential_diagnoses.length > 0 && (
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: '700', color: '#856404', marginBottom: '8px', textTransform: 'uppercase' }}>
                            Differential Diagnoses:
                          </div>
                          {diagnosisPrediction.differential_diagnoses.map((diagnosis, index) => (
                            <div key={index} style={{ 
                              marginBottom: '10px',
                              paddingBottom: '10px',
                              borderBottom: index < diagnosisPrediction.differential_diagnoses.length - 1 ? '1px solid rgba(255,193,7,0.3)' : 'none'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                <span style={{ 
                                  fontWeight: '700',
                                  color: '#856404',
                                  minWidth: '18px'
                                }}>
                                  {index + 1}.
                                </span>
                                <span style={{ fontWeight: '600' }}>{diagnosis.condition}</span>
                                <span style={{ 
                                  marginLeft: 'auto',
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: 'rgba(133,100,4,0.15)',
                                  color: '#856404',
                                  fontWeight: '600'
                                }}>
                                  {diagnosis.confidence_percentage || 
                                   (diagnosis.confidence === 'High' ? '85' : 
                                    diagnosis.confidence === 'Moderate' ? '65' : '45')}%
                                </span>
                              </div>
                              {diagnosis.icd10_code && (
                                <div style={{ fontSize: '9px', color: '#856404', marginBottom: '4px', marginLeft: '24px' }}>
                                  ICD-10: {diagnosis.icd10_code}
                                </div>
                              )}
                              <div style={{ fontSize: '10px', color: '#6c757d', marginLeft: '24px', marginBottom: '4px' }}>
                                {diagnosis.rationale}
                              </div>
                              
                              {/* Confirmatory Findings for differential */}
                              {diagnosis.confirmatory_findings && diagnosis.confirmatory_findings.length > 0 && (
                                <div style={{ marginTop: '6px', marginLeft: '24px', paddingTop: '6px', borderTop: '1px solid rgba(133,100,4,0.15)' }}>
                                  <div style={{ fontSize: '9px', fontWeight: '600', color: '#856404', marginBottom: '3px' }}>
                                    To confirm/rule out:
                                  </div>
                                  <ul style={{ margin: '0', paddingLeft: '16px', fontSize: '9px', color: '#6c757d' }}>
                                    {diagnosis.confirmatory_findings.map((finding, idx) => (
                                      <li key={idx} style={{ marginBottom: '2px' }}>{finding}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Red Flags */}
                      {diagnosisPrediction.red_flags && diagnosisPrediction.red_flags.length > 0 && (
                        <div style={{ 
                          marginTop: '12px',
                          padding: '8px',
                          backgroundColor: 'rgba(220, 53, 69, 0.1)',
                          borderRadius: '4px',
                          borderLeft: '3px solid #dc3545'
                        }}>
                          <div style={{ fontSize: '10px', fontWeight: '700', color: '#dc3545', marginBottom: '4px' }}>
                            ⚠️ Red Flags:
                          </div>
                          <ul style={{ margin: '0', paddingLeft: '18px', fontSize: '9px', color: '#721c24' }}>
                            {diagnosisPrediction.red_flags.map((flag, idx) => (
                              <li key={idx} style={{ marginBottom: '2px' }}>{flag}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Key Symptoms */}
                      {diagnosisPrediction.key_symptoms && diagnosisPrediction.key_symptoms.length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: '#856404', marginBottom: '4px' }}>
                            Key Symptoms:
                          </div>
                          <div style={{ fontSize: '9px', color: '#6c757d', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {diagnosisPrediction.key_symptoms.map((symptom, idx) => (
                              <span key={idx} style={{
                                padding: '2px 6px',
                                backgroundColor: 'rgba(133,100,4,0.1)',
                                borderRadius: '3px',
                                border: '1px solid rgba(133,100,4,0.2)'
                              }}>
                                {symptom}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Clinical Notes */}
                      {diagnosisPrediction.clinical_notes && (
                        <div style={{ 
                          marginTop: '10px',
                          padding: '8px',
                          backgroundColor: 'rgba(133,100,4,0.08)',
                          borderRadius: '4px',
                          fontSize: '9px',
                          color: '#6c757d',
                          fontStyle: 'italic'
                        }}>
                          <strong style={{ color: '#856404', fontStyle: 'normal' }}>Clinical Notes:</strong> {diagnosisPrediction.clinical_notes}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: '#856404', fontStyle: 'italic' }}>
                      Exploring possible diagnoses...
                    </div>
                  )}
                  </div>
                  )}
                </div>

                {/* Clinical Decision Support - Smart Condition Matching */}
                <div style={{
                  padding: '15px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  marginTop: '12px',
                  textAlign: 'left'
                }}>
                  <div 
                    onClick={() => setCdsExpanded(!cdsExpanded)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      marginBottom: cdsExpanded ? '12px' : '0'
                    }}
                  >
                    <div style={{
                      fontSize: '10px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      color: '#6c757d',
                      letterSpacing: '0.5px'
                    }}>
                      Clinical Decision Knowledge Base
                    </div>
                    <span style={{
                      fontSize: '9px',
                      color: '#6c757d',
                      opacity: 0.6,
                      transition: 'transform 0.2s',
                      transform: cdsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      display: 'inline-block'
                    }}>
                      ▼
                    </span>
                  </div>
                  
                  {cdsExpanded && (
                    <div>
                      {/* AUTO-MATCHED CONDITIONS BASED ON VITALS */}
                      {matchedConditions.length > 0 && (
                        <div style={{ marginBottom: '15px' }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#17a2b8',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Conditions Matching Current Vitals
              </div>
                          <div style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            border: '2px solid #17a2b8',
                            borderRadius: '6px',
                            backgroundColor: 'white'
                          }}>
                            {matchedConditions.map((condition, idx) => (
                              <details 
                                key={idx}
                                style={{
                                  padding: '10px',
                                  borderBottom: idx < matchedConditions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                <summary style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  cursor: 'pointer',
                                  listStyle: 'none'
                                }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                      <span style={{
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        color: '#495057'
                                      }}>
                                        {condition.condition}
                                      </span>
                                      <span style={{
                                        fontSize: '9px',
                                        padding: '2px 5px',
                                        borderRadius: '3px',
                                        backgroundColor: condition.severity === 'Critical' ? '#dc3545' : condition.severity === 'High' ? '#fd7e14' : '#ffc107',
                                        color: 'white',
                                        fontWeight: '600'
                                      }}>
                                        {condition.severity}
                                      </span>
                                      <span style={{
                                        fontSize: '8px',
                                        padding: '2px 5px',
                                        borderRadius: '3px',
                                        backgroundColor: '#e9ecef',
                                        color: '#495057',
                                        fontWeight: '600'
                                      }}>
                                        {condition.icd10}
                                      </span>
                                    </div>
                                    <div style={{
                                      fontSize: '9px',
                                      color: '#17a2b8',
                                      fontWeight: '600',
                                      marginBottom: '2px'
                                    }}>
                                      Match: {Math.round(condition.matchScore * 100)}% ({condition.matchCount} vitals)
                                    </div>
                                    <div style={{
                                      fontSize: '8px',
                                      color: '#6c757d',
                                      fontStyle: 'italic'
                                    }}>
                                      {condition.matchReasons.join(' • ')}
                                    </div>
                                  </div>
                                </summary>
                                
                                {/* Expanded Details */}
                                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f0f0f0' }}>
                                  {/* Presentations */}
                                  <div style={{ marginBottom: '8px' }}>
                                    <div style={{ fontSize: '9px', fontWeight: '700', color: '#495057', marginBottom: '3px' }}>Clinical Presentation:</div>
                                    <div style={{ fontSize: '9px', color: '#6c757d', lineHeight: '1.4' }}>
                                      • {condition.presentations.join(' • ')}
                                    </div>
                                  </div>
                                  
                                  {/* Confirmatory Findings */}
                                  <div style={{ marginBottom: '8px' }}>
                                    <div style={{ fontSize: '9px', fontWeight: '700', color: '#495057', marginBottom: '3px' }}>To Confirm/Rule Out:</div>
                                    {condition.confirmatoryFindings.map((finding, fi) => (
                                      <div key={fi} style={{ fontSize: '9px', color: '#6c757d', lineHeight: '1.4', marginLeft: '10px' }}>
                                        • {finding}
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Differentials */}
                                  <div style={{ marginBottom: '8px' }}>
                                    <div style={{ fontSize: '9px', fontWeight: '700', color: '#495057', marginBottom: '3px' }}>Differential Diagnoses:</div>
                                    <div style={{ fontSize: '9px', color: '#6c757d', lineHeight: '1.4' }}>
                                      {condition.differentials.join(' • ')}
                                    </div>
                                  </div>
                                  
                                  {/* Red Flags */}
                                  <div style={{ marginBottom: '8px' }}>
                                    <div style={{ fontSize: '9px', fontWeight: '700', color: '#dc3545', marginBottom: '3px' }}>⚠️ Red Flags:</div>
                                    {condition.redFlags.map((flag, fi) => (
                                      <div key={fi} style={{ fontSize: '9px', color: '#dc3545', lineHeight: '1.4', marginLeft: '10px' }}>
                                        • {flag}
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Assessment Pathway */}
                                  <div>
                                    <div style={{ fontSize: '9px', fontWeight: '700', color: '#495057', marginBottom: '3px' }}>Assessment Pathway:</div>
                                    <div style={{ fontSize: '9px', color: '#6c757d', lineHeight: '1.4', fontStyle: 'italic' }}>
                                      {condition.assessmentPathway}
                                    </div>
                                  </div>
                                </div>
                              </details>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* MANUAL SEARCH FOR ALL CONDITIONS */}
                      <div style={{
                        paddingTop: matchedConditions.length > 0 ? '15px' : '0',
                        borderTop: matchedConditions.length > 0 ? '2px dashed #dee2e6' : 'none'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#6c757d',
                          marginBottom: '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          📚 Browse All Conditions ({EMERGENCY_CONDITIONS.length})
                        </div>
                        
                        {/* Search Input */}
                        <input
                          type="text"
                          placeholder="Search by condition, ICD-10 code, or symptom..."
                          value={cdsSearchTerm}
                          onChange={(e) => setCdsSearchTerm(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: '1px solid #ced4da',
                            marginBottom: '8px',
                            fontFamily: "'Poppins', sans-serif"
                          }}
                        />
                        
                        {/* Category Filter */}
                        <select
                          value={cdsSelectedCategory}
                          onChange={(e) => setCdsSelectedCategory(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: '1px solid #ced4da',
                            marginBottom: '8px',
                            fontFamily: "'Poppins', sans-serif"
                          }}
                        >
                          <option value="All">All Categories</option>
                          <option value="Cardiovascular">Cardiovascular</option>
                          <option value="Respiratory">Respiratory</option>
                          <option value="Neurological">Neurological</option>
                          <option value="Trauma">Trauma</option>
                          <option value="Orthopaedic">Orthopaedic/Fractures</option>
                          <option value="Abdominal">Abdominal</option>
                          <option value="Shock">Shock/Sepsis</option>
                          <option value="Metabolic">Metabolic/Endocrine</option>
                          <option value="Poisoning">Poisoning/Toxicology</option>
                          <option value="Allergic">Allergic/Immunologic</option>
                          <option value="Obstetric">Obstetric</option>
                          <option value="Renal">Renal</option>
                          <option value="Psychiatric">Psychiatric</option>
                          <option value="ENT">ENT</option>
                          <option value="Ophthalmology">Ophthalmology</option>
                          <option value="Dermatology">Dermatology</option>
                          <option value="Urological">Urological</option>
                        </select>
                        
                        {/* Results */}
                        <div style={{
                          maxHeight: '300px',
                          overflowY: 'auto',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          backgroundColor: 'white'
                        }}>
                          {(() => {
                            const filtered = EMERGENCY_CONDITIONS.filter(condition => {
                              const searchLower = cdsSearchTerm.toLowerCase();
                              const matchesSearch = !cdsSearchTerm || 
                                condition.condition.toLowerCase().includes(searchLower) ||
                                condition.icd10.toLowerCase().includes(searchLower) ||
                                condition.presentations.some(p => p.toLowerCase().includes(searchLower)) ||
                                condition.category.toLowerCase().includes(searchLower);
                              const matchesCategory = cdsSelectedCategory === 'All' || condition.category.includes(cdsSelectedCategory);
                              return matchesSearch && matchesCategory;
                            });
                            
                            if (filtered.length === 0) {
                              return (
                                <div style={{
                                  padding: '20px',
                                  textAlign: 'center',
                                  fontSize: '11px',
                                  color: '#6c757d',
                                  fontStyle: 'italic'
                                }}>
                                  No conditions found
                                </div>
                              );
                            }
                            
                            return filtered.map((condition, idx) => (
                              <details 
                                key={idx}
                                style={{
                                  padding: '8px 10px',
                                  borderBottom: idx < filtered.length - 1 ? '1px solid #f0f0f0' : 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                <summary style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  cursor: 'pointer',
                                  listStyle: 'none'
                                }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                                      <span style={{
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        color: '#495057'
                                      }}>
                                        {condition.condition}
                                      </span>
                                      <span style={{
                                        fontSize: '8px',
                                        padding: '2px 5px',
                                        borderRadius: '3px',
                                        backgroundColor: '#e9ecef',
                                        color: '#495057',
                                        fontWeight: '600',
                                        fontFamily: "'Courier New', monospace"
                                      }}>
                                        {condition.icd10}
                                      </span>
                                    </div>
                                    <div style={{
                                      fontSize: '8px',
                                      color: '#6c757d'
                                    }}>
                                      {condition.category} • {condition.severity}
                                    </div>
                                  </div>
                                </summary>
                                
                                {/* Same expanded details as auto-matched conditions */}
                                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                                  <div style={{ marginBottom: '6px' }}>
                                    <div style={{ fontSize: '8px', fontWeight: '700', color: '#495057', marginBottom: '2px' }}>Clinical Presentation:</div>
                                    <div style={{ fontSize: '8px', color: '#6c757d', lineHeight: '1.3' }}>
                                      • {condition.presentations.join(' • ')}
                                    </div>
                                  </div>
                                  
                                  <div style={{ marginBottom: '6px' }}>
                                    <div style={{ fontSize: '8px', fontWeight: '700', color: '#495057', marginBottom: '2px' }}>To Confirm/Rule Out:</div>
                                    {condition.confirmatoryFindings.slice(0, 3).map((finding, fi) => (
                                      <div key={fi} style={{ fontSize: '8px', color: '#6c757d', lineHeight: '1.3', marginLeft: '8px' }}>
                                        • {finding}
                                      </div>
                                    ))}
                                  </div>
                                  
                                  <div style={{ marginBottom: '6px' }}>
                                    <div style={{ fontSize: '8px', fontWeight: '700', color: '#dc3545', marginBottom: '2px' }}>⚠️ Red Flags:</div>
                                    {condition.redFlags.map((flag, fi) => (
                                      <div key={fi} style={{ fontSize: '8px', color: '#dc3545', lineHeight: '1.3', marginLeft: '8px' }}>
                                        • {flag}
                                      </div>
                                    ))}
                                  </div>
                                  
                                  <div>
                                    <div style={{ fontSize: '8px', fontWeight: '700', color: '#495057', marginBottom: '2px' }}>Assessment Pathway:</div>
                                    <div style={{ fontSize: '8px', color: '#6c757d', lineHeight: '1.3', fontStyle: 'italic' }}>
                                      {condition.assessmentPathway}
                                    </div>
                                  </div>
                                </div>
                              </details>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Old diagnosis sections removed - now in conversational format above */}
                
                {false && diagnosisPrediction?.differential_diagnoses && diagnosisPrediction.differential_diagnoses.length > 0 && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    color: '#212529',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      fontSize: '12px', 
                      opacity: 0.9, 
                      marginBottom: '8px',
                      fontWeight: '700', 
                      textTransform: 'uppercase',
                      color: '#6c757d',
                      letterSpacing: '0.5px'
                    }}>
                      Top Diagnoses
                    </div>
                    {diagnosisPrediction.differential_diagnoses.slice(0, 2).map((diagnosis, index) => (
                      <div key={index} style={{
                        marginBottom: index < 1 ? '12px' : '6px',
                        paddingBottom: index < 1 ? '12px' : '6px',
                        borderBottom: index < 1 ? '1px solid #e9ecef' : 'none'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginBottom: '4px'
                        }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '18px',
                            height: '18px',
                            borderRadius: '4px',
                            background: index === 0 ? '#dc3545' : '#6c757d',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: '900'
                          }}>
                            {index + 1}
                          </span>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '700',
                            color: '#495057',
                            flex: 1,
                            textAlign: 'left'
                          }}>
                            {diagnosis.condition}
                          </span>
                          {/* Prominent confidence percentage */}
                          {(diagnosis.confidence_percentage || diagnosis.confidence_score) && (
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '900',
                              color: index === 0 ? '#dc3545' : '#495057',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              backgroundColor: 'rgba(0,0,0,0.05)',
                              border: `2px solid ${index === 0 ? '#dc3545' : '#6c757d'}`
                            }}>
                              {diagnosis.confidence_percentage || (diagnosis.confidence_score * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                        
                        {/* Visual Percentage Bar */}
                        {(diagnosis.confidence_percentage || diagnosis.confidence_score) && (
                        <div style={{
                            marginLeft: '24px',
                            marginBottom: '4px',
                            marginTop: '3px'
                          }}>
                            <div style={{
                              width: '100%',
                              height: '14px',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '7px',
                              overflow: 'hidden',
                              position: 'relative',
                              border: '1px solid #dee2e6'
                            }}>
                              <div style={{
                                width: `${diagnosis.confidence_percentage || (diagnosis.confidence_score * 100)}%`,
                                height: '100%',
                                background: index === 0 
                                  ? 'linear-gradient(90deg, #dc3545 0%, #c82333 100%)'
                                  : 'linear-gradient(90deg, #6c757d 0%, #5a6268 100%)',
                                transition: 'width 0.5s ease-in-out',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                paddingRight: '6px'
                              }}>
                                <span style={{
                                  color: 'white',
                                  fontSize: '9px',
                                  fontWeight: '900',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                }}>
                                  {diagnosis.confidence_percentage || (diagnosis.confidence_score * 100).toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        <div style={{
                          fontSize: '9px',
                          color: '#6c757d',
                          marginLeft: '24px',
                          lineHeight: '1.3',
                          textAlign: 'left',
                          marginBottom: '3px'
                        }}>
                          {diagnosis.rationale}
                        </div>
                        <div style={{
                          marginLeft: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{
                            fontSize: '8px',
                            fontWeight: '700',
                            padding: '1px 5px',
                            borderRadius: '6px',
                            backgroundColor: diagnosis.confidence === 'High' ? '#d4edda' :
                                           diagnosis.confidence === 'Moderate' ? '#fff3cd' : '#f8d7da',
                            color: diagnosis.confidence === 'High' ? '#155724' :
                                   diagnosis.confidence === 'Moderate' ? '#856404' : '#721c24'
                          }}>
                            {diagnosis.confidence}
                          </span>
                        </div>

                        {/* Confirmatory Findings - Timeline style */}
                        {diagnosis.confirmatory_findings && diagnosis.confirmatory_findings.length > 0 && (
                  <div style={{
                            marginTop: '5px',
                            marginLeft: '24px',
                            padding: '6px 8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #dee2e6'
                  }}>
                    <div style={{ 
                              fontSize: '8px',
                              fontWeight: '700',
                              color: '#495057',
                              marginBottom: '5px',
                      textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                              opacity: 0.9,
                              textAlign: 'left'
                    }}>
                              Assessment Pathway:
                    </div>
                            {diagnosis.confirmatory_findings.map((finding, fidx) => (
                              <div key={fidx} style={{
                                display: 'flex',
                                gap: '6px',
                                position: 'relative',
                                marginBottom: fidx < diagnosis.confirmatory_findings.length - 1 ? '8px' : '0'
                              }}>
                                {/* Timeline dot and line */}
                      <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  paddingTop: '2px'
                                }}>
                                  {/* Step number circle */}
                          <div style={{
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    backgroundColor: '#6c757d',
                                    border: '2px solid #495057',
                            display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '7px',
                                    fontWeight: '900',
                                    color: 'white',
                                    flexShrink: 0
                                  }}>
                                    {fidx + 1}
                                  </div>
                                  {/* Connecting line */}
                                  {fidx < diagnosis.confirmatory_findings.length - 1 && (
                                  <div style={{
                                      width: '2px',
                                      flex: 1,
                                      backgroundColor: '#6c757d',
                                      marginTop: '2px',
                                      minHeight: '8px'
                                    }} />
                          )}
                        </div>
                                {/* Step content */}
                      <div style={{
                                  flex: 1,
                                  fontSize: '8px',
                                  color: '#495057',
                                  lineHeight: '1.3',
                                  paddingTop: '1px',
                                  textAlign: 'left'
                                }}>
                                  {finding}
                                </div>
                              </div>
                            ))}
                      </div>
                    )}
                      </div>
                    ))}
                  </div>
                )}

                {/* ABCDE Assessment Completeness */}
                {abcdeFindings && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    color: '#212529',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      fontSize: '12px', 
                      opacity: 0.9, 
                      marginBottom: '8px',
                      fontWeight: '700', 
                      textTransform: 'uppercase',
                      color: '#6c757d',
                      letterSpacing: '0.5px'
                    }}>
                      ABCDE Completeness
                    </div>
                    {Object.entries(abcdeFindings).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e9ecef' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '6px'
                        }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '700',
                            color: '#495057'
                          }}>
                            {key}
                          </span>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '800',
                            padding: '2px 8px',
                            borderRadius: '8px',
                            backgroundColor: value.completeness_percentage >= 80 ? '#d4edda' :
                                   value.completeness_percentage >= 60 ? '#fff3cd' : '#f8d7da',
                            color: value.completeness_percentage >= 80 ? '#155724' :
                                   value.completeness_percentage >= 60 ? '#856404' : '#721c24'
                          }}>
                            {value.completeness_percentage}%
                          </span>
                        </div>
                        {/* Mini Progress Bar */}
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '3px',
                          overflow: 'hidden',
                          marginBottom: '6px'
                        }}>
                          <div style={{
                            width: `${value.completeness_percentage}%`,
                            height: '100%',
                            background: value.completeness_percentage >= 80 
                              ? 'linear-gradient(90deg, #28a745 0%, #20c997 100%)'
                              : value.completeness_percentage >= 60 
                              ? 'linear-gradient(90deg, #ffc107 0%, #ffda6a 100%)'
                              : 'linear-gradient(90deg, #dc3545 0%, #f46a6a 100%)',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        {/* Status Description */}
                        {value.status && (
                          <div style={{
                            fontSize: '10px',
                            color: '#6c757d',
                            marginBottom: '6px',
                            lineHeight: '1.4',
                            fontStyle: 'italic',
                            opacity: 0.85
                          }}>
                            {value.status}
                          </div>
                        )}
                        {/* Outstanding Questions */}
                        {value.outstanding_questions && value.outstanding_questions.length > 0 && (
                          <div style={{
                            fontSize: '10px',
                            color: '#856404',
                            backgroundColor: '#fff3cd',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            marginTop: '6px',
                            border: '1px solid #ffc107'
                          }}>
                            <div style={{ fontWeight: '700', marginBottom: '3px' }}>Outstanding Questions:</div>
                            {value.outstanding_questions.map((q, idx) => (
                              <div key={idx} style={{ marginLeft: '8px', marginBottom: '2px' }}>
                                • {q}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Assessment Stage */}
                {assessmentStage && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#e8f4f8',
                    borderRadius: '8px',
                    border: '2px solid #17a2b8'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#0c5460', 
                      fontWeight: '700', 
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📊 Stage
                    </div>
                    <div style={{ fontSize: '12px', color: '#0c5460', fontWeight: '600' }}>
                      {assessmentStage}
                    </div>
                  </div>
                )}

                {/* Medication Recommendations */}
                {medicationRecommendations && medicationRecommendations.recommended_medications && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#f8d7da',
                    borderRadius: '8px',
                    border: '2px solid #dc3545',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#721c24', 
                      fontWeight: '700', 
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      💊 Medication Recommendations
                    </div>
                    
                    {/* Critical Alerts */}
                    {medicationRecommendations.drug_interaction_alerts?.filter(a => a.severity === 'Critical').length > 0 && (
                      <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#721c24', color: 'white', borderRadius: '4px', fontSize: '9px', fontWeight: '700' }}>
                        ⚠️ CRITICAL INTERACTIONS DETECTED
                      </div>
                    )}
                    
                    {medicationRecommendations.allergy_alerts?.length > 0 && (
                      <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#856404', color: 'white', borderRadius: '4px', fontSize: '9px', fontWeight: '700' }}>
                        ⚠️ ALLERGY ALERTS
                      </div>
                    )}
                    
                    {/* Top 3 Medications */}
                    {medicationRecommendations.recommended_medications.slice(0, 3).map((med, index) => (
                      <div key={index} style={{
                        marginBottom: '10px',
                        padding: '10px',
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        borderRadius: '4px',
                        borderLeft: `4px solid ${med.priority === 'Immediate' ? '#dc3545' : med.priority === 'High' ? '#ffc107' : '#28a745'}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#333' }}>
                            {med.drug_name}
                          </span>
                          <span style={{
                            fontSize: '8px',
                            backgroundColor: med.priority === 'Immediate' ? '#dc3545' : med.priority === 'High' ? '#ffc107' : '#28a745',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontWeight: '600'
                          }}>
                            {med.priority}
                          </span>
                        </div>
                        <div style={{ fontSize: '9px', color: '#666', marginBottom: '4px' }}>
                          {med.generic_name && <div><strong>Generic:</strong> {med.generic_name}</div>}
                          <div><strong>Dose:</strong> {med.dosage?.amount} {med.dosage?.unit} {med.dosage?.route} {med.dosage?.frequency}</div>
                          <div><strong>Indication:</strong> {med.indication}</div>
                        </div>
                        {med.drug_interactions?.length > 0 && (
                          <div style={{ fontSize: '8px', color: '#dc3545', marginTop: '4px' }}>
                            ⚠️ Interactions: {med.drug_interactions.slice(0, 2).join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {medicationRecommendations.recommended_medications.length > 3 && (
                      <div style={{ fontSize: '8px', color: '#6c757d', textAlign: 'center', fontStyle: 'italic' }}>
                        +{medicationRecommendations.recommended_medications.length - 3} more medications...
                      </div>
                    )}
                  </div>
                )}

                {/* Transportation Recommendations */}
                {transportationRecommendations && transportationRecommendations.recommended_transport && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#d1ecf1',
                    borderRadius: '8px',
                    border: '2px solid #0c5460'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#0c5460', 
                      fontWeight: '700', 
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      🚁 Transport Recommendation
                    </div>
                    
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#0c5460' }}>
                          {transportationRecommendations.recommended_transport.method}
                        </span>
                        <span style={{
                          fontSize: '9px',
                          backgroundColor: transportationRecommendations.recommended_transport.urgency?.includes('Immediate') ? '#dc3545' : 
                                          transportationRecommendations.recommended_transport.urgency?.includes('Urgent') ? '#ffc107' : '#28a745',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontWeight: '600'
                        }}>
                          {transportationRecommendations.recommended_transport.urgency}
                        </span>
                      </div>
                      <div style={{ fontSize: '9px', color: '#0c5460' }}>
                        {transportationRecommendations.recommended_transport.rationale}
                      </div>
                    </div>
                    
                    {transportationRecommendations.destination_facility && (
                      <div style={{ marginBottom: '8px', padding: '8px', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '4px' }}>
                        <div style={{ fontSize: '9px', fontWeight: '700', color: '#0c5460', marginBottom: '3px' }}>
                          Destination: {transportationRecommendations.destination_facility.type}
                        </div>
                        <div style={{ fontSize: '8px', color: '#666' }}>
                          {transportationRecommendations.destination_facility.distance_estimate && (
                            <div>Distance: {transportationRecommendations.destination_facility.distance_estimate}</div>
                          )}
                          {transportationRecommendations.recommended_transport.estimated_time && (
                            <div>ETA: {transportationRecommendations.recommended_transport.estimated_time}</div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {transportationRecommendations.patient_preparation && (
                      <div style={{ fontSize: '8px', color: '#0c5460' }}>
                        <strong>Prep Time:</strong> {transportationRecommendations.patient_preparation.estimated_prep_time}
                      </div>
                    )}
                  </div>
                )}

                {/* Emergency Guidelines - Collapsible */}
                {emergencyGuidelines?.protocols && emergencyGuidelines.protocols.length > 0 && (
                  <div style={{
                    backgroundColor: '#d4edda',
                    borderRadius: '8px',
                    border: '2px solid #28a745',
                    overflow: 'hidden'
                  }}>
                    <div 
                      onClick={() => setIsGuidelinesExpanded(!isGuidelinesExpanded)}
                      style={{
                        padding: '15px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#28a745',
                        color: 'white',
                        transition: 'background-color 0.3s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                    >
                      <div style={{
                        fontSize: '11px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        🚑 Emergency Protocols ({emergencyGuidelines.protocols.length})
                      </div>
                      <span style={{
                        fontSize: '16px',
                        transition: 'transform 0.3s ease',
                        transform: isGuidelinesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        display: 'inline-block'
                      }}>
                        ▼
                      </span>
                    </div>
                    {isGuidelinesExpanded && (
                      <div style={{ padding: '12px', backgroundColor: '#d4edda' }}>
                        {emergencyGuidelines.protocols.map((protocol, index) => (
                          <div key={index} style={{
                            marginBottom: '10px',
                            paddingBottom: '10px',
                            borderBottom: index < emergencyGuidelines.protocols.length - 1 ? '1px solid #c3e6cb' : 'none'
                          }}>
                            <div style={{
                              fontSize: '11px',
                              fontWeight: '700',
                              color: '#155724',
                              marginBottom: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#155724',
                                color: 'white',
                                fontSize: '9px',
                                fontWeight: '900'
                              }}>
                                {index + 1}
                              </span>
                              {protocol.protocol_name}
                            </div>
                            <div style={{
                              fontSize: '10px',
                              color: '#1e7e34',
                              lineHeight: '1.4',
                              marginLeft: '22px'
                            }}>
                              {protocol.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Analysis Status */}
                {isAnalyzing && (
                  <div style={{
                    padding: '15px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '8px',
                    color: 'white',
                    textAlign: 'center',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      border: '3px solid rgba(255,255,255,0.3)',
                      borderTop: '3px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 10px'
                    }} />
                    <div style={{ fontSize: '13px', fontWeight: '600' }}>
                      AI Analyzing...
                    </div>
                  </div>
                )}
              </div>}

              {/* BOTTOM SECTION - Charts (Spans all 3 columns) */}
              <div style={{
                backgroundColor: '#ffffff',
                padding: '20px',
                gridColumn: '1 / -1',
                borderTop: '2px solid #e0e7ef'
              }}>
              </div>
            </div>
          </div>
        )}

        {/* OLD DUPLICATE CONTENT BELOW - KEEPING FOR NOW TO AVOID BREAKING */}
        {false && isWebcamActive && (
          <div>
            {/* Heart Rate Graph - Last 10 Seconds */}
            {bpmHistory.length > 0 && (
                    <div style={{
                      marginTop: '20px',
                padding: '20px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                maxWidth: '800px',
                margin: '20px auto'
              }}>
                <h5 style={{ marginBottom: '15px', textAlign: 'center' }}>Heart Rate Over Time (10-Second Intervals)</h5>
                <Line 
                  data={{
                    labels: bpmHistory.map(entry => 
                      entry.time.toLocaleTimeString('en-US', { 
                        hour12: false, 
                        minute: '2-digit', 
                        second: '2-digit'
                      })
                    ),
                    datasets: [{
                      label: 'Heart Rate (BPM)',
                      data: bpmHistory.map(entry => entry.bpm),
                      borderColor: 'rgb(220, 53, 69)',
                      backgroundColor: 'rgba(220, 53, 69, 0.1)',
                      borderWidth: 2,
                      fill: true,
                      tension: 0.4,
                      pointRadius: 2,
                      pointHoverRadius: 5,
                      pointBackgroundColor: 'rgb(220, 53, 69)',
                      pointBorderColor: '#fff',
                      pointBorderWidth: 2
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    aspectRatio: 2.5,
                    animation: {
                      duration: 0 // Disable animation for smooth real-time updates
                    },
                    scales: {
                      y: {
                        beginAtZero: false,
                        min: Math.max(0, Math.min(...bpmHistory.map(e => e.bpm)) - 10),
                        max: Math.max(...bpmHistory.map(e => e.bpm)) + 10,
                        title: {
                          display: true,
                          text: 'BPM',
                          font: {
                            size: 14,
                            weight: 'bold'
                          }
                        },
                        ticks: {
                          stepSize: 5
                        },
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)'
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Time',
                          font: {
                            size: 14,
                            weight: 'bold'
                          }
                        },
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45,
                          autoSkip: true,
                          maxTicksLimit: 10
                        },
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)'
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        display: true,
                        position: 'top',
                        labels: {
                          font: {
                            size: 12
                          },
                          boxWidth: 15,
                          padding: 15
                        }
                      },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: 12,
                        cornerRadius: 6,
                        titleFont: {
                          size: 14
                        },
                        bodyFont: {
                          size: 13
                        },
                        callbacks: {
                          label: function(context) {
                            return `${context.parsed.y} BPM`;
                          }
                        }
                      }
                    }
                  }}
                />
                              </div>
            )}

            {/* Webcam Analysis Results Display */}
            {frameDescription && isWebcamActive && (
              <div id="analysis-results" style={{
                marginTop: '40px',
                padding: '0',
                maxWidth: '1400px',
                margin: '40px auto'
              }}>
                
                {/* ANALYZING STATUS BADGE */}
                {isAnalyzing && (
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '15px 25px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '3px solid rgba(255,255,255,0.3)',
                      borderTop: '3px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>
                      🔄 AI Analysis in Progress...
                    </div>
                  </div>
                )}
                
                {/* QUICK SUMMARY BANNER */}
                {!isAnalyzing && triageScore && (
                  <div style={{
                    background: triageScore <= 2 
                      ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
                      : triageScore === 3
                      ? 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)'
                      : 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
                    color: 'white',
                    padding: '20px 30px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '15px'
                  }}>
                    <div style={{ flex: 1, minWidth: '300px' }}>
                      <div style={{ 
                        fontSize: '18px', 
                        fontWeight: '800', 
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        📋 Executive Summary
                      </div>
                      <div style={{ fontSize: '15px', lineHeight: '1.5', opacity: 0.95 }}>
                        {triageScore === 1 ? '🚨 Critical emergency requiring immediate intervention' :
                         triageScore === 2 ? '⚠️ High priority situation - urgent medical attention needed' :
                         triageScore === 3 ? '📊 Moderate priority - standard medical assessment required' :
                         triageScore === 4 ? '✓ Low priority - routine evaluation recommended' :
                         '✓ Minimal concern - observation advised'}
                        {diagnosisPrediction?.differential_diagnoses?.[0] && 
                          ` • Primary concern: ${diagnosisPrediction.differential_diagnoses[0].condition}`}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        window.print();
                      }}
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '2px solid rgba(255,255,255,0.5)',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(10px)'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>📄</span>
                      Export Report
                    </button>
                  </div>
                )}
                
                {/* CRITICAL ALERT BANNER */}
                {triageScore && triageScore <= 2 && (
                  <div style={{
                    background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                    color: 'white',
                    padding: '20px 30px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    boxShadow: '0 6px 20px rgba(220, 53, 69, 0.4)',
                    border: '3px solid #fff',
                    animation: 'pulse 2s ease-in-out infinite',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px'
                  }}>
                    <div style={{
                      fontSize: '48px',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }}>
                      🚨
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        margin: '0 0 8px 0',
                        fontSize: '24px',
                        fontWeight: '900',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}>
                        {triageScore === 1 ? 'CRITICAL EMERGENCY' : 'HIGH PRIORITY ALERT'}
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: '16px',
                        opacity: 0.95
                      }}>
                        Immediate medical attention required - Priority Level {triageScore}
                      </p>
                    </div>
                    <div style={{
                      fontSize: '64px',
                      fontWeight: '900',
                      opacity: 0.3
                    }}>
                      {triageScore}
                    </div>
                  </div>
                )}

                {/* Header Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, #0066cc 0%, #004c99 100%)',
                  color: 'white',
                  padding: '30px',
                  borderRadius: '16px 16px 0 0',
                  boxShadow: '0 4px 12px rgba(0,102,204,0.3)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h2 style={{
                        margin: '0 0 8px 0',
                        fontSize: '32px',
                        fontWeight: '800',
                        letterSpacing: '0.5px',
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        📹 Live Medical Assessment
                      </h2>
                      <p style={{
                        margin: '0',
                        fontSize: '15px',
                        opacity: 0.9
                      }}>
                        Real-time AI-powered scene analysis • {new Date().toLocaleTimeString()}
                      </p>
                    </div>
                    <div style={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      padding: '12px 20px',
                      borderRadius: '12px',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>STATUS</div>
                      <div style={{ fontSize: '18px', fontWeight: '700' }}>🟢 LIVE</div>
                    </div>
                  </div>
                </div>

                {/* DASHBOARD SUMMARY CARDS */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '15px',
                  padding: '20px',
                  backgroundColor: '#f0f4f8',
                  borderBottom: '1px solid #e0e7ef'
                }}>
                  {/* Priority Card */}
                  <div style={{
                    background: triageScore <= 2 ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' :
                                triageScore === 3 ? 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)' :
                                'linear-gradient(135deg, #28a745 0%, #218838 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, fontWeight: '600', marginBottom: '8px' }}>PRIORITY</div>
                    <div style={{ fontSize: '36px', fontWeight: '900', marginBottom: '4px' }}>{triageScore || '—'}</div>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>
                      {triageScore === 1 ? 'Critical' : triageScore === 2 ? 'High' : triageScore === 3 ? 'Moderate' : triageScore === 4 ? 'Low' : 'Minimal'}
                    </div>
                  </div>

                  {/* Assessment Stage Card */}
                  <div style={{
                    background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, fontWeight: '600', marginBottom: '8px' }}>STAGE</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', marginBottom: '4px' }}>📊</div>
                    <div style={{ fontSize: '13px', fontWeight: '600' }}>
                      {assessmentStage || 'Pending'}
                    </div>
                  </div>

                  {/* Diagnoses Card */}
                  <div style={{
                    background: 'linear-gradient(135deg, #6f42c1 0%, #5a32a3 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, fontWeight: '600', marginBottom: '8px' }}>DIAGNOSES</div>
                    <div style={{ fontSize: '36px', fontWeight: '900', marginBottom: '4px' }}>
                      {diagnosisPrediction?.differential_diagnoses?.length || 0}
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>Identified</div>
                  </div>

                  {/* Actions Card */}
                  <div style={{
                    background: 'linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, fontWeight: '600', marginBottom: '8px' }}>AI ACTIONS</div>
                    <div style={{ fontSize: '36px', fontWeight: '900', marginBottom: '4px' }}>
                      {actionPlan?.ai_driven_actions?.length || 0}
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>Queued</div>
                  </div>
                </div>

                {/* Voice Interface
                <div style={{ padding: '20px', paddingBottom: '0' }}>
                  <VoiceInterface 
                    onCommand={(command, data) => {
                      console.log('[Voice] Command executed:', command, data);
                    }}
                    currentStatus={{
                      heartRate,
                      breathingRate: webcamBreathingRate,
                      triage: triageScore?.priority
                    }}
                  />
                </div> */}

                {/* Main Content Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '0 0 12px 12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  
                  {/* LEFT COLUMN */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    
                    {/* Triage Score - PROMINENT */}
                    {triageScore && (
                      <div style={{
                        padding: '30px',
                        backgroundColor:
                          triageScore === 1 ? '#dc3545' :
                          triageScore === 2 ? '#fd7e14' :
                          triageScore === 3 ? '#ffc107' :
                          triageScore === 4 ? '#28a745' :
                          '#6c757d',
                        color: 'white',
                        borderRadius: '12px',
                        textAlign: 'center',
                        boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
                        border: '3px solid rgba(255,255,255,0.3)'
                      }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          marginBottom: '10px',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          opacity: 0.9
                        }}>
                          🚨 Priority Level
                        </div>
                        <div style={{
                          fontSize: '48px',
                          fontWeight: '900',
                          marginBottom: '8px'
                        }}>
                          {triageScore}
                        </div>
                        <div style={{
                          fontSize: '22px',
                          fontWeight: '700',
                          marginBottom: '15px'
                        }}>
                          {triageScore === 1 ? 'CRITICAL' :
                           triageScore === 2 ? 'HIGH PRIORITY' :
                           triageScore === 3 ? 'MODERATE' :
                           triageScore === 4 ? 'LOW PRIORITY' :
                           'MINIMAL'}
                        </div>
                        {triageReasoning && (
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '400',
                            fontStyle: 'italic',
                            opacity: 0.95,
                            lineHeight: '1.5',
                            borderTop: '1px solid rgba(255,255,255,0.3)',
                            paddingTop: '15px',
                            marginTop: '10px'
                          }}>
                            {triageReasoning}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Scene Description - COLLAPSIBLE */}
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderLeft: '5px solid #007bff',
                      overflow: 'hidden'
                    }}>
                      <div 
                        onClick={() => setIsSceneExpanded(!isSceneExpanded)}
                        style={{
                          padding: '20px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: isSceneExpanded ? 'white' : '#f8f9fa',
                          transition: 'background-color 0.3s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f4f8'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = isSceneExpanded ? 'white' : '#f8f9fa'}
                      >
                        <h5 style={{
                          margin: 0,
                          color: '#007bff',
                          fontSize: '18px',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          🔍 Scene Description
                        </h5>
                        <span style={{
                          fontSize: '20px',
                          color: '#007bff',
                          transition: 'transform 0.3s ease',
                          transform: isSceneExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          display: 'inline-block'
                        }}>
                          ▼
                        </span>
                      </div>
                      {isSceneExpanded && (
                        <div style={{ padding: '0 20px 20px 20px' }}>
                          <p style={{
                            margin: 0,
                            lineHeight: '1.7',
                            color: '#495057',
                            fontSize: '15px'
                          }}>
                            {frameDescription}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Assessment Stage */}
                    {assessmentStage && (
                      <div style={{
                        padding: '20px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderLeft: '5px solid #17a2b8'
                      }}>
                        <h5 style={{
                          margin: '0 0 10px 0',
                          color: '#17a2b8',
                          fontSize: '18px',
                          fontWeight: '700'
                        }}>
                          📊 Assessment Stage
                        </h5>
                        <p style={{
                          fontWeight: '600',
                          marginBottom: 0,
                          fontSize: '16px',
                          color: '#0c5460'
                        }}>
                          {assessmentStage}
                        </p>
                      </div>
                    )}

                    {/* ABCDE Findings - COLLAPSIBLE */}
                    {abcdeFindings && (
                      <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderLeft: '5px solid #ffc107',
                        overflow: 'hidden'
                      }}>
                        <div 
                          onClick={() => setIsAbcdeExpanded(!isAbcdeExpanded)}
                          style={{
                            padding: '20px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: isAbcdeExpanded ? 'white' : '#f8f9fa',
                            transition: 'background-color 0.3s ease'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fff9e6'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = isAbcdeExpanded ? 'white' : '#f8f9fa'}
                        >
                          <h5 style={{
                            margin: 0,
                            color: '#856404',
                            fontSize: '18px',
                            fontWeight: '700'
                          }}>
                            🏥 ABCDE Assessment
                          </h5>
                          <span style={{
                            fontSize: '20px',
                            color: '#856404',
                            transition: 'transform 0.3s ease',
                            transform: isAbcdeExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'inline-block'
                          }}>
                            ▼
                          </span>
                        </div>
                        {isAbcdeExpanded && (
                        <div style={{ padding: '0 20px 20px 20px' }}>
                        {Object.entries(abcdeFindings).map(([key, value]) => (
                          <div key={key} style={{
                            marginBottom: '18px',
                            paddingBottom: '18px',
                            borderBottom: '1px solid #e9ecef'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '10px'
                            }}>
                              <strong style={{
                                textTransform: 'uppercase',
                                fontSize: '15px',
                                color: '#212529',
                                fontWeight: '800',
                                letterSpacing: '0.5px'
                              }}>
                                {key}
                              </strong>
                              <span style={{
                                fontSize: '15px',
                                fontWeight: '800',
                                padding: '4px 10px',
                                borderRadius: '8px',
                                backgroundColor: value.completeness_percentage >= 80 ? '#d4edda' :
                                       value.completeness_percentage >= 60 ? '#fff3cd' : '#f8d7da',
                                color: value.completeness_percentage >= 80 ? '#155724' :
                                       value.completeness_percentage >= 60 ? '#856404' : '#721c24'
                              }}>
                                {value.completeness_percentage}%
                              </span>
                            </div>
                            
                            {/* VISUAL PROGRESS BAR */}
                            <div style={{
                              width: '100%',
                              height: '12px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              marginBottom: '10px',
                              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <div style={{
                                width: `${value.completeness_percentage}%`,
                                height: '100%',
                                background: value.completeness_percentage >= 80 
                                  ? 'linear-gradient(90deg, #28a745 0%, #20c997 100%)'
                                  : value.completeness_percentage >= 60 
                                  ? 'linear-gradient(90deg, #ffc107 0%, #ffda6a 100%)'
                                  : 'linear-gradient(90deg, #dc3545 0%, #f46a6a 100%)',
                                transition: 'width 0.5s ease-in-out',
                                borderRadius: '10px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }} />
                            </div>
                            
                            <div style={{
                              fontSize: '14px',
                              color: '#495057',
                              marginBottom: '8px',
                              fontWeight: '500'
                            }}>
                              {value.status}
                            </div>
                            {value.outstanding_questions && value.outstanding_questions.length > 0 && (
                              <div style={{
                                fontSize: '13px',
                                color: '#856404',
                                fontStyle: 'italic',
                                backgroundColor: '#fff3cd',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                marginTop: '6px'
                              }}>
                                ⚠️ <strong>Outstanding:</strong> {value.outstanding_questions.join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                        </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* RIGHT COLUMN */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                    {/* Diagnosis - Exactly like ABCDE Quick View */}
                    {diagnosisPrediction && diagnosisPrediction.differential_diagnoses && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        color: '#212529',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          marginBottom: '10px'
                        }}>
                          <div style={{ 
                            fontSize: '10px', 
                            opacity: 0.9, 
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            color: '#6c757d'
                          }}>
                            🩺 Differential Diagnoses
                        </div>
                          {diagnosisPrediction.differential_diagnoses[0] && (
                              <div style={{
                              fontSize: '8px',
                              fontWeight: '600',
                              color: diagnosisPrediction.differential_diagnoses[0].confidence === 'High' ? '#28a745' : 
                                     diagnosisPrediction.differential_diagnoses[0].confidence === 'Moderate' ? '#ffc107' : '#6c757d',
                              backgroundColor: diagnosisPrediction.differential_diagnoses[0].confidence === 'High' ? 'rgba(40, 167, 69, 0.1)' : 
                                               diagnosisPrediction.differential_diagnoses[0].confidence === 'Moderate' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(108, 117, 125, 0.1)',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}>
                              {diagnosisPrediction.differential_diagnoses[0].confidence}
                              </div>
                            )}
                        </div>
                        {diagnosisPrediction.differential_diagnoses.map((diagnosis, index) => (
                          <div key={index} style={{ marginBottom: '8px' }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '3px',
                              fontSize: '11px'
                            }}>
                              <span style={{ 
                                color: '#212529',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                minWidth: index === 0 ? '30px' : '20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px'
                              }}>
                                {index === 0 && (
                                <span style={{
                                    fontSize: '7px',
                                    padding: '1px 3px',
                                    borderRadius: '2px',
                                    backgroundColor: '#dc3545',
                                    color: '#ffffff',
                                    fontWeight: '600'
                                  }}>
                                    1°
                                </span>
                                )}
                                {index + 1}.
                              </span>
                              <span style={{
                                flex: 1,
                                marginLeft: '8px',
                                fontSize: '9px',
                                color: '#495057',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                <strong style={{ color: '#212529' }}>{diagnosis.condition}:</strong> {diagnosis.rationale}
                              </span>
                              <span style={{ 
                                fontSize: '7px',
                                marginLeft: '6px',
                                fontWeight: '600',
                                color: '#6c757d',
                                minWidth: '28px',
                                textAlign: 'right'
                              }}>
                                {diagnosis.confidence === 'High' ? '85%' : diagnosis.confidence === 'Moderate' ? '65%' : '40%'}
                              </span>
                            </div>
                            {/* Mini Progress Bar */}
                            <div style={{
                              width: '100%',
                              height: '3px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '2px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: diagnosis.confidence === 'High' ? '85%' : diagnosis.confidence === 'Moderate' ? '65%' : '40%',
                                height: '100%',
                                backgroundColor: diagnosis.confidence === 'High' ? '#28a745' : diagnosis.confidence === 'Moderate' ? '#ffc107' : '#6c757d',
                                transition: 'width 0.3s ease',
                                borderRadius: '2px'
                              }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* AI-Driven Actions - CHECKLIST STYLE - COLLAPSIBLE */}
                    {actionPlan && actionPlan.ai_driven_actions && (
                      <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: '2px solid #6f42c1',
                        overflow: 'hidden'
                      }}>
                        <div 
                          onClick={() => setIsActionsExpanded(!isActionsExpanded)}
                          style={{
                            padding: '25px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: isActionsExpanded ? 'white' : '#f8f9fa',
                            transition: 'background-color 0.3s ease'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3e8ff'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = isActionsExpanded ? 'white' : '#f8f9fa'}
                        >
                          <h5 style={{
                            margin: 0,
                            color: '#5a32a3',
                            fontSize: '20px',
                            fontWeight: '800',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                          }}>
                            <span style={{ fontSize: '24px' }}>🤖</span>
                            AI-Driven Actions - {actionPlan.ai_driven_actions.length} Queued
                          </h5>
                          <span style={{
                            fontSize: '20px',
                            color: '#5a32a3',
                            transition: 'transform 0.3s ease',
                            transform: isActionsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'inline-block'
                          }}>
                            ▼
                          </span>
                        </div>
                        {isActionsExpanded && (
                        <div style={{ padding: '0 25px 25px 25px' }}>
                        {actionPlan.ai_driven_actions.map((rec, index) => (
                          <div key={index} style={{
                            marginBottom: '18px',
                            padding: '18px',
                            background: rec.urgency === 'Critical' ? 'linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)' :
                                       rec.urgency === 'High' ? 'linear-gradient(135deg, #fff9f0 0%, #ffe9d5 100%)' :
                                       rec.urgency === 'Moderate' ? 'linear-gradient(135deg, #fffef0 0%, #fffacd 100%)' :
                                       'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                            borderRadius: '12px',
                            border: `3px solid ${
                              rec.urgency === 'Critical' ? '#dc3545' :
                              rec.urgency === 'High' ? '#fd7e14' :
                              rec.urgency === 'Moderate' ? '#ffc107' : '#28a745'
                            }`,
                            boxShadow: '0 3px 8px rgba(0,0,0,0.1)',
                            display: 'flex',
                            gap: '15px',
                            alignItems: 'start'
                          }}>
                            {/* BIG STEP NUMBER */}
                            <div style={{
                              minWidth: '50px',
                              height: '50px',
                              borderRadius: '12px',
                              background: rec.urgency === 'Critical' ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' :
                                         rec.urgency === 'High' ? 'linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)' :
                                         rec.urgency === 'Moderate' ? 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)' :
                                         'linear-gradient(135deg, #28a745 0%, #218838 100%)',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '24px',
                              fontWeight: '900',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                              flexShrink: 0
                            }}>
                              {index + 1}
                            </div>
                            
                            <div style={{ flex: 1 }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '10px',
                                flexWrap: 'wrap',
                                gap: '8px'
                              }}>
                                <span style={{
                                  fontSize: '13px',
                                  fontWeight: '800',
                                  padding: '6px 12px',
                                  borderRadius: '20px',
                                  backgroundColor:
                                    rec.urgency === 'Critical' ? '#dc3545' :
                                    rec.urgency === 'High' ? '#fd7e14' :
                                    rec.urgency === 'Moderate' ? '#ffc107' : '#28a745',
                                  color: 'white',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  {rec.urgency}
                                </span>
                                <span style={{
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  backgroundColor: '#e7f3ff',
                                  color: '#0066cc',
                                  border: '1px solid #cce5ff'
                                }}>
                                  {rec.action_type}
                                </span>
                                <span style={{
                                  fontSize: '13px',
                                  color: '#6c757d',
                                  fontWeight: '600',
                                  backgroundColor: 'rgba(255,255,255,0.8)',
                                  padding: '6px 12px',
                                  borderRadius: '20px'
                                }}>
                                  ⏱️ {rec.estimated_duration}
                                </span>
                              </div>
                              <div style={{
                                fontWeight: '700',
                                fontSize: '16px',
                                color: '#212529',
                                marginBottom: '8px',
                                lineHeight: '1.4'
                              }}>
                                {rec.action}
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#495057',
                                lineHeight: '1.6',
                                marginBottom: '10px',
                                paddingLeft: '12px',
                                borderLeft: '3px solid rgba(0,0,0,0.1)'
                              }}>
                                {rec.description}
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: '#6c757d',
                                backgroundColor: '#f8f9fa',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                marginTop: '8px'
                              }}>
                                <div style={{ marginBottom: '4px' }}>
                                  <strong>Requires Approval:</strong> {rec.approval_level}
                                </div>
                                {rec.resources_needed && rec.resources_needed.length > 0 && (
                                  <div>
                                    <strong>Resources:</strong> {rec.resources_needed.join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        </div>
                        )}
                      </div>
                    )}

                    {/* Emergency Guidelines - COLLAPSIBLE */}
                    {emergencyGuidelines && emergencyGuidelines.protocols && (
                      <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderLeft: '5px solid #155724',
                        overflow: 'hidden'
                      }}>
                        <div 
                          onClick={() => setIsGuidelinesExpanded(!isGuidelinesExpanded)}
                          style={{
                            padding: '20px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: isGuidelinesExpanded ? 'white' : '#f8f9fa',
                            transition: 'background-color 0.3s ease'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#d4edda'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = isGuidelinesExpanded ? 'white' : '#f8f9fa'}
                        >
                          <h5 style={{
                            margin: 0,
                            color: '#155724',
                            fontSize: '18px',
                            fontWeight: '700'
                          }}>
                            🚨 Emergency Protocols
                          </h5>
                          <span style={{
                            fontSize: '20px',
                            color: '#155724',
                            transition: 'transform 0.3s ease',
                            transform: isGuidelinesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'inline-block'
                          }}>
                            ▼
                          </span>
                        </div>
                        {isGuidelinesExpanded && (
                        <div style={{ padding: '0 20px 20px 20px' }}>
                        {emergencyGuidelines.protocols.map((protocol, index) => (
                          <div key={index} style={{
                            marginBottom: '12px',
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px'
                          }}>
                            <div style={{
                              fontWeight: '700',
                              fontSize: '15px',
                              color: '#155724',
                              marginBottom: '8px'
                            }}>
                              {protocol.protocol_name}
                            </div>
                            <div style={{
                              fontSize: '14px',
                              color: '#6c757d',
                              lineHeight: '1.5'
                            }}>
                              {protocol.description}
                            </div>
                          </div>
                        ))}
                        </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>
        )}


        <canvas
          ref={canvasRef}
          style={{ display: 'none' }}
        />

        {/* Main video/webcam analysis results display uses shared state */}
        {/* Triage, ABCDE, Diagnosis, Action Plan, etc. appear in history section */}

        {/* Video Controls - Below main layout */}
        <div className="controls" style={{ 
          marginBottom: '20px', 
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center'
        }}>
          <button 
            className="btn btn-primary" 
            onClick={analyzeFrame}
            disabled={!videoFile || isAnalyzing}
          >
            {isAnalyzing ? 'Analyzing...' : 'Analyze Current Frame'}
          </button>

          <button 
            className="btn btn-secondary" 
            onClick={() => setIsAutoAnalysisEnabled(!isAutoAnalysisEnabled)}
            disabled={!videoFile}
            style={{ 
              backgroundColor: isAutoAnalysisEnabled ? '#dc3545' : '#6c757d', 
              color: 'white', 
              border: 'none' 
            }}
          >
            {isAutoAnalysisEnabled ? 'Stop Auto Analysis' : 'Start Auto Analysis'}
          </button>

          {isAutoAnalysisEnabled && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>Frame Interval:</label>
              <select 
                value={autoAnalysisInterval} 
                onChange={(e) => setAutoAnalysisInterval(parseInt(e.target.value))}
                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value={0}>Continuous (0 seconds)</option>
                <option value={2000}>2 seconds</option>
                <option value={3000}>3 seconds</option>
                <option value={5000}>5 seconds</option>
                <option value={10000}>10 seconds</option>
                <option value={15000}>15 seconds</option>
                <option value={30000}>30 seconds</option>
              </select>
            </div>
          )}

          {isAutoAnalysisEnabled && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>Meta-Analysis:</label>
              <select 
                value={metaAnalysisInterval} 
                onChange={(e) => setMetaAnalysisInterval(parseInt(e.target.value))}
                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value={5000}>5 seconds</option>
                <option value={10000}>10 seconds</option>
                <option value={15000}>15 seconds</option>
                <option value={30000}>30 seconds</option>
                <option value={60000}>1 minute</option>
                <option value={120000}>2 minutes</option>
              </select>
              {isMetaAnalyzing && (
                <span style={{ fontSize: '11px', color: '#007bff', fontWeight: '500' }}>
                  🔄 Analyzing {analysisHistory.length} frames...
                </span>
              )}
            </div>
          )}
          
          <button 
            className="btn btn-secondary" 
            onClick={async () => {
              try {
                await axios.post('/api/init-heart-rate');
                setSuccess('Heart rate service initialized successfully');
              } catch (error) {
                setError('Failed to initialize heart rate service');
              }
            }}
            style={{ backgroundColor: '#28a745', color: 'white', border: 'none' }}
          >
            Initialize Heart Rate Detection
          </button>

          <button 
            className="btn btn-secondary" 
            onClick={() => {
              if (videoFile) {
                const frameData = extractFrame();
                if (frameData) {
                  detectHeartRate(frameData);
                }
              }
            }}
            disabled={!videoFile}
            style={{ backgroundColor: '#007bff', color: 'white', border: 'none' }}
          >
            Test Heart Rate Now
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={() => seekToTime(0)}
            disabled={!videoFile}
          >
            Go to Start
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={() => seekToTime(videoRef.current?.duration / 2 || 0)}
            disabled={!videoFile}
          >
            Go to Middle
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={() => seekToTime(videoRef.current?.duration || 0)}
            disabled={!videoFile}
          >
            Go to End
          </button>
        </div>

        {/* API Request Status */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '15px', 
          marginTop: '20px',
          marginBottom: '20px', 
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          maxWidth: '1400px',
          margin: '20px auto'
        }}>
          <h6 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
            ⏱️ API Request Status
          </h6>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: lastAnalyzeFrameRequest ? '#10b981' : '#6b7280' 
              }}></div>
              <span style={{ fontSize: '13px', color: '#374151' }}>
                Frame Analysis: {formatElapsedTime(lastAnalyzeFrameRequest)}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: lastAssessmentStageRequest ? '#10b981' : '#6b7280' 
              }}></div>
              <span style={{ fontSize: '13px', color: '#374151' }}>
                Assessment: {formatElapsedTime(lastAssessmentStageRequest)}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: lastTriageScoreRequest ? '#10b981' : '#6b7280' 
              }}></div>
              <span style={{ fontSize: '13px', color: '#374151' }}>
                Triage Score: {formatElapsedTime(lastTriageScoreRequest)}
              </span>
            </div>
          </div>
        </div>

        {/* VIDEO SOURCE & BELIEF-GUIDED VISION CONTROLS */}
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto 20px auto',
          display: 'flex',
          gap: '15px',
          flexWrap: 'wrap'
        }}>
          
          {/* VIDEO SOURCE TOGGLE */}
          {isWebcamActive && (
            <div style={{
              flex: '1 1 300px',
              padding: '12px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <div style={{ 
                marginBottom: '10px', 
                fontSize: '10px', 
                fontWeight: '700',
                textTransform: 'uppercase',
                color: '#6c757d'
              }}>
                Video Source
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button
                  onClick={async () => {
                    if (videoSource !== 'local') {
                      setVideoSource('local');
                      await stopWebcamAnalysis();
                      setTimeout(() => startWebcamAnalysis(), 500);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 14px',
                    backgroundColor: videoSource === 'local' ? '#28a745' : '#e9ecef',
                    color: videoSource === 'local' ? 'white' : '#495057',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  Local Webcam
                </button>
                <button
                  onClick={async () => {
                    if (videoSource !== 'ngrok') {
                      setVideoSource('ngrok');
                      await stopWebcamAnalysis();
                      setTimeout(() => startWebcamAnalysis(), 500);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 14px',
                    backgroundColor: videoSource === 'ngrok' ? '#28a745' : '#e9ecef',
                    color: videoSource === 'ngrok' ? 'white' : '#495057',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  Remote Stream
                </button>
              </div>
              {videoSource === 'ngrok' && (
                <input
                  type="text"
                  value={ngrokUrl}
                  onChange={(e) => setNgrokUrl(e.target.value)}
                  placeholder="https://your-tunnel.ngrok.io/video_feed"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '11px'
                  }}
                />
              )}
              <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '8px' }}>
                {videoSource === 'local' ? 'Using local camera' : 'Using remote stream'}
              </div>
            </div>
          )}
          
          {/* BELIEF-GUIDED VISION TOGGLE */}
          <div style={{
            flex: '1 1 300px',
            padding: '12px',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ 
                fontSize: '10px', 
                fontWeight: '700',
                textTransform: 'uppercase',
                color: '#6c757d'
              }}>
                Belief-Guided Vision
              </div>
              <button 
                className={`btn ${beliefGuidedVision.enabled ? 'btn-success' : 'btn-secondary'}`}
                onClick={() => {
                  setBeliefGuidedVision(prev => ({
                    ...prev,
                    enabled: !prev.enabled
                  }));
                  console.log('[BGV] Toggled:', !beliefGuidedVision.enabled);
                }}
                style={{ padding: '6px 14px', fontSize: '11px', minWidth: '60px' }}
              >
                {beliefGuidedVision.enabled ? 'ON' : 'OFF'}
              </button>
            </div>
            <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
              {beliefGuidedVision.enabled ? (
                beliefGuidedVision.initialized ? (
                  <>
                    Frame {beliefGuidedVision.frameCount} · 
                    Structure: {beliefGuidedVision.precision?.structure ? (beliefGuidedVision.precision.structure * 100).toFixed(0) : '50'}% · 
                    Dynamics: {beliefGuidedVision.precision?.dynamics ? (beliefGuidedVision.precision.dynamics * 100).toFixed(0) : '50'}%
                  </>
                ) : (
                  'Enabled · Awaiting first frame...'
                )
              ) : (
                'MedGemma brain-like perception: Only reports changes, not full re-descriptions'
              )}
            </div>
          </div>
        </div>

        {/* SECRET VITAL SIGN OVERRIDE SECTION */}
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto 40px auto',
          padding: '12px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}>
          <div 
            onClick={() => setShowOverrideSection(!showOverrideSection)}
            style={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: showOverrideSection ? '10px' : '0',
              fontSize: '10px',
              fontWeight: '700',
              textTransform: 'uppercase',
              color: '#6c757d',
              transition: 'all 0.2s ease'
            }}
          >
            <span>Manual Vital Sign Override (Developer Mode)</span>
            <span style={{ 
              fontSize: '9px',
              opacity: 0.6,
              transition: 'transform 0.2s ease',
              transform: showOverrideSection ? 'rotate(0deg)' : 'rotate(-90deg)'
            }}>▼</span>
          </div>

          {showOverrideSection && (
            <div>
              <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '15px' }}>
                Override values will persist for the entire session. Leave blank to use real-time values.
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '15px' 
              }}>
                {/* Heart Rate Override */}
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#6c757d', display: 'block', marginBottom: '5px' }}>
                    Heart Rate (bpm)
                  </label>
                  <input
                    type="number"
                    value={vitalOverrides.heartRate || ''}
                    onChange={(e) => {
                      const newValue = e.target.value ? parseFloat(e.target.value) : null;
                      setVitalOverrides(prev => ({
                        ...prev,
                        heartRate: newValue
                      }));
                      if (newValue !== null && newValue !== vitalOverrides.heartRate) {
                        logManualVitalEntry('Heart Rate', newValue, ' bpm');
                      }
                    }}
                    placeholder="Auto"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: vitalOverrides.heartRate ? '#e8f5e9' : '#ffffff'
                    }}
                  />
                </div>

                {/* Breathing Rate Override */}
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#6c757d', display: 'block', marginBottom: '5px' }}>
                    Breathing Rate (br/min)
                  </label>
                  <input
                    type="number"
                    value={vitalOverrides.breathingRate || ''}
                    onChange={(e) => {
                      const newValue = e.target.value ? parseFloat(e.target.value) : null;
                      setVitalOverrides(prev => ({
                        ...prev,
                        breathingRate: newValue
                      }));
                      if (newValue !== null && newValue !== vitalOverrides.breathingRate) {
                        logManualVitalEntry('Breathing Rate', newValue, ' br/min');
                      }
                    }}
                    placeholder="Auto"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: vitalOverrides.breathingRate ? '#e8f5e9' : '#ffffff'
                    }}
                  />
                </div>

                {/* Blood Pressure Systolic Override */}
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#6c757d', display: 'block', marginBottom: '5px' }}>
                    BP Systolic (mmHg)
                  </label>
                  <input
                    type="number"
                    value={vitalOverrides.bloodPressureSystolic || ''}
                    onChange={(e) => {
                      const newValue = e.target.value ? parseFloat(e.target.value) : null;
                      setVitalOverrides(prev => ({
                        ...prev,
                        bloodPressureSystolic: newValue
                      }));
                      if (newValue !== null && newValue !== vitalOverrides.bloodPressureSystolic) {
                        logManualVitalEntry('BP Systolic', newValue, ' mmHg');
                      }
                    }}
                    placeholder="Auto"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: vitalOverrides.bloodPressureSystolic ? '#e8f5e9' : '#ffffff'
                    }}
                  />
                </div>

                {/* Blood Pressure Diastolic Override */}
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#6c757d', display: 'block', marginBottom: '5px' }}>
                    BP Diastolic (mmHg)
                  </label>
                  <input
                    type="number"
                    value={vitalOverrides.bloodPressureDiastolic || ''}
                    onChange={(e) => {
                      const newValue = e.target.value ? parseFloat(e.target.value) : null;
                      setVitalOverrides(prev => ({
                        ...prev,
                        bloodPressureDiastolic: newValue
                      }));
                      if (newValue !== null && newValue !== vitalOverrides.bloodPressureDiastolic) {
                        logManualVitalEntry('BP Diastolic', newValue, ' mmHg');
                      }
                    }}
                    placeholder="Auto"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: vitalOverrides.bloodPressureDiastolic ? '#e8f5e9' : '#ffffff'
                    }}
                  />
                </div>

                {/* SpO2 Override */}
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#6c757d', display: 'block', marginBottom: '5px' }}>
                    SpO₂ (%)
                  </label>
                  <input
                    type="number"
                    value={vitalOverrides.spo2 || ''}
                    onChange={(e) => {
                      const newValue = e.target.value ? parseFloat(e.target.value) : null;
                      setVitalOverrides(prev => ({
                        ...prev,
                        spo2: newValue
                      }));
                      if (newValue !== null && newValue !== vitalOverrides.spo2) {
                        logManualVitalEntry('SpO₂', newValue, '%');
                      }
                    }}
                    placeholder="Auto"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: vitalOverrides.spo2 ? '#e8f5e9' : '#ffffff'
                    }}
                  />
                </div>

                {/* Temperature Override */}
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#6c757d', display: 'block', marginBottom: '5px' }}>
                    Temperature (°C)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={vitalOverrides.temperature || ''}
                    onChange={(e) => {
                      const newValue = e.target.value ? parseFloat(e.target.value) : null;
                      setVitalOverrides(prev => ({
                        ...prev,
                        temperature: newValue
                      }));
                      if (newValue !== null && newValue !== vitalOverrides.temperature) {
                        logManualVitalEntry('Temperature', newValue, '°C');
                      }
                    }}
                    placeholder="Auto"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: vitalOverrides.temperature ? '#e8f5e9' : '#ffffff'
                    }}
                  />
                </div>

                {/* BMI Override */}
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#6c757d', display: 'block', marginBottom: '5px' }}>
                    BMI
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={vitalOverrides.bmi || ''}
                    onChange={(e) => {
                      const newValue = e.target.value ? parseFloat(e.target.value) : null;
                      setVitalOverrides(prev => ({
                        ...prev,
                        bmi: newValue
                      }));
                      if (newValue !== null && newValue !== vitalOverrides.bmi) {
                        logManualVitalEntry('BMI', newValue, '');
                      }
                    }}
                    placeholder="Auto"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: vitalOverrides.bmi ? '#e8f5e9' : '#ffffff'
                    }}
                  />
                </div>
              </div>

              {/* Clear All Button */}
              <div style={{ marginTop: '15px', textAlign: 'right' }}>
                <button
                  onClick={() => setVitalOverrides({
                    heartRate: null,
                    breathingRate: null,
                    bloodPressureSystolic: null,
                    bloodPressureDiastolic: null,
                    spo2: null,
                    temperature: null,
                    bmi: null
                  })}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Clear All Overrides
                </button>
              </div>
            </div>
          )}
        </div>

                    </div>

      {error && <div className="error">{error}</div>}

      {success && <div className="success">{success}</div>}

      {/* Medicare Card Verification Modal */}
      {showMedicareCardModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '30px',
            maxWidth: '500px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '3px solid #dc2626'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📋</div>
            <h2 style={{ 
              color: '#dc2626', 
              marginBottom: '20px',
              fontFamily: 'Arial, sans-serif'
            }}>
              Patient Identification Required
            </h2>
            <p style={{ 
              fontSize: '18px', 
              marginBottom: '25px',
              color: '#374151',
              lineHeight: '1.5'
            }}>
              {!medicareCardScanned 
                ? "Please position the patient's Medicare card in front of the camera for scanning."
                : isAnalyzingMedicareCard
                ? "Analyzing Medicare card details..."
                : "Medicare card successfully captured and analyzed."
              }
            </p>
            
            {/* Medicare Card Display - only show after scanning */}
            {medicareCardScanned && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '2px solid #dc2626',
                borderRadius: '10px',
                padding: '20px',
                marginBottom: '25px',
                textAlign: 'center'
              }}>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#666', 
                  marginBottom: '15px',
                  fontWeight: 'bold'
                }}>
                  📋 Captured Medicare Card
                </p>
                <img
                  src="/mock_medicare_card.jpg"
                  alt="Medicare Card"
                  style={{
                    width: '100%',
                    maxWidth: '350px',
                    height: 'auto',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  onError={(e) => {
                    // Fallback if image doesn't load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{
                  display: 'none',
                  color: '#666',
                  fontStyle: 'italic',
                  padding: '20px'
                }}>
                  Medicare card image not available
                </div>
              </div>
            )}
            
            {/* Medicare Card Analysis Results */}
            {medicareCardAnalysis && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '2px solid #17a2b8',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <h4 style={{ 
                  color: '#17a2b8', 
                  margin: '0 0 10px 0',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>
                  📋 Card Analysis
                </h4>
                <div style={{
                  fontSize: '14px',
                  color: '#495057',
                  lineHeight: '1.4',
                  whiteSpace: 'pre-wrap'
                }}>
                  {medicareCardAnalysis}
                </div>
              </div>
            )}
            
            {/* Loading indicator for analysis */}
            {isAnalyzingMedicareCard && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '2px solid #ffc107',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid #ffc107',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <span style={{ color: '#856404', fontWeight: 'bold' }}>
                    Analyzing Medicare card...
                  </span>
                </div>
              </div>
            )}
            
            <div style={{
              backgroundColor: medicareCardScanned ? '#d1f2eb' : '#fef3c7',
              border: `2px solid ${medicareCardScanned ? '#27ae60' : '#f59e0b'}`,
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '25px'
            }}>
              <p style={{ 
                color: medicareCardScanned ? '#186741' : '#92400e', 
                margin: 0,
                fontWeight: 'bold'
              }}>
                {medicareCardScanned 
                  ? "✅ Medicare card verified successfully"
                  : "🔒 Ready to scan Medicare card..."
                }
              </p>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              marginBottom: '15px'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#dc2626',
                animation: 'pulse 1.5s ease-in-out infinite'
              }}></div>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#dc2626',
                animation: 'pulse 1.5s ease-in-out infinite 0.3s'
              }}></div>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#dc2626',
                animation: 'pulse 1.5s ease-in-out infinite 0.6s'
              }}></div>
            </div>
            <button
              onClick={async () => {
                if (!medicareCardScanned && !isAnalyzingMedicareCard) {
                  // First click: "Scan Card" - show the Medicare card and analyze it
                  setMedicareCardScanned(true);
                  
                  // Analyze the Medicare card
                  try {
                    await analyzeMedicareCard();
                  } catch (error) {
                    console.error('Failed to analyze Medicare card:', error);
                  }
                  
                  
                } else if (medicareCardScanned && !isAnalyzingMedicareCard) {
                  // Second click: "Continue" - close Medicare modal and open patient details modal
                  setShowMedicareCardModal(false);
                  setMedicareCardScanned(false); // Reset for next time
                  // Keep medicareCardAnalysis for confirmation modal - don't clear it here
                  
                  // Open patient details modal
                  setShowPatientDetailsModal(true);
                  
                  if (window.closeMedicareModal) {
                    window.closeMedicareModal();
                    window.closeMedicareModal = null; // Clean up
                  }
                }
              }}
              disabled={isAnalyzingMedicareCard}
              style={{
                backgroundColor: isAnalyzingMedicareCard ? '#ccc' : (medicareCardScanned ? '#27ae60' : '#dc2626'),
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isAnalyzingMedicareCard ? 'not-allowed' : 'pointer',
                marginTop: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {isAnalyzingMedicareCard && (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              )}
              {isAnalyzingMedicareCard ? 'Analyzing...' : (medicareCardScanned ? 'Continue' : 'Scan Card')}
            </button>
          </div>
        </div>
      )}

      {/* Patient Details Voice Modal */}
      {showPatientDetailsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '3px solid #17a2b8'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎤</div>
            <h2 style={{ 
              color: '#17a2b8', 
              marginBottom: '20px',
              fontFamily: 'Arial, sans-serif'
            }}>
              Patient Details Verification
            </h2>
            <p style={{ 
              fontSize: '18px', 
              marginBottom: '25px',
              color: '#374151',
              lineHeight: '1.5'
            }}>
              Please speak clearly and state your:
              <br />
              <strong>Full name, address, and date of birth</strong>
            </p>
            
            {/* Voice status indicator */}
            <div style={{
              backgroundColor: isListeningForDetails ? '#d1f2eb' : '#fef3c7',
              border: `2px solid ${isListeningForDetails ? '#27ae60' : '#f59e0b'}`,
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '25px'
            }}>
              <p style={{ 
                color: isListeningForDetails ? '#186741' : '#92400e', 
                margin: 0,
                fontWeight: 'bold'
              }}>
                {isListeningForDetails 
                  ? "🎤 Listening... Please speak now"
                  : "🔇 Ready to listen"
                }
              </p>
            </div>

            {/* Transcript display */}
            {patientDetailsTranscript && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '2px solid #17a2b8',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px',
                textAlign: 'left',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                <h4 style={{ 
                  color: '#17a2b8', 
                  margin: '0 0 10px 0',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>
                  📝 Transcribed Details
                </h4>
                <div style={{
                  fontSize: '14px',
                  color: '#495057',
                  lineHeight: '1.4',
                  whiteSpace: 'pre-wrap'
                }}>
                  {patientDetailsTranscript}
                </div>
              </div>
            )}


            {/* Control buttons */}
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={isListeningForDetails ? stopPatientDetailsListening : startPatientDetailsListening}
                style={{
                  backgroundColor: isListeningForDetails ? '#dc3545' : '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {isListeningForDetails ? '⏹️ Stop' : '🎤 Start Listening'}
              </button>

              <button
                onClick={clearPatientDetailsTranscript}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                🗑️ Clear
              </button>

              <button
                onClick={() => {
                  // Stop listening if active
                  if (isListeningForDetails) {
                    stopPatientDetailsListening();
                  }
                  // Close patient details modal and open confirmation modal
                  setShowPatientDetailsModal(false);
                  setShowConfirmationModal(true);
                }}
                disabled={!patientDetailsTranscript.trim()}
                style={{
                  backgroundColor: patientDetailsTranscript.trim() ? '#17a2b8' : '#ccc',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: patientDetailsTranscript.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                ✅ Review Information
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Information Confirmation Modal */}
      {showConfirmationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '15px',
            padding: '40px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            border: '3px solid #28a745'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</div>
            <h2 style={{ 
              color: '#28a745', 
              marginBottom: '20px',
              fontFamily: 'Arial, sans-serif',
              fontSize: '24px'
            }}>
              Confirm Patient Information
            </h2>
            <p style={{ 
              fontSize: '16px', 
              marginBottom: '30px',
              color: '#374151',
              lineHeight: '1.5'
            }}>
              Please review the information below and confirm it is correct before accessing your My Health Record.
            </p>

            {/* Medicare Card Information */}
            <div style={{
              backgroundColor: '#f8f9fa',
              border: '2px solid #dc2626',
              borderRadius: '10px',
              padding: '20px',
              marginBottom: '25px',
              textAlign: 'left'
            }}>
              <h3 style={{ 
                color: '#dc2626', 
                margin: '0 0 15px 0',
                fontSize: '18px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                🆔 Medicare Card Details
              </h3>
              <div style={{
                fontSize: '14px',
                color: '#495057',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                backgroundColor: 'white',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                {medicareCardAnalysis || 'Medicare card information is being processed or was not successfully analyzed. Please ensure the card was scanned properly.'}
              </div>
              {!medicareCardAnalysis && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '6px',
                  border: '1px solid #ffc107'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '12px',
                    color: '#856404',
                    fontWeight: 'bold'
                  }}>
                    ⚠️ Note: Medicare card analysis may not have completed successfully. Please verify the information manually.
                  </p>
                </div>
              )}
            </div>

            {/* Spoken Patient Details */}
            {patientDetailsTranscript && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '2px solid #17a2b8',
                borderRadius: '10px',
                padding: '20px',
                marginBottom: '25px',
                textAlign: 'left'
              }}>
                <h3 style={{ 
                  color: '#17a2b8', 
                  margin: '0 0 15px 0',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  🎤 Spoken Details
                </h3>
                <div style={{
                  fontSize: '14px',
                  color: '#495057',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  backgroundColor: 'white',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  {patientDetailsTranscript}
                </div>
              </div>
            )}

            {/* Authorization Notice */}
            <div style={{
              backgroundColor: '#fff3cd',
              border: '2px solid #ffc107',
              borderRadius: '10px',
              padding: '20px',
              marginBottom: '30px'
            }}>
              <h3 style={{ 
                color: '#856404', 
                margin: '0 0 15px 0',
                fontSize: '18px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                🔒 My Health Record Authorization
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#856404',
                margin: 0,
                lineHeight: '1.5'
              }}>
                By confirming below, you authorize the Royal Flying Doctor Service to access your My Health Record for emergency medical care purposes. This access will include your medical history, current medications, allergies, and other relevant health information to provide you with the best possible care.
              </p>
            </div>

            {/* Action buttons */}
            <div style={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => {
                  // Go back to patient details modal for corrections
                  setShowConfirmationModal(false);
                  setShowPatientDetailsModal(true);
                }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                ⬅️ Back to Edit
              </button>

              <button
                onClick={() => {
                  // Close all modals and reset states
                  setShowConfirmationModal(false);
                  setPatientDetailsTranscript('');
                  setMedicareCardAnalysis('');
                  // Don't proceed with MHR access
                }}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                ❌ Cancel
              </button>

              <button
                onClick={() => {
                  // Close confirmation modal and proceed with MHR access
                  setShowConfirmationModal(false);
                  
                  // Continue with the original MHR workflow
                  if (window.closeMedicareModal) {
                    window.closeMedicareModal();
                    window.closeMedicareModal = null;
                  }
                  
                  // Clear the transcript states (keep analysis for records)
                  // setPatientDetailsTranscript(''); // Keep for now, could clear later
                  // setMedicareCardAnalysis(''); // Keep for now, could clear later
                }}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
                }}
              >
                ✅ Confirm & Authorize MHR Access
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>

      {/* Vital Sign Chart Modal */}
      {renderVitalChart()}
    </div>
  );
};


export default VideoFrameAnalyzer;
